import type { ResolvedAppLocale } from "./locale";

const EN_MESSAGES = {
  "guestShare.guestName": "{name} is a guest",
  "guestShare.waiting": "{count} guest messages waiting",
  "guestShare.dismissFrom": "Dismiss message from {name}",
  "guestShare.sendFrom": "Send message from {name}",
  "permissions.changedFor": "Permissions have changed for {label}",
  "migration.restoringThreads": "Restoring your threads…",
  "migration.restoringDescription":
    "Migrating {count} threads from the previous version. You can keep working while this finishes.",

  "chat.queuedEdit.threadSuffix": " in “{title}”",
  "chat.queuedEdit.recovered": "Your unsaved edit{where} was kept in the composer.",
  "chat.queuedEdit.discarded": "Your unsaved edit{where} was discarded.",
  "addProjectToMachine.description":
    "Clone {project} and bring the local files git does not track.",
  "addProjectToMachine.fileOne": "+ {count} file",
  "addProjectToMachine.fileMany": "+ {count} files",
  "addProjectToMachine.folderOn": "Folder on {machine}",
  "addProjectToMachine.selectedFiles": "{selected} of {total}",
  "addProjectToMachine.truncated": "Showing the first {count} files.",
  "addProjectToMachine.cloneTo": "Clone to {machine}",
  "branchToolbar.previousWorktreeBranch": "Previous worktree ({branch})",
  "connectAgent.title": "Connect {client}",
  "connectAgent.requestDescription":
    "This agent wants to use the threads in every project on {host}.",
  "connectAgent.localWarning":
    "The name is chosen by the agent. Approval returns to {host} on the computer that opened this page. Only approve a sign-in you just started.",
  "connectAgent.remoteWarning":
    "The name is chosen by the agent. Approval gives access to whoever runs {host}. Only approve a sign-in you just started there.",
  "connectAgent.pairingHelp":
    "Create one in Settings → Sharing & access, or run {command} on this machine.",
  "connectProfile.linkDateUnavailable": "Link date unavailable",
  "connectProfile.linkedAt": "Linked {date}",
  "connectProfile.confirmDeregister": "Confirm deregistration of {name}",
  "connectProfile.removeDescription": "“{name}” will be removed from this account.",
  "time.relative.justNow": "just now",
  "time.relative.minutesAgo": "{count}m ago",
  "time.relative.hoursAgo": "{count}h ago",
  "time.relative.daysAgo": "{count}d ago",
  "chat.queuedEdit.tooManyAttachments": "A message can have at most {count} attachments.",
  "pairing.hosted.retryError":
    "{error} If the backend accepted this one-time token, request a new pairing link before retrying.",
  "pullRequests.comments.olderConversations": "Show {count} older conversations ({hidden} hidden)",
  "pullRequests.comments.recentConversations": "Show only {count} recent conversations",
  "pullRequests.comments.replyOne": "{count} reply",
  "pullRequests.comments.replyMany": "{count} replies",
  "pullRequests.comments.moreReplyOne": "{count} more reply",
  "pullRequests.comments.moreReplyMany": "{count} more replies",
  "pullRequests.comments.moreReplies": "More replies",
  "pullRequests.comments.viewOnHost": "View on host",
  "pullRequests.handoff.checkoutUnconfirmed": "Checked out, but not confirmed on the latest commit",
  "pullRequests.handoff.checkoutUnconfirmedDescription":
    "This checkout could not be confirmed at the pull request's latest commit, so its code may differ. Local commits or uncommitted work keep a checkout where it is.",
  "pullRequests.detail.copied": "{label} copied",
  "pullRequests.code.reviewContext": "PR #{number} review",
  "pullRequests.guide.removedDiffLines": "removed code, showing {shown} of {total} diff lines",
  "pullRequests.guide.diffLines": "showing {shown} of {total} diff lines",
  "pullRequests.diff.openOnProvider": "Open on {provider}",
  "pullRequests.diff.technicalDetails": "Technical details",
  "pullRequests.diff.rateLimitTitle": "{provider} rate limit reached",
  "pullRequests.diff.rateLimitDescription":
    "{provider} is temporarily limiting requests, so this diff couldn't be loaded. Try again after the limit resets, or view the changes on {provider}.",
  "pullRequests.diff.unavailableTitle": "Couldn't load the changes",
  "pullRequests.diff.unavailableDescription":
    "The environment couldn't load this pull request's diff. Try again, or view the changes on {provider}.",
  "providers.status.signInUnchecked": "Installed · Sign-in not checked",
  "providerUpdate.description.manualOne":
    "Update {providers} with the app or tool that installed it.",
  "providerUpdate.description.manualMany":
    "Update {providers} with the app or tool that installed them.",
  "draftHero.questionShort": "What should we build?",
  "draftHero.questionPrefix": "What should we build in",
  "draftHero.questionSuffix": "?",
  "pullRequests.guide.fileOne": "{count} file",
  "pullRequests.guide.fileMany": "{count} files",
  "pullRequests.guide.noteOne": "{count} note",
  "pullRequests.guide.noteMany": "{count} notes",
  "pullRequests.guide.loadingFile": "Loading {path}...",
  "pullRequests.guide.linesMissing": "lines {start}-{end} are not in the loaded diff.",
  "pullRequests.handoff.checkoutThreadMissing":
    "The checkout is ready on `{branch}`. Point a thread at it from the branch picker, then ask again.",

  "pullRequests.checks.showLess": "Show less",
  "pullRequests.checks.showAll": "Show all",
  "pullRequests.checks.openAria": "Open checks: {status}",
  "pullRequests.checks.failedCount": "{count} failed",
  "pullRequests.comments.filterAria": "Filter comments",
  "pullRequests.comments.hidden": "{count} hidden",
  "pullRequests.comments.onlyHidden": "The only comment is hidden by the filter.",
  "pullRequests.comments.allHidden": "All {count} comments are hidden by the filter.",
  "pullRequests.detail.stackedOn": "Stacked on {branch}",
  "pullRequests.detail.openOnHostAria": "Open pull request #{number} on host",
  "pullRequests.detail.notFound": "Pull request #{number} not found",
  "pullRequests.detail.copyFailed": "Failed to copy {label}",
  "pullRequests.branch.deleteTitle": "Delete branch {branch}?",
  "pullRequests.branch.deleteDescription":
    "This deletes {branch} from {repository}. Local branches and worktrees are not changed.",
  "pullRequests.speed.aria": "Quick actions for pull request #{number}",
  "pullRequests.speed.immediately": "{action} immediately",
  "pullRequests.speed.closedPartial": "Closed {closed} of {total} pull requests",
  "pullRequests.speed.closedOne": "Closed {count} pull request",
  "pullRequests.speed.closedMany": "Closed {count} pull requests",
  "pullRequests.guide.openFile": "Open {path} in Code",
  "pullRequests.guide.stopRead": "Stop {number}, read",
  "pullRequests.guide.next": "Next: {title}",
  "pullRequests.guide.readCount": "{read} of {total} read",
  "pullRequests.guide.stopCount": "Stop {index} of {total}",
  "appearance.themeEditor.family.searchMatches": "Search matches",
  "appearance.themeEditor.family.currentMatch": "Current match",

  "newProject.repositoryError": "Could not create the {provider} repository",
  "newProject.publishedRepository": "Published to {provider}",
  "newProject.createRepositoryOn": "Create private repository on {provider}",
  "newProject.providerAccount": "Your {provider} account",
  "newProject.createWithShortcut": "Create ({shortcut})",

  "newProject.openError": "Failed to open project",
  "sidebar.working": "Working ({count})",
  "composer.menu.findingPullRequest": "Finding pull request...",
  "composer.messageLabel": "Message",
  "composer.menu.label.paths": "Files and folders",
  "composer.menu.label.pullRequests": "Pull requests",
  "composer.menu.label.commands": "Commands",
  "composer.menu.label.skills": "Skills",
  "composer.traits.ultrafastModeOn": "Ultrafast mode on",
  "composer.traits.fastModeOn": "Fast mode on",
  "composer.traits.ultrafast": "Ultrafast",
  "mobileClients.emptyDescription":
    "Install T3 Code on your phone and sign in to T3 Connect to get push notifications and Live Activities.",
  "mobileClients.description": "Mobile devices that get notifications from your environments.",
  "appearance.fontPreview.prompt": "Prompt font preview",
  "settings.workingShelf.name": "Working section",
  "settings.workingShelf.title": "Working section (beta)",
  "settings.workingShelf.description":
    "Fold working and monitoring threads into a Working section. They return to the top of the inbox when they need you.",
  "settings.restartContinuation.description":
    "Automatically resume interrupted threads after an update, crash, or machine restart on the selected environments.",
  "providerUpdate.pill.updatingOne": "Updating {provider}",
  "providerUpdate.pill.updatingMany": "Updating {count} providers",
  "providerUpdate.pill.progress": "{providers} update in progress.",
  "providerUpdate.pill.failedOne": "{provider} update failed",
  "providerUpdate.pill.failedMany": "{count} provider updates failed",
  "providerUpdate.pill.failedDetail":
    "{providers} failed to update. Check provider settings for details.",
  "providerUpdate.pill.unchangedOne": "{provider} still needs an update",
  "providerUpdate.pill.unchangedMany": "{count} providers still need updates",
  "providerUpdate.pill.unchangedDetail":
    "{providers} still appear outdated. Review provider settings for details.",
  "providerUpdate.pill.updatedOne": "{provider} updated",
  "providerUpdate.pill.updatedVersion": "{provider} updated: {version}",
  "providerUpdate.pill.updatedMany": "{count} providers updated",
  "providerUpdate.pill.updatedDetail": "New sessions will use the updated provider.",
  "providerUpdate.pill.dismiss": "Dismiss provider update notice",
  "providerUpdate.pill.dismissDetail": "Dismiss until provider status changes",
  "providerUpdate.action.updating": "Updating…",
  "providerUpdate.action.updateAll": "Update all",
  "providerUpdate.run.failedFallback": "Provider update failed.",
  "providerUpdate.run.unfinished": "Provider update did not finish.",
  "providerUpdate.run.updated": "Provider updated",
  "providerUpdate.run.updatedMany": "{count} providers updated",
  "providerUpdate.run.partialFailure": "{failed} of {total} provider updates failed",
  "providerUpdate.run.failed": "Provider update failed",
  "providerUpdate.run.failedMany": "Provider updates failed",
  "providerUpdate.run.descriptionSingle": "New sessions will use the updated provider.",
  "providerUpdate.run.descriptionMany": "New sessions will use the updated providers.",
  "draftHero.noProject": "No project",
  "draftHero.startWithoutProject": "or start without a project",
  "home.heroDescriptionWithScratch": "Add a project, or start without one.",
  "home.startWithoutProject": "Start without a project",
  "newProject.noProject": "No project",
  "newProject.newThreadWithoutProject": "New thread without a project",
  "newProject.description": "Start a new Git repository from a name",
  "newProject.environments": "Environments",
  "newProject.addExisting": "Add existing project",
  "newProject.addExistingDescription": "Open a folder or clone a repository",
  "newProject.options": "Options",
  "newProject.createGitHub": "Create private repository on GitHub",
  "newProject.gitHubAccount": "Your GitHub account",
  "newProject.create": "Create",
  "newProject.creating": "Creating",
  "newProject.createShortcut": "Create (Enter)",
  "newProject.toggle": "Toggle",
  "newProject.createsPath": "Creates {path}",
  "newProject.goesInPath": "Goes in {path}",
  "newProject.onEnvironment": " on {environment}",
  "newProject.restartAgent": "Restart agent session",
  "newProject.agentWillRestart": "Agent session will restart",
  "newProject.agentWillRestartDescription": "Your next message starts a fresh session.",
  "newProject.gitHubError": "Could not create the GitHub repository",
  "newProject.gitHubErrorDescription":
    "{error} Use Publish Repository in the Git menu to try again.",
  "newProject.publishedGitHub": "Published to GitHub",
  "newProject.createError": "Could not create the project",
  "newProject.created": "Created {name}",
  "newProject.createdWithoutCommit": "Created {name} without a first commit",
  "newProject.commitErrorDescription": "{error} The project is in {path}.",
  "newProject.openErrorDescription":
    "{error} It will appear in the sidebar once this client catches up.",
  "newProject.scratchError": "Could not start without a project",
  "keybindings.command.chatNewWithoutProject": "Chat: New Without Project",
  "upstream.comments.older": "Show {count} older comments ({hidden} hidden)",
  "upstream.comments.recent": "Show only {count} recent comments",
  "upstream.comments.olderBots": "Show {count} older bot comments ({hidden} hidden)",
  "upstream.comments.recentBots": "Show only {count} recent bot comments",
  "upstream.comments.bots": "{count} bot comments",
  "upstream.comments.finished": "{count} resolved or dismissed comments",
  "upstream.usage.unpriced": "API estimate excludes {share} unpriced records.",
  "upstream.sidebar.projectFilter": "Filter by {project}",
  "upstream.sidebar.allProjects": "Show all projects",
  "upstream.sidebar.autoSettle": "Auto-settle behavior",
  "upstream.sidebar.enabled": "Enabled",
  "upstream.sidebar.disabled": "Disabled",
  "common.cancel": "Cancel",
  "common.yes": "Yes",
  "common.no": "No",
  "common.confirm": "Confirm",
  "common.confirmAction": "Confirm action",
  "common.confirmationRequired": "This action requires your confirmation.",
  "common.back": "Back",
  "common.retry": "Try again",
  "common.continue": "Continue",
  "common.reload": "Reload app",
  "common.done": "Done",
  "common.close": "Close",
  "common.delete": "Delete",
  "common.undo": "Undo",
  "common.search": "Search",
  "common.clear": "Clear",
  "common.error": "An error occurred.",
  "common.enable": "Enable",
  "common.save": "Save",
  "common.saving": "Saving...",
  "common.edit": "Edit",
  "common.next": "Next",
  "common.dismiss": "Dismiss",
  "common.abort": "Abort",
  "common.none": "None",
  "common.sidebar.toggle": "Toggle main sidebar",
  "common.sidebar.resize": "Resize sidebar",
  "common.sidebar.resizeHint": "Drag to resize sidebar",
  "common.sidebar.mobileTitle": "Sidebar",
  "common.sidebar.mobileDescription": "Displays the mobile sidebar.",
  "quit.holdOrDoublePress": "Hold {shortcut} or press twice to quit",
  "quit.pressAgain": "Press {shortcut} again to quit",

  "settings.title": "Settings",
  "settings.breadcrumb": "Settings breadcrumb",
  "settings.diagnostics": "Diagnostics",
  "settings.section.general": "General",
  "settings.section.appearance": "Appearance",
  "settings.section.keybindings": "Keybindings",
  "settings.section.providers": "Providers",
  "settings.section.integrations": "Integrations",
  "settings.section.sourceControl": "Source Control",
  "settings.section.connections": "Connections",
  "settings.section.archive": "Archive",
  "settings.search.placeholder": "Search",
  "settings.search.label": "Search settings",
  "settings.search.clear": "Clear settings search",
  "settings.search.empty": "No settings found",
  "settings.search.results": "Settings search results",
  "settings.language.title": "Language",
  "settings.language.description":
    "Choose the language used by T3 Code. Follow system uses your browser or operating system language.",
  "settings.language.label": "App language",
  "settings.language.system": "Follow system",
  "settings.language.english": "English",
  "settings.language.chineseSimplified": "Simplified Chinese",
  "settings.item.colorScheme": "Color scheme",
  "settings.item.themes": "Themes",
  "settings.item.appearanceContrast": "Contrast",
  "settings.item.glassOpacity": "Glass opacity",
  "settings.item.environmentIdentification": "Environment identification",
  "settings.item.interfaceFont": "Interface font",
  "settings.item.promptFont": "Prompt font",
  "settings.item.codeFont": "Code font",
  "settings.item.terminalFont": "Terminal font",
  "settings.item.fontSmoothing": "Font smoothing",
  "settings.item.wordWrap": "Word wrap",
  "settings.item.projectGrouping": "Project grouping",
  "settings.item.autoSettleInactiveThreads": "Auto-settle inactive threads",
  "settings.item.autoSettleMergedThreads": "Auto-settle merged threads",
  "settings.item.timeFormat": "Time format",
  "settings.item.hideWhitespaceChanges": "Hide whitespace changes",
  "settings.item.skillsInSlashMenu": "Show skills in slash menu",
  "settings.item.providerUpdateChecks": "Provider update checks",
  "settings.item.newThreads": "New threads",
  "settings.item.startFromOrigin": "Start from origin",
  "settings.item.addProjectStartsIn": "Add project starts in",
  "settings.item.archiveConfirmation": "Archive confirmation",
  "settings.item.unpinConfirmation": "Unpin confirmation",
  "settings.item.deleteConfirmation": "Delete confirmation",
  "settings.item.quitConfirmation": "Hold to quit",
  "settings.item.textGenerationModel": "Text generation model",
  "settings.item.diagnostics": "Diagnostics",
  "settings.item.legacyPlanMode": "Plan mode (legacy)",
  "settings.item.legacyTokenStreaming": "Stream token by token (legacy)",
  "settings.item.legacySidebar": "Sidebar (legacy)",
  "settings.item.keybindings": "Keybindings",
  "settings.item.providers": "Providers",
  "settings.item.agentBrowserAccess": "Agent browser access",
  "settings.item.browserDefaultViewport": "Default browser viewport",
  "settings.item.browserDefaultZoom": "Default browser zoom",
  "settings.item.browserDefaultAppearance": "Default browser appearance",
  "settings.item.browserAutoShowFloatingPreview": "Auto-show floating preview",
  "settings.item.sourceControl": "Source control",
  "settings.item.remoteEnvironments": "Remote environments",
  "settings.item.archivedThreads": "Archived threads",

  "settings.integrations.browser.title": "Browser",
  "settings.sharedMismatch.description":
    "Settings differ on {environments}. Thread and source control preferences are meant to match on every environment.",
  "settings.sharedMismatch.apply": "Apply to all",
  "settings.integrations.agentBrowserAccess.description":
    "Let agents open and drive the preview browser. When off, the browser tools and the instructions describing them are withheld from agent sessions. Your own browser panel is unaffected.",
  "settings.integrations.agentBrowserAccess.status":
    "Applies to sessions started from now on; a running agent keeps the tools it was given.",
  "settings.integrations.agentBrowserAccess.aria": "Allow agent browser access",
  "settings.integrations.browserDefaultViewport.description":
    "The viewport a browser tab opens at, for both you and agents. Fill sizes the page to the panel; any other choice opens the device toolbar at that size.",
  "settings.integrations.browserDefaultViewport.fill": "Fill panel",
  "settings.integrations.browserDefaultViewport.responsive": "Responsive",
  "settings.integrations.browserDefaultViewport.standard": "Standard",
  "settings.integrations.browserDefaultViewport.width": "Default viewport width",
  "settings.integrations.browserDefaultViewport.height": "Default viewport height",
  "settings.integrations.browserDefaultViewport.rotateTo": "Rotate to {orientation}",
  "settings.integrations.browserDefaultViewport.orientation.landscape": "landscape",
  "settings.integrations.browserDefaultViewport.orientation.portrait": "portrait",
  "settings.integrations.browserDefaultViewport.rotate": "Rotate",
  "settings.integrations.browserDefaultZoom.description": "Page zoom applied to new browser tabs.",
  "settings.integrations.browserDefaultAppearance.description":
    "The color scheme pages are told to prefer. System follows your OS setting.",
  "settings.integrations.browserDefaultAppearance.system": "System",
  "settings.integrations.browserDefaultAppearance.light": "Light",
  "settings.integrations.browserDefaultAppearance.dark": "Dark",
  "settings.integrations.browserAutoShowFloatingPreview.description":
    "Pop the floating preview into view when an agent opens a browser. An agent that explicitly asks to show or hide its preview still gets what it asked for.",
  "settings.integrations.desktopOnly": "Only available in the desktop app.",
  "settings.integrations.profiles.add": "Add profile",
  "settings.integrations.profiles.new": "New profile",
  "settings.integrations.profiles.blank": "Blank profile",
  "settings.integrations.profiles.limit": "You’ve reached the profile limit",
  "settings.integrations.profiles.importFrom": "Import from",
  "settings.integrations.profiles.looking": "Looking for browsers…",
  "settings.integrations.profiles.noneSupported": "No supported browsers found",
  "settings.integrations.profiles.connectToImport": "Connect to an environment to import cookies",
  "settings.integrations.profiles.rename": "Rename {profile}",
  "settings.integrations.profiles.default": "Default",
  "settings.integrations.profiles.options": "{profile} options",
  "settings.integrations.profiles.setDefault": "Set as default",
  "settings.integrations.profiles.clear": "Clear cookies and cache",
  "settings.integrations.profiles.removeData": "Remove profile and data",
  "settings.integrations.profiles.connectToClear":
    "Connect to an environment to clear profile data",
  "settings.integrations.profiles.checkingEnvironments": "Checking environments…",
  "settings.integrations.profiles.clearFailed": "Could not clear {profile}’s data",
  "settings.integrations.profiles.clearSuccess": "Cleared {profile}’s cookies and cache",
  "settings.integrations.profiles.connectFirst": "You’re not connected to a server yet.",
  "settings.integrations.profiles.connectBeforeRemove":
    "Connect to an environment before removing this profile.",
  "settings.integrations.profiles.deleteFailed": "Profile data could not be deleted. Try again.",
  "settings.integrations.profiles.removeTitle": "Remove “{profile}”?",
  "settings.integrations.profiles.removeDescription":
    "Its cookies and logins are deleted. Tabs already open in this profile stay open until you close them.",
  "settings.integrations.profiles.connectToRemove":
    "Connect to an environment to remove this profile and its data.",
  "settings.integrations.profiles.removing": "Removing…",
  "settings.integrations.profiles.remove": "Remove profile",
  "browserImport.targetUnavailable":
    "That profile is no longer available. Choose where to import these cookies.",
  "browserImport.profileLimit":
    "You've reached the profile limit. Choose an existing profile to import into.",
  "browserImport.quit.title": "Quit {browser} to import",
  "browserImport.quit.description":
    "{browser} is open, so its cookies can’t be read yet. Quit it, then continue.",
  "browserImport.quit.confirm": "I’ve quit it",
  "browserImport.cookies.none": "No cookies",
  "browserImport.cookies.one": "{count} cookie",
  "browserImport.cookies.many": "{count} cookies",
  "browserImport.configure.title": "Import from {browser}",
  "browserImport.configure.description": "Choose which cookies to import for {environment}.",
  "browserImport.from": "From",
  "browserImport.into": "Into",
  "browserImport.newProfile": "New profile",
  "browserImport.newProfile.description": "Created for these cookies",
  "browserImport.existingProfile": "Existing profile",
  "browserImport.import": "Import",
  "browserImport.importing.title": "Importing cookies",
  "browserImport.importing.description": "This may take a moment.",
  "browserImport.importing.progress": "Importing…",
  "browserImport.checking.title": "Checking {browser}",
  "browserImport.checking.description": "Checking whether the browser has closed.",
  "browserImport.checking.progress": "Checking…",
  "browserImport.done.importedTitle": "Imported {count}",
  "browserImport.done.skippedTitle": "Skipped {count}",
  "browserImport.done.emptyTitle": "No cookies found",
  "browserImport.done.importedDescription": "Added to {profile} for {environment}.{skipped}",
  "browserImport.done.skippedSuffix": " {count} skipped.",
  "browserImport.done.noneImported": "No cookies were imported for {environment}.",
  "browserImport.done.noCookies": "There were no cookies to import for {environment}.",
  "browserImport.done.skipped": "Skipped",
  "browserImport.blocked.title": "Couldn’t import from {browser}",
  "usageSource.add.title": "Add a CLIProxyAPI hub",
  "usageSource.add.description":
    "Show the quota of every account the hub pools, next to the providers on {environment}. The key stays on that server.",
  "usageSource.add.url": "Hub URL",
  "usageSource.add.managementKey": "Management key",
  "usageSource.add.label": "Label (optional)",
  "usageSource.add.labelPlaceholder": "Defaults to the hub's host name",
  "usageSource.add.action": "Add hub",
  "usageSource.empty": "No usage providers configured.",
  "usageSource.disabled": "Disabled",
  "usageSource.remove.title": "Remove {label}?",
  "usageSource.remove.description":
    "The hub's management key is deleted from this server. Its accounts leave the Limits view; the hub itself is untouched. Add it again with the URL and key to bring them back.",
  "usageSource.remove.action": "Remove hub",
  "environmentIcon.label": "Environment icon",
  "environmentIcon.automatic": "Automatic",
  "environmentIcon.automaticDetected": "Automatic ({icon})",
  "projectIconPicker.title": "Choose project icon",
  "projectIconPicker.description": "Pick any Lucide icon and color, or use an emoji.",
  "projectIconPicker.type": "Icon type",
  "projectIconPicker.icons": "Icons",
  "projectIconPicker.emoji": "Emoji",
  "projectIconPicker.color": "Icon color",
  "projectIconPicker.search": "Search Lucide icons",
  "projectIconPicker.searchPlaceholder": "Search all Lucide icons",
  "projectIconPicker.empty": "No icons found.",
  "projectIconPicker.pasteAny": "Or paste any emoji",
  "projectIconPicker.customEmoji": "Custom emoji",
  "projectIconPicker.pasteEmoji": "Paste an emoji",
  "projectIconPicker.save": "Save icon",

  "settings.restoreDefaults": "Restore defaults",
  "settings.restore.confirmTitle": "Restore default settings?",
  "settings.restore.confirmDescription": "This will reset: {settings}.",
  "settings.resetSetting": "Reset {setting} to default",
  "settings.resetDefault": "Reset to default",
  "settings.policyDetails": "Background policy details",
  "settings.general.description.projectGrouping":
    "Combine matching repositories across environments.",
  "settings.general.description.autoSettleMerged":
    "Settle a thread when its pull request merges. Closed pull requests still settle automatically.",
  "settings.general.description.autoSettleInactive":
    "Sidebar threads with no activity for this long settle automatically.",
  "settings.general.autoSettleDays.title": "Days of inactivity before auto-settle",
  "settings.general.description.autoSettleDays":
    "Any new activity un-settles a thread automatically.",
  "settings.general.description.timeFormat":
    "System default follows your browser or OS clock preference.",
  "settings.general.timeFormat.system": "System default",
  "settings.general.timeFormat.12Hour": "12-hour",
  "settings.general.timeFormat.24Hour": "24-hour",
  "settings.general.description.hideWhitespace":
    "Set whether the diff panel ignores whitespace-only edits by default.",
  "settings.general.description.skillsInSlashMenu":
    "Also include skills in the / command menu. Skills always appear when you type $.",
  "settings.general.description.providerUpdateChecks":
    "Check installed provider CLIs for newer available versions.",
  "settings.general.background.title": "Background activity",
  "settings.general.background.profile.balanced": "Balanced",
  "settings.general.background.profile.performance": "Performance",
  "settings.general.background.profile.batterySaver": "Battery saver",
  "settings.general.background.profile.advanced": "Advanced",
  "settings.general.background.description.balanced":
    "Pauses background probes when clients are idle, the host is locked, or low power mode is active.",
  "settings.general.background.description.performance":
    "Allows scoped background probes while any subscribed client remains connected.",
  "settings.general.background.description.batterySaver":
    "Also pauses background probes when the host or client is on battery.",
  "settings.general.background.description.advanced":
    "Uses custom background intervals with the selected shared power policy.",
  "settings.general.background.currentPolicy": "Current shared policy: {profile}.",
  "settings.general.background.dialog.title": "Background activity",
  "settings.general.background.dialog.description":
    "Tune the shared power policy and the background intervals that feed it.",
  "settings.general.background.sharedPolicy.title": "Shared policy",
  "settings.general.background.sharedPolicy.description":
    "Controls whether background work may run after a subscribed interval fires.",
  "settings.general.background.sharedPolicy.label": "Shared background policy",
  "settings.general.background.gitFetch.title": "Git fetch interval",
  "settings.general.background.gitFetch.description":
    "Refresh remote branch status in the background.",
  "settings.general.background.providerHealth.title": "Provider health interval",
  "settings.general.background.providerHealth.description":
    "Refresh provider availability, versions, auth state, and model metadata.",
  "settings.general.background.hostPower.title": "Host power monitor",
  "settings.general.background.hostPower.description":
    "Poll host power state while clients are active.",
  "settings.general.background.idleHost.title": "Idle host monitor",
  "settings.general.background.idleHost.description":
    "Poll host power state when no foreground client is active.",
  "settings.general.background.pause.hostLocked": "Pause when host is locked",
  "settings.general.background.pause.hostLowPower": "Pause on host low power",
  "settings.general.background.pause.clientLowPower": "Pause on client low power",
  "settings.general.background.pause.onBattery": "Pause on battery",
  "settings.general.background.interval.decrease": "Decrease {name}",
  "settings.general.background.interval.inSeconds": "{name} in seconds",
  "settings.general.background.interval.increase": "Increase {name}",
  "settings.general.background.seconds": "seconds",
  "settings.general.background.resetAll": "Reset all",
  "settings.general.background.configure": "Configure advanced background activity",
  "settings.general.description.newThreads":
    "Pick the default workspace mode for newly created draft threads.",
  "settings.general.threadMode.local": "Local",
  "settings.general.threadMode.newWorktree": "New worktree",
  "settings.general.description.startFromOrigin":
    "Creates the worktree from the latest matching branch on origin instead of your local branch.",
  "settings.general.description.addProjectStartsIn":
    'Leave empty to use "~/" when the Add Project browser opens.',
  "settings.general.description.unpinConfirmation":
    "Ask before unpinning a thread from the pinned section.",
  "settings.general.description.archiveConfirmation":
    "Require a second click on the inline archive action before a thread is archived.",
  "settings.general.description.deleteConfirmation":
    "Ask before deleting a thread and its chat history.",
  "settings.general.description.quitConfirmation":
    "Require holding the quit shortcut before the desktop app quits. A quick tap shows a hint instead.",
  "settings.general.description.textGenerationModel":
    "Default model for generated text like thread titles and source control content. Source control settings can override it with a dedicated source control writer model.",
  "settings.about.title": "About",
  "settings.about.version": "Version",
  "settings.about.currentVersion": "Current version of the application.",
  "settings.about.updateAvailable": "Update available.",
  "settings.about.viewDiagnostics": "View diagnostics",
  "settings.about.action.download": "Download",
  "settings.about.action.install": "Install",
  "settings.about.action.checking": "Checking…",
  "settings.about.action.downloading": "Downloading…",
  "settings.about.action.upToDate": "Up to Date",
  "settings.about.action.checkForUpdates": "Check for Updates",
  "settings.about.updateTrack.title": "Update track",
  "settings.about.updateTrack.description.desktop":
    "Stable follows full releases. Nightly follows the nightly desktop channel and can switch back to stable immediately.",
  "settings.about.updateTrack.description.hosted": "Switches the hosted app release channel.",
  "settings.about.updateTrack.option.stable": "Stable",
  "settings.about.updateTrack.option.nightly": "Nightly",
  "settings.about.updateTrack.option.latest": "Latest",
  "settings.diagnostics.mode.localTrace": "Local trace file",
  "settings.diagnostics.mode.terminalOnly": "Terminal logs only",
  "settings.diagnostics.modeSentence": "{mode}.",
  "settings.diagnostics.export.combined": "Exporting OTEL to {url}.",
  "settings.diagnostics.export.signals":
    "Exporting OTEL traces to {tracesUrl} and metrics to {metricsUrl}.",
  "settings.diagnostics.export.traces": "Exporting OTEL traces to {url}.",
  "settings.diagnostics.export.metrics": "Exporting OTEL metrics to {url}.",
  "diagnostics.section.resourceMonitor": "Resource monitor",
  "diagnostics.section.hostCollection": "Host & collection",
  "diagnostics.section.resourceTimeline": "Resource timeline",
  "diagnostics.section.liveProcessTree": "Live process tree",
  "diagnostics.section.instrumentedIo": "Instrumented application I/O",
  "diagnostics.section.liveProcesses": "Live Processes",
  "diagnostics.section.resourceHistory": "Resource History",
  "diagnostics.section.traceDiagnostics": "Trace Diagnostics",
  "diagnostics.section.latestFailures": "Latest Failures",
  "diagnostics.section.commonFailures": "Most Common Failures",
  "diagnostics.section.slowestSpans": "Slowest Spans",
  "diagnostics.section.spanLogs": "Span Logs",
  "diagnostics.section.topSpanNames": "Top Span Names",
  "diagnostics.action.refreshTelemetry": "Refresh resource telemetry",
  "diagnostics.action.refreshSnapshot": "Refresh telemetry snapshot",
  "diagnostics.action.refreshHistory": "Refresh resource history",
  "diagnostics.action.refreshProcesses": "Refresh process diagnostics",
  "diagnostics.action.refreshTraces": "Refresh trace diagnostics",
  "diagnostics.action.openLogs": "Open logs folder",
  "diagnostics.action.retryMonitor": "Retry monitor",
  "diagnostics.action.showFullMessage": "Show full message",
  "diagnostics.state.waitingSample": "Waiting for sample",
  "diagnostics.state.checking": "Checking",
  "diagnostics.state.checkedUnavailable": "Checked unavailable",
  "diagnostics.state.checked": "Checked {time}",
  "diagnostics.state.unknown": "Unknown",
  "diagnostics.state.justNow": "just now",
  "diagnostics.state.ago": "{time} ago",
  "diagnostics.state.updated": "Updated {time}",
  "diagnostics.state.loadingProcesses": "Loading live processes...",
  "diagnostics.state.noProcesses": "No live descendant processes found.",
  "diagnostics.state.collectingSamples": "Collecting process resource samples...",
  "diagnostics.state.noSamples": "No process resource samples found for this window.",
  "diagnostics.state.waitingNativeMonitor": "Waiting for the native process monitor.",
  "diagnostics.state.noRetainedSamples": "No retained process samples in this window.",
  "diagnostics.state.noInstrumentedIo": "No instrumented application I/O has been recorded yet.",
  "diagnostics.state.loadingFailures": "Loading failures...",
  "diagnostics.state.noFailures": "No failed spans found.",
  "diagnostics.state.loadingFailureGroups": "Loading failure groups...",
  "diagnostics.state.noRepeatedFailures": "No repeated failures found.",
  "diagnostics.state.loadingSlowSpans": "Loading slow spans...",
  "diagnostics.state.noSpans": "No spans found.",
  "diagnostics.state.loadingLogs": "Loading recent logs...",
  "diagnostics.state.noWarnings": "No warnings or errors found.",
  "diagnostics.state.loadingSpanNames": "Loading span names...",
  "diagnostics.state.noTraceRecords": "No trace records",
  "diagnostics.metric.currentCpu": "Current CPU",
  "diagnostics.metric.residentMemory": "Resident memory",
  "diagnostics.metric.processCount": "Process count",
  "diagnostics.metric.readThroughput": "Read throughput",
  "diagnostics.metric.writeThroughput": "Write throughput",
  "diagnostics.metric.cpuSpeedLimit": "CPU speed limit",
  "diagnostics.metric.childProcesses": "Child Processes",
  "diagnostics.metric.cpu": "CPU",
  "diagnostics.metric.memory": "Memory",
  "diagnostics.metric.serverPid": "Server PID",
  "diagnostics.metric.cpuTime": "CPU Time",
  "diagnostics.metric.samples": "Samples",
  "diagnostics.metric.interval": "Interval",
  "diagnostics.metric.processes": "Processes",
  "diagnostics.metric.spans": "Spans",
  "diagnostics.metric.failures": "Failures",
  "diagnostics.metric.slowSpans": "Slow Spans",
  "diagnostics.metric.parseErrors": "Parse Errors",
  "diagnostics.metric.cpuAverage": "CPU average",
  "diagnostics.metric.ioReads": "I/O reads",
  "diagnostics.metric.ioWrites": "I/O writes",
  "diagnostics.column.process": "Process",
  "diagnostics.column.name": "Name",
  "diagnostics.column.category": "Category",
  "diagnostics.column.command": "Command",
  "diagnostics.column.type": "Type",
  "diagnostics.column.kill": "Kill",
  "diagnostics.column.cpuTime": "CPU Time",
  "diagnostics.column.memory": "Memory",
  "diagnostics.column.peakCpu": "Peak CPU",
  "diagnostics.column.peakMemory": "Peak Mem",
  "diagnostics.column.read": "Read",
  "diagnostics.column.write": "Write",
  "diagnostics.column.readPerSecond": "Read/s",
  "diagnostics.column.writePerSecond": "Write/s",
  "diagnostics.column.readTotal": "Read Total",
  "diagnostics.column.writeTotal": "Write Total",
  "diagnostics.column.samples": "Samples",
  "diagnostics.column.component": "Component",
  "diagnostics.column.operation": "Operation",
  "diagnostics.column.logicalRead": "Logical Read",
  "diagnostics.column.logicalWrite": "Logical Write",
  "diagnostics.column.count": "Count",
  "diagnostics.column.time": "Time",
  "diagnostics.column.span": "Span",
  "diagnostics.column.cause": "Cause",
  "diagnostics.column.duration": "Duration",
  "diagnostics.column.ended": "Ended",
  "diagnostics.column.lastSeen": "Last Seen",
  "diagnostics.column.trace": "Trace",
  "diagnostics.column.level": "Level",
  "diagnostics.column.message": "Message",
  "diagnostics.column.average": "Average",
  "diagnostics.column.current": "Current",
  "diagnostics.column.peak": "Peak",
  "diagnostics.column.max": "Max",
  "diagnostics.resource.systemFootprint": "T3 system footprint",
  "diagnostics.resource.systemDescription":
    "Live native counters for the server, providers, terminals, desktop processes, and the monitor itself.",
  "diagnostics.resource.samplingEvery": "Sampling every {interval}",
  "diagnostics.resource.secondValue": "{value} second",
  "diagnostics.resource.secondsValue": "{value} seconds",
  "diagnostics.resource.observedCpuTime": "{value} observed CPU time",
  "diagnostics.resource.combinedPeaks": "{value} combined process peaks",
  "diagnostics.resource.startsExits": "{starts} starts · {exits} exits",
  "diagnostics.resource.observed": "{value} observed",
  "diagnostics.resource.thermalState": "{state} thermal state",
  "diagnostics.resource.thermalStateUnknown": "Thermal state: unknown",
  "diagnostics.resource.backendAgents": "Backend + agents",
  "diagnostics.resource.desktop": "Desktop",
  "diagnostics.resource.monitorOverhead": "Monitor overhead",
  "diagnostics.resource.hostState": "Host state",
  "diagnostics.resource.hostDisconnected": "Desktop host signals not connected",
  "diagnostics.resource.hostDisconnectedDescription":
    "Power, idle, lock, and thermal state are supplied by the desktop host. Process telemetry remains fully active in this browser session.",
  "diagnostics.resource.collectionHealth": "Collection health",
  "diagnostics.resource.waitingHealth": "Waiting for collector health.",
  "diagnostics.resource.identity": "Identity: PID + start time",
  "diagnostics.resource.logicalBytes": "Logical bytes by operation",
  "diagnostics.resource.instrumentedDescription":
    "Native counters identify which process is reading or writing. These application-level counters identify known T3 operations so process spikes can be correlated with specific persistence and logging paths.",
  "diagnostics.resource.processOne": "{count} process",
  "diagnostics.resource.processMany": "{count} processes",
  "diagnostics.resource.native": "Native",
  "diagnostics.resource.nativeMonitor": "Native process monitor",
  "diagnostics.resource.electronMain": "Electron main process",
  "diagnostics.resource.desktopAvailable": "Available when this page runs inside the desktop app.",
  "diagnostics.resource.desktopOnly": "Desktop only",
  "diagnostics.resource.noErrors": "No reported errors",
  "diagnostics.resource.collectionTime": "Collection time",
  "diagnostics.resource.processScan": "Process scan",
  "diagnostics.resource.retained": "{retained}/{scanned} retained",
  "diagnostics.resource.inaccessible": "Inaccessible",
  "diagnostics.resource.sidecar": "Sidecar",
  "diagnostics.resource.restarts": "Restarts",
  "diagnostics.resource.binaryMissing": "Resource monitor binary was not found for {platform}.",
  "diagnostics.resource.status.starting": "starting",
  "diagnostics.resource.status.healthy": "healthy",
  "diagnostics.resource.status.degraded": "degraded",
  "diagnostics.resource.status.unavailable": "unavailable",
  "diagnostics.resource.status.stopped": "stopped",
  "diagnostics.category.server": "Server",
  "diagnostics.category.serverChild": "Backend child",
  "diagnostics.category.provider": "Provider",
  "diagnostics.category.terminal": "Terminal",
  "diagnostics.category.electronMain": "Electron main",
  "diagnostics.category.renderer": "Renderer",
  "diagnostics.category.gpu": "GPU",
  "diagnostics.category.utility": "Electron utility",
  "diagnostics.category.monitor": "Monitor",
  "diagnostics.category.t3Process": "T3 process",
  "diagnostics.io.storage": "Storage bytes",
  "diagnostics.io.logical": "Logical bytes",
  "diagnostics.io.all": "All I/O bytes",
  "diagnostics.io.unavailable": "Unavailable",
  "diagnostics.tooltip.liveCpu":
    "Total CPU across live child processes of the current server process. The desktop shell and other parent processes are not included.",
  "diagnostics.tooltip.liveMemory":
    "Total resident memory across live child processes of the current server process. The desktop shell and other parent processes are not included.",
  "diagnostics.tooltip.cpuTime":
    "Approximate active CPU time for the T3 server root process and its descendants during the selected window.",
  "diagnostics.tooltip.samples":
    "In-memory process samples retained by the server. This resets when the server restarts.",
  "diagnostics.tooltip.details": "{label} details",
  "diagnostics.tooltip.cpuBucket": "Average CPU {average}%, peak CPU {peak}%",
  "diagnostics.tooltip.sendSignal": "Send {signal}",
  "diagnostics.signal.confirmKill":
    "Send SIGKILL to process {pid}? This cannot be handled by the process.",
  "diagnostics.signal.confirmFailed": "Could not confirm signal",
  "diagnostics.signal.sendFailed": "Failed to send {signal}.",
  "diagnostics.signal.sendProcessFailed": "Failed to send {signal} to process {pid}.",
  "diagnostics.signal.sendFailedTitle": "Could not send {signal}",
  "diagnostics.signal.processExited": "Process already exited",
  "diagnostics.signal.processExitedDescription":
    "The process is not a child of the T3 Server. It might already have exited.",
  "diagnostics.logs.noEditors": "No available editors found.",
  "diagnostics.logs.noEnvironment": "No environment is selected.",
  "diagnostics.logs.openFailed": "Unable to open logs folder.",
  "diagnostics.tooltip.slowSpans": "Spans at or above the configured slow-span threshold.",
  "diagnostics.tooltip.slowSpansValue": "Spans with a duration of {duration} or longer.",
  "diagnostics.warning.partialTrace":
    "Some trace files could not be read, so diagnostics may be incomplete. {error}",
  "settings.legacy.title": "Legacy features",
  "settings.legacy.planModeDescription":
    "Brings back the Build/Plan toggle in the composer along with the /plan and /default commands and the Shift+Tab shortcut. While off, every thread runs in build mode.",
  "settings.legacy.tokenStreamingDescription":
    "Paints assistant output token by token instead of in complete chunks. Not recommended: it is significantly slower, and long responses become harder to follow. Kept only for compatibility with the old behavior.",
  "settings.legacy.tokenStreamingConfirmTitle": "Turn on token-by-token output?",
  "settings.legacy.tokenStreamingConfirmDescription":
    "It is significantly slower than the default buffered output and hurts the reading experience. This switch exists only for backwards compatibility.",
  "settings.legacy.sidebarDescription":
    "Brings back the original sidebar with per-project thread trees. The default sidebar shows one flat list: active work as rich cards, settled threads as compact rows.",

  "appearance.description": "Choose how T3 Code looks. Use a built-in theme or make your own.",
  "appearance.colorScheme": "Color scheme",
  "appearance.mode.group": "Appearance mode",
  "appearance.mode.system": "System",
  "appearance.mode.light": "Light",
  "appearance.mode.dark": "Dark",
  "appearance.mode.followSystem": "Follow the system appearance",
  "appearance.mode.use": "Use {mode} mode",
  "appearance.themes": "Themes",
  "appearance.createTheme": "Create theme",
  "appearance.importTheme": "Import theme",
  "appearance.glassOpacity.description":
    "Control how transparent glass surfaces are. Higher values make menus, dialogs, and the composer more solid.",
  "appearance.glassOpacity.label": "Glass opacity",
  "appearance.contrast.description":
    "Adjust the contrast of colors and borders across the interface.",
  "appearance.environment.description": "Choose how Dev and Nightly environments are identified.",
  "appearance.environment.label": "Environment identification",
  "appearance.environment.artwork": "Artwork",
  "appearance.environment.pill": "Label",
  "appearance.environment.none": "None",
  "appearance.typography.title": "Typography",
  "appearance.typography.advanced": "Advanced",
  "appearance.typography.showAdvanced": "Show advanced typography settings",
  "appearance.font.interface.description": "Everything outside code blocks and the terminal.",
  "appearance.font.prompt.description": "Only the box you write prompts in. Mono works well here.",
  "appearance.font.code.description": "Code blocks, diffs, and file previews.",
  "appearance.font.terminal.description":
    "Terminal output, independent from code blocks and diffs.",
  "appearance.font.monospace.title": "Monospace font",
  "appearance.font.monospace.description": "Code blocks, diffs, file previews, and the terminal.",
  "appearance.font.size.interface": "Interface font size",
  "appearance.font.size.prompt": "Prompt font size",
  "appearance.font.size.code": "Code font size",
  "appearance.font.size.terminal": "Terminal font size",
  "appearance.font.smoothing.description":
    "Render text with thinner grayscale anti-aliasing instead of macOS's heavier default.",
  "appearance.font.smoothing.label": "Font smoothing",
  "appearance.wordWrap.description":
    "Wrap long lines in code blocks, tables, diffs, and file previews by default.",
  "appearance.wordWrap.label": "Wrap code, tables, diffs, and file previews by default",
  "appearance.fontPicker.default": "default",
  "appearance.fontPicker.search": "Search fonts...",
  "appearance.fontPicker.empty": "No fonts found.",
  "appearance.fontPreview.terminal": "Terminal font preview",
  "appearance.fontPreview.sample":
    "Use $frontend-design to fix the flaky test in [surface.test.ts](apps/web/src/terminal/ghostty/surface.test.ts) and align the header with [SettingsPanels.tsx](apps/web/src/components/settings/SettingsPanels.tsx) before shipping.",
  "appearance.theme.duplicate": "Duplicate theme",
  "appearance.theme.use": "Use {theme}",
  "appearance.theme.useActive": "Use {theme}, currently active",
  "appearance.theme.duplicateNamed": "Duplicate {theme}",
  "appearance.theme.edit": "Edit theme",
  "appearance.theme.export": "Export theme file",
  "appearance.theme.remove": "Remove theme",
  "appearance.theme.removeMany": "Remove themes",
  "appearance.theme.useLightOnly": "Use for light mode only",
  "appearance.theme.useDarkOnly": "Use for dark mode only",
  "appearance.theme.variants.group": "Light and dark theme variants",
  "appearance.theme.variants.choose": "Choose {mode} variant, {count} options, currently {theme}",
  "appearance.theme.variants.use": "Use {mode} variant, currently {theme}",
  "appearance.theme.variants.useForMode": "Use {theme} for {mode} mode",
  "appearance.theme.variants.useForModeActive": "Use {theme} for {mode} mode, currently active",
  "appearance.theme.editNamed": "Edit {theme}",
  "appearance.theme.exportNamed": "Export {theme}",
  "appearance.theme.removeNamed": "Remove {theme}",
  "appearance.theme.removeCollectionNamed": "Remove themes from {collection}",
  "appearance.theme.useFirstVariants": "Use the first variants for light and dark",
  "appearance.theme.useBothModes": "Use for both light and dark",
  "appearance.theme.useModeOnly": "Use for {mode} mode only",
  "appearance.theme.toast.selectionFailed": "Couldn’t save theme selection",
  "appearance.theme.toast.removeFailed": "Couldn’t remove theme",
  "appearance.theme.toast.restoreFailed": "Couldn’t restore theme settings",
  "appearance.theme.cancel": "Cancel",
  "appearance.theme.removeSelected": "Remove selected",
  "appearance.theme.removeSelectedCount": "Remove selected ({count})",
  "appearance.theme.removeTitle": "Remove “{theme}”?",
  "appearance.theme.removeCollectionTitle": "Remove themes from “{collection}”?",
  "appearance.theme.removeDescription": "You can bring it back anytime by importing its JSON file.",
  "appearance.theme.removeCollectionDescription":
    "Select the variants you want to remove. You can restore them by importing the extension again.",
  "appearance.theme.import.addTitle": "Add a theme",
  "appearance.theme.import.orFile": "or import a file",
  "appearance.theme.import.chooseFiles": "Choose files",
  "appearance.theme.import.reading": "Reading...",
  "appearance.theme.import.json": "Theme JSON",
  "appearance.theme.import.installed": "Already installed",
  "appearance.theme.import.updateExisting": "Update existing",
  "appearance.theme.import.keepBoth": "Keep both",
  "appearance.theme.import.back": "Back",
  "appearance.theme.import.file": "Theme file",
  "appearance.theme.import.dropFiles": "Drop T3 Code or VS Code .json files",
  "appearance.theme.import.add": "Add theme",
  "appearance.theme.search.title": "Search community themes",
  "appearance.theme.search.description": "Find open-source themes from Open VSX.",
  "appearance.theme.search.label": "Search Open VSX themes",
  "appearance.theme.search.placeholder": "try dracula, nord, catppuccin...",
  "appearance.theme.search.action": "Search",
  "appearance.theme.search.popular": "Popular",
  "appearance.theme.search.sort": "Sort",
  "appearance.theme.search.sortLabel": "Sort themes",
  "appearance.theme.search.sort.downloaded": "Most downloaded",
  "appearance.theme.search.sort.rated": "Best rated",
  "appearance.theme.search.sort.newest": "Newest",
  "appearance.theme.search.sort.relevant": "Most relevant",
  "appearance.theme.search.finding": "Finding themes...",
  "appearance.theme.search.empty": "No supported open-source themes found",
  "appearance.theme.search.tryBroader": "Try a broader search.",
  "appearance.theme.search.downloads": "{count} downloads",
  "appearance.theme.search.fallbackDescription": "A community color theme for your editor.",
  "appearance.theme.search.source": "Source",
  "appearance.theme.search.add": "Add",
  "appearance.theme.search.adding": "Adding",
  "appearance.theme.search.update": "Update",
  "appearance.theme.search.updating": "Updating",
  "appearance.theme.search.updateTitle": "Update “{theme}”?",
  "appearance.theme.search.updateDescription":
    "This replaces its installed variants, including any local edits. Variants no longer in the extension will be removed.",
  "appearance.theme.search.updateAction": "Update theme",
  "appearance.themeEditor.createTitle": "Create theme",
  "appearance.themeEditor.editTitle": "Edit theme",
  "appearance.themeEditor.selectColor": "Select a color below",
  "appearance.themeEditor.selectElement": "Select an element · Esc to cancel",
  "appearance.themeEditor.usageOne": "{label} · {count} use",
  "appearance.themeEditor.usageMany": "{label} · {count} uses",
  "appearance.themeEditor.inspect": "Inspect",
  "appearance.themeEditor.inspectColors": "Inspect app colors",
  "appearance.themeEditor.cancelInspect": "Cancel inspecting app colors",
  "appearance.themeEditor.pickColor": "Pick a color from the app",
  "appearance.themeEditor.cancelSelection": "Cancel and clear the selection",
  "appearance.themeEditor.expand": "Expand the theme editor",
  "appearance.themeEditor.minimize": "Minimize the theme editor",
  "appearance.themeEditor.close": "Close the theme editor",
  "appearance.themeEditor.name": "Theme name",
  "appearance.themeEditor.namePlaceholder": "e.g. Aurora",
  "appearance.themeEditor.appearance": "Appearance",
  "appearance.themeEditor.appearanceGroup": "Theme appearance",
  "appearance.themeEditor.light": "Light",
  "appearance.themeEditor.dark": "Dark",
  "appearance.themeEditor.colors": "Colors",
  "appearance.themeEditor.derived": "Two colors, rest derived",
  "appearance.themeEditor.filter": "Filter colors",
  "appearance.themeEditor.advanced": "Advanced",
  "appearance.themeEditor.useAdvanced": "Use advanced theme colors",
  "appearance.themeEditor.background": "Background",
  "appearance.themeEditor.accent": "Accent",
  "appearance.themeEditor.mainColors": "Main colors",
  "appearance.themeEditor.foundationColors": "Foundation",
  "appearance.themeEditor.brandContentColors": "Brand & content",
  "appearance.themeEditor.contextColors": "Context",
  "appearance.themeEditor.statusColors": "Status colors",
  "appearance.themeEditor.otherColors": "Other colors",
  "appearance.themeEditor.family.background": "Background",
  "appearance.themeEditor.family.surface": "Surface",
  "appearance.themeEditor.family.raisedSurface": "Raised surface",
  "appearance.themeEditor.family.overlay": "Overlay",
  "appearance.themeEditor.family.text": "Text",
  "appearance.themeEditor.family.mutedText": "Muted text",
  "appearance.themeEditor.family.border": "Border",
  "appearance.themeEditor.family.input": "Input",
  "appearance.themeEditor.family.subtleSurface": "Subtle surface",
  "appearance.themeEditor.family.highlightSurface": "Highlight surface",
  "appearance.themeEditor.family.accent": "Accent",
  "appearance.themeEditor.family.action": "Action",
  "appearance.themeEditor.family.messageSurface": "Message surface",
  "appearance.themeEditor.family.codeSurface": "Code surface",
  "appearance.themeEditor.family.sidebarBackground": "Sidebar background",
  "appearance.themeEditor.family.sidebarControls": "Sidebar controls",
  "appearance.themeEditor.family.sidebarSelection": "Sidebar selection",
  "appearance.themeEditor.family.terminalBackground": "Terminal background",
  "appearance.themeEditor.family.error": "Error",
  "appearance.themeEditor.family.warning": "Warning",
  "appearance.themeEditor.noMatches": "No matches.",
  "appearance.themeEditor.saveChanges": "Save changes",
  "appearance.themeEditor.merge": "Merge into “{theme}”",
  "appearance.themeEditor.addPalette": "Add {appearance} palette",
  "appearance.themeEditor.color.choose": "Choose a color",
  "appearance.themeEditor.color.chooseNamed": "Choose {label} color",
  "appearance.themeEditor.color.showUsage": "Show {label} usage",
  "appearance.themeEditor.color.hideUsage": "Hide {label} usage",
  "appearance.themeEditor.toast.saveFailed": "Could not save your theme",
  "appearance.themeEditor.toast.storageUnavailable":
    "Browser storage is unavailable, so the change was not kept.",
  "appearance.themeEditor.toast.updated": "{theme} updated",
  "appearance.themeEditor.toast.paletteAdded": "Its {appearance} palette was added.",
  "appearance.themeEditor.toast.saved": "{theme} saved",
  "appearance.themeEditor.toast.changesActive": "Your changes are now active.",
  "appearance.themeEditor.toast.changesSaved": "Your changes are saved.",
  "appearance.themeEditor.toast.created": "{theme} created",
  "appearance.themeEditor.toast.nowActive": "It’s now active.",
  "appearance.panelAnimations.replay": "Replay panel animation preview",

  "archive.loading": "Loading archived threads",
  "archive.loadFailed": "Could not load archived threads",
  "archive.empty": "No archived threads",
  "archive.checking": "Checking connected environments.",
  "archive.emptyDescription": "Archived threads will appear here.",
  "archive.unarchive": "Unarchive",
  "archive.failedUnarchive": "Failed to unarchive thread",
  "archive.failedDelete": "Failed to delete thread",
  "archive.actionFailed": "Archived thread action failed",
  "archive.archivedAgo": "Archived {time}",
  "archive.createdAgo": "Created {time}",
  "archive.justNow": "just now",
  "archive.ago": "{time} ago",

  "providers.title": "Providers",
  "providers.accent.customHex": "Custom hex accent color",
  "providers.accent.chooseFor": "Choose accent color for {provider}",
  "providers.accent.clear": "Clear color",
  "providers.accent.label": "Accent color",
  "providers.healthCheck.title": "Health check interval",
  "providers.healthCheck.description":
    "Refresh provider availability, versions, auth state, and model metadata in the background. Set this to 0 seconds to rely on manual refreshes.",
  "providers.healthCheck.policyTooltip":
    "This interval is configured here, then the shared Background activity policy decides whether provider probes may run when the timer fires. Custom intervals appear as Advanced in General settings.",
  "providers.healthCheck.seconds": "seconds",
  "providers.devices.primary": "Primary device",
  "providers.devices.local": "Local device",
  "providers.devices.remote": "Remote device",
  "providers.lastChecked.unavailable": "Checked unavailable",
  "providers.lastChecked.justNow": "Checked just now",
  "providers.lastChecked.ago": "Checked {time} ago",
  "providers.action.add": "Add provider",
  "providers.action.refresh": "Refresh provider status",
  "providers.action.toggleDetails": "Toggle {provider} details",
  "providers.action.enable": "Enable {provider}",
  "providers.action.delete": "Delete provider instance {instance}",
  "providers.action.deleteShort": "Delete instance",
  "providers.action.copyUpdate": "Copy update command",
  "providers.action.copyCommand": "Copy command",
  "providers.action.toggleEmail": "Toggle account email visibility",
  "providers.action.revealEmail": "Click to reveal email",
  "providers.action.hideEmail": "Click to hide email",
  "providers.add.title": "Add provider instance",
  "providers.add.description":
    "Configure an additional provider instance on {environment}, for example a second Codex install pointed at a different workspace.",
  "providers.add.step.driver": "Driver",
  "providers.add.step.identity": "Identity",
  "providers.add.step.config": "Config",
  "providers.add.step.label": "{step}, step {index}",
  "providers.add.workspace": "{driver} Workspace",
  "providers.add.comingSoon": "Coming Soon",
  "providers.add.label": "Label",
  "providers.add.labelPlaceholder": "e.g. Work",
  "providers.add.labelDescription": "Shown in the provider list. Optional.",
  "providers.add.instanceId": "Instance ID",
  "providers.add.instanceIdDescription":
    "Routing key used by threads and sessions. Letters, digits, '-', or '_'.",
  "providers.add.validation.required": "Instance ID is required.",
  "providers.add.validation.tooLong": "Instance ID must be 64 characters or fewer.",
  "providers.add.validation.pattern":
    "Instance ID must start with a letter and use only letters, digits, '-', or '_'.",
  "providers.add.validation.exists": "An instance named '{id}' already exists.",
  "providers.add.accent": "Accent color",
  "providers.add.accentLabel": "Provider instance accent color",
  "providers.add.useAccent": "Use {color} accent",
  "providers.add.clearAccent": "Clear",
  "providers.add.accentDescription": "Optional marker shown in the picker.",
  "providers.add.noConfig":
    "This driver has no required configuration. You can add the instance now.",
  "providers.add.next": "Next",
  "providers.add.addInstance": "Add instance",
  "providers.add.toast.added": "Provider instance added",
  "providers.add.toast.addedDescription": "{provider} instance '{id}' was added.",
  "providers.add.toast.failed": "Could not add provider instance",
  "providers.add.toast.updateFailed": "Update failed.",
  "providers.config.binaryPath": "Binary path",
  "providers.config.binaryPathDescription": "Path to the {provider} binary used by this instance.",
  "providers.config.codexHome": "CODEX_HOME path",
  "providers.config.codexHomeDescription": "Custom Codex home and config directory.",
  "providers.config.shadowHome": "Shadow home path",
  "providers.config.shadowHomeDescription":
    "Account-specific Codex home. Keeps auth.json separate while sharing state from CODEX_HOME.",
  "providers.config.claudeHome": "CLAUDE_CONFIG_DIR path",
  "providers.config.claudeHomeDescription":
    "Custom Claude home and config directory. Keeps .claude.json and .claude separate.",
  "providers.config.launchArgs": "Launch arguments",
  "providers.config.launchArgsDescription": "Additional CLI arguments passed on session start.",
  "providers.config.codexLaunchArgsDescription":
    "Additional CLI arguments passed to codex app-server on session start.",
  "providers.config.apiEndpoint": "API endpoint",
  "providers.config.apiEndpointDescription": "Override the Cursor API endpoint for this instance.",
  "providers.config.serverUrl": "Server URL",
  "providers.config.serverUrlDescription":
    "Leave blank to let T3 Code spawn the server when needed.",
  "providers.config.serverPassword": "Server password",
  "providers.config.serverPasswordDescription": "Stored in plain text on disk.",
  "providers.config.autoCompactWindow": "Auto-compact after",
  "providers.config.autoCompactWindowDescription":
    "Compact after 100,000 to 1,000,000 tokens. Leave empty to use Claude's default.",
  "providers.config.autoCompactWindowPlaceholder": "e.g. 300000",
  "providers.config.optional": "Optional",
  "providers.unavailable.loading": "Loading provider settings",
  "providers.unavailable.connectFailed": "Could not connect to this device",
  "providers.unavailable.title": "Provider settings are unavailable",
  "providers.unavailable.checkingPermissions": "Checking what this session is allowed to change.",
  "providers.unavailable.waitingConfiguration": "Waiting for {environment}'s configuration.",
  "providers.connection.connecting": "Connecting...",
  "providers.connection.connected": "Connected",
  "providers.connection.disconnected": "Disconnected",
  "providers.connection.reconnecting": "Reconnecting...",
  "providers.status.checking": "Checking provider status",
  "providers.status.waiting":
    "Waiting for the server to report installation and authentication details.",
  "providers.status.disabled": "Disabled",
  "providers.status.disabledDetail":
    "This provider is installed but disabled for new sessions in T3 Code.",
  "providers.status.notFound": "Not found",
  "providers.status.cliNotDetected": "CLI not detected on PATH.",
  "providers.status.authenticated": "Authenticated",
  "providers.status.authenticatedWith": "Authenticated · {method}",
  "providers.status.authenticatedAs": "Authenticated as",
  "providers.status.notAuthenticated": "Not authenticated",
  "providers.status.needsAttention": "Needs attention",
  "providers.status.needsAttentionDetail":
    "The provider is installed, but the server could not fully verify it.",
  "providers.status.unavailable": "Unavailable",
  "providers.status.unavailableDetail": "The provider failed its startup checks.",
  "providers.status.available": "Available",
  "providers.status.availableDetail":
    "Installed and ready, but authentication could not be verified.",
  "providers.status.disabledMessage": "{provider} is disabled in T3 Code settings.",
  "providers.status.cliMissing": "{provider} CLI ({command}) is not installed or not on PATH.",
  "providers.status.versionTooOld":
    "{provider} {current} is too old for {model}. Upgrade to {required} or newer to access it.",
  "providers.badge.earlyAccess": "Early Access",
  "providers.badge.new": "New",
  "providers.update.available": "Update available",
  "providers.update.availableDetails": "Update available - view details",
  "providers.update.updating": "Updating",
  "providers.update.now": "Update now",
  "providers.update.manual": "or, update manually using",
  "providers.update.installVersion": "Update available: install {version}.",
  "providers.update.installLatest": "Update available: install the latest provider version.",
  "providers.update.commandCopied": "{provider} update command copied",
  "providers.update.commandCopiedDescription": "Run it in a terminal when you are ready to update.",
  "providers.update.copyFailed": "Could not copy {provider} update command",
  "providers.instance.displayName": "Display name",
  "providers.instance.placeholder": "Instance label",
  "providers.instance.displayNameDescription": "Optional label shown in the provider list.",
  "providers.instance.accentDescription":
    "Used to distinguish this instance in picker rails and model lists.",
  "providers.instance.accentColor": "Accent color",
  "providers.instance.customAccent": "Choose a custom accent color for {provider}",
  "providers.instance.customAccentHex": "Custom accent color hex value",
  "providers.instance.selectAccent": "Select accent color {color}",
  "providers.instance.clearAccent": "Clear accent color for {provider}",
  "providers.instance.environmentVariables": "Environment variables",
  "providers.instance.addVariable": "Add variable",
  "providers.instance.environmentVariablesDescription":
    "Add variables to pass API keys, base URLs, or other per-instance CLI settings.",
  "providers.instance.sensitiveDescription":
    "Sensitive values are stored separately and are not returned to the app after saving.",
  "providers.instance.variable": "Variable",
  "providers.instance.value": "Value",
  "providers.instance.valuePlaceholder": "Value",
  "providers.instance.redactedValuePlaceholder": "Stored secret - enter a new value to replace",
  "providers.instance.variableName": "Environment variable name {index}",
  "providers.instance.variableValue": "Environment variable value {index}",
  "providers.instance.markSensitive": "Mark environment variable {variable} as sensitive",
  "providers.instance.removeVariable": "Remove environment variable {variable}",
  "providers.instance.sensitive": "Sensitive",
  "providers.instance.sensitiveStored": "Sensitive, stored separately",
  "providers.instance.plainText": "Plain text",
  "providers.instance.options": "Options",
  "providers.instance.configuration": "Configuration",
  "providers.instance.authenticatedAs": "Authenticated as",
  "providers.instance.unknownDriver":
    "This provider driver is not included in the current build. Configuration values are preserved but cannot be edited.",
  "providers.models.title": "Models",
  "providers.models.hidden": "hidden",
  "providers.models.custom": "custom",
  "providers.models.moveUp": "Move up",
  "providers.models.moveDown": "Move down",
  "providers.models.remove": "Remove custom model",
  "providers.models.removeNamed": "Remove custom model {model}",
  "providers.models.available.one": "{count} model available.",
  "providers.models.available.many": "{count} models available.",
  "providers.models.details": "Details for {model}",
  "providers.models.fastMode": "Fast mode",
  "providers.models.thinking": "Thinking",
  "providers.models.reasoning": "Reasoning",
  "providers.models.addFavorite": "Add to favorites",
  "providers.models.removeFavorite": "Remove from favorites",
  "providers.models.addFavoriteNamed": "Add {model} to favorites",
  "providers.models.removeFavoriteNamed": "Remove {model} from favorites",
  "providers.models.moveUpNamed": "Move {model} up",
  "providers.models.moveDownNamed": "Move {model} down",
  "providers.models.show": "Show in picker",
  "providers.models.hide": "Hide from picker",
  "providers.models.showNamed": "Show {model}",
  "providers.models.hideNamed": "Hide {model}",
  "providers.models.add": "Add",
  "providers.models.addCustom": "Add custom model",
  "providers.models.filter": "Filter models",
  "providers.models.customAlwaysShown": "Custom models are always shown in the picker",
  "providers.models.hiddenFromPicker": "Hidden from picker",
  "providers.models.shownInPicker": "Shown in picker",
  "providers.models.count.one": "{count} model",
  "providers.models.count.many": "{count} models",
  "providers.models.count.favorites": "{count} favorites",
  "providers.models.count.hidden": "{count} hidden",
  "providers.models.noMatch": "No models match.",
  "providers.models.notReported": "No models reported for this provider yet.",
  "providers.models.group.favorites": "Favorites",
  "providers.models.group.all": "All",
  "providers.models.empty": "No providers configured.",
  "providers.models.validation.required": "Enter a model slug.",
  "providers.models.validation.builtIn": "That model is already built in.",
  "providers.models.validation.tooLong": "Model slugs must be {count} characters or less.",
  "providers.models.validation.duplicate": "That custom model is already saved.",
  "providers.devices.title": "Devices",
  "providers.devices.loading": "Loading devices",
  "providers.devices.loadingDescription": "Reading connected execution environments.",
  "providers.devices.empty": "No connected devices",
  "providers.devices.emptyDescription":
    "Connect an execution environment before configuring providers.",
  "providers.deviceUnavailable.title": "Device unavailable",
  "providers.deviceUnavailable.description":
    "Reconnect this device to set up its provider, or select another device.",
  "providers.permissions.limited": "Limited permissions",
  "providers.permissions.limitedDescription":
    "This session can view {environment}'s providers, but its credential does not allow changing their configuration.",
  "providers.instance.missing": "This provider instance is no longer available on this device.",
  "providers.setup.aria": "Antigravity setup",
  "providers.setup.runsOn": "Antigravity runs on {environment}.",
  "providers.setup.enableDescription": "Enable it to use it in threads.",
  "providers.setup.enable": "Enable Antigravity",
  "providers.setup.readOnly": "This connection cannot change provider setup.",
  "providers.setup.updateEnvironment":
    "Update this environment to install Antigravity and sign in with Google here.",
  "providers.setup.auth.reading": "Reading sign-in status.",
  "providers.setup.auth.signedIn": "Signed in with Google.",
  "providers.setup.auth.connected": "Connected.",
  "providers.setup.install.downloading": "Downloading {downloaded} MB{total}.",
  "providers.setup.install.ofTotal": " of {total} MB",
  "providers.setup.install.extracting": "Extracting Antigravity.",
  "providers.setup.install.verifying": "Checking the downloaded runtime.",
  "providers.setup.install.installed": "Antigravity is installed.",
  "providers.setup.install.customUnavailable": "The configured Antigravity runtime is unavailable.",
  "providers.setup.install.customUnchecked":
    "The configured Antigravity runtime has not been checked.",
  "providers.setup.install.required": "Install the official Antigravity runtime before signing in.",
  "providers.setup.failed": "Provider setup failed.",
  "providers.setup.failedRetry": "Provider setup failed. Try again.",
  "providers.setup.auth.openFailed":
    "Could not open the sign-in page. Copy the link and open it in your browser.",
  "providers.setup.auth.copyFailed": "Could not copy the sign-in link. Use Open sign-in page.",
  "providers.setup.pending.redirect": "Checking redirect",
  "providers.setup.auth.signOut": "Sign out of Google",
  "providers.setup.auth.disconnect": "Disconnect",
  "providers.setup.auth.signOutConfirm":
    "{action} for {provider} on {environment}? This stops its running threads. Thread history is kept.",
  "providers.setup.pending.signingOut": "Signing out",
  "providers.setup.install.removeConfirm":
    "Remove the downloaded Antigravity runtime from {environment}? Google sign-in and thread history are kept.",
  "providers.setup.pending.removing": "Removing runtime",
  "providers.setup.runtime": "Runtime",
  "providers.setup.install.downloadAria": "Antigravity download",
  "providers.setup.install.customDescription":
    "This instance uses the binary path below. Installing a managed runtime does not change that path.",
  "providers.setup.install.downloadSize": "Downloads {size} MB from Google.",
  "providers.setup.install.unavailable":
    "Automatic installation is unavailable here. Set an existing binary path below or use a supported remote environment.",
  "providers.setup.pending.cancellingInstall": "Cancelling installation",
  "providers.setup.install.cancel": "Cancel installation",
  "providers.setup.pending.startingInstall": "Starting installation",
  "providers.setup.install.update": "Update Antigravity",
  "providers.setup.install.reinstall": "Reinstall Antigravity",
  "providers.setup.install.retry": "Retry installation",
  "providers.setup.install.managed": "Install managed runtime",
  "providers.setup.install.install": "Install Antigravity",
  "providers.setup.install.remove": "Remove downloaded runtime",
  "providers.setup.auth.open": "Open sign-in page",
  "providers.setup.auth.copied": "Link copied",
  "providers.setup.auth.copy": "Copy sign-in link",
  "providers.setup.auth.expiresAt": "Link expires at",
  "providers.setup.auth.callbackDescription":
    "If the final localhost page does not load, paste its full URL here.",
  "providers.setup.auth.otherClient":
    "Sign-in is open in another client. Complete or cancel it there.",
  "providers.setup.pending.cancellingSignIn": "Cancelling sign-in",
  "providers.setup.auth.cancel": "Cancel sign-in",
  "providers.setup.pending.startingSignIn": "Starting sign-in",
  "providers.setup.auth.retryGoogle": "Retry Google sign-in",
  "providers.setup.auth.signInGoogle": "Sign in with Google",
  "providers.setup.auth.retryConnection": "Retry connection",
  "providers.setup.auth.connect": "Connect",
  "providers.setup.retryStatus": "Retry setup status",

  "serverUpdate.stage.downloading": "Downloading…",
  "serverUpdate.stage.restarting": "Restarting…",
  "serverUpdate.status.updating": "Updating {server}",
  "serverUpdate.status.failed": "Could not update {server}",
  "serverUpdate.failure.default": "Server update failed.",
  "serverUpdate.action": "Update",
  "serverUpdate.commandCopied": "Update command copied",
  "serverUpdate.commandCopiedDescription": "Run `{command}` on {server} to update it.",
  "serverUpdate.copyFailed": "Could not copy update command",
  "serverUpdate.failed": "Server update failed",
  "serverUpdate.updated": "{server} updated",
  "serverUpdate.reconnected": "Reconnected on t3@{version}.",
  "serverUpdate.desktopManaged": "Update the desktop app on that machine to update this server.",
  "serverUpdate.copyCommand": "Copy update command",

  "connections.thisEnvironment.title": "This environment",
  "connections.networkAccess.title": "Network access",
  "connections.networkAccess.localOnly":
    "This backend is only reachable on this machine. Restart it with a non-loopback host to enable remote pairing.",
  "connections.networkAccess.remoteConfigured":
    "This backend is already configured for remote access. Network exposure changes must be made where the server is launched.",
  "connections.networkAccess.limited": "Limited to this machine.",
  "connections.networkAccess.loading": "Loading…",
  "connections.networkAccess.reachableAt": "Reachable at",
  "connections.networkAccess.hide": "Hide",
  "connections.networkAccess.exposed": "Exposed on all interfaces.",
  "connections.networkAccess.exposedWithHost":
    "Exposed on all interfaces. Pairing links use {host}.",
  "connections.remoteEnvironments.title": "Remote environments",
  "connections.addEnvironment.action": "Add environment",
  "connections.cloudLink.signInRequired": "Sign in to T3 Connect to manage this environment.",
  "connections.cloudLink.permissionRequired":
    "Your session does not have permission to manage T3 Connect access.",
  "connections.cloudLink.linked": "T3 Connect linked",
  "connections.cloudLink.tunnelDisabled": "T3 Connect tunnel disabled",
  "connections.cloudLink.unlinked": "T3 Connect unlinked",
  "connections.cloudLink.available": "This environment is available through T3 Connect.",
  "connections.cloudLink.tunnelRemovedPublishingOn":
    "The managed tunnel was removed. Agent activity publishing stays on.",
  "connections.cloudLink.unavailable":
    "This environment is no longer available through T3 Connect.",
  "connections.cloudLink.activityEnabled": "Agent activity enabled",
  "connections.cloudLink.activityDisabled": "Agent activity disabled",
  "connections.cloudLink.activityPublishing":
    "This environment publishes agent activity to your mobile clients.",
  "connections.cloudLink.activityStopped": "This environment will stop publishing agent activity.",
  "connections.cloudLink.otherDevicesAvailable":
    "This environment is available to your other devices through T3 Connect.",
  "connections.cloudLink.otherDevicesEnable":
    "Make this environment available to your other devices through T3 Connect.",
  "connections.cloudLink.publishDescription":
    "Send activity to mobile notifications and Live Activities without T3 Connect.",
  "connections.cloudLink.publishAria": "Publish agent activity to mobile clients",
  "connections.ssh.hostOrAlias": "SSH host or alias",
  "connections.ssh.searchPlaceholder": "Search hosts or type devbox",
  "connections.ssh.loadingHosts": "Loading hosts…",
  "connections.environmentIcon.description":
    "The machine other devices see this environment as. Automatic uses what the server detected.",
  "connections.addEnvironment.adding": "Adding...",
  "connections.addEnvironment.title": "Add Environment",
  "connections.addEnvironment.description": "Pair another environment to this client.",
  "connections.remoteLink.title": "Remote link",
  "connections.remoteLink.description": "Enter a backend host and pairing code.",
  "connections.remoteLink.host": "Host",
  "connections.remoteLink.pairingCode": "Pairing code",
  "connections.remoteLink.pairingUrlHint":
    "Paste a full pairing URL here to fill both fields automatically.",
  "connections.saved.connect": "Connect",
  "connections.saved.connecting": "Connecting...",
  "connections.saved.disconnect": "Disconnect",
  "connections.saved.disconnecting": "Disconnecting...",
  "connections.saved.remove": "Remove",
  "connections.saved.removing": "Removing...",
  "connections.empty.title": "No saved remote environments",
  "connections.empty.withCloud":
    "Click “Add environment” to pair another environment, or connect one from T3 Connect.",
  "connections.empty.withoutCloud": "Click “Add environment” to pair another environment.",
  "connections.networkAccess.enable": "Enable network access",
  "connections.networkAccess.enableTitle": "Enable network access?",
  "connections.networkAccess.disableTitle": "Disable network access?",
  "connections.networkAccess.enableDescription":
    "T3 Code will restart to expose this environment over the network.",
  "connections.networkAccess.disableDescription":
    "T3 Code will restart and limit this environment back to this machine.",
  "connections.networkAccess.restarting": "Restarting...",
  "connections.networkAccess.restartEnable": "Restart and enable",
  "connections.networkAccess.restartDisable": "Restart and disable",
  "connections.wsl.confirm.disableOnlyTitle": "Turn off WSL and switch back to Windows?",
  "connections.wsl.confirm.disableTitle": "Disable WSL backend?",
  "connections.wsl.confirm.distroTitle": "Switch WSL distro?",
  "connections.wsl.confirm.enableTitle": "Start the WSL backend",
  "connections.wsl.confirm.onlyTitle": "Run only the WSL backend?",
  "connections.wsl.confirm.windowsTitle": "Re-enable the Windows backend?",
  "connections.wsl.confirm.disableOnlyDescription":
    "T3 Code will restart on the Windows backend. Threads and projects opened against WSL stay safe inside the distro and become available again when you re-enable WSL.",
  "connections.wsl.confirm.disableDescription":
    "The WSL backend will stop. Threads and projects opened against WSL stay safe inside the distro, but they'll be unavailable in T3 Code until you re-enable WSL.",
  "connections.wsl.confirm.distroDescription":
    "T3 Code will restart the WSL backend on the new distro. Sessions still running on the current distro will be interrupted.",
  "connections.wsl.confirm.enableDescription":
    "Run the WSL backend alongside the Windows one, or stop the Windows backend and use only WSL? You can change this later from Settings.",
  "connections.wsl.confirm.onlyDescription":
    "T3 Code will restart and start only the WSL backend. Your Windows-side projects won't be accessible until you turn this off again.",
  "connections.wsl.confirm.windowsDescription":
    "T3 Code will restart and bring the Windows backend back up alongside WSL.",
  "connections.wsl.confirm.applying": "Applying...",
  "connections.wsl.confirm.onlyAction": "Use only WSL",
  "connections.wsl.confirm.bothAction": "Run both backends",
  "connections.wsl.confirm.windowsAction": "Switch to Windows",
  "connections.wsl.confirm.disableAction": "Disable WSL",
  "connections.wsl.confirm.distroAction": "Switch distro",
  "connections.access.scope.one": "{count} scope",
  "connections.access.scope.many": "{count} scopes",
  "connections.access.scope.show": "{label}: show {count}",
  "connections.access.scope.granted": "Granted scopes",
  "connections.access.authorizedClients": "Authorized clients",
  "connections.access.empty": "No pairing links or client sessions.",
  "connections.access.revoke": "Revoke",
  "connections.access.revoking": "Revoking…",
  "connections.access.revokeOthers": "Revoke others",
  "connections.access.createLink": "Create link",
  "connections.access.createLinkTitle": "Create pairing link",
  "connections.access.createLinkDescription":
    "Generate a one-time link that another device can use to pair with this backend as an authorized client.",
  "connections.access.clientLabel": "Client label (optional)",
  "connections.access.clientLabelPlaceholder": "e.g. Living room iPad",
  "connections.access.permissions": "Permissions",
  "connections.access.permissionsDescription": "Limit what the paired client can do.",
  "connections.access.readOnly": "Read only",
  "connections.access.standard": "Standard",
  "connections.access.selectPermission": "Select at least one permission.",
  "connections.access.manageWarning": "This client can create or revoke access for other devices.",
  "connections.access.scope.viewEnvironment": "View environment",
  "connections.access.scope.viewEnvironmentDescription":
    "Read threads, status, diffs, and configuration.",
  "connections.access.scope.operateTasks": "Operate tasks",
  "connections.access.scope.operateTasksDescription":
    "Start tasks and perform changes in the environment.",
  "connections.access.scope.useTerminals": "Use terminals",
  "connections.access.scope.useTerminalsDescription":
    "Create terminals and send input to running shells.",
  "connections.access.scope.writeReviews": "Write reviews",
  "connections.access.scope.writeReviewsDescription": "Create comments while reviewing changes.",
  "connections.access.scope.viewAccess": "View access",
  "connections.access.scope.viewAccessDescription": "Inspect pairing links and authorized clients.",
  "connections.access.scope.manageAccess": "Manage access",
  "connections.access.scope.manageAccessDescription":
    "Issue and revoke credentials for other clients.",
  "connections.access.scope.viewRelay": "View relay",
  "connections.access.scope.viewRelayDescription": "Inspect managed relay connectivity.",
  "connections.access.scope.manageRelay": "Manage relay",
  "connections.access.scope.manageRelayDescription": "Change managed tunnel connectivity.",
  "connections.access.creating": "Creating…",
  "connections.access.thisDevice": "This device",
  "connections.access.pairingLink": "Pairing link",
  "connections.access.pairingCode": "Pairing code",
  "connections.access.share": "Share",
  "connections.access.copyCode": "Copy code",
  "connections.access.copyCodeOnly": "Copy code only",
  "connections.access.copyLink": "Copy link",
  "connections.access.showLink": "Show link",
  "connections.access.showCode": "Show code",
  "connections.access.reachVia": "Reach this machine via",
  "connections.access.qrTitle": "Pairing link - scan to open on another device",
  "connections.access.createdAt": "Link created at {time}",
  "connections.access.pairingScopes": "Pairing link scopes",
  "connections.access.clientScopes": "Client scopes",
  "connections.access.noReachableHost":
    "Copy the token and pair from another client using this backend's reachable host.",
  "connections.access.hostedLink": "Hosted app pairing link",
  "connections.access.hostedLinkDescription":
    "Clipboard copy is unavailable here. Open or manually copy this hosted app link on the device you want to connect.",
  "connections.access.pairingLinkDescription":
    "Clipboard copy is unavailable here. Open or manually copy this full pairing URL on the device you want to connect.",
  "connections.access.pairingCodeDescription":
    "Clipboard copy is unavailable here. Manually copy this code into another client.",
  "connections.access.noQr":
    "No QR is available for this endpoint. Copy the URL for use on this machine instead.",
  "connections.access.endpoint.lan": "Devices on the same network",
  "connections.access.endpoint.private": "Devices on your private network",
  "connections.access.endpoint.public": "Reachable from anywhere",
  "connections.access.endpoint.loopback": "Clients on this machine",
  "connections.access.endpoint.hosted": "Opens the hosted app, no install needed",
  "connections.endpoint.setupRequired": "Setup required",
  "connections.endpoint.default": "Default",
  "connections.endpoint.restarting": "Restarting…",
  "connections.endpoint.setup": "Setup",
  "connections.endpoint.disable": "Disable",
  "connections.endpoint.setDefault": "Set as default",
  "connections.ssh.description": "Use local SSH config, agent, and tunnels for the backend.",
  "connections.ssh.username": "Username",
  "connections.ssh.port": "Port",
  "connections.ssh.suggested": "Suggested hosts",
  "connections.ssh.suggestedDescription": "From SSH config and known hosts",
  "connections.ssh.noneDiscovered": "No new SSH hosts were discovered.",
  "connections.wsl.title": "WSL backend",
  "connections.wsl.loadFailed": "Couldn't load the WSL backend state.",
  "connections.wsl.retrying": "Retrying…",
  "connections.wsl.unavailableDescription":
    "WSL is no longer available, so the Windows backend is running instead. Switch off the WSL backend to clear this preference.",
  "connections.wsl.switchWindows": "Switch to Windows",
  "connections.wsl.description":
    "Run a second backend inside a WSL distro alongside the Windows one. Pick a distro to start it; pick Off to stop it. Projects opened against the WSL backend live on the Linux side; Windows projects stay where they are.",
  "connections.wsl.startFailed": "WSL backend couldn't start: {error}",
  "connections.wsl.off": "Off",
  "connections.wsl.defaultDistro": "Default distro",
  "connections.wsl.defaultSuffix": "default",
  "connections.wsl.onlyTitle": "WSL only",
  "connections.wsl.onlyDescription":
    "Stop the Windows backend and run only the WSL backend. Useful if you develop entirely inside WSL and don't want a second backend process. T3 Code restarts when you change this.",
  "connections.wsl.onlyLabel": "Run WSL only",
  "connections.tailscale.title": "Tailscale HTTPS",
  "connections.tailscale.enable": "Enable Tailscale HTTPS",
  "connections.tailscale.description.available":
    "Use Tailscale Serve to expose this backend through a MagicDNS HTTPS URL.",
  "connections.tailscale.description.unavailable":
    "Start Tailscale to set up HTTPS access through MagicDNS.",
  "connections.tailscale.port": "HTTPS port",
  "connections.tailscale.endpoint": "HTTPS endpoint",
  "connections.tailscale.portInvalid": "Enter a port from 1 to 65535.",
  "connections.tailscale.pendingEndpoint": "Pending MagicDNS endpoint",
  "connections.tailscale.disableTitle": "Disable Tailscale HTTPS?",
  "connections.tailscale.disableDescription":
    "T3 Code will restart the local backend without Tailscale Serve.",
  "connections.tailscale.setupTitle": "Set up Tailscale HTTPS?",
  "connections.tailscale.setupDescription":
    "T3 Code will restart the local backend with Tailscale Serve enabled and ask Tailscale to proxy HTTPS traffic to this backend.",
  "connections.tailscale.restartDisable": "Restart and disable",
  "connections.admin.title": "Administrative access",
  "connections.admin.description":
    "Pairing links and client-session management require the access:write scope for this backend.",

  "sourceControl.versionControl": "Version Control",
  "sourceControl.providers": "Source Control Providers",
  "sourceControl.textGeneration": "Text generation",
  "sourceControl.writingStyle.title": "Source control writing style",
  "sourceControl.writingStyle.repoConventions": "Repository conventions",
  "sourceControl.writingStyle.repoConventionsDescription":
    "In each project, matches recent change descriptions and change request titles.",
  "sourceControl.writingStyle.conventionalCommits": "Conventional Commits",
  "sourceControl.writingStyle.conventionalCommitsDescription":
    "Uses Conventional Commit prefixes for change descriptions; change request titles and descriptions stay concise.",
  "sourceControl.writingStyle.custom": "Custom instructions",
  "sourceControl.writingStyle.customDescription":
    "Applies your instructions to change descriptions and change request titles and descriptions in every project.",
  "sourceControl.writingStyle.customPlaceholder":
    "Keep titles concise. Use short bullet points in descriptions.",
  "sourceControl.followTemplates.title": "Follow change request templates",
  "sourceControl.followTemplates.description":
    "Structures change request descriptions using the current repository's template when one is available.",
  "sourceControl.writerModel.title": "Source control writer model",
  "sourceControl.writerModel.description":
    "Optional model override for change descriptions, change request titles and descriptions, and branch or bookmark names. Off uses the global text generation model.",
  "sourceControl.writerModel.none": "No text generation providers available.",
  "sourceControl.action.rescan": "Rescan server environment",
  "sourceControl.action.rescanTooltip": "Rescan Git and hosting integrations",
  "sourceControl.action.toggleDetails": "Toggle {provider} details",
  "sourceControl.action.availability": "{provider} availability",
  "sourceControl.action.accountVisibility": "Toggle source control account visibility",
  "sourceControl.action.revealAccount": "Click to reveal account",
  "sourceControl.action.hideAccount": "Click to hide account",
  "sourceControl.status.authenticated": "Authenticated",
  "sourceControl.status.authenticatedAs": "Authenticated as",
  "sourceControl.changeRequest.pullRequest": "pull request",
  "sourceControl.changeRequest.mergeRequest": "merge request",
  "sourceControl.changeRequest.generic": "change request",
  "sourceControl.status.notAuthenticated": "Not authenticated",
  "sourceControl.status.unknown": "Status unknown",
  "sourceControl.status.available": "Available",
  "sourceControl.status.comingSoon": "Coming Soon",
  "sourceControl.summary.comingSoon": "Support for {provider} is coming soon.",
  "sourceControl.summary.unavailable": "Not available on this server: {hint}",
  "sourceControl.summary.availableHint": "Available. {hint}",
  "sourceControl.summary.notAuthenticated":
    "{provider} is not authenticated on this server. Sign in or configure credentials using the {command} tool on the server host to enable change request features.",
  "sourceControl.summary.verifyFailed": "Could not verify {provider}. {hint}",
  "sourceControl.hint.azure":
    "Install the Azure command-line tools (`az`), then enable Azure DevOps support with `az extension add --name azure-devops`.",
  "sourceControl.hint.git":
    "Install Git from https://git-scm.com/downloads or with your package manager.",
  "sourceControl.hint.jujutsu":
    "Install Jujutsu with `brew install jj` or from https://github.com/jj-vcs/jj.",
  "sourceControl.hint.github":
    "Install the GitHub command-line tool (`gh`) via https://cli.github.com/ or your package manager (for example `brew install gh`).",
  "sourceControl.hint.gitlab":
    "Install the GitLab command-line tool (`glab`) from https://gitlab.com/gitlab-org/cli or your package manager (for example `brew install glab`).",
  "sourceControl.hint.bitbucket":
    "Set T3CODE_BITBUCKET_EMAIL and T3CODE_BITBUCKET_API_TOKEN on the server. Use a Bitbucket API token with pull request, repository, and user read scopes.",
  "sourceControl.fetch.title": "Fetch interval",
  "sourceControl.fetch.policyTooltip":
    "This interval is configured for Git only. The shared Background activity policy still decides whether Git refreshes may run when the timer fires. Custom intervals appear as Advanced in General settings.",
  "sourceControl.fetch.description":
    "Refresh remote branch status in the background. Set this to 0 seconds if Git credentials or security keys should only be prompted by explicit Git actions.",
  "sourceControl.fetch.seconds": "seconds",
  "sourceControl.fetch.decrease": "Decrease fetch interval",
  "sourceControl.fetch.inSeconds": "Automatic Git fetch interval in seconds",
  "sourceControl.fetch.increase": "Increase fetch interval",
  "sourceControl.empty.section": "Server environment",
  "sourceControl.empty.scanFailed": "Could not scan the server environment",
  "sourceControl.empty.title": "Nothing detected yet",
  "sourceControl.empty.description":
    "Install Git on the server, add optional hosting integrations or credentials your workspace needs, then rescan.",
  "sourceControl.empty.scan": "Scan",

  "keybindings.browserWarning":
    "Some shortcuts may be claimed by the browser before T3 Code sees them. Use the desktop app for better keybinding support.",
  "keybindings.column.command": "Command",
  "keybindings.column.keybinding": "Keybinding",
  "keybindings.column.when": "When",
  "keybindings.column.status": "Status",
  "keybindings.count.one": "{count} binding",
  "keybindings.count.many": "{count} bindings",
  "keybindings.search": "Search keybindings",
  "keybindings.add": "Add keybinding",
  "keybindings.openConfig": "Open keybindings.json",
  "keybindings.empty": "No keybindings match your search.",
  "keybindings.always": "Always",
  "keybindings.edit": "Edit",
  "keybindings.actionsFor": "Actions for {command}",
  "keybindings.pressShortcut": "Press shortcut",
  "keybindings.unassigned": "Unassigned",
  "keybindings.saving": "Saving",
  "keybindings.save": "Save",
  "keybindings.reset": "Reset to default",
  "keybindings.remove": "Remove",
  "keybindings.cancel": "Cancel",
  "keybindings.editShortcutFor": "Edit shortcut for {command}: {shortcut}",
  "keybindings.bindingFor": "Keybinding for {command}",
  "keybindings.cancelNew": "Cancel new keybinding",
  "keybindings.source.custom": "Custom",
  "keybindings.error.openTitle": "Unable to open keybindings file",
  "keybindings.error.openDescription": "The keybindings file was not opened.",
  "keybindings.error.saveTitle": "Unable to save keybinding",
  "keybindings.error.saveDescription": "The keybinding was not saved.",
  "keybindings.error.removeTitle": "Unable to remove keybinding",
  "keybindings.error.removeDescription": "The keybinding was not removed.",
  "keybindings.when.title": "When",
  "keybindings.when.condition": "Condition",
  "keybindings.when.group": "Group",
  "keybindings.when.not": "Not",
  "keybindings.when.and": "and",
  "keybindings.when.or": "or",
  "keybindings.when.expression": "When expression",
  "keybindings.when.removeCondition": "Remove condition",
  "keybindings.when.removeGroup": "Remove group",
  "keybindings.when.removeNegatedGroup": "Remove negated group",
  "keybindings.when.negate": "Negate {condition}",
  "keybindings.when.negateGroup": "Negate group",
  "keybindings.when.fixExpression": "Fix the expression above to continue editing visually.",
  "keybindings.when.unknownOne": "Unknown condition: {condition}",
  "keybindings.when.unknownMany": "Unknown conditions: {conditions}",
  "keybindings.when.unknownDescription":
    "T3 Code does not recognize this condition yet. It can still be saved, but it may not match unless the runtime provides it.",
  "keybindings.conflict.one": "Conflicts with {binding}.",
  "keybindings.conflict.many": "Conflicts with {bindings}{more}.",
  "keybindings.conflict.more": ", and more",
  "keybindings.conflict.description":
    "{conflict} The most recent matching binding wins when both conditions can apply.",
  "keybindings.command.sidebarToggle": "Sidebar: Toggle",
  "keybindings.command.terminalToggle": "Terminal: Toggle",
  "keybindings.command.rightPanelToggle": "Right Panel: Toggle",
  "keybindings.command.terminalSplit": "Terminal: Split",
  "keybindings.command.terminalSplitVertical": "Terminal: Split Vertical",
  "keybindings.command.terminalNew": "Terminal: New",
  "keybindings.command.terminalClose": "Terminal: Close",
  "keybindings.command.diffToggle": "Diff: Toggle",
  "keybindings.command.previewToggle": "Preview: Toggle",
  "keybindings.command.previewRefresh": "Preview: Refresh",
  "keybindings.command.previewFocusUrl": "Preview: Focus Url",
  "keybindings.command.previewZoomIn": "Preview: Zoom In",
  "keybindings.command.previewZoomOut": "Preview: Zoom Out",
  "keybindings.command.previewResetZoom": "Preview: Reset Zoom",
  "keybindings.command.commandPaletteToggle": "Command Palette: Toggle",
  "keybindings.command.filePickerToggle": "File Picker: Toggle",
  "keybindings.command.projectSearchToggle": "Project Search: Toggle",
  "keybindings.command.themeEditorToggle": "Theme Editor: Toggle",
  "keybindings.command.composerStash": "Composer: Stash",
  "keybindings.command.chatNew": "Chat: New",
  "keybindings.command.chatNewLocal": "Chat: New Local",
  "keybindings.command.modelPickerToggle": "Model Picker: Toggle",
  "keybindings.command.editorOpenFavorite": "Editor: Open Favorite",
  "keybindings.command.threadPrevious": "Thread: Previous",
  "keybindings.command.threadNext": "Thread: Next",
  "keybindings.command.threadSettle": "Thread: Settle or restore",
  "keybindings.command.threadJump": "Thread: Jump: {index}",
  "keybindings.command.modelPickerJump": "Model Picker: Jump: {index}",
  "keybindings.command.runScript": "Run Script: {name}",

  "providerUpdate.title.single": "Update Available: {provider} {version}",
  "providerUpdate.title.multiple": "Updates Available: {count} providers",
  "providerUpdate.description.installOrSettings":
    "Install the update now or review provider settings.",
  "providerUpdate.description.settingsOnly": "{providers} can be updated from provider settings.",
  "providerUpdate.providerList.two": "{first} and {second}",
  "providerUpdate.providerList.many": "{leading}, and {last}",
  "providerUpdate.action.settings": "Settings",
  "providerUpdate.action.update": "Update",
  "providerUpdate.action.retry": "Retry",

  "pairing.pending.title": "Pairing with this environment",
  "pairing.pending.description": "Validating the pairing link and preparing your session.",
  "pairing.title": "Pair with this environment",
  "pairing.token.label": "Pairing token",
  "pairing.token.placeholder": "Paste a one-time token or pairing secret",
  "pairing.submitting": "Pairing...",
  "pairing.auth.trustedRequired":
    "This environment expects a trusted pairing credential before the app can connect.",
  "pairing.auth.enterToken": "Enter a pairing token to start a session with this environment.",
  "pairing.auth.desktopAndToken":
    "Desktop-managed pairing and one-time pairing tokens are both accepted for this environment.",
  "pairing.auth.desktopOnly":
    "This environment is desktop-managed. Open it from the desktop app or paste a bootstrap credential if one was issued explicitly.",
  "pairing.auth.tokenOnly":
    "This environment accepts one-time pairing tokens. Pairing links can open this page directly, or you can paste the token here.",
  "pairing.hosted.connecting": "Connecting to this backend.",
  "pairing.hosted.missingRequest": "This pairing link is missing its backend host or token.",
  "pairing.hosted.tokenUsed":
    "This one-time pairing token was already submitted. Request a new pairing link.",
  "pairing.hosted.defaultEnvironment": "The environment",
  "pairing.hosted.saved": "{environment} is saved in this browser.",
  "pairing.hosted.retryWithNewLink":
    "{error} If the backend accepted this one-time token, request a new pairing link before retrying.",
  "pairing.hosted.pairedTitle": "Backend paired",
  "pairing.hosted.failedTitle": "Pairing failed",
  "pairing.hosted.pairingTitle": "Pairing backend",
  "pairing.hosted.host": "Host:",
  "pairing.hosted.troubleshooting":
    "Verify the backend is reachable from this browser, supports CORS for hosted clients, and is served over HTTPS when opening this page from HTTPS.",
  "pairing.hosted.openApp": "Open app",
  "pairing.authenticationFailed": "Authentication failed.",

  "sidebar.status.working": "Working",
  "sidebar.status.monitoring": "Monitoring",
  "sidebar.status.approval": "Approval",
  "sidebar.status.input": "Input",
  "sidebar.status.failed": "Failed",
  "sidebar.status.woke": "Woke",
  "sidebar.status.done": "Done",
  "sidebar.status.pinned": "Pinned",
  "sidebar.pinnedThreads": "Pinned threads",
  "sidebar.goToThreads": "Go to threads",
  "sidebar.settings": "Settings",
  "sidebar.pullRequests": "Pull Requests",
  "sidebar.usage": "Usage",
  "sidebar.threadTitle": "Thread title",
  "sidebar.searchThreads": "Search threads",
  "sidebar.clearSearch": "Clear thread search",
  "sidebar.newThread": "New thread",
  "sidebar.newThreadCurrentProject": "New thread in current project: Shift+click",
  "sidebar.filterByProject": "Filter threads by project",
  "sidebar.searchProjects": "Search projects",
  "sidebar.searchProjectsPlaceholder": "Search projects...",
  "sidebar.noMatchingProjects": "No matching projects.",
  "sidebar.discardDraft": "Discard draft",
  "sidebar.unsentDraft": "Unsent draft",
  "sidebar.allProjects": "All projects",
  "sidebar.projectSettings": "Project settings for {project}",
  "sidebar.newProject": "New project",
  "sidebar.searchResults": "Thread search results",
  "sidebar.noThreadsFound": "No threads found",
  "sidebar.snoozedTitle": "Snoozed",
  "sidebar.snoozed": "Snoozed ({count})",
  "sidebar.settledTitle": "Settled",
  "sidebar.settled": "Settled ({count})",
  "sidebar.showMore": "Show {count} more",
  "sidebar.noProjects": "No projects yet",
  "sidebar.addProject": "Add project",
  "sidebar.noProjectThreads": "No threads in {project} yet",
  "sidebar.noThreads": "No threads yet",
  "sidebar.snooze.hour": "In 1 hour",
  "sidebar.snooze.threeHours": "In 3 hours",
  "sidebar.snooze.evening": "This evening",
  "sidebar.snooze.tomorrow": "Tomorrow",
  "sidebar.snooze.nextWeek": "Next week",
  "sidebar.action.newThreadOnBranch": "New thread on {branch}",
  "sidebar.action.pin": "Pin thread",
  "sidebar.action.unpin": "Unpin thread",
  "sidebar.action.settle": "Settle thread",
  "sidebar.bulk.settle": "Settle",
  "sidebar.action.unsettle": "Un-settle thread",
  "sidebar.action.wake": "Wake thread",
  "sidebar.action.snooze": "Snooze",
  "sidebar.action.rename": "Rename thread",
  "sidebar.action.regenerateTitle": "Regenerate title",
  "sidebar.action.regenerating": "Regenerating...",
  "sidebar.bulk.regenerateTitles": "Regenerate titles",
  "sidebar.bulk.regeneratingTitles": "Regenerating…",
  "sidebar.action.markUnread": "Mark unread",
  "sidebar.action.copy": "Copy",
  "sidebar.action.path": "Path",
  "sidebar.action.branch": "Branch",
  "sidebar.action.threadId": "Thread ID",
  "sidebar.action.copyPath": "Copy path",
  "sidebar.action.copyBranch": "Copy branch",
  "sidebar.action.shareGuest": "Share with guest",
  "sidebar.action.copyThreadId": "Copy thread ID",
  "sidebar.action.archive": "Archive thread",
  "sidebar.action.dismissWoke": "Dismiss Woke notification",
  "sidebar.confirmDeleteThread": 'Delete thread "{title}"?',
  "sidebar.confirmDeleteThreadDescription":
    "This permanently clears conversation history for this thread.",
  "sidebar.confirmDeleteThreadsOne": "Delete 1 thread?",
  "sidebar.confirmDeleteThreadsMany": "Delete {count} threads?",
  "sidebar.confirmDeleteThreadsDescription":
    "This permanently clears conversation history for these threads.",
  "sidebar.thisThread": "this thread",
  "sidebar.deleteThreadFailed": "Failed to delete thread",
  "sidebar.deleteThreadsFailed": "Failed to delete threads",

  "composer.mode.supervised": "Supervised",
  "composer.mode.supervisedDescription": "Ask before commands and file changes.",
  "composer.mode.autoAcceptEdits": "Auto-accept edits",
  "composer.mode.autoAcceptEditsDescription": "Auto-approve edits, ask before other actions.",
  "composer.mode.auto": "Auto",
  "composer.mode.autoDescription": "Supported providers approve routine actions; others still ask.",
  "composer.mode.fullAccess": "Full access",
  "composer.mode.fullAccessDescription": "Allow commands and edits without prompts.",
  "composer.mode.plan": "Plan",
  "composer.mode.build": "Build",
  "composer.mode.switchToBuild": "Plan mode - click to return to normal build mode",
  "composer.mode.switchToPlan": "Default mode - click to enter plan mode",
  "composer.runtimeMode": "Runtime mode",
  "composer.preparingWorktree": "Preparing worktree...",
  "composer.action.submitting": "Submitting...",
  "composer.action.submit": "Submit",
  "composer.action.next": "Next",
  "composer.action.nextQuestion": "Next question",
  "composer.action.submitAnswers": "Submit answers",
  "composer.action.submitAnswer": "Submit answer",
  "composer.action.stopGeneration": "Stop generation",
  "composer.action.previousQuestion": "Previous question",
  "composer.action.previous": "Previous",
  "composer.action.sending": "Sending...",
  "composer.action.refine": "Refine",
  "composer.action.implement": "Implement",
  "composer.action.implementationActions": "Implementation actions",
  "composer.action.implementNewThread": "Implement in a new thread",
  "composer.action.sendMessage": "Send message",
  "composer.action.writeCustomAnswer": "Write custom answer",
  "composer.action.expand": "Expand composer",
  "composer.state.environmentDisconnected": "Environment disconnected",
  "composer.state.connecting": "Connecting",
  "composer.state.preparingWorktree": "Preparing worktree",
  "composer.state.sending": "Sending",
  "composer.state.sendingFeedback": "Sending feedback",
  "composer.state.messagesLoading": "Messages loading",
  "composer.state.messagesSyncing": "Syncing messages",
  "composer.command.modelDescription": "Switch response model for this thread",
  "composer.command.planDescription": "Switch this thread into plan mode",
  "composer.command.defaultDescription": "Switch this thread back to normal build mode",
  "composer.command.runProviderCommand": "Run provider command",
  "composer.command.runProviderSkill": "Run provider skill",
  "composer.command.scopedSkill": "{scope} skill",
  "composer.menu.searchingSkills": "Searching workspace skills...",
  "composer.menu.searchingFiles": "Searching workspace files...",
  "composer.menu.noSkills": "No skills found. Try / to browse provider commands.",
  "composer.menu.noPaths": "No matching files or folders.",
  "composer.menu.noCommands": "No matching command.",
  "composer.skillSource.app": "App",
  "composer.skillSource.repo": "Repo",
  "composer.skillSource.project": "Project",
  "composer.skillSource.personal": "Personal",
  "composer.skillSource.system": "System",
  "composer.skillSource.provider": "Provider",
  "composer.skillSource.suffix": " Skill",
  "composer.banner.wokeTitle": "This thread woke from snooze",
  "composer.banner.wokeDescription":
    "Dismiss to clear the Woke indicator, or send a message to keep going.",
  "composer.banner.wokeDismiss": "Dismiss Woke notification",
  "composer.banner.snoozedTitle": "This thread is snoozed",
  "composer.banner.snoozedDescription":
    "Sending a message wakes it and moves it back to Active in the sidebar.",
  "composer.banner.waking": "Waking...",
  "composer.banner.wakeNow": "Wake now",
  "composer.banner.settledTitle": "This thread is settled",
  "composer.banner.settledDescription": "Sending a message moves it back to Active in the sidebar.",
  "composer.banner.unsettling": "Un-settling...",
  "composer.banner.unsettle": "Un-settle",
  "composer.banner.showOtherNotices": "Show other notices",
  "composer.banner.dismissWarning": "Dismiss warning",
  "composer.tasks.title": "Tasks",
  "composer.tasks.openLabel": "Tasks: {completed} of {total} complete. Current task: {task}",
  "composer.tasks.collapseLabel":
    "Collapse tasks: {completed} of {total} complete. Current task: {task}",
  "composer.tasks.listLabel": "Task list. {completed} of {total} complete.",
  "composer.tasks.now": "now",
  "composer.stash.title": "Stash",
  "composer.stash.badgeLabel": "Stashed prompts: {count}. Open stash.",
  "composer.stash.close": "Close stash",
  "composer.stash.listLabel": "Stashed prompts",
  "composer.stash.empty": "Nothing stashed yet.",
  "composer.stash.emptyHint": " Press {shortcut} with a prompt in the composer to stash it.",
  "composer.stash.restore": "Restore stashed prompt: {prompt}",
  "composer.stash.delete": "Delete stashed prompt",
  "composer.stash.entry.empty": "(empty)",
  "composer.stash.entry.kind.attachment": "attachment",
  "composer.stash.entry.kind.file": "file",
  "composer.stash.entry.kind.image": "image",
  "composer.stash.entry.attachmentCount.one": "({count} {kind})",
  "composer.stash.entry.attachmentCount.many": "({count} {kind}s)",
  "composer.stash.savingImages.one": "saving {count} image…",
  "composer.stash.savingImages.many": "saving {count} images…",
  "composer.stash.droppedImages.one": "{count} image dropped",
  "composer.stash.droppedImages.many": "{count} images dropped",
  "composer.stash.toast.otherEnvironmentTitle": "Stashed files belong to another environment",
  "composer.stash.toast.otherEnvironmentDescription":
    "Restore this prompt in the environment that received its files.",
  "composer.stash.toast.restoreNotDurableTitle": "Restored prompt may reappear in the stash",
  "composer.stash.toast.restoreNotDurableDescription":
    "Browser storage rejected the update, so this entry could still be there after a reload.",
  "composer.stash.toast.tooLarge":
    "{files} exceeded the stash size limit when this prompt was saved.",
  "composer.stash.toast.unreadable": "{files} could not be read when this prompt was saved.",
  "composer.stash.toast.atLimit":
    "{files} could not be restored: the composer is at its {count}-attachment limit.",
  "composer.stash.toast.expired":
    "{files}: stashed files are kept for 24 hours and this upload expired. Attach the file again.",
  "composer.stash.toast.notRestoredTitle": "Some attachments were not restored",
  "composer.stash.toast.deleteNotDurableTitle": "Stash entry may come back",
  "composer.stash.toast.deleteNotDurableDescription":
    "Browser storage rejected the delete, so this prompt could reappear after a reload.",
  "composer.stash.toast.reattachBeforeStash":
    "Attach dropped files again or remove them before stashing",
  "composer.stash.toast.waitForUploads": "Wait for file uploads before stashing this prompt",
  "composer.stash.toast.saveFailedTitle": "Could not stash this prompt",
  "composer.stash.toast.saveFailedDescription":
    "Browser storage rejected the write, so the composer was left as-is. Free up site data and try again.",
  "composer.stash.toast.sessionOnlyTitle": "Stashed prompt will not survive a reload",
  "composer.stash.toast.sessionOnlyDescription":
    "Browser storage is unavailable, so this stash is kept in memory only for this session.",
  "composer.stash.toast.evictedTitle": "Oldest stashed prompt discarded",
  "composer.stash.toast.evictedDescription":
    "The stash holds {count} prompts; the oldest was removed to make room.",
  "composer.stash.toast.imagesNotSavedTitle": "Stashed images were not saved",
  "composer.stash.toast.imagesNotSavedDescription":
    "The prompt was stashed, but browser storage rejected its images. They will be missing if you reload.",
  "composer.stash.toast.imagesDidNotAttachTitle": "Stashed images did not attach",
  "composer.stash.toast.imagesDidNotAttachDescription.one":
    "That prompt was restored or deleted before {count} image finished saving. Re-attach it if you still need it.",
  "composer.stash.toast.imagesDidNotAttachDescription.many":
    "That prompt was restored or deleted before {count} images finished saving. Re-attach them if you still need them.",
  "composer.compaction.sendOrClearDraft": "Send or clear your draft before compacting",
  "composer.compaction.chooseProject": "Choose a project before compacting",
  "composer.compaction.enableClaude": "Enable a Claude provider before compacting",
  "composer.compaction.unavailable": "Compacting is unavailable right now",
  "composer.compaction.resumeTitle": "Resume with less context",
  "composer.compaction.resumeDescription": "{tokens} tokens from an older session",
  "composer.compaction.keepFullHistory": "Keep full history",
  "composer.compaction.compact": "Compact",
  "composer.compaction.compactContext": "Compact context",
  "composer.compaction.native.header": "Resume session",
  "composer.compaction.native.question":
    "This session is {age} old and uses {tokens} tokens. Compact it before continuing?",
  "composer.compaction.native.ageMinutes": "{minutes}m",
  "composer.compaction.native.ageHoursMinutes": "{hours}h {minutes}m",
  "composer.compaction.native.compactAndContinue": "Compact and continue",
  "composer.compaction.native.compactDescription": "Resume with a summary and use fewer tokens.",
  "composer.compaction.native.keepDescription": "Resume without changing the conversation.",
  "composer.compaction.native.dontAskAgain": "Don't ask again",
  "composer.compaction.native.dontAskDescription":
    "Keep full history and skip future resume prompts.",
  "composer.imageCompression.inProgress": "Still compressing a pasted image.",
  "composer.imageCompression.sendAgain": "Send again once its thumbnail appears.",
  "composer.imageCompression.compactAgain": "Compact again once its thumbnail appears.",
  "composer.image.unsupportedFile":
    "Unsupported file type for '{file}'. Please attach image files only.",
  "composer.image.unsupportedType":
    "'{file}' is not a supported image type. Attach GIF, HEIC, HEIF, JPEG, PNG, or WebP images.",
  "composer.image.limit": "You can attach up to {count} images per message.",
  "composer.image.unreadable": "'{file}' could not be read as an image.",
  "composer.image.tooLarge": "'{file}' is too large to attach, even after compression.",
  "composer.thread.settleFailed": "Failed to settle thread",
  "composer.thread.unsettleFailed": "Failed to un-settle thread",
  "composer.approval.commandRequested": "Command approval requested",
  "composer.approval.fileReadRequested": "File-read approval requested",
  "composer.approval.fileChangeRequested": "File-change approval requested",
  "composer.approval.appAccessLabel": "App access approval",
  "composer.approval.appAccessRequest": "App access request",
  "composer.approval.commandLabel": "Command approval",
  "composer.approval.fileReadLabel": "File read approval",
  "composer.approval.fileChangeLabel": "File change approval",
  "composer.approval.command": "Command",
  "composer.approval.fileToRead": "File to read",
  "composer.approval.fileChange": "File change",
  "composer.approval.pending": "PENDING APPROVAL",
  "composer.approval.cancelTurn": "Cancel",
  "composer.approval.decline": "Decline",
  "composer.approval.allowSession": "Always allow this session",
  "composer.approval.approveOnce": "Approve",
  "composer.selectMultiple": "Select one or more options.",
  "composer.question.show": "Show the question and its options",
  "composer.question.hide": "Hide the question and its options",
  "composer.question.dismiss": "Dismiss question without answering",
  "composer.attachmentWarningLabel": "Draft attachment may not persist",
  "composer.attachmentWarningDescription":
    "Draft attachment could not be saved locally and may be lost on navigation.",
  "composer.retryAttachmentUpload": "Retry upload for {name}",
  "composer.attachmentUpload.retryOrRemoveOne": "Retry or remove the failed image",
  "composer.attachmentUpload.retryOrRemoveMany": "Retry or remove the failed images",
  "composer.attachmentUpload.uploadingOne": "Image still uploading",
  "composer.attachmentUpload.uploadingMany": "Images still uploading",
  "composer.attachmentUpload.resolveBeforeSending":
    "Retry or remove failed image uploads before sending.",
  "composer.attachment.reattachOne": "Attach the interrupted file again or remove it",
  "composer.attachment.reattachMany": "Attach the interrupted files again or remove them",
  "composer.attachment.attachFiles": "Attach files",
  "composer.attachment.remove": "Remove {name}",
  "composer.attachment.play": "Play {name}",
  "composer.attachment.loading": "Loading {name}",
  "composer.attachment.attachAfterQuestions": "Attach files after answering plan questions.",
  "composer.attachment.retryOrRemoveBeforeSending":
    "Retry or remove failed uploads before sending.",
  "composer.attachment.fileLimit": "You can attach up to {count} files per message.",
  "composer.attachment.serverUnsupported": "This server does not support file attachments.",
  "composer.attachment.emptyOrUnreadable": "'{file}' is empty or could not be read.",
  "composer.noProviderAvailable": "No provider available",
  "composer.planReady": "Plan Ready",
  "composer.placeholder.approval": "Resolve this approval request to continue",
  "composer.placeholder.customAnswer":
    "Type your own answer, or leave this blank to use the selected option",
  "composer.placeholder.refinePlan":
    "Add feedback to refine the plan, or leave this blank to implement it",
  "composer.placeholder.chooseProject": "Choose a project above to start a thread",
  "composer.placeholder.enableProvider": "Enable a provider in Settings to send a message",
  "composer.placeholder.collapsedEnableProvider": "Enable a provider in Settings",
  "composer.placeholder.disconnected": "Ask for changes, send follow-ups, or attach images",
  "composer.placeholder.default":
    "Ask anything, @tag files/folders, $use skills, or / for commands",
  "composer.placeholder.collapsedDefault": "Ask anything...",

  "feedback.startThreadTitle": "Start a Codex thread first",
  "feedback.startThreadDescription": "Send a message before you submit feedback.",
  "feedback.sendFailed": "Could not send feedback to OpenAI",
  "feedback.sent": "Feedback sent to OpenAI",
  "feedback.threadId": "Thread ID: {id}",
  "feedback.copyId": "Copy ID",
  "feedback.clipboardTarget": "Codex feedback thread ID",
  "feedback.copyFailed": "Could not copy thread ID",
  "feedback.dismiss": "Dismiss feedback notice",
  "feedback.sending": "Sending feedback to OpenAI...",
  "feedback.sentMessage": "Feedback sent to OpenAI.\n\nThread ID: `{id}`",
  "feedback.failedMessage": "Could not send feedback to OpenAI.\n\n{error}",
  "backgroundThread.started": "Started in background",
  "backgroundThread.open": "Open",
  "backgroundThread.startedWarning": "Task started in the background",
  "backgroundThread.openFreshComposerFailed": "Could not open a fresh composer.",
  "backgroundThread.openFreshComposerFailedWithReason": "Could not open a fresh composer: {reason}",

  "chat.imageUnavailable": "Image unavailable",
  "chat.imageUnavailableWithAlt": "Image unavailable · {alt}",
  "chat.imageLoading": "Loading image",
  "chat.activity.thinking": "Thinking",

  "terminal.splitHorizontal": "Split Terminal Horizontally",
  "terminal.splitHorizontalMax": "Split Terminal Horizontally (max {max} per group)",
  "terminal.splitVertical": "Split Terminal Vertically",
  "terminal.splitVerticalMax": "Split Terminal Vertically (max {max} per group)",
  "terminal.new": "New Terminal",
  "terminal.close": "Close Terminal",
  "terminal.empty": "No terminal sessions for this thread yet.",
  "terminal.group.single": "Single",
  "terminal.group.stacked": "Stacked",
  "terminal.group.sideBySide": "Side by side",
  "terminal.defaultLabel": "Terminal",
  "terminal.closeNamed": "Close {name}",

  "draftHero.changeProject": "Change project",
  "draftHero.chooseProject": "Choose a project",
  "draftHero.newProject": "New project",
  "draftHero.addProject": "Add a project",
  "draftHero.question": "What should we build in {project}?",
  "draftHero.chooseProjectToStart": "{project} to start",
  "draftHero.addProjectToStart": "Add a project to start",

  "emptyThread.title": "No active thread",
  "emptyThread.heading": "Pick a thread to continue",
  "emptyThread.description": "Select an existing thread or create a new one to get started.",
  "home.startFailed": "Couldn’t start a new thread",
  "home.startFailedDescription": "The project is still available. Try opening the draft again.",
  "home.heroTitle": "What should we work on?",
  "home.heroDescription": "Add a project to start your first thread.",
  "home.addProject": "Add project",
  "home.connectEnvironment": "Connect an environment to get started",
  "home.connectCloudDescription":
    "Sign in to T3 Connect to connect a linked environment through its managed tunnel, or add a reachable backend manually.",
  "home.connectManualDescription":
    "Add a reachable backend manually to start working from this browser.",
  "home.openConnections": "Open Connections",
  "home.addEnvironment": "Add environment",
  "home.connectComputer": "Connect to a computer running T3 Code",
  "home.connectComputerDescription":
    "This browser connects to T3 Code running on your computer or a server. Start the T3 Code desktop app or command-line server on that machine and keep it running.",
  "home.connectComputerCloud":
    "Enable T3 Connect on that machine, then open Connections here to sign in with the same account. You can also add the machine using a pairing link.",
  "home.connectComputerManual":
    "Open Connections and add that machine using its pairing link. This browser must be able to reach it.",

  "commandPalette.dialog.command": "Command palette",
  "commandPalette.dialog.files": "File picker",
  "commandPalette.dialog.content": "Search project contents",
  "commandPalette.search": "Search...",
  "commandPalette.search.root": "Search commands, projects, and threads...",
  "commandPalette.search.rootBrowse": "Enter project path (e.g. ~/projects/my-app)",
  "commandPalette.search.submenu": "Search...",
  "commandPalette.search.submenuBrowse": "Enter path (e.g. ~/projects/my-app)",
  "commandPalette.navigate": "Navigate",
  "commandPalette.select": "Select",
  "commandPalette.group.sources": "Sources",
  "commandPalette.group.repository": "Repository",
  "commandPalette.source.localFolder": "Local folder",
  "commandPalette.source.localFolderDescription": "Browse a folder on disk",
  "commandPalette.source.gitUrl": "Git URL",
  "commandPalette.source.repository": "{provider} repository",
  "commandPalette.source.cloneUrl": "Clone from a remote URL",
  "commandPalette.source.cloneRepository": "Clone {provider} {path}",
  "commandPalette.source.setupRequired": "Setup Required",
  "commandPalette.source.configure": "Open Settings -> Source Control to configure this provider.",
  "commandPalette.empty.enterGitUrl": "Enter a Git clone URL and press Enter to continue.",
  "commandPalette.empty.enterRepository": "Enter a repository path and press Enter to look it up.",
  "commandPalette.empty.chooseDestination": "Choose a destination path and press Enter to clone.",
  "commandPalette.empty.relativePath": "Relative paths require an active project.",
  "commandPalette.empty.createFolder": "Press Enter to create this folder and add it as a project.",
  "commandPalette.empty.searchingThreads": "Searching thread messages...",
  "commandPalette.action.lookup": "Lookup",
  "commandPalette.action.working": "Working",
  "commandPalette.action.cloning": "Cloning",
  "commandPalette.action.clone": "Clone",
  "commandPalette.action.createAndClone": "Create & Clone",
  "commandPalette.action.createAndAdd": "Create & Add",
  "commandPalette.action.add": "Add",
  "commandPalette.action.openIn": "Open in {fileManager}",
  "commandPalette.group.cloneDestination": "Select where to clone",

  "pullRequests.title": "Pull Requests",
  "pullRequests.breadcrumb": "Pull requests breadcrumb",
  "pullRequests.scope": "Pull request scope",
  "pullRequests.search.placeholder": "Search pull requests, or label:bug",
  "pullRequests.search.label": "Search pull requests",
  "pullRequests.refresh": "Refresh pull requests",
  "pullRequests.filter.label": "Filter pull requests",
  "pullRequests.filter.state": "State",
  "pullRequests.filter.involvement": "Involvement",
  "pullRequests.filter.draft": "Draft",
  "pullRequests.filter.review": "Review",
  "pullRequests.filter.checks": "Checks",
  "pullRequests.filter.host": "Host",
  "pullRequests.filter.server": "Server",
  "pullRequests.filter.project": "Project",
  "pullRequests.filter.all": "All",
  "pullRequests.filter.reviewing": "Reviewing",
  "pullRequests.filter.authored": "Authored",
  "pullRequests.filter.open": "Open",
  "pullRequests.filter.closed": "Closed",
  "pullRequests.filter.merged": "Merged",
  "pullRequests.filter.draftsOnly": "Drafts only",
  "pullRequests.filter.hideDrafts": "Hide drafts",
  "pullRequests.filter.approved": "Approved",
  "pullRequests.filter.changesRequested": "Changes requested",
  "pullRequests.filter.reviewRequired": "Review required",
  "pullRequests.filter.noReviews": "No reviews",
  "pullRequests.filter.passing": "Passing",
  "pullRequests.filter.failing": "Failing",
  "pullRequests.filter.allProjects": "All projects",
  "pullRequests.filter.allHosts": "All hosts",
  "pullRequests.filter.allServers": "All servers",
  "pullRequests.filter.byState": "Filter by state",
  "pullRequests.filter.byInvolvement": "Filter by involvement",
  "pullRequests.filter.byHost": "Filter by host",
  "pullRequests.filter.unavailable": "Unavailable",
  "pullRequests.filter.trigger": "Filters",
  "pullRequests.filter.author": "Author",
  "pullRequests.filter.anyone": "Anyone",
  "pullRequests.filter.searchAuthors": "Search authors",
  "pullRequests.filter.noAuthors": "No authors found",
  "pullRequests.filter.mergesLoaded": "{count} merges loaded",
  "pullRequests.filter.labels": "Labels",
  "pullRequests.filter.any": "Any",
  "pullRequests.filter.selected": "{count} selected",
  "pullRequests.filter.noLabels": "No labels in this view",
  "pullRequests.empty.noProjects": "No projects in this workspace",
  "pullRequests.empty.noProjectsDescription":
    "Add a project, and the pull requests from its repository appear here.",
  "pullRequests.empty.searching": "Searching every host for \u201c{query}\u201d",
  "pullRequests.empty.noMatches": "Nothing matches \u201c{query}\u201d",
  "pullRequests.empty.noMatchesDescription":
    "The hosts were searched for it. Try fewer words, or search by number, author or branch.",
  "pullRequests.empty.clearSearch": "Clear search",
  "pullRequests.empty.checking": "Checking...",
  "pullRequests.empty.checkAgain": "Check again",
  "pullRequests.empty.filtered": "Nothing under these filters",
  "pullRequests.empty.none": "No pull requests",
  "pullRequests.empty.filteredDescription":
    "Widen the state, involvement or project filter to see more.",
  "pullRequests.empty.noneDescription":
    "Pull requests from every project in this workspace appear here.",
  "pullRequests.empty.loading": "Loading...",
  "pullRequests.empty.loadMore": "Load more pull requests",
  "pullRequests.unavailable.title": "Pull requests unavailable",
  "pullRequests.unavailable.upgrade": "Update your T3 Code servers to browse pull requests.",
  "pullRequests.unavailable.host": "This host could not be read.",
  "pullRequests.latestFailed": "The latest request failed. Showing the last pull requests loaded.",
  "pullRequests.latestLoaded": "Showing the last pull requests loaded.",
  "pullRequests.updating": "Updating pull requests",
  "pullRequests.narrowSearch": "Narrow your search to find more pull requests.",
  "pullRequests.loadingMore": "Loading more",
  "pullRequests.panel.selectFirst": "Select a pull request first",
  "pullRequests.unavailable.loadFailed": "Could not load pull requests",
  "pullRequests.unavailable.retry": "Retry",
  "pullRequests.openOnGitHub": "Open on GitHub",
  "pullRequests.activity.loadFailed": "Could not load pull request activity",
  "pullRequests.labels.removeFailed": "Could not take {label} off",
  "pullRequests.labels.addFailed": "Could not put {label} on",
  "pullRequests.labels.permissionFailure":
    "The host refused it. Check that you have triage access on this repository.",
  "pullRequests.labels.change": "Change labels",
  "pullRequests.labels.needsTriage": "Changing labels needs triage access on this repository",
  "pullRequests.labels.search": "Search labels",
  "pullRequests.labels.empty": "This repository has no labels.",
  "pullRequests.labels.noMatch": "No label matches that.",
  "pullRequests.labels.readFailed": "The labels could not be read.",
  "pullRequests.labels.truncated":
    "This repository has more labels than are listed here. Apply the rest on the host.",
  "pullRequests.labels.applied": "Applied",
  "pullRequests.labels.title": "Labels",
  "pullRequests.reviewers.withdrawFailed": "Could not take back the review request to {reviewer}",
  "pullRequests.reviewers.requestFailed": "Could not ask {reviewer} for a review",
  "pullRequests.reviewers.permissionFailure":
    "The host refused it. Check that you have write access on this repository, and that they still have access to it.",
  "pullRequests.reviewers.withdrawn": "Review request to {reviewer} taken back",
  "pullRequests.reviewers.requested": "Review requested from {reviewer}",
  "pullRequests.reviewers.request": "Request a review",
  "pullRequests.reviewers.needsWrite":
    "Asking someone to review needs write access on this repository",
  "pullRequests.reviewers.search": "Search people with access",
  "pullRequests.reviewers.empty": "Nobody else has access to this repository.",
  "pullRequests.reviewers.noMatch": "Nobody with access matches that.",
  "pullRequests.reviewers.readFailed": "The people with access could not be read.",
  "pullRequests.reviewers.truncated":
    "This repository has more people with access than are listed here. Ask for the rest on the host.",
  "pullRequests.reviewers.team": "team",
  "pullRequests.reviewers.alreadyAsked": "Already asked",
  "pullRequests.reviewers.title": "Reviewers",
  "pullRequests.comments.title": "Comments",
  "pullRequests.comments.edit": "Edit comment",
  "pullRequests.comments.postFailed": "Could not post the comment",
  "pullRequests.comments.saveFailed": "Could not save the comment",
  "pullRequests.comments.leave": "Leave a comment",
  "pullRequests.comments.onPullRequest": "Comment on this pull request",
  "pullRequests.comments.closeWithComment": "Close with comment",
  "pullRequests.comments.reopenWithComment": "Reopen with comment",
  "pullRequests.comments.posting": "Posting...",
  "pullRequests.comments.comment": "Comment",
  "pullRequests.comments.showOldestOne": "Show {count} oldest comment",
  "pullRequests.comments.showOldestMany": "Show {count} oldest comments",
  "pullRequests.comments.loading": "Loading conversation…",
  "pullRequests.comments.unavailable": "Conversation unavailable",
  "pullRequests.comments.countOne": "{count} comment",
  "pullRequests.comments.countMany": "{count} comments",
  "pullRequests.comments.showOldestFirst": "Show oldest comments first",
  "pullRequests.comments.showNewestFirst": "Show newest comments first",
  "pullRequests.comments.newestFirst": "Newest first",
  "pullRequests.comments.oldestFirst": "Oldest first",
  "pullRequests.comments.truncated":
    "This conversation is longer than this page reads in one go. The most recent {count} are here; open it on the host to read the rest.",
  "pullRequests.comments.empty": "No comments yet.",
  "pullRequests.comments.resolved": "Resolved",
  "pullRequests.comments.approvalDismissed": "Approval dismissed",
  "pullRequests.description.title": "Description",
  "pullRequests.description.label": "Pull request description",
  "pullRequests.description.placeholder": "Describe this pull request",
  "pullRequests.description.emptyMarkdown": "_No description provided._",
  "pullRequests.description.edit": "Edit description",
  "pullRequests.description.saveFailed": "Could not save the description",
  "pullRequests.checks.title": "Checks",
  "pullRequests.checks.none": "No checks reported.",
  "pullRequests.checks.openFailed": "Unable to open check details",
  "pullRequests.actions.closing": "Closing...",
  "pullRequests.actions.reopening": "Reopening...",
  "pullRequests.actions.preparing": "Preparing...",
  "pullRequests.review.approved": "Approved",
  "pullRequests.review.changesRequested": "Changes requested",
  "pullRequests.review.dismissed": "Review dismissed",
  "pullRequests.review.approvedEarlier": "Approved earlier changes",
  "pullRequests.review.changesRequestedEarlier": "Requested changes to earlier changes",
  "pullRequests.review.dismissedEarlier": "Earlier review dismissed",
  "pullRequests.state.merged": "Merged",
  "pullRequests.state.closed": "Closed",
  "pullRequests.state.draft": "Draft",
  "pullRequests.state.conflictsWith": "Conflicts with {branch}",
  "pullRequests.state.conflicts": "Has conflicts",
  "pullRequests.state.open": "Open",
  "pullRequests.checks.status.running": "Running",
  "pullRequests.checks.status.awaitingAction": "Awaiting action",
  "pullRequests.checks.status.awaitingApproval": "Awaiting approval",
  "pullRequests.checks.status.passed": "Passed",
  "pullRequests.checks.status.failed": "Failed",
  "pullRequests.checks.status.cancelled": "Cancelled",
  "pullRequests.checks.status.skipped": "Skipped",
  "pullRequests.checks.status.neutral": "Neutral",
  "pullRequests.checks.allPassed": "All checks have passed",
  "pullRequests.checks.someFailed": "Some checks were not successful",
  "pullRequests.checks.somePending": "Some checks haven't completed yet",
  "pullRequests.checks.nonePlain": "No checks reported",
  "pullRequests.checks.failingCount": "{failed} of {total} failing",
  "pullRequests.checks.workflowsAndChecksAwaiting":
    "{workflows} workflow(s) and {checks} check(s) awaiting action",
  "pullRequests.checks.workflowsAwaitingApproval": "{count} workflow(s) awaiting approval",
  "pullRequests.checks.checksAwaitingAction": "{count} check(s) awaiting action",
  "pullRequests.checks.runningCount": "{pending} of {total} running",
  "pullRequests.checks.allPassedShort": "All checks passed",
  "pullRequests.checks.passingCount": "{passed} of {total} passing",
  "pullRequests.checks.loading": "Loading checks…",
  "pullRequests.checks.details": "Details",
  "pullRequests.checks.aria": "Checks: {status}",
  "pullRequests.detail.titleSaveFailed": "The title could not be saved",
  "pullRequests.detail.titleRefused": "The host refused the new title.",
  "pullRequests.detail.openRepository": "Open {repository} repository",
  "pullRequests.detail.openOnHost": "Open pull request #{number} on host",
  "pullRequests.detail.stacked": "Stacked pull request",
  "pullRequests.detail.receivesChangesFrom": "receives changes from",
  "pullRequests.detail.changedFiles": "{count} changed files",
  "pullRequests.detail.editTitle": "Edit title",
  "pullRequests.detail.title": "Pull request title",
  "pullRequests.detail.updated": "updated {time}",
  "pullRequests.detail.copyCheckoutCommand": "Copy checkout command",
  "pullRequests.detail.checkoutCommandCopied": "Checkout command copied",
  "pullRequests.detail.copyCheckoutCommandFailed": "Could not copy checkout command",
  "pullRequests.detail.copyBranch": "Copy pull request branch",
  "pullRequests.detail.branchCopied": "Branch name copied",
  "pullRequests.detail.files": "file(s)",
  "pullRequests.detail.tabs": "Pull request tabs",
  "pullRequests.detail.collapsePanel": "Collapse pull request panel",
  "pullRequests.branch.outOfDate": "This branch is out-of-date with {branch}.",
  "pullRequests.branch.behind": "This branch is {count} commit(s) behind {branch}.",
  "pullRequests.branch.cleanMerge": "Changes can be cleanly merged.",
  "pullRequests.branch.updateRebase": "Update with rebase",
  "pullRequests.branch.update": "Update branch",
  "pullRequests.checkout.action": "Check out",
  "pullRequests.checkout.checkingOut": "Checking out...",
  "pullRequests.checkout.separateWorktree": "In a separate worktree",
  "pullRequests.checkout.separateWorktreeDescription":
    "Its own folder and thread. Nothing you have open moves.",
  "pullRequests.checkout.inRepository": "In this repository",
  "pullRequests.checkout.inRepositoryDescription":
    "Switches the branch you are working in, like `gh pr checkout`.",
  "pullRequests.autoMerge.label": "Auto-merge",
  "pullRequests.autoMerge.method": "Auto-merge ({method})",
  "pullRequests.autoMerge.hostWillMerge":
    "{label}: the host will merge this on its own once its requirements are met",
  "pullRequests.autoMerge.enable": "Enable auto-merge",
  "pullRequests.autoMerge.disable": "Disable auto-merge",
  "pullRequests.actions.resolveConflicts": "Resolve conflicts",
  "pullRequests.actions.readyForReview": "Ready for review",
  "pullRequests.actions.convertToDraft": "Convert to draft",
  "pullRequests.actions.enabling": "Enabling...",
  "pullRequests.actions.merging": "Merging...",
  "pullRequests.actions.approving": "Approving...",
  "pullRequests.actions.approveWorkflows": "Approve workflows to run",
  "pullRequests.actions.more": "More pull request actions",
  "pullRequests.actions.opening": "Opening...",
  "pullRequests.actions.askQuestion": "Ask a question",
  "pullRequests.actions.explain": "Explain this PR",
  "pullRequests.actions.explainDescription": "A walk through the diff and what to read closely.",
  "pullRequests.actions.mergeNow": "Merge now",
  "pullRequests.actions.close": "Close pull request",
  "pullRequests.actions.closeShort": "Close",
  "pullRequests.actions.reopen": "Reopen pull request",
  "pullRequests.actions.revert": "Revert changes",
  "pullRequests.handoff.addedToComposer": "Added to the composer",
  "pullRequests.handoff.questionInComposer":
    "The question is in the composer: read it over, then send.",
  "pullRequests.handoff.requestInComposer":
    "The pull request is in the composer: type your question, then send.",
  "pullRequests.handoff.openThreadFailed": "Could not open a thread",
  "pullRequests.handoff.openThreadFailedDescription":
    "Try again from the project, or open a thread first.",
  "pullRequests.handoff.askedInThread": "Asked in a thread",
  "pullRequests.handoff.taskInComposer": "The task is in the composer: read it over, then send.",
  "pullRequests.handoff.preparingCheckout": "Preparing the pull request checkout...",
  "pullRequests.handoff.openCheckoutThreadFailed": "Could not open a thread for the checkout",
  "pullRequests.handoff.prepareCheckoutFailed": "Could not prepare the pull request checkout",
  "pullRequests.handoff.threadNotMoved": "Checked out, but the thread stayed where it was",
  "pullRequests.handoff.threadNotMovedDescription":
    "The checkout is ready on `{branch}`. Point a thread at it from the branch picker, then ask again.",
  "pullRequests.handoff.checkoutStale": "Checked out, but not on the latest commits",
  "pullRequests.handoff.checkoutStaleDescription":
    "The checkout could not be moved onto the pull request's latest commits, so the code there is older than the pull request. Uncommitted work or local commits keep it where it is.",
  "pullRequests.handoff.checkedOutHere": "Checked out here",
  "pullRequests.handoff.checkedOut": "Checked out",
  "pullRequests.handoff.localReadyDescription":
    "This repository is on the pull request's branch, with a thread open on it.",
  "pullRequests.handoff.worktreeReadyDescription":
    "The pull request is in its own worktree, with a thread open on it.",
  "pullRequests.handoff.checkoutReady": "Checkout ready",
  "pullRequests.handoff.addToCurrentComposer": "Adds the pull request to this thread's composer.",
  "pullRequests.handoff.openAwareThread": "Opens a thread that knows which pull request you mean.",
  "pullRequests.commits.unavailable": "Commits unavailable",
  "pullRequests.commits.count": "{count} commit(s)",
  "pullRequests.review.approvalCount": "{count} approval(s)",
  "pullRequests.timeline.showOldestFirst": "Show oldest activity first",
  "pullRequests.timeline.showNewestFirst": "Show newest activity first",
  "pullRequests.code.loadingDiff": "Loading pull request diff...",
  "pullRequests.code.remainderLoadFailed": "The rest of this diff could not be loaded.",
  "pullRequests.code.loadingMoreFiles": "Loading more files...",
  "pullRequests.code.loadMoreFiles": "Load more files",
  "pullRequests.code.expandDiff": "Expand diff",
  "pullRequests.code.collapseDiff": "Collapse diff",
  "pullRequests.code.fixInThread": "Fix in a thread",
  "pullRequests.code.addToReview": "Add to review",
  "pullRequests.code.addToAgent": "Add to agent",
  "pullRequests.code.closeReview": "Close review",
  "pullRequests.code.review": "Review",
  "pullRequests.code.allCommits": "All commits",
  "pullRequests.code.diffScope": "Diff scope: {scope}",
  "pullRequests.code.showMoreCommits": "Show more ({count} left)",
  "pullRequests.code.fileCountOne": "{count} file",
  "pullRequests.code.fileCountMany": "{count} files",
  "pullRequests.code.diffPartiallyHidden": "Some of this diff was not shown",
  "pullRequests.code.diffPartiallyHiddenDescription":
    "The host withheld part of this diff: a binary file, or a change too large to inline.",
  "pullRequests.code.commentsUseWholeChange": "Line comments are written from the whole change",
  "pullRequests.code.commentsUseWholeChangeDescription":
    "A comment is anchored to the whole change, so switch to All commits to write one.",
  "pullRequests.code.expandAllFiles": "Expand all files",
  "pullRequests.code.collapseAllFiles": "Collapse all files",
  "pullRequests.code.stackedDiffView": "Stacked diff view",
  "pullRequests.code.diffLayout": "Diff layout",
  "pullRequests.code.splitDiffView": "Split diff view",
  "pullRequests.code.disableDiffWrapping": "Disable diff line wrapping",
  "pullRequests.code.enableDiffWrapping": "Enable diff line wrapping",
  "pullRequests.code.disableWrapping": "Disable line wrapping",
  "pullRequests.code.enableWrapping": "Enable line wrapping",
  "pullRequests.code.hideFileTree": "Hide file tree",
  "pullRequests.code.showFileTree": "Show file tree",
  "pullRequests.code.noPullRequestChanges": "This pull request has no file changes.",
  "pullRequests.code.noCommitChanges": "This commit has no file changes.",
  "pullRequests.code.orphanConversationsCurrent": "Conversations not on the current diff",
  "pullRequests.code.orphanConversationsLoaded": "Conversations not on the diff loaded so far",
  "pullRequests.code.conversationCountOne": "{count} conversation",
  "pullRequests.code.conversationCountMany": "{count} conversations",
  "pullRequests.code.line": "Line {line}",
  "pullRequests.code.fileTreeAria": "Pull request #{number} files",
  "pullRequests.comments.loadMoreFailed": "More comments could not be loaded",
  "pullRequests.comments.replyFailed": "Reply could not be posted",
  "pullRequests.comments.updateConversationFailed": "The conversation could not be updated",
  "pullRequests.confirm.mergeTitle": "Merge pull request?",
  "pullRequests.confirm.autoMergeTitle": "Enable auto-merge?",
  "pullRequests.confirm.revertTitle": "Revert these changes?",
  "pullRequests.confirm.approveTitle": "Approve workflows to run?",
  "pullRequests.confirm.closeTitle": "Close pull request?",
  "pullRequests.confirm.mergeDescription": "This merges #{number} using {method}.",
  "pullRequests.confirm.autoMergeDescription":
    "This merges #{number} using {method} as soon as the host considers it ready, which may be immediately.",
  "pullRequests.confirm.revertDescription":
    "This opens a new pull request that reverses the changes merged by #{number}.",
  "pullRequests.confirm.approveDescription":
    "This allows {count} workflow(s) from #{number} to run. Review the code and workflow changes first.",
  "pullRequests.confirm.closeDescription": "This closes #{number} without merging it.",
  "pullRequests.confirm.createRevert": "Create revert PR",
  "pullRequests.confirm.approveAndRun": "Approve and run",
  "pullRequests.row.matchedDescription": "Matched in the description",
  "pullRequests.reactions.saveFailed": "The reaction could not be saved",
  "pullRequests.reactions.add": "Add a reaction",
  "pullRequests.review.pendingComment": "Pending: sent when you submit the review",
  "pullRequests.review.discardComment": "Discard this comment",
  "pullRequests.review.unresolve": "Unresolve",
  "pullRequests.review.resolve": "Resolve",
  "pullRequests.review.beforeLatest": ", before the latest commits",
  "pullRequests.comments.loadMore": "Load more comments",
  "pullRequests.comments.reply": "Reply",
  "pullRequests.comments.replyToConversation": "Reply to this conversation",
  "pullRequests.timeline.openOnHost": "Open activity on host",
  "pullRequests.timeline.authorCount": "{count} author(s)",
  "pullRequests.timeline.viewCommit": "View commit {commit}",
  "pullRequests.timeline.untitledCommit": "Untitled commit",
  "pullRequests.timeline.opened": "Pull request opened",
  "pullRequests.timeline.merged": "Pull request merged",
  "pullRequests.timeline.closed": "Pull request closed",
  "pullRequests.timeline.empty": "No activity yet.",
  "pullRequests.review.submitFailed": "The review could not be submitted",
  "pullRequests.review.submittedSuccess": "Review submitted",
  "pullRequests.review.approvedSuccess": "Pull request approved",
  "pullRequests.review.changesRequestedSuccess": "Changes requested",
  "pullRequests.review.noLineComments": "No line comments yet",
  "pullRequests.review.pendingCommentCountOne": "{count} comment pending",
  "pullRequests.review.pendingCommentCountMany": "{count} comments pending",
  "pullRequests.review.discard": "Discard",
  "pullRequests.review.summaryPlaceholder": "Summarize your review (optional)",
  "pullRequests.review.summaryAria": "Review summary",
  "pullRequests.review.approve": "Approve",
  "pullRequests.review.requestChanges": "Request changes",
  "pullRequests.markdown.write": "Write",
  "pullRequests.markdown.preview": "Preview",
  "pullRequests.markdown.nothingToPreview": "Nothing to preview.",
  "pullRequests.markdown.saving": "Saving...",
  "pullRequests.markdown.playVideoOnHost": "Play video on GitHub",
  "pullRequests.markdown.openAttachmentOnHost": "Open attachment on GitHub",
  "pullRequests.loading.list": "Loading pull requests",
  "pullRequests.loading.detail": "Loading pull request",
  "pullRequests.loading.people": "Loading people",
  "pullRequests.loading.timeline": "Loading timeline",
  "pullRequests.loading.conversation": "Loading pull request conversation",
  "files.copyPath": "Copy file path",
  "files.breadcrumb.backTo": "Back to {path}",
  "files.breadcrumb.loadingFolder": "Loading folder…",
  "files.breadcrumb.retryFolder": "Retry loading folder",
  "files.breadcrumb.unavailableFolder": "This folder is no longer available.",
  "files.breadcrumb.partialEmpty":
    "No entries from this folder are available in the partial workspace index.",
  "files.breadcrumb.emptyFolder": "This folder is empty.",
  "files.breadcrumb.refreshFailed": "Refresh failed: retry",
  "files.breadcrumb.truncated": "Some workspace entries are not shown.",
  "files.breadcrumb.browse": "Browse {path}",
  "citation.viewText": "View cited assistant text: {text}",
  "citation.viewSource": "View source",
  "citation.editComment": "Edit citation comment",
  "citation.addComment": "Add comment to citation",
  "citation.remove": "Remove assistant citation",
  "citation.commentOnSelection": "Comment on selected text",
  "citation.commentKeyboardHelp":
    "Enter to save the citation comment; Command/Ctrl+Enter to save and send; Shift+Enter for a new line.",
  "citation.commentPlaceholder": "Add an optional comment...",
  "citation.commentTooLong": "Comments can contain up to {count} characters.",
  "citation.shortenComment": "Shorten comment",
  "media.videoUnavailable": "Video unavailable",
  "media.retrying": "Retrying…",
  "media.retryVideo": "Retry video",
  "media.videoPreview": "Video preview",
  "media.loadingVideo": "Loading video",
  "sidebar.update.readyToDownload": "Update ready to download",
  "sidebar.update.viewRelease": "View release on GitHub",
  "sidebar.update.moreChange": "{count} more change on GitHub",
  "sidebar.update.moreChanges": "{count} more changes on GitHub",
  "sidebar.update.whatsChanged": "What's changed",
  "sidebar.update.changesIn": "Changes in {version}",
  "sidebar.update.olderRelease": "{count} older release on GitHub",
  "sidebar.update.olderReleases": "{count} older releases on GitHub",

  "usage.title": "Usage",
  "usage.breadcrumb": "Usage breadcrumb",
  "usage.range.separator": "to",
  "usage.window.past24h": "Past 24h",
  "usage.window.days": "{count} days",
  "usage.refresh": "Refresh usage",
  "usage.metric.label": "Usage metric",
  "usage.period.label": "Usage period",
  "usage.metric.cost": "Cost",
  "usage.metric.tokens": "Tokens",
  "usage.metric.limits": "Limits",
  "usage.limits.refresh": "Refresh limits",
  "usage.limits.title": "Usage limits",
  "usage.limits.dismiss": "Dismiss usage limits",
  "usage.limits.accounts": "{count} accounts",
  "usage.limits.account": "Account {account}",
  "usage.limits.plan": "Plan",
  "usage.limits.signedIn": "Signed in",
  "usage.limits.via": "Via",
  "usage.limits.left": "Left",
  "usage.limits.resetsLabel": "Resets",
  "usage.limits.restores": "Restores",
  "usage.limits.restoresPool": "+{percent}% of pool",
  "usage.limits.segment": "Segment",
  "usage.rawTokenCost": "Raw token cost",
  "usage.processedTokens": "Processed tokens",
  "usage.fullApiRate": "* if billed at full API rate",
  "usage.sessionSummary": "Input, cache reads and output across {count} sessions.",
  "usage.session": "{count} session",
  "usage.sessions": "{count} sessions",
  "usage.sessionEstimate": "{count} session · API estimate",
  "usage.sessionsEstimate": "{count} sessions · API estimate",
  "usage.provider.costShare": "{share} of cost · {tokens} tokens",
  "usage.provider.tokenShare": "{share} of tokens · {cost}",
  "usage.hourlyCost": "Hourly cost",
  "usage.dailyCost": "Daily cost",
  "usage.hourlyTokens": "Hourly processed tokens",
  "usage.dailyTokens": "Daily processed tokens",
  "usage.chart.aria": "{period} {metric} by provider",
  "usage.period.hourly": "Hourly",
  "usage.period.daily": "Daily",
  "usage.period.hour": "Hour",
  "usage.period.day": "Day",
  "usage.metric.cachedInput": "Cached input",
  "usage.metric.uncachedInput": "Uncached input",
  "usage.metric.output": "Output",
  "usage.metric.cacheSavings": "Cache savings",
  "usage.metric.perActive": "{value} per active {period}",
  "usage.metric.observedInput": "{share} of observed input",
  "usage.metric.cacheWrites": "{count} cache writes",
  "usage.metric.reasoning": "includes {count} reasoning",
  "usage.metric.rawCostMultiple": "{value}x the raw token cost",
  "usage.metric.fullInputRates": "vs full input rates",
  "usage.breakdown": "Breakdown",
  "usage.breakdown.label": "Usage breakdown",
  "usage.totals": "Totals",
  "usage.breakdown.model": "Model",
  "usage.breakdown.total": "Total",
  "usage.breakdown.cost": "Cost",
  "usage.breakdown.share": "Share",
  "usage.breakdown.tokens": "Tokens",
  "usage.noActivity": "No activity in this window.",
  "usage.coverage.failed": "{environment} could not report usage.",
  "usage.coverage.stale": "{environment} runs an older server version and is excluded from totals.",
  "usage.coverage.duplicates":
    "Counted once across environments sharing a transcript directory: {sources}",
  "usage.scanning.one": "1 device still scanning",
  "usage.scanning.many": "{count} devices still scanning",
  "usage.limits.paceAhead": "Ahead of pace: spending faster than the window elapses",
  "usage.limits.paceOn": "On pace with the window",
  "usage.limits.paceUnder": "Under pace: headroom left for the rest of the window",
  "usage.limits.used": "{percent}% used",
  "usage.limits.remaining": "{percent}% left",
  "usage.limits.windowRemaining": "{percent}% of the window left",
  "usage.limits.elapsed": "{percent}% of the window elapsed",
  "usage.limits.evenLine": "The line is where even spending would be.",
  "usage.limits.resetsAt": "Resets {time}",
  "usage.limits.resetsNow": "resets now",
  "usage.limits.resetsIn": "resets in {duration}",
  "usage.limits.resetApplied": "Reset applied. Your windows have cleared.",
  "usage.limits.nothingToReset": "Nothing to reset right now.",
  "usage.limits.noCredit": "No reset credit left.",
  "usage.limits.alreadyRedeemed": "That credit was already redeemed.",
  "usage.limits.noneBanked": "No reset credits banked",
  "usage.limits.creditBanked": "1 reset credit banked",
  "usage.limits.creditsBanked": "{count} reset credits banked",
  "usage.limits.nextExpires": "next expires in {duration}",
  "usage.limits.useFailed": "Could not use the reset credit.",
  "usage.limits.usingCredit": "Using credit…",
  "usage.limits.useReset": "Use reset",
  "usage.limits.expiresIn": "expires in {duration}",
  "usage.limits.useResetCredit": "Use a reset credit",
  "usage.limits.confirmTitle": "Use a reset credit?",
  "usage.limits.confirmDescription":
    "This redeems one credit on your account and clears the current rate-limit windows. It cannot be undone.",
  "usage.limits.useCredit": "Use credit",
  "usage.limits.allAccountsShown": "All accounts are shown by connected providers.",
  "usage.limits.noAccounts": "No accounts reported.",
  "usage.limits.empty": "No provider on a connected environment reports subscription limits.",

  "headerAction.addAction": "Add action",
  "headerAction.open": "Open",
  "headerAction.openInEditor": "Open in editor",
  "headerAction.openPreferredEditor": "Open file in preferred editor",
  "headerAction.chooseEditor": "Choose editor",
  "headerAction.options": "Copy options",
  "headerAction.noEditors": "No installed editors found",
  "headerAction.noSshRoute": "No SSH route to {environment}",
  "headerAction.opensOverSsh": "Opens over SSH. Needs your key on {environment}",
  "gitAction.group": "Git actions",
  "gitAction.options": "Git action options",
  "gitAction.commit": "Commit",
  "gitAction.commitPush": "Commit & push",
  "gitAction.commitPushCreateChangeRequest": "Commit, push & {request}",
  "gitAction.pull": "Pull",
  "gitAction.push": "Push",
  "gitAction.pushCreateChangeRequest": "Push & create {request}",
  "gitAction.syncRef": "Sync branch",
  "gitAction.createChangeRequest": "Create {request}",
  "gitAction.viewChangeRequest": "View {request}",
  "gitAction.publishRepository": "Publish repository...",
  "gitAction.hint.actionInProgress": "Git action in progress.",
  "gitAction.hint.branchDiverged": "Branch has diverged from upstream. Rebase or merge first.",
  "gitAction.hint.branchRequired":
    "Create and check out a branch before pushing or opening a {request}.",
  "gitAction.hint.worktreeClean": "Worktree is clean. Make changes before committing.",
  "gitAction.hint.commitUnavailable": "Commit is currently unavailable.",
  "gitAction.hint.detachedBeforePush": "Detached HEAD: check out a branch before pushing.",
  "gitAction.hint.localChangesBeforePush": "Commit or stash local changes before pushing.",
  "gitAction.hint.behindBeforePush": "Branch is behind upstream. Pull or rebase before pushing.",
  "gitAction.hint.originBeforePush": 'Add an "origin" remote before pushing.',
  "gitAction.hint.noLocalCommits": "No local commits to push.",
  "gitAction.hint.pushUnavailable": "Push is currently unavailable.",
  "gitAction.hint.viewUnavailable": "Viewing the {request} is currently unavailable.",
  "gitAction.hint.detachedBeforeCreate":
    "Detached HEAD: check out a branch before creating a {request}.",
  "gitAction.hint.localChangesBeforeCreate": "Commit local changes before creating a {request}.",
  "gitAction.hint.originBeforeCreate": 'Add an "origin" remote before creating a {request}.',
  "gitAction.hint.noLocalCommitsForCreate": "No local commits to include in a {request}.",
  "gitAction.hint.behindBeforeCreate":
    "Branch is behind upstream. Pull or rebase before creating a {request}.",
  "gitAction.hint.createUnavailable": "Creating a {request} is currently unavailable.",
  "gitAction.hint.statusUnavailable": "Git status is unavailable.",
  "gitAction.hint.unavailable": "This action is currently unavailable.",
  "gitAction.hint.upToDate": "Branch is up to date. No action needed.",

  "projectAction.dialog.addTitle": "Add Action",
  "projectAction.dialog.editTitle": "Edit Action",
  "projectAction.dialog.description":
    "Actions are project-scoped commands you can run from the top bar or keybindings.",
  "projectAction.icon.choose": "Choose icon",
  "projectAction.icon.play": "Play",
  "projectAction.icon.test": "Test",
  "projectAction.icon.lint": "Lint",
  "projectAction.icon.configure": "Configure",
  "projectAction.icon.build": "Build",
  "projectAction.icon.debug": "Debug",
  "projectAction.field.name": "Name",
  "projectAction.field.keybinding": "Keybinding",
  "projectAction.field.command": "Command",
  "projectAction.field.previewUrl": "Preview URL (optional)",
  "projectAction.keybinding.placeholder": "Press shortcut",
  "projectAction.keybinding.hint": "Press a shortcut. Use {key} to clear.",
  "projectAction.preview.description": "Open this URL in the in-app preview when this action runs.",
  "projectAction.runOnWorktreeCreate": "Run automatically on worktree creation",
  "projectAction.autoOpenPreview": "Open preview automatically when this action runs",
  "projectAction.validation.nameRequired": "Name is required.",
  "projectAction.validation.commandRequired": "Command is required.",
  "projectAction.validation.saveFailed": "Failed to save action.",
  "projectAction.action.saveChanges": "Save changes",
  "projectAction.action.save": "Save action",
  "projectAction.action.delete": "Delete action",
  "projectAction.delete.title": 'Delete action "{name}"?',
  "projectAction.delete.description": "This action cannot be undone.",

  "plan.badge": "Plan",
  "plan.fallbackTitle": "Proposed plan",
  "plan.actions": "Plan actions",
  "plan.copied": "Copied!",
  "plan.copy": "Copy to clipboard",
  "plan.download": "Download as markdown",
  "plan.saveToWorkspace": "Save to workspace",
  "plan.expand": "Expand plan",
  "plan.collapse": "Collapse plan",
  "plan.copyFailed": "Could not copy plan",
  "plan.copyFailedDescription": "An error occurred while copying.",
  "plan.workspaceUnavailable": "Workspace path is unavailable",
  "plan.workspaceUnavailableDescription":
    "This thread does not have a workspace path to save into.",
  "plan.enterWorkspacePath": "Enter a workspace path",
  "plan.saved": "Plan saved to workspace",
  "plan.saveFailed": "Could not save plan",
  "plan.saveFailedDescription": "An error occurred while saving.",
  "plan.dialog.title": "Save plan to workspace",
  "plan.dialog.description": "Enter a path relative to {workspace}.",
  "plan.dialog.workspaceFallback": "the workspace",
  "plan.dialog.workspacePath": "Workspace path",

  "relayInstall.step.checking": "Checking current installation",
  "relayInstall.step.waiting": "Waiting for installer",
  "relayInstall.step.downloading": "Downloading relay client",
  "relayInstall.step.verifying": "Verifying download",
  "relayInstall.step.installing": "Installing relay client",
  "relayInstall.step.validating": "Validating executable",
  "relayInstall.step.activating": "Activating installation",
  "relayInstall.title.installing": "Installing relay client",
  "relayInstall.title.confirm": "Install relay client?",
  "relayInstall.description.installing":
    "T3 Code is preparing this environment for secure access through T3 Connect.",
  "relayInstall.description.confirm":
    "T3 Code needs the relay client to make this environment available through T3 Connect.",
  "relayInstall.progress": "{current} of {total}",
  "relayInstall.progressLabel": "Relay client installation progress",
  "relayInstall.keepOpen": "Keep T3 Code open while the relay client is installed.",
  "relayInstall.managedClient": "Managed relay client",
  "relayInstall.versionDescription": "T3 Code will download and install version {version} locally.",
  "relayInstall.downloadAndInstall": "Download and install",

  "sshPassword.title": "SSH verification required",
  "sshPassword.description":
    "Enter the password or verification code required by {target}. T3 Code passes your response to the local SSH process and keeps it in memory to reuse for this host. T3 Code does not write it to disk.",
  "sshPassword.password": "Password",
  "sshPassword.expired": "Expired",
  "sshPassword.expiredDescription": "This SSH verification prompt expired. Try connecting again.",
  "sshPassword.promptFailed": "SSH verification prompt failed.",
  "sshPassword.keyHint":
    "SSH keys may replace a password, but your server can still require a verification code.",

  "connectOnboarding.title": "Set up T3 Connect",
  "connectOnboarding.description":
    "Mesh your devices together - publish this environment and connect the rest, all in one place.",
  "connectOnboarding.enabled": "T3 Connect enabled",
  "connectOnboarding.enabled.environment":
    "This environment is available to your other devices through T3 Connect.",
  "connectOnboarding.enabled.activity":
    "This environment publishes agent activity to your mobile clients.",
  "connectOnboarding.dontShowAgain": "Don't show this again",
  "connectOnboarding.notNow": "Not now",
  "connectOnboarding.enabling": "Enabling...",
  "connectOnboarding.step": "Step {index}",
  "connectOnboarding.step.publish": "Publish",
  "connectOnboarding.step.devices": "Connect devices",
  "connectOnboarding.publishEnvironment": "Publish this environment",
  "connectOnboarding.publishEnvironmentDescription":
    "Make this environment available to your other devices through T3 Connect.",
  "connectOnboarding.publishActivity": "Publish agent activity",
  "connectOnboarding.publishActivityDescription":
    "Send activity from this environment to your mobile clients for push notifications and Live Activities.",
  "connectOnboarding.emptyDevices":
    "No other environments are published to your account yet. Publish one from another device and it will show up here.",
  "connectEnvironment.added": "Environment added",
  "connectEnvironment.connectingThrough": "Connecting to {environment} through T3 Connect.",
  "connectEnvironment.connectFailed": "Could not connect environment",
  "connectEnvironment.connectFailedDescription": "Could not connect the T3 Connect environment.",
  "connectEnvironment.copyTraceId": "Copy trace ID",
  "connectEnvironment.offline": "You appear to be offline.",
  "connectEnvironment.loadFailed": "Could not load T3 Connect environments",
  "connectEnvironment.status.online": "Available - Relay online",
  "connectEnvironment.status.offline": "Available - Relay offline",
  "connectEnvironment.status.checking": "Available - Checking relay status...",
  "connectEnvironment.status.unavailable": "Available - Relay status unavailable",
  "connectEnvironment.relay.online": "Relay online",
  "connectEnvironment.relay.offline": "Relay offline",
  "connectEnvironment.relay.checking": "Checking relay status",
  "connectEnvironment.relay.unavailable": "Relay status unavailable",
  "connectEnvironment.connect": "Connect",
  "connectEnvironment.connecting": "Connecting...",
  "connectEnvironment.connection.connected": "Connected",
  "connectEnvironment.connection.reconnecting": "Reconnecting...",
  "connectEnvironment.connection.reconnectingAfterError":
    "Reconnecting after connection error: {reason}",
  "connectEnvironment.connection.failed": "Connection failed",
  "connectEnvironment.connection.failedWithReason": "Connection failed: {reason}",
  "connectEnvironment.connection.offline": "Offline",
  "connectEnvironment.connection.notConnected": "Not connected",

  "chat.branchSwitch.title": "Switch to {branch}?",
  "chat.branchSwitch.description":
    "You have uncommitted changes. They'll carry over to the other branch, or block the switch if they conflict.",
  "chat.branchSwitch.action": "Switch branch",

  "sidebar.projectRename.title": "Rename project",
  "sidebar.projectRename.description": "Update the title for {path}.",
  "sidebar.projectRename.descriptionGeneric": "Update the project title.",
  "sidebar.projectRename.field": "Project title",
  "sidebar.projectRename.environment": "Environment: {environment}",
  "sidebar.projectGrouping.title": "Project grouping",
  "sidebar.projectGrouping.description": "Choose how {path} should be grouped in the sidebar.",
  "sidebar.projectGrouping.descriptionGeneric":
    "Choose how this project should be grouped in the sidebar.",
  "sidebar.projectGrouping.rule": "Grouping rule",
  "sidebar.projectGrouping.useGlobal": "Use global default",
  "sidebar.projectGrouping.useGlobalValue": "Use global default ({value})",
  "sidebar.projectGrouping.repository": "Group by repository",
  "sidebar.projectGrouping.repositoryPath": "Group by repository path",
  "sidebar.projectGrouping.separate": "Keep separate",
  "sidebar.projectGrouping.repositoryDescription":
    "Projects from the same repository share one sidebar row.",
  "sidebar.projectGrouping.repositoryPathDescription":
    "Only projects with the same repository and repo-relative path share one sidebar row.",
  "sidebar.projectGrouping.separateDescription": "Keep this project on its own sidebar row.",

  "pullRequestThread.title": "Checkout {request}",
  "pullRequestThread.description":
    "Resolve a {provider} {request}, then create the draft thread in the main repository or in a dedicated worktree.",
  "pullRequestThread.request": "Pull request",
  "pullRequestThread.placeholder": "{label} URL, checkout command, or #42",
  "pullRequestThread.validation.empty":
    "Paste a {request} URL, checkout command, or enter 123 / #123.",
  "pullRequestThread.validation.invalid": "Use a {request} URL, checkout command, 123, or #123.",
  "pullRequestThread.prepareFailed": "Failed to prepare {request} thread.",
  "pullRequestThread.branchRange": "{head} to {base}",
  "pullRequestThread.state.open": "Open",
  "pullRequestThread.state.closed": "Closed",
  "pullRequestThread.state.merged": "Merged",
  "pullRequestThread.resolving": "Resolving {request}...",
  "pullRequestThread.local": "Local",
  "pullRequestThread.worktree": "Worktree",
  "pullRequestThread.preparingLocal": "Preparing local...",
  "pullRequestThread.preparingWorktree": "Preparing worktree...",
  "pullRequestConfirm.mergeTitle": "Merge pull request?",
  "pullRequestConfirm.autoMergeTitle": "Enable auto-merge?",
  "pullRequestConfirm.closeTitle": "Close pull request?",
  "pullRequestConfirm.mergeDescription": "This merges #{number} using {method}.",
  "pullRequestConfirm.autoMergeDescription":
    "This merges #{number} using {method} as soon as the host considers it ready, which may be immediately.",
  "pullRequestConfirm.closeDescription": "This closes #{number} without merging it.",
  "pullRequestConfirm.merge": "Merge",
  "pullRequestConfirm.enableAutoMerge": "Enable auto-merge",

  "gitDialog.publish.title": "Publish repository",
  "gitDialog.publish.description":
    "Pick where to host it, then point us at a repository to push to.",
  "gitDialog.publish.step": "Step {index}",
  "gitDialog.publish.provider": "Provider",
  "gitDialog.publish.repository": "Repository",
  "gitDialog.publish.summary": "Summary",
  "gitDialog.publish.setupRequired": "Setup Required",
  "gitDialog.publish.configureProvider":
    "Open Settings -> Source Control to configure this provider.",
  "gitDialog.publish.statusUnavailable":
    "Provider status unavailable. Open Settings -> Source Control and rescan.",
  "gitDialog.publish.notAuthenticated":
    "{provider} is not authenticated. Open Settings -> Source Control for setup guidance.",
  "gitDialog.publish.visibility": "Visibility",
  "gitDialog.publish.private": "Private",
  "gitDialog.publish.privateDescription": "Only invited people",
  "gitDialog.publish.public": "Public",
  "gitDialog.publish.publicDescription": "Anyone on the web",
  "gitDialog.publish.advanced": "Advanced",
  "gitDialog.publish.remote": "Remote",
  "gitDialog.publish.protocol": "Protocol",
  "gitDialog.publish.publishingTo": "Publishing repository to {provider}...",
  "gitDialog.publish.failed": "Publish failed",
  "gitDialog.publish.published": "Repository published",
  "gitDialog.publish.created": "Repository created",
  "gitDialog.publish.publishedDescription": "{branch} is now live on {provider}.",
  "gitDialog.publish.createdDescription":
    'Remote "{remote}" is set up. Make a commit and push it to share your code.',
  "gitDialog.publish.openOn": "Open on {provider}",
  "gitDialog.publish.resultUnavailable": "Publish result unavailable.",
  "gitDialog.publish.publishing": "Publishing...",
  "gitDialog.publish.action": "Publish",
  "gitDialog.commit.title": "Commit changes",
  "gitDialog.commit.description":
    "Review and confirm your commit. Leave the message blank to auto-generate one.",
  "gitDialog.commit.branch": "Branch",
  "gitDialog.commit.detachedHead": "detached HEAD",
  "gitDialog.commit.defaultBranchWarning": "Warning: default branch",
  "gitDialog.commit.files": "Files",
  "gitDialog.commit.fileCount": "{selected} of {total}",
  "gitDialog.commit.excluded": "Excluded",
  "gitDialog.commit.message": "Commit message (optional)",
  "gitDialog.commit.messagePlaceholder": "Leave empty to auto-generate",
  "gitDialog.commit.onNewBranch": "Commit on new branch",
  "gitDialog.defaultBranch.fallbackTitle": "Run action on default branch?",
  "gitDialog.defaultBranch.commitPushTitle": "Commit & push to default branch?",
  "gitDialog.defaultBranch.commitPushDescription":
    'This action will commit and push changes on "{branch}". You can continue on this branch or create a feature branch and run the same action there.',
  "gitDialog.defaultBranch.commitPushContinue": "Commit & push to {branch}",
  "gitDialog.defaultBranch.pushTitle": "Push to default branch?",
  "gitDialog.defaultBranch.pushDescription":
    'This action will push changes on "{branch}". You can continue on this branch or create a feature branch and run the same action there.',
  "gitDialog.defaultBranch.pushContinue": "Push to {branch}",
  "gitDialog.defaultBranch.pushCreateTitle": "Push & create {request} from default branch?",
  "gitDialog.defaultBranch.pushCreateDescription":
    'This action will push local commits and create a {request} from "{branch}". You can continue on this branch or create a feature branch and run the same action there.',
  "gitDialog.defaultBranch.pushCreateContinue": "Push & create {request} from {branch}",
  "gitDialog.defaultBranch.commitCreateTitle":
    "Commit, push & create {request} from default branch?",
  "gitDialog.defaultBranch.commitCreateDescription":
    'This action will commit, push, and create a {request} from "{branch}". You can continue on this branch or create a feature branch and run the same action there.',
  "gitDialog.defaultBranch.commitCreateContinue": "Commit, push & create {request}",
  "gitDialog.defaultBranch.checkoutFeature": "Checkout feature branch & continue",
  "gitDialog.defaultBranch.pullRequest": "pull request",
  "gitDialog.defaultBranch.mergeRequest": "merge request",
  "gitDialog.defaultBranch.changeRequest": "change request",

  "filePicker.label": "File picker",
  "filePicker.openFile": "Open file",
  "filePicker.search": "Search files...",
  "filePicker.searching": "Searching workspace files...",
  "filePicker.indexing": "Indexing workspace files...",
  "filePicker.noMatches": "No matching files.",
  "filePicker.empty": "No files found.",
  "filePicker.openProject": "Open a project to search its files.",
  "contextWindow.title": "Context Window",
  "contextWindow.usedPercentage": "Context window {percentage} used",
  "contextWindow.usedTokens": "Context window {tokens} tokens used",
  "contextWindow.usage": "Context window usage",
  "contextWindow.totalProcessed": "Total processed",
  "contextWindow.compactsAt": "Compacts automatically at {tokens} tokens.",
  "contextWindow.compactsForModel": "Context for {model} compacts automatically when needed.",
  "contextWindow.compactsAutomatically": "Context compacts automatically when needed.",
  "markdownFile.openInEditor": "Open in editor",
  "markdownFile.openInNamedEditor": "Open in {editor}",
  "markdownFile.revealInFinder": "Reveal in Finder",
  "markdownFile.revealInFileExplorer": "Reveal in File Explorer",
  "markdownFile.revealInFiles": "Reveal in Files",
  "markdownFile.openInIntegratedBrowser": "Open in integrated browser",
  "markdownFile.openInSystemBrowser": "Open in system browser",
  "markdownFile.copyLink": "Copy link",
  "markdownFile.copyRelativePath": "Copy relative path",
  "markdownFile.copyFullPath": "Copy full path",
  "markdownFile.relativePath": "relative path",
  "markdownFile.fullPath": "full path",
  "markdownFile.copied": "Copied {target}",
  "markdownFile.copyFailed": "Could not copy {target}",
  "markdownFile.clipboardUnavailable": "Clipboard access is unavailable.",
  "markdownFile.openFailed": "Unable to open file",
  "markdownFile.openBrowserFailed": "Unable to open file in browser",
  "markdownFile.revealFailed": "Unable to reveal file",
  "markdownFile.fileOptions": "File options for {file}",
  "markdownLink.linkThread": "Link to thread",
  "markdownLink.unlinkThread": "Unlink from thread",
  "markdownLink.pullRequestUnavailable": "The pull request is not available in this environment.",
  "markdownLink.linkFailed": "Unable to link pull request",
  "markdownLink.unlinkFailed": "Unable to unlink pull request",
  "markdownLink.requestFailed": "The request failed.",
  "markdown.toggleTask": "Toggle task",
  "projectIcon.label": "Choose project icon",
  "projectIcon.select": "Select icon",
  "projectIcon.search": "Search image files...",
  "projectIcon.searching": "Searching project files...",
  "projectIcon.indexing": "Indexing project files...",
  "projectIcon.noMatches": "No matching image files.",
  "projectIcon.empty": "No image files found.",
  "projectIcon.openFailed": "Could not open image picker",
  "contentSearch.label": "Search project contents",
  "contentSearch.labelInProject": "Search file contents in {project}",
  "contentSearch.search": "Search project contents...",
  "contentSearch.searchIn": "Search in {project}",
  "contentSearch.matchCase": "Match case",
  "contentSearch.wholeWord": "Match whole word",
  "contentSearch.regex": "Use regular expression",
  "contentSearch.searching": "Searching...",
  "contentSearch.invalidRegex": "Invalid regular expression",
  "contentSearch.results": "{results} results in {files} files",
  "contentSearch.noResults": "No results found.",
  "contentSearch.typeToSearch": "Type to search across your project.",
  "imagePreview.label": "Expanded image preview",
  "imagePreview.close": "Close image preview",
  "imagePreview.previous": "Previous image",
  "imagePreview.next": "Next image",
  "mediaPreview.image": "image",
  "mediaPreview.video": "video",
  "mediaPreview.expanded": "Expanded {media} preview",
  "mediaPreview.close": "Close {media} preview",
  "mediaPreview.videoDownloadFailed": "Could not download this video.",
  "mediaPreview.videoUnsupported": "This video format cannot be played here.",
  "mediaPreview.videoDownloading": "Downloading…",
  "mediaPreview.videoDownload": "Download video",
  "browserPreview.navigation": "Navigation",
  "browserPreview.back": "Back",
  "browserPreview.forward": "Forward",
  "browserPreview.stop": "Stop",
  "browserPreview.refresh": "Refresh",
  "browserPreview.loading": "Loading…",
  "browserPreview.urlPlaceholder": "Search or enter URL",
  "browserPreview.openSystemBrowser": "Open in system browser",
  "browserPreview.cancelAnnotation": "Cancel annotation",
  "browserPreview.annotate": "Annotate preview",
  "browserPreview.cancelAnnotationHint": "Cancel annotation (Esc)",
  "browserPreview.annotateHint": "Annotate elements, regions, and drawings",
  "browserPreview.stopRecording": "Stop recording",
  "browserPreview.captureScreenshot": "Capture screenshot",
  "browserPreview.recordHint": "Screenshot · Shift-click to record",
  "browserPreview.float": "Float preview over chat",
  "browserPreview.closeFloating": "Close floating preview",
  "browserPreview.floatingLabel": "Floating browser preview",
  "browserPreview.openRightPanel": "Open preview in right panel",
  "browserPreview.closePoppedOut": "Close popped-out preview",
  "browserPreview.popOut": "Pop preview into separate window",
  "browserPreview.closeSeparateWindow": "Close separate window",
  "browserPreview.popIntoSeparateWindow": "Pop into separate window",
  "browserPreview.reconnecting": "Reconnecting preview…",
  "browserPreview.popOutUpdateFailed": "Unable to update popped-out preview",
  "common.copied": "Copied!",
  "common.copyFailed": "Failed to copy",
  "common.copyLink": "Copy link",
  "common.copyToClipboard": "Copy to clipboard",
  "common.loading": "Loading…",
  "composer.controls.more": "More composer controls",
  "composer.controls.mode": "Mode",
  "composer.controls.chat": "Chat",
  "composer.controls.plan": "Plan",
  "composer.controls.access": "Access",
  "composer.controls.supervised": "Supervised",
  "composer.controls.autoAcceptEdits": "Auto-accept edits",
  "composer.controls.auto": "Auto",
  "composer.controls.fullAccess": "Full access",
  "changedFiles.count": "{count} changed file(s)",
  "changedFiles.countOne": "{count} changed file",
  "changedFiles.countMany": "{count} changed files",
  "changedFiles.show": "Show files",
  "changedFiles.hide": "Hide files",
  "changedFiles.expandAll": "Expand all folders",
  "changedFiles.collapseAll": "Collapse all folders",
  "changedFiles.openDiff": "Open diff",
  "changedFiles.openFullDiff": "Open the full diff",
  "changedFiles.scopeCount": "{count} file(s)",
  "changedFiles.scopeCountOne": "{count} file",
  "changedFiles.scopeCountMany": "{count} files",
  "timeline.empty": "Send a message to start the conversation.",
  "timeline.revert": "Revert to this message",
  "timeline.loadingAttachment": "Loading {name}",
  "timeline.playAttachment": "Play {name}",
  "timeline.downloadAttachment": "Download {name}",
  "projectSettings.breadcrumb": "Project settings breadcrumb",
  "projectSettings.projects": "Projects",
  "projectSettings.switchProject": "Switch project",
  "projectSettings.unavailable": "Unavailable project",
  "projectSettings.empty": "Add a project from the sidebar to configure it here.",
  "projectSettings.removed": "This project is no longer available.",
  "projectSettings.pathCopied": "Path copied",
  "projectSettings.copyPathFailed": "Failed to copy path",
  "projectSettings.titleEmpty": "Project title cannot be empty",
  "projectSettings.renameFailed": "Failed to rename project",
  "projectSettings.modelUpdateFailed": "Failed to update default model",
  "projectSettings.workspaceUpdateFailed": "Failed to update new-thread workspace",
  "projectSettings.iconUpdateFailed": "Failed to update project icon",
  "projectSettings.autoPullUpdateFailed": "Failed to update automatic pull setting",
  "projectSettings.scriptsSaveFailed": "Failed to save scripts",
  "projectSettings.keybindingSaveFailed": "Failed to save keybinding",
  "projectSettings.keybindingRemoveFailed": "Failed to remove keybinding",
  "projectSettings.importFailed": "Failed to import action.",
  "projectSettings.removeFailed": "Failed to remove {name}",
  "projectSettings.failureOnEnvironment": "{failure} on {environment}",
  "projectSettings.currentEnvironment": "the current environment",
  "projectSettings.scriptSaving": "Another script change is still saving. Try again.",
  "projectSettings.removeConfirmWithThreads":
    "Remove project “{name}” and delete its {count} thread(s)?",
  "projectSettings.removeConfirm": "Remove project “{name}”?",
  "projectSettings.path": "Path: {path}",
  "projectSettings.environment": "Environment: {environment}",
  "projectSettings.removeGroupedEntries": "This removes {count} grouped project entries.",
  "projectSettings.clearHistory": "This permanently clears conversation history for those threads.",
  "projectSettings.clearHistoryWithArchived":
    "This permanently clears conversation history for those threads and any archived threads.",
  "projectSettings.clearArchivedHistory":
    "This permanently clears any archived conversation history.",
  "projectSettings.removeEntriesOnly":
    "This removes only the project entries, not the files on disk.",
  "projectSettings.otherEntriesUnaffected": "Other entries in this grouped project are unaffected.",
  "projectSettings.cannotUndo": "This action cannot be undone.",
  "projectSettings.section.project": "Project",
  "projectSettings.name": "Name",
  "projectSettings.nameDescription":
    "The shared name for this project group in the sidebar and thread lists.",
  "projectSettings.projectName": "Project name",
  "projectSettings.icon": "Project icon",
  "projectSettings.automatic": "Automatic",
  "projectSettings.chooseIcon": "Choose a project icon file",
  "projectSettings.chooseBuiltInIcon": "Choose icon",
  "projectSettings.chooseFile": "Choose file",
  "projectSettings.section.newThreads": "New threads",
  "projectSettings.model": "Model",
  "projectSettings.modelDescription":
    "New threads in this project start with this model. Applies to every checkout in this group.",
  "projectSettings.noProviders": "No providers available",
  "projectSettings.workspace": "Workspace",
  "projectSettings.workspaceDescription":
    "Where new threads in this project start. Overrides t3.json and the global default; applies to every checkout in this group.",
  "projectSettings.newThreadWorkspace": "New-thread workspace",
  "projectSettings.defaultPerCheckout": "Default (per checkout)",
  "projectSettings.defaultValue": "Default ({value})",
  "projectSettings.defaultEachCheckout": "Default (each checkout's t3.json or global setting)",
  "projectSettings.defaultSource": "Default ({source}: {value})",
  "projectSettings.worktree": "Worktree",
  "projectSettings.local": "Local checkout",
  "projectSettings.global": "global setting",
  "projectSettings.autoPull": "Automatically pull",
  "projectSettings.autoPullDescription":
    "Keeps the default branch current in the background when the checkout has no local changes or commits.",
  "projectSettings.autoPullAria": "Automatically pull the default branch",
  "projectSettings.section.checkout": "Checkout",
  "projectSettings.selectedCheckout": "Selected checkout",
  "projectSettings.thisMachine": "This machine",
  "projectSettings.copyCheckoutPath": "Copy checkout path",
  "projectSettings.copyPath": "Copy path",
  "projectSettings.threadCount": "{count} thread(s)",
  "projectSettings.grouping": "Project grouping",
  "projectSettings.groupingDescription":
    "How this checkout joins project groups in the sidebar. Changing it can move you to a different project group.",
  "projectSettings.groupingRule": "Grouping rule for {checkout}",
  "projectSettings.groupByRepository": "Group by repository",
  "projectSettings.groupByRepositoryPath": "Group by repository path",
  "projectSettings.keepSeparate": "Keep separate",
  "projectSettings.useGlobalDefault": "Use global default",
  "projectSettings.removeCheckout": "Remove checkout",
  "projectSettings.removeCheckoutDescription":
    "Removes this checkout and its threads from the project group. Files on disk are not touched.",
  "projectSettings.actions": "Actions",
  "projectSettings.actionsDescription": "Saved and run only in {checkout}.",
  "projectSettings.importScripts": "Import scripts",
  "projectSettings.importFromT3": "Import from t3.json",
  "projectSettings.importDescription":
    "Add actions declared by this checkout without editing them first.",
  "projectSettings.addAction": "Add action",
  "projectSettings.noActions": "No actions configured for this checkout.",
  "projectSettings.setupBadge": "setup",
  "projectSettings.previewBadge": "preview · desktop only",
  "projectSettings.editAction": "Edit {name}",
  "projectSettings.invalidT3": "t3.json is invalid",
  "projectSettings.invalidT3Description":
    "A t3.json exists in this checkout but fails to parse, so every action and icon it declares is ignored. Check the JSON syntax and icon values.",
  "projectSettings.section.danger": "Danger",
  "projectSettings.removeEverywhere": "Remove this project everywhere",
  "projectSettings.removeProject": "Remove project",
  "projectSettings.removeAllEntries": "Remove all entries",
  "projectSettings.removeEverywhereDescription":
    "Deletes all {count} checkout entries and their threads on every machine. Files on disk are not touched.",
  "projectSettings.removeDescription":
    "Deletes the project entry and its threads. Files on disk are not touched.",
  "chat.reconnectFailed": "Could not reconnect environment",
  "chat.reconnectFailedDescription": "Failed to reconnect.",
  "chat.environmentUpdating": "It may be finishing an update. One moment.",
  "chat.environmentDisconnected": "The environment is not connected.",
  "chat.attachmentPlayFailed": "Could not play {name}",
  "chat.attachmentDownloadFailed": "Could not download {name}",
  "chat.attachmentUnavailable": "The attachment is unavailable.",
  "chat.addToChatFailed": "Unable to add to chat",
  "chat.composerBusy": "The composer is busy; try again once it is ready.",
  "chat.actionDeleted": "Deleted action “{name}”",
  "chat.actionUnknown": "Unknown",
  "chat.actionDeleteFailed": "Could not delete action",
  "chat.unexpectedError": "An unexpected error occurred.",
  "chat.copyPathFailed": "Failed to copy path",
  "chat.pathCopied": "Path copied",
  "chat.wakeFailed": "Failed to wake thread",
  "chat.checkoutSwitchFailed": "Failed to switch checkout",
  "chat.checkoutThreadUpdateFailed": "Checkout switched, but the thread could not be updated",
  "chat.branchChangedWas": "Branch changed: was",
  "chat.branchChangedDescription":
    "This thread last ran on {previous}. Sending will continue on {current}.",
  "chat.annotationAttached": "Annotation attached to draft",
  "chat.annotationSendUnavailable":
    "Sending is unavailable right now. Finish the current action, then send.",
  "chat.messageNotSentDisconnected": "Not connected: message not sent",
  "chat.reconnectingDescription":
    "Reconnecting to the environment. Try again once it is connected.",
  "chat.chooseProjectFirst": "Choose a project first",
  "chat.draftProjectUnavailable": "This draft no longer points to an available project.",
  "chat.implementationStartFailed": "Could not start implementation thread",
  "chat.implementationCreateError": "An error occurred while creating the new thread.",
  "chat.pullRequestsUnavailable": "Pull requests unavailable",
  "chat.pullRequestsUpdateServer":
    "Update this environment's T3 Code server to browse pull requests.",
  "chat.scrollToEnd": "Scroll to end",
  "chat.connectingTo": "Connecting to {environment}",
  "chat.reconnectingTo": "Reconnecting to {environment}",
  "chat.reconnectBeforeActions":
    "Reconnect this environment before sending messages or running actions.",
  "chat.reconnect": "Reconnect",
  "chat.serverUpdateAvailable": "Server update available",
  "chat.dismissUpdateNotice": "Dismiss update notice",
  "chat.restoringBranch": "Restoring...",
  "chat.restoreBranch": "Restore branch",
  "chat.dismissBranchNotice": "Dismiss branch change notice",
  "sidebar.tooltip.branchMismatch": "You're currently checked out on another branch.",
  "sidebar.tooltip.terminalProcesses": "{count} terminal process(es) running",
  "sidebar.tooltip.errorOccurred": "Error occurred",
  "sidebar.pathCopied": "Path copied",
  "sidebar.copyPathFailed": "Failed to copy path",
  "sidebar.branchCopied": "Branch copied",
  "sidebar.copyBranchFailed": "Failed to copy branch",
  "sidebar.threadIdCopied": "Thread ID copied",
  "sidebar.copyThreadIdFailed": "Failed to copy thread ID",
  "sidebar.titleEmpty": "Thread title cannot be empty",
  "sidebar.renameFailed": "Failed to rename thread",
  "sidebar.wakeFailed": "Failed to wake thread",
  "sidebar.pinFailed": "Failed to pin thread",
  "sidebar.unpinFailed": "Failed to unpin thread",
  "sidebar.reorderFailed": "Failed to reorder pinned threads",
  "sidebar.snoozeFailed": "Failed to snooze thread",
  "sidebar.snoozedUntil": "Snoozed until {time}",
  "sidebar.snoozeManyFailed": "Failed to snooze threads",
  "sidebar.regenerateManyFailed": "Failed to regenerate thread titles",
  "sidebar.createFailed": "Could not create thread",
  "sidebar.regenerateFailed": "Failed to regenerate thread title",
  "sidebar.pathUnavailable": "Path unavailable",
  "sidebar.pathUnavailableDescription": "This thread does not have a workspace path to copy.",
  "sidebar.snoozedMany": "Snoozed {count} thread(s)",
  "sidebar.snoozedPartial": "Snoozed {done} of {total} threads",
  "sidebar.snoozePartialDescription": "{count} thread(s) couldn't be snoozed.",
  "sidebar.confirmArchiveThread": "Archive thread “{title}”?",
  "sidebar.archiveFailed": "Failed to archive thread",
  "sidebar.archiveNavigationFailed": "Thread archived, but navigation failed",
  "settings.about.error.trackTitle": "Could not change update track",
  "settings.about.error.trackDescription": "Update track change failed.",
  "settings.about.error.downloadTitle": "Could not download update",
  "settings.about.error.downloadDescription": "Download failed.",
  "settings.about.error.confirmTitle": "Could not confirm update",
  "settings.about.error.confirmDescription": "Update confirmation failed.",
  "settings.about.error.installTitle": "Could not install update",
  "settings.about.error.installDescription": "Install failed.",
  "settings.about.error.checkTitle": "Could not check for updates",
  "settings.about.error.unavailable": "Automatic updates are not available in this build.",
  "settings.about.error.checkDescription": "Update check failed.",
  "appearance.theme.import.toastAdded": "{theme} added",
  "appearance.theme.import.toastUpdated": "{theme} updated",
  "appearance.theme.import.toastManyAdded": "{count} themes added",
  "appearance.theme.import.toastManyUpdated": "{count} themes updated",
  "appearance.theme.import.toastAppearanceActive": "It’s now your {appearance} theme.",
  "providers.update.failedTitle": "Could not update {provider}",
  "providers.update.failedDescription": "The provider update command could not be started.",
} as const;

export type WebMessageKey = keyof typeof EN_MESSAGES;
export type WebMessages = Readonly<Record<WebMessageKey, string>>;
export type WebMessageValues = Readonly<Record<string, string | number>>;

const ZH_CN_MESSAGES = {
  "guestShare.guestName": "访客：{name}",
  "guestShare.waiting": "{count} 条访客消息待处理",
  "guestShare.dismissFrom": "忽略 {name} 的消息",
  "guestShare.sendFrom": "发送 {name} 的消息",
  "permissions.changedFor": "{label} 的权限已变更",
  "migration.restoringThreads": "正在恢复您的会话…",
  "migration.restoringDescription": "正在从旧版本迁移 {count} 个会话，您可以继续工作。",

  "chat.queuedEdit.threadSuffix": "（会话“{title}”）",
  "chat.queuedEdit.recovered": "未保存的编辑{where}已保留在输入框中。",
  "chat.queuedEdit.discarded": "未保存的编辑{where}已丢弃。",
  "addProjectToMachine.description": "克隆 {project}，并复制 Git 未跟踪的本地文件。",
  "addProjectToMachine.fileOne": "+ {count} 个文件",
  "addProjectToMachine.fileMany": "+ {count} 个文件",
  "addProjectToMachine.folderOn": "{machine} 上的文件夹",
  "addProjectToMachine.selectedFiles": "已选择 {selected}/{total}",
  "addProjectToMachine.truncated": "仅显示前 {count} 个文件。",
  "addProjectToMachine.cloneTo": "克隆到{machine}",
  "branchToolbar.previousWorktreeBranch": "上一个工作树（{branch}）",
  "connectAgent.title": "连接 {client}",
  "connectAgent.requestDescription": "此智能体想要访问 {host} 上所有项目中的对话。",
  "connectAgent.localWarning":
    "此名称由智能体自行指定。批准后将返回打开此页面的计算机上的 {host}。请仅批准你刚刚发起的登录。",
  "connectAgent.remoteWarning":
    "此名称由智能体自行指定。批准后，运行 {host} 的人将获得访问权限。请仅批准你刚刚在那里发起的登录。",
  "connectAgent.pairingHelp": "在“设置 → 共享与访问”中创建配对码，或在此设备上运行 {command}。",
  "connectProfile.linkDateUnavailable": "关联日期不可用",
  "connectProfile.linkedAt": "关联于 {date}",
  "connectProfile.confirmDeregister": "确认取消 {name} 的注册",
  "connectProfile.removeDescription": "“{name}”将从此账户中移除。",
  "time.relative.justNow": "刚刚",
  "time.relative.minutesAgo": "{count} 分钟前",
  "time.relative.hoursAgo": "{count} 小时前",
  "time.relative.daysAgo": "{count} 天前",
  "chat.queuedEdit.tooManyAttachments": "每条消息最多可添加 {count} 个附件。",
  "pairing.hosted.retryError": "{error} 如果后端已接受此一次性令牌，请获取新的配对链接后再重试。",

  "pullRequests.comments.olderConversations": "显示更早的 {count} 段讨论（已隐藏 {hidden} 段）",
  "pullRequests.comments.recentConversations": "仅显示最近的 {count} 段讨论",
  "pullRequests.comments.replyOne": "{count} 条回复",
  "pullRequests.comments.replyMany": "{count} 条回复",
  "pullRequests.comments.moreReplyOne": "还有 {count} 条回复",
  "pullRequests.comments.moreReplyMany": "还有 {count} 条回复",
  "pullRequests.comments.moreReplies": "更多回复",
  "pullRequests.comments.viewOnHost": "在托管平台查看",
  "pullRequests.handoff.checkoutUnconfirmed": "已检出，但尚未确认是否为最新提交",
  "pullRequests.handoff.checkoutUnconfirmedDescription":
    "无法确认此检出目录是否位于拉取请求的最新提交，因此代码可能不同。本地提交或未提交的更改会使检出目录保持原状。",
  "pullRequests.detail.copied": "已复制{label}",
  "pullRequests.code.reviewContext": "PR #{number} 审查",
  "pullRequests.guide.removedDiffLines": "已移除的代码，显示 {shown}/{total} 行差异",
  "pullRequests.guide.diffLines": "显示 {shown}/{total} 行差异",
  "pullRequests.diff.openOnProvider": "在 {provider} 中打开",
  "pullRequests.diff.technicalDetails": "技术详情",
  "pullRequests.diff.rateLimitTitle": "已达到 {provider} 请求频率限制",
  "pullRequests.diff.rateLimitDescription":
    "{provider} 暂时限制了请求频率，无法加载此差异。请在限制重置后重试，或前往 {provider} 查看更改。",
  "pullRequests.diff.unavailableTitle": "无法加载更改",
  "pullRequests.diff.unavailableDescription":
    "环境无法加载此拉取请求的差异。请重试，或前往 {provider} 查看更改。",
  "providers.status.signInUnchecked": "已安装 · 尚未检查登录状态",
  "providerUpdate.description.manualOne": "请使用安装时的应用或工具更新 {providers}。",
  "providerUpdate.description.manualMany": "请使用安装时的应用或工具更新 {providers}。",
  "draftHero.questionShort": "我们来构建什么？",
  "draftHero.questionPrefix": "我们要在",
  "draftHero.questionSuffix": "中构建什么？",
  "pullRequests.guide.fileOne": "{count} 个文件",
  "pullRequests.guide.fileMany": "{count} 个文件",
  "pullRequests.guide.noteOne": "{count} 条注释",
  "pullRequests.guide.noteMany": "{count} 条注释",
  "pullRequests.guide.loadingFile": "正在加载 {path}...",
  "pullRequests.guide.linesMissing": "的第 {start}-{end} 行不在已加载的差异中。",
  "pullRequests.handoff.checkoutThreadMissing":
    "已在 `{branch}` 准备好检出目录。请从分支选择器将任务指向该目录，然后重试。",

  "pullRequests.checks.showLess": "收起",
  "pullRequests.checks.showAll": "显示全部",
  "pullRequests.checks.openAria": "打开检查：{status}",
  "pullRequests.checks.failedCount": "{count} 项失败",
  "pullRequests.comments.filterAria": "筛选评论",
  "pullRequests.comments.hidden": "已隐藏 {count} 条",
  "pullRequests.comments.onlyHidden": "唯一的评论已被筛选器隐藏。",
  "pullRequests.comments.allHidden": "全部 {count} 条评论均已被筛选器隐藏。",
  "pullRequests.detail.stackedOn": "堆叠于 {branch}",
  "pullRequests.detail.openOnHostAria": "在代码托管平台打开拉取请求 #{number}",
  "pullRequests.detail.notFound": "未找到拉取请求 #{number}",
  "pullRequests.detail.copyFailed": "复制{label}失败",
  "pullRequests.branch.deleteTitle": "删除分支 {branch}？",
  "pullRequests.branch.deleteDescription":
    "这将从 {repository} 中删除 {branch}。本地分支和工作树不会改变。",
  "pullRequests.speed.aria": "拉取请求 #{number} 的快捷操作",
  "pullRequests.speed.immediately": "立即{action}",
  "pullRequests.speed.closedPartial": "已关闭 {total} 个拉取请求中的 {closed} 个",
  "pullRequests.speed.closedOne": "已关闭 {count} 个拉取请求",
  "pullRequests.speed.closedMany": "已关闭 {count} 个拉取请求",
  "pullRequests.guide.openFile": "在代码中打开 {path}",
  "pullRequests.guide.stopRead": "第 {number} 站，已阅读",
  "pullRequests.guide.next": "下一站：{title}",
  "pullRequests.guide.readCount": "已读 {read}/{total}",
  "pullRequests.guide.stopCount": "第 {index} 站，共 {total} 站",
  "appearance.themeEditor.family.searchMatches": "搜索匹配项",
  "appearance.themeEditor.family.currentMatch": "当前匹配项",

  "newProject.repositoryError": "无法创建 {provider} 仓库",
  "newProject.publishedRepository": "已发布到 {provider}",
  "newProject.createRepositoryOn": "在 {provider} 创建私有仓库",
  "newProject.providerAccount": "您的 {provider} 账户",
  "newProject.createWithShortcut": "创建（{shortcut}）",

  "newProject.openError": "打开项目失败",
  "sidebar.working": "进行中（{count}）",
  "composer.menu.findingPullRequest": "正在查找拉取请求…",
  "composer.messageLabel": "消息",
  "composer.menu.label.paths": "文件和文件夹",
  "composer.menu.label.pullRequests": "拉取请求",
  "composer.menu.label.commands": "命令",
  "composer.menu.label.skills": "技能",
  "composer.traits.ultrafastModeOn": "已开启极速模式",
  "composer.traits.fastModeOn": "已开启快速模式",
  "composer.traits.ultrafast": "极速",
  "mobileClients.emptyDescription":
    "在手机上安装 T3 Code 并登录 T3 Connect，即可接收推送通知和实时活动。",
  "mobileClients.description": "接收来自你的环境的通知的移动设备。",
  "appearance.fontPreview.prompt": "输入区字体预览",
  "settings.workingShelf.name": "进行中分区",
  "settings.workingShelf.title": "进行中分区（测试版）",
  "settings.workingShelf.description":
    "将正在执行和监控中的任务收纳到“进行中”分区；需要你处理时，它们会返回收件箱顶部。",
  "settings.restartContinuation.description":
    "在所选环境更新、崩溃或设备重启后，自动恢复被中断的任务。",
  "providerUpdate.pill.updatingOne": "正在更新 {provider}",
  "providerUpdate.pill.updatingMany": "正在更新 {count} 个供应商",
  "providerUpdate.pill.progress": "正在更新 {providers}。",
  "providerUpdate.pill.failedOne": "{provider} 更新失败",
  "providerUpdate.pill.failedMany": "{count} 个供应商更新失败",
  "providerUpdate.pill.failedDetail": "{providers} 更新失败，请到供应商设置查看详情。",
  "providerUpdate.pill.unchangedOne": "{provider} 仍需更新",
  "providerUpdate.pill.unchangedMany": "{count} 个供应商仍需更新",
  "providerUpdate.pill.unchangedDetail": "{providers} 仍为旧版本，请到供应商设置查看详情。",
  "providerUpdate.pill.updatedOne": "{provider} 已更新",
  "providerUpdate.pill.updatedVersion": "{provider} 已更新：{version}",
  "providerUpdate.pill.updatedMany": "{count} 个供应商已更新",
  "providerUpdate.pill.updatedDetail": "新会话将使用更新后的供应商。",
  "providerUpdate.pill.dismiss": "关闭供应商更新提示",
  "providerUpdate.pill.dismissDetail": "隐藏至供应商状态发生变化",
  "providerUpdate.action.updating": "正在更新…",
  "providerUpdate.action.updateAll": "全部更新",
  "providerUpdate.run.failedFallback": "服务提供方更新失败。",
  "providerUpdate.run.unfinished": "服务提供方更新未完成。",
  "providerUpdate.run.updated": "服务提供方已更新",
  "providerUpdate.run.updatedMany": "已更新 {count} 个服务提供方",
  "providerUpdate.run.partialFailure": "{total} 个服务提供方中有 {failed} 个更新失败",
  "providerUpdate.run.failed": "服务提供方更新失败",
  "providerUpdate.run.failedMany": "服务提供方更新失败",
  "providerUpdate.run.descriptionSingle": "新会话将使用更新后的服务提供方。",
  "providerUpdate.run.descriptionMany": "新会话将使用更新后的服务提供方。",
  "draftHero.noProject": "无项目",
  "draftHero.startWithoutProject": "或不选择项目直接开始",
  "home.heroDescriptionWithScratch": "添加项目，或直接开始。",
  "home.startWithoutProject": "不选择项目，直接开始",
  "newProject.noProject": "无项目",
  "newProject.newThreadWithoutProject": "新建无项目任务",
  "newProject.description": "输入名称以创建新的 Git 仓库",
  "newProject.environments": "环境",
  "newProject.addExisting": "添加现有项目",
  "newProject.addExistingDescription": "打开文件夹或克隆仓库",
  "newProject.options": "选项",
  "newProject.createGitHub": "在 GitHub 上创建私有仓库",
  "newProject.gitHubAccount": "你的 GitHub 账户",
  "newProject.create": "创建",
  "newProject.creating": "正在创建",
  "newProject.createShortcut": "创建（Enter）",
  "newProject.toggle": "切换",
  "newProject.createsPath": "将创建 {path}",
  "newProject.goesInPath": "将保存到 {path}",
  "newProject.onEnvironment": "（{environment}）",
  "newProject.restartAgent": "重启 Agent 会话",
  "newProject.agentWillRestart": "Agent 会话将重启",
  "newProject.agentWillRestartDescription": "发送下一条消息时将启动新会话。",
  "newProject.gitHubError": "无法创建 GitHub 仓库",
  "newProject.gitHubErrorDescription": "{error} 可在 Git 菜单中选择“发布仓库”重试。",
  "newProject.publishedGitHub": "已发布到 GitHub",
  "newProject.createError": "无法创建项目",
  "newProject.created": "已创建 {name}",
  "newProject.createdWithoutCommit": "已创建 {name}，但未完成首次提交",
  "newProject.commitErrorDescription": "{error} 项目位于 {path}。",
  "newProject.openErrorDescription": "{error} 此客户端同步后，项目将显示在侧栏中。",
  "newProject.scratchError": "无法启动无项目任务",
  "keybindings.command.chatNewWithoutProject": "聊天：新建无项目任务",
  "upstream.comments.older": "显示较早的 {count} 条评论（隐藏了 {hidden} 条）",
  "upstream.comments.recent": "仅显示最近 {count} 条评论",
  "upstream.comments.olderBots": "显示较早的 {count} 条机器人评论（隐藏了 {hidden} 条）",
  "upstream.comments.recentBots": "仅显示最近 {count} 条机器人评论",
  "upstream.comments.bots": "{count} 条机器人评论",
  "upstream.comments.finished": "{count} 条已解决或已撤销的评论",
  "upstream.usage.unpriced": "API 费用估算不包含 {share} 的未定价记录。",
  "upstream.sidebar.projectFilter": "按 {project} 筛选",
  "upstream.sidebar.allProjects": "显示所有项目",
  "upstream.sidebar.autoSettle": "自动收起行为",
  "upstream.sidebar.enabled": "已启用",
  "upstream.sidebar.disabled": "已禁用",
  "common.cancel": "取消",
  "common.yes": "是",
  "common.no": "否",
  "common.confirm": "确认",
  "common.confirmAction": "确认操作",
  "common.confirmationRequired": "此操作需要你的确认。",
  "common.back": "返回",
  "common.retry": "重试",
  "common.continue": "继续",
  "common.reload": "重新加载应用",
  "common.done": "完成",
  "common.close": "关闭",
  "common.delete": "删除",
  "common.undo": "撤销",
  "common.search": "搜索",
  "common.clear": "清除",
  "common.error": "发生错误。",
  "common.enable": "启用",
  "common.save": "保存",
  "common.saving": "正在保存...",
  "common.edit": "编辑",
  "common.next": "下一步",
  "common.dismiss": "关闭",
  "common.abort": "取消操作",
  "common.none": "无",
  "common.sidebar.toggle": "切换主侧栏",
  "common.sidebar.resize": "调整侧栏宽度",
  "common.sidebar.resizeHint": "拖动以调整侧栏宽度",
  "common.sidebar.mobileTitle": "侧栏",
  "common.sidebar.mobileDescription": "显示移动端侧栏。",
  "quit.holdOrDoublePress": "长按 {shortcut} 或快速按两次即可退出",
  "quit.pressAgain": "再次按下 {shortcut} 即可退出",

  "settings.title": "设置",
  "settings.breadcrumb": "设置导航",
  "settings.diagnostics": "诊断",
  "settings.section.general": "常规",
  "settings.section.appearance": "外观",
  "settings.section.keybindings": "快捷键",
  "settings.section.providers": "服务提供方",
  "settings.section.integrations": "集成",
  "settings.section.sourceControl": "源代码管理",
  "settings.section.connections": "连接",
  "settings.section.archive": "归档",
  "settings.search.placeholder": "搜索",
  "settings.search.label": "搜索设置",
  "settings.search.clear": "清除设置搜索",
  "settings.search.empty": "未找到设置",
  "settings.search.results": "设置搜索结果",
  "settings.language.title": "语言",
  "settings.language.description":
    "选择 T3 Code 的界面语言。跟随系统时，将使用浏览器或操作系统的语言。",
  "settings.language.label": "应用语言",
  "settings.language.system": "跟随系统",
  "settings.language.english": "English",
  "settings.language.chineseSimplified": "简体中文",
  "settings.item.colorScheme": "配色方案",
  "settings.item.themes": "主题",
  "settings.item.appearanceContrast": "界面对比度",
  "settings.item.glassOpacity": "玻璃效果透明度",
  "settings.item.environmentIdentification": "环境标识",
  "settings.item.interfaceFont": "界面字体",
  "settings.item.promptFont": "输入字体",
  "settings.item.codeFont": "代码字体",
  "settings.item.terminalFont": "终端字体",
  "settings.item.fontSmoothing": "字体平滑",
  "settings.item.wordWrap": "自动换行",
  "settings.item.projectGrouping": "项目分组",
  "settings.item.autoSettleInactiveThreads": "自动收起不活跃任务",
  "settings.item.autoSettleMergedThreads": "合并后自动收起任务",
  "settings.item.timeFormat": "时间格式",
  "settings.item.hideWhitespaceChanges": "隐藏空白字符更改",
  "settings.item.skillsInSlashMenu": "在斜杠菜单中显示技能",
  "settings.item.providerUpdateChecks": "服务提供方更新检查",
  "settings.item.newThreads": "新任务",
  "settings.item.startFromOrigin": "从 origin 开始",
  "settings.item.addProjectStartsIn": "添加项目时的起始位置",
  "settings.item.archiveConfirmation": "归档确认",
  "settings.item.unpinConfirmation": "取消置顶确认",
  "settings.item.deleteConfirmation": "删除确认",
  "settings.item.quitConfirmation": "长按退出",
  "settings.item.textGenerationModel": "文本生成模型",
  "settings.item.diagnostics": "诊断",
  "settings.item.legacyPlanMode": "方案模式（旧版）",
  "settings.item.legacyTokenStreaming": "逐字输出（旧版）",
  "settings.item.legacySidebar": "侧栏（旧版）",
  "settings.item.keybindings": "快捷键",
  "settings.item.providers": "服务提供方",
  "settings.item.agentBrowserAccess": "Agent 浏览器访问权限",
  "settings.item.browserDefaultViewport": "默认浏览器视口",
  "settings.item.browserDefaultZoom": "默认浏览器缩放",
  "settings.item.browserDefaultAppearance": "默认浏览器外观",
  "settings.item.browserAutoShowFloatingPreview": "自动显示浮动预览",
  "settings.item.sourceControl": "源代码管理",
  "settings.item.remoteEnvironments": "远程环境",
  "settings.item.archivedThreads": "已归档任务",

  "settings.integrations.browser.title": "浏览器",
  "settings.sharedMismatch.description":
    "{environments} 上的设置不一致。任务和源代码管理偏好应在所有环境中保持一致。",
  "settings.sharedMismatch.apply": "应用到全部环境",
  "settings.integrations.agentBrowserAccess.description":
    "允许 Agent 打开并操作预览浏览器。关闭后，Agent 会话将无法获得浏览器工具及相关说明；你自己的浏览器面板不受影响。",
  "settings.integrations.agentBrowserAccess.status":
    "仅对之后启动的会话生效；正在运行的 Agent 会保留已获得的工具。",
  "settings.integrations.agentBrowserAccess.aria": "允许 Agent 访问浏览器",
  "settings.integrations.browserDefaultViewport.description":
    "设置你和 Agent 新建浏览器标签页时使用的视口。填满面板会使页面适应面板大小；选择其他尺寸时，将以该尺寸打开设备工具栏。",
  "settings.integrations.browserDefaultViewport.fill": "填满面板",
  "settings.integrations.browserDefaultViewport.responsive": "响应式",
  "settings.integrations.browserDefaultViewport.standard": "标准设备",
  "settings.integrations.browserDefaultViewport.width": "默认视口宽度",
  "settings.integrations.browserDefaultViewport.height": "默认视口高度",
  "settings.integrations.browserDefaultViewport.rotateTo": "旋转为{orientation}",
  "settings.integrations.browserDefaultViewport.orientation.landscape": "横向",
  "settings.integrations.browserDefaultViewport.orientation.portrait": "纵向",
  "settings.integrations.browserDefaultViewport.rotate": "旋转",
  "settings.integrations.browserDefaultZoom.description": "应用于新浏览器标签页的页面缩放比例。",
  "settings.integrations.browserDefaultAppearance.description":
    "告知网页优先使用的配色方案；跟随系统时，将采用操作系统设置。",
  "settings.integrations.browserDefaultAppearance.system": "跟随系统",
  "settings.integrations.browserDefaultAppearance.light": "浅色",
  "settings.integrations.browserDefaultAppearance.dark": "深色",
  "settings.integrations.browserAutoShowFloatingPreview.description":
    "Agent 打开浏览器时自动显示浮动预览。若 Agent 明确要求显示或隐藏预览，仍会按其要求执行。",
  "settings.integrations.desktopOnly": "仅桌面客户端支持这些设置。",
  "settings.integrations.profiles.add": "添加配置文件",
  "settings.integrations.profiles.new": "新配置文件",
  "settings.integrations.profiles.blank": "空白配置文件",
  "settings.integrations.profiles.limit": "配置文件数量已达上限",
  "settings.integrations.profiles.importFrom": "导入来源",
  "settings.integrations.profiles.looking": "正在查找浏览器…",
  "settings.integrations.profiles.noneSupported": "未找到支持的浏览器",
  "settings.integrations.profiles.connectToImport": "连接到环境后即可导入 Cookie",
  "settings.integrations.profiles.rename": "重命名{profile}",
  "settings.integrations.profiles.default": "默认",
  "settings.integrations.profiles.options": "{profile}选项",
  "settings.integrations.profiles.setDefault": "设为默认",
  "settings.integrations.profiles.clear": "清除 Cookie 和缓存",
  "settings.integrations.profiles.removeData": "移除配置文件及其数据",
  "settings.integrations.profiles.connectToClear": "连接到环境后即可清除配置文件数据",
  "settings.integrations.profiles.checkingEnvironments": "正在检查环境…",
  "settings.integrations.profiles.clearFailed": "无法清除{profile}的数据",
  "settings.integrations.profiles.clearSuccess": "已清除{profile}的 Cookie 和缓存",
  "settings.integrations.profiles.connectFirst": "尚未连接到服务端。",
  "settings.integrations.profiles.connectBeforeRemove": "移除此配置文件前，请先连接到环境。",
  "settings.integrations.profiles.deleteFailed": "无法删除配置文件数据，请重试。",
  "settings.integrations.profiles.removeTitle": "移除“{profile}”？",
  "settings.integrations.profiles.removeDescription":
    "将删除该配置文件的 Cookie 和登录状态；其中已打开的标签页会保留至关闭。",
  "settings.integrations.profiles.connectToRemove": "连接到环境后即可移除此配置文件及其数据。",
  "settings.integrations.profiles.removing": "正在移除…",
  "settings.integrations.profiles.remove": "移除配置文件",
  "browserImport.targetUnavailable": "该配置文件已不可用，请重新选择 Cookie 的导入位置。",
  "browserImport.profileLimit": "配置文件数量已达上限，请选择已有配置文件作为导入位置。",
  "browserImport.quit.title": "退出 {browser} 后导入",
  "browserImport.quit.description":
    "{browser} 仍在运行，暂时无法读取其中的 Cookie。请先退出浏览器，然后继续。",
  "browserImport.quit.confirm": "已退出浏览器",
  "browserImport.cookies.none": "没有 Cookie",
  "browserImport.cookies.one": "{count} 个 Cookie",
  "browserImport.cookies.many": "{count} 个 Cookie",
  "browserImport.configure.title": "从 {browser} 导入",
  "browserImport.configure.description": "选择要导入到 {environment} 的 Cookie。",
  "browserImport.from": "来源",
  "browserImport.into": "导入到",
  "browserImport.newProfile": "新建配置文件",
  "browserImport.newProfile.description": "专门存放这些 Cookie",
  "browserImport.existingProfile": "已有配置文件",
  "browserImport.import": "导入",
  "browserImport.importing.title": "正在导入 Cookie",
  "browserImport.importing.description": "这可能需要一点时间。",
  "browserImport.importing.progress": "正在导入…",
  "browserImport.checking.title": "正在检查 {browser}",
  "browserImport.checking.description": "正在确认浏览器是否已退出。",
  "browserImport.checking.progress": "正在检查…",
  "browserImport.done.importedTitle": "已导入 {count}",
  "browserImport.done.skippedTitle": "已跳过 {count}",
  "browserImport.done.emptyTitle": "未找到 Cookie",
  "browserImport.done.importedDescription": "已添加到 {environment} 的 {profile}。{skipped}",
  "browserImport.done.skippedSuffix": "另有 {count} 被跳过。",
  "browserImport.done.noneImported": "没有为 {environment} 导入任何 Cookie。",
  "browserImport.done.noCookies": "没有可导入到 {environment} 的 Cookie。",
  "browserImport.done.skipped": "已跳过",
  "browserImport.blocked.title": "无法从 {browser} 导入",
  "usageSource.add.title": "添加 CLIProxyAPI Hub",
  "usageSource.add.description":
    "在 {environment} 的服务提供方旁显示该 Hub 汇集的所有账号限额。管理密钥只保存在该服务器上。",
  "usageSource.add.url": "Hub URL",
  "usageSource.add.managementKey": "管理密钥",
  "usageSource.add.label": "名称（可选）",
  "usageSource.add.labelPlaceholder": "默认使用 Hub 主机名",
  "usageSource.add.action": "添加 Hub",
  "usageSource.empty": "尚未配置用量服务。",
  "usageSource.disabled": "已禁用",
  "usageSource.remove.title": "移除 {label}？",
  "usageSource.remove.description":
    "将从此服务器删除该 Hub 的管理密钥，其账号也会从“限额”视图中移除；Hub 本身不会受到影响。之后可使用 URL 和密钥重新添加。",
  "usageSource.remove.action": "移除 Hub",
  "environmentIcon.label": "环境图标",
  "environmentIcon.automatic": "自动",
  "environmentIcon.automaticDetected": "自动（{icon}）",
  "projectIconPicker.title": "选择项目图标",
  "projectIconPicker.description": "选择任意 Lucide 图标和颜色，或使用表情符号。",
  "projectIconPicker.type": "图标类型",
  "projectIconPicker.icons": "图标",
  "projectIconPicker.emoji": "表情符号",
  "projectIconPicker.color": "图标颜色",
  "projectIconPicker.search": "搜索 Lucide 图标",
  "projectIconPicker.searchPlaceholder": "搜索全部 Lucide 图标",
  "projectIconPicker.empty": "未找到图标。",
  "projectIconPicker.pasteAny": "或粘贴任意表情符号",
  "projectIconPicker.customEmoji": "自定义表情符号",
  "projectIconPicker.pasteEmoji": "粘贴表情符号",
  "projectIconPicker.save": "保存图标",

  "settings.restoreDefaults": "恢复默认设置",
  "settings.restore.confirmTitle": "恢复默认设置？",
  "settings.restore.confirmDescription": "以下设置将被重置：{settings}。",
  "settings.resetSetting": "将{setting}恢复为默认值",
  "settings.resetDefault": "恢复默认值",
  "settings.policyDetails": "后台策略详情",
  "settings.general.description.projectGrouping": "合并不同环境中相同的代码仓库。",
  "settings.general.description.autoSettleMerged":
    "拉取请求合并后自动收起任务；已关闭的拉取请求也会自动收起。",
  "settings.general.description.autoSettleInactive": "侧栏中的任务在这段时间内没有活动时自动收起。",
  "settings.general.autoSettleDays.title": "自动收起前的无活动天数",
  "settings.general.description.autoSettleDays": "有新活动时，任务会自动恢复。",
  "settings.general.description.timeFormat": "跟随系统时，将使用浏览器或操作系统的时间格式。",
  "settings.general.timeFormat.system": "跟随系统",
  "settings.general.timeFormat.12Hour": "12 小时制",
  "settings.general.timeFormat.24Hour": "24 小时制",
  "settings.general.description.hideWhitespace": "设置差异面板是否默认忽略仅包含空白字符的更改。",
  "settings.general.description.skillsInSlashMenu":
    "同时在 / 命令菜单中显示技能；输入 $ 时始终会显示技能。",
  "settings.general.description.providerUpdateChecks": "检查已安装的服务提供方 CLI 是否有新版本。",
  "settings.general.background.title": "后台活动",
  "settings.general.background.profile.balanced": "均衡",
  "settings.general.background.profile.performance": "性能优先",
  "settings.general.background.profile.batterySaver": "省电",
  "settings.general.background.profile.advanced": "高级",
  "settings.general.background.description.balanced":
    "客户端空闲、主机锁定或启用低功耗模式时暂停后台检查。",
  "settings.general.background.description.performance":
    "只要有订阅的客户端保持连接，就允许执行限定范围的后台检查。",
  "settings.general.background.description.batterySaver":
    "主机或客户端使用电池供电时也会暂停后台检查。",
  "settings.general.background.description.advanced":
    "使用自定义后台间隔，并遵循所选的共享电源策略。",
  "settings.general.background.currentPolicy": "当前共享策略：{profile}。",
  "settings.general.background.dialog.title": "后台活动",
  "settings.general.background.dialog.description": "调整共享电源策略及其使用的后台检查间隔。",
  "settings.general.background.sharedPolicy.title": "共享策略",
  "settings.general.background.sharedPolicy.description":
    "决定订阅的检查间隔到期后，是否允许执行后台任务。",
  "settings.general.background.sharedPolicy.label": "共享后台策略",
  "settings.general.background.gitFetch.title": "Git 拉取间隔",
  "settings.general.background.gitFetch.description": "在后台刷新远程分支状态。",
  "settings.general.background.providerHealth.title": "服务提供方健康检查间隔",
  "settings.general.background.providerHealth.description":
    "刷新服务提供方的可用性、版本、认证状态和模型信息。",
  "settings.general.background.hostPower.title": "主机电源监控",
  "settings.general.background.hostPower.description": "客户端活跃时轮询主机电源状态。",
  "settings.general.background.idleHost.title": "空闲主机监控",
  "settings.general.background.idleHost.description": "没有前台客户端活跃时轮询主机电源状态。",
  "settings.general.background.pause.hostLocked": "主机锁定时暂停",
  "settings.general.background.pause.hostLowPower": "主机进入低电量模式时暂停",
  "settings.general.background.pause.clientLowPower": "客户端进入低电量模式时暂停",
  "settings.general.background.pause.onBattery": "使用电池供电时暂停",
  "settings.general.background.interval.decrease": "缩短{name}",
  "settings.general.background.interval.inSeconds": "{name}（秒）",
  "settings.general.background.interval.increase": "延长{name}",
  "settings.general.background.seconds": "秒",
  "settings.general.background.resetAll": "全部重置",
  "settings.general.background.configure": "配置高级后台活动",
  "settings.general.description.newThreads": "选择新建草稿任务时默认使用的工作区模式。",
  "settings.general.threadMode.local": "本地",
  "settings.general.threadMode.newWorktree": "新建工作树",
  "settings.general.description.startFromOrigin":
    "从 origin 上最新的同名分支创建工作树，而不是使用本地分支。",
  "settings.general.description.addProjectStartsIn": "留空时，“添加项目”浏览器将从“~/”开始。",
  "settings.general.description.unpinConfirmation": "从置顶区域取消置顶任务前进行确认。",
  "settings.general.description.archiveConfirmation": "归档任务前，需要再次点击行内归档操作。",
  "settings.general.description.deleteConfirmation": "删除任务及其聊天记录前进行确认。",
  "settings.general.description.quitConfirmation":
    "退出桌面应用前需要长按退出快捷键；快速按一下只会显示提示。",
  "settings.general.description.textGenerationModel":
    "用于生成任务标题和源代码管理内容等文本的默认模型。源代码管理设置可以使用专用写作模型覆盖此项。",
  "settings.about.title": "关于",
  "settings.about.version": "版本",
  "settings.about.currentVersion": "当前应用版本。",
  "settings.about.updateAvailable": "有可用更新。",
  "settings.about.viewDiagnostics": "查看诊断信息",
  "settings.about.action.download": "下载",
  "settings.about.action.install": "安装",
  "settings.about.action.checking": "正在检查…",
  "settings.about.action.downloading": "正在下载…",
  "settings.about.action.upToDate": "已是最新版本",
  "settings.about.action.checkForUpdates": "检查更新",
  "settings.about.updateTrack.title": "更新通道",
  "settings.about.updateTrack.description.desktop":
    "稳定版跟随正式发布；夜间版跟随夜间桌面通道，并可随时切回稳定版。",
  "settings.about.updateTrack.description.hosted": "切换托管应用的发布通道。",
  "settings.about.updateTrack.option.stable": "稳定版",
  "settings.about.updateTrack.option.nightly": "夜间版",
  "settings.about.updateTrack.option.latest": "最新版",
  "settings.diagnostics.mode.localTrace": "本地跟踪文件",
  "settings.diagnostics.mode.terminalOnly": "仅终端日志",
  "settings.diagnostics.modeSentence": "{mode}。",
  "settings.diagnostics.export.combined": "正在将 OTEL 导出到 {url}。",
  "settings.diagnostics.export.signals":
    "正在将 OTEL 跟踪导出到 {tracesUrl}，并将指标导出到 {metricsUrl}。",
  "settings.diagnostics.export.traces": "正在将 OTEL 跟踪导出到 {url}。",
  "settings.diagnostics.export.metrics": "正在将 OTEL 指标导出到 {url}。",
  "diagnostics.section.resourceMonitor": "资源监控",
  "diagnostics.section.hostCollection": "主机与采集状态",
  "diagnostics.section.resourceTimeline": "资源时间线",
  "diagnostics.section.liveProcessTree": "实时进程树",
  "diagnostics.section.instrumentedIo": "应用 I/O 统计",
  "diagnostics.section.liveProcesses": "实时进程",
  "diagnostics.section.resourceHistory": "资源历史",
  "diagnostics.section.traceDiagnostics": "跟踪诊断",
  "diagnostics.section.latestFailures": "最近失败",
  "diagnostics.section.commonFailures": "常见失败",
  "diagnostics.section.slowestSpans": "最慢 Span",
  "diagnostics.section.spanLogs": "Span 日志",
  "diagnostics.section.topSpanNames": "高频 Span 名称",
  "diagnostics.action.refreshTelemetry": "刷新资源监控",
  "diagnostics.action.refreshSnapshot": "刷新监控快照",
  "diagnostics.action.refreshHistory": "刷新资源历史",
  "diagnostics.action.refreshProcesses": "刷新进程诊断",
  "diagnostics.action.refreshTraces": "刷新跟踪诊断",
  "diagnostics.action.openLogs": "打开日志文件夹",
  "diagnostics.action.retryMonitor": "重试监控",
  "diagnostics.action.showFullMessage": "显示完整消息",
  "diagnostics.state.waitingSample": "等待采样",
  "diagnostics.state.checking": "正在检查",
  "diagnostics.state.checkedUnavailable": "无法获取检查时间",
  "diagnostics.state.checked": "检查于 {time}",
  "diagnostics.state.unknown": "未知",
  "diagnostics.state.justNow": "刚刚",
  "diagnostics.state.ago": "{time}前",
  "diagnostics.state.updated": "更新于 {time}",
  "diagnostics.state.loadingProcesses": "正在加载实时进程...",
  "diagnostics.state.noProcesses": "未找到实时子进程。",
  "diagnostics.state.collectingSamples": "正在收集进程资源样本...",
  "diagnostics.state.noSamples": "所选时间范围内没有进程资源样本。",
  "diagnostics.state.waitingNativeMonitor": "正在等待原生进程监控器。",
  "diagnostics.state.noRetainedSamples": "所选时间范围内没有保留的进程样本。",
  "diagnostics.state.noInstrumentedIo": "尚未记录应用 I/O 统计。",
  "diagnostics.state.loadingFailures": "正在加载失败记录...",
  "diagnostics.state.noFailures": "未发现失败的 Span。",
  "diagnostics.state.loadingFailureGroups": "正在加载失败分组...",
  "diagnostics.state.noRepeatedFailures": "未发现重复失败。",
  "diagnostics.state.loadingSlowSpans": "正在加载慢 Span...",
  "diagnostics.state.noSpans": "未发现 Span。",
  "diagnostics.state.loadingLogs": "正在加载近期日志...",
  "diagnostics.state.noWarnings": "未发现警告或错误。",
  "diagnostics.state.loadingSpanNames": "正在加载 Span 名称...",
  "diagnostics.state.noTraceRecords": "暂无跟踪记录",
  "diagnostics.metric.currentCpu": "当前 CPU",
  "diagnostics.metric.residentMemory": "常驻内存",
  "diagnostics.metric.processCount": "进程数",
  "diagnostics.metric.readThroughput": "读取吞吐量",
  "diagnostics.metric.writeThroughput": "写入吞吐量",
  "diagnostics.metric.cpuSpeedLimit": "CPU 速度限制",
  "diagnostics.metric.childProcesses": "子进程",
  "diagnostics.metric.cpu": "CPU",
  "diagnostics.metric.memory": "内存",
  "diagnostics.metric.serverPid": "服务器 PID",
  "diagnostics.metric.cpuTime": "CPU 时间",
  "diagnostics.metric.samples": "样本",
  "diagnostics.metric.interval": "间隔",
  "diagnostics.metric.processes": "进程",
  "diagnostics.metric.spans": "Span",
  "diagnostics.metric.failures": "失败",
  "diagnostics.metric.slowSpans": "慢 Span",
  "diagnostics.metric.parseErrors": "解析错误",
  "diagnostics.metric.cpuAverage": "CPU 平均值",
  "diagnostics.metric.ioReads": "I/O 读取",
  "diagnostics.metric.ioWrites": "I/O 写入",
  "diagnostics.column.process": "进程",
  "diagnostics.column.name": "名称",
  "diagnostics.column.category": "类别",
  "diagnostics.column.command": "命令",
  "diagnostics.column.type": "类型",
  "diagnostics.column.kill": "终止",
  "diagnostics.column.cpuTime": "CPU 时间",
  "diagnostics.column.memory": "内存",
  "diagnostics.column.peakCpu": "CPU 峰值",
  "diagnostics.column.peakMemory": "内存峰值",
  "diagnostics.column.read": "读取",
  "diagnostics.column.write": "写入",
  "diagnostics.column.readPerSecond": "每秒读取",
  "diagnostics.column.writePerSecond": "每秒写入",
  "diagnostics.column.readTotal": "累计读取",
  "diagnostics.column.writeTotal": "累计写入",
  "diagnostics.column.samples": "样本",
  "diagnostics.column.component": "组件",
  "diagnostics.column.operation": "操作",
  "diagnostics.column.logicalRead": "逻辑读取",
  "diagnostics.column.logicalWrite": "逻辑写入",
  "diagnostics.column.count": "数量",
  "diagnostics.column.time": "时间",
  "diagnostics.column.span": "Span",
  "diagnostics.column.cause": "原因",
  "diagnostics.column.duration": "耗时",
  "diagnostics.column.ended": "结束时间",
  "diagnostics.column.lastSeen": "最近出现",
  "diagnostics.column.trace": "Trace",
  "diagnostics.column.level": "级别",
  "diagnostics.column.message": "消息",
  "diagnostics.column.average": "平均值",
  "diagnostics.column.current": "当前值",
  "diagnostics.column.peak": "峰值",
  "diagnostics.column.max": "最大值",
  "diagnostics.resource.systemFootprint": "T3 系统资源占用",
  "diagnostics.resource.systemDescription":
    "实时显示服务器、服务提供方、终端、桌面进程及监控器自身的原生资源数据。",
  "diagnostics.resource.samplingEvery": "每 {interval} 采样一次",
  "diagnostics.resource.secondValue": "{value} 秒",
  "diagnostics.resource.secondsValue": "{value} 秒",
  "diagnostics.resource.observedCpuTime": "已观测 CPU 时间 {value}",
  "diagnostics.resource.combinedPeaks": "各进程峰值合计 {value}",
  "diagnostics.resource.startsExits": "启动 {starts} 次 · 退出 {exits} 次",
  "diagnostics.resource.observed": "已观测 {value}",
  "diagnostics.resource.thermalState": "温控状态：{state}",
  "diagnostics.resource.thermalStateUnknown": "温控状态：未知",
  "diagnostics.resource.backendAgents": "后端与 Agent",
  "diagnostics.resource.desktop": "桌面端",
  "diagnostics.resource.monitorOverhead": "监控开销",
  "diagnostics.resource.hostState": "主机状态",
  "diagnostics.resource.hostDisconnected": "未连接桌面主机信号",
  "diagnostics.resource.hostDisconnectedDescription":
    "电源、空闲、锁定和温控状态由桌面主机提供。当前浏览器会话中的进程监控仍可正常工作。",
  "diagnostics.resource.collectionHealth": "采集状态",
  "diagnostics.resource.waitingHealth": "正在等待采集器状态。",
  "diagnostics.resource.identity": "标识：PID + 启动时间",
  "diagnostics.resource.logicalBytes": "按操作统计的逻辑字节数",
  "diagnostics.resource.instrumentedDescription":
    "原生计数器用于识别具体读写进程；应用级计数器用于识别已知的 T3 操作，以便将资源波动关联到具体的持久化和日志路径。",
  "diagnostics.resource.processOne": "{count} 个进程",
  "diagnostics.resource.processMany": "{count} 个进程",
  "diagnostics.resource.native": "原生监控",
  "diagnostics.resource.nativeMonitor": "原生进程监控器",
  "diagnostics.resource.electronMain": "Electron 主进程",
  "diagnostics.resource.desktopAvailable": "在桌面应用中打开此页面时可用。",
  "diagnostics.resource.desktopOnly": "仅桌面端",
  "diagnostics.resource.noErrors": "未报告错误",
  "diagnostics.resource.collectionTime": "采集耗时",
  "diagnostics.resource.processScan": "进程扫描",
  "diagnostics.resource.retained": "保留 {retained}/{scanned}",
  "diagnostics.resource.inaccessible": "无法访问",
  "diagnostics.resource.sidecar": "伴随进程",
  "diagnostics.resource.restarts": "重启次数",
  "diagnostics.resource.binaryMissing": "未找到适用于 {platform} 的资源监控程序。",
  "diagnostics.resource.status.starting": "正在启动",
  "diagnostics.resource.status.healthy": "正常",
  "diagnostics.resource.status.degraded": "性能下降",
  "diagnostics.resource.status.unavailable": "不可用",
  "diagnostics.resource.status.stopped": "已停止",
  "diagnostics.category.server": "服务器",
  "diagnostics.category.serverChild": "后端子进程",
  "diagnostics.category.provider": "服务提供方",
  "diagnostics.category.terminal": "终端",
  "diagnostics.category.electronMain": "Electron 主进程",
  "diagnostics.category.renderer": "渲染进程",
  "diagnostics.category.gpu": "GPU",
  "diagnostics.category.utility": "Electron 工具进程",
  "diagnostics.category.monitor": "监控器",
  "diagnostics.category.t3Process": "T3 进程",
  "diagnostics.io.storage": "存储字节数",
  "diagnostics.io.logical": "逻辑字节数",
  "diagnostics.io.all": "全部 I/O 字节数",
  "diagnostics.io.unavailable": "不可用",
  "diagnostics.tooltip.liveCpu":
    "当前服务器进程的所有实时子进程 CPU 总用量，不含桌面外壳及其他父进程。",
  "diagnostics.tooltip.liveMemory":
    "当前服务器进程的所有实时子进程常驻内存总量，不含桌面外壳及其他父进程。",
  "diagnostics.tooltip.cpuTime": "所选时间范围内，T3 服务器根进程及其子进程的近似活跃 CPU 时间。",
  "diagnostics.tooltip.samples": "服务器内存中保留的进程样本，服务器重启后会清空。",
  "diagnostics.tooltip.details": "查看{label}详情",
  "diagnostics.tooltip.cpuBucket": "CPU 平均值 {average}%，峰值 {peak}%",
  "diagnostics.tooltip.sendSignal": "发送 {signal}",
  "diagnostics.signal.confirmKill": "确定向进程 {pid} 发送 SIGKILL 吗？进程无法处理此信号。",
  "diagnostics.signal.confirmFailed": "无法确认信号操作",
  "diagnostics.signal.sendFailed": "发送 {signal} 失败。",
  "diagnostics.signal.sendProcessFailed": "向进程 {pid} 发送 {signal} 失败。",
  "diagnostics.signal.sendFailedTitle": "无法发送 {signal}",
  "diagnostics.signal.processExited": "进程已退出",
  "diagnostics.signal.processExitedDescription": "此进程已不再是 T3 服务器的子进程，可能已经退出。",
  "diagnostics.logs.noEditors": "未找到可用的编辑器。",
  "diagnostics.logs.noEnvironment": "尚未选择环境。",
  "diagnostics.logs.openFailed": "无法打开日志文件夹。",
  "diagnostics.tooltip.slowSpans": "达到或超过当前慢 Span 阈值的 Span。",
  "diagnostics.tooltip.slowSpansValue": "耗时达到 {duration} 或更长的 Span。",
  "diagnostics.warning.partialTrace": "部分跟踪文件无法读取，诊断结果可能不完整。{error}",
  "settings.legacy.title": "旧版功能",
  "settings.legacy.planModeDescription":
    "恢复输入区中的“构建/方案”切换，以及 /plan、/default 命令和 Shift+Tab 快捷键。关闭后，所有任务都以构建模式运行。",
  "settings.legacy.tokenStreamingDescription":
    "逐个令牌绘制助手输出，而不是分块显示完整内容。不建议启用：速度会明显变慢，长回复也更难阅读；此选项仅用于兼容旧行为。",
  "settings.legacy.tokenStreamingConfirmTitle": "启用逐令牌输出？",
  "settings.legacy.tokenStreamingConfirmDescription":
    "这会比默认的缓冲输出慢很多，并影响阅读体验。此开关仅用于向后兼容。",
  "settings.legacy.sidebarDescription":
    "恢复按项目显示任务树的原版侧栏。默认侧栏使用一个扁平列表：活跃工作显示为详细卡片，已收起任务显示为紧凑行。",

  "appearance.description": "选择 T3 Code 的显示方式。使用内置主题，或创建自己的主题。",
  "appearance.colorScheme": "配色方案",
  "appearance.mode.group": "外观模式",
  "appearance.mode.system": "跟随系统",
  "appearance.mode.light": "浅色",
  "appearance.mode.dark": "深色",
  "appearance.mode.followSystem": "跟随系统外观",
  "appearance.mode.use": "使用{mode}模式",
  "appearance.themes": "主题",
  "appearance.createTheme": "创建主题",
  "appearance.importTheme": "导入主题",
  "appearance.glassOpacity.description":
    "控制玻璃界面的透明度。数值越高，菜单、对话框和输入区越不透明。",
  "appearance.glassOpacity.label": "玻璃效果透明度",
  "appearance.contrast.description": "调整整个界面的颜色和边框对比度。",
  "appearance.environment.description": "选择如何标识 Dev 和 Nightly 环境。",
  "appearance.environment.label": "环境标识",
  "appearance.environment.artwork": "背景图案",
  "appearance.environment.pill": "标签",
  "appearance.environment.none": "不显示",
  "appearance.typography.title": "字体排版",
  "appearance.typography.advanced": "高级",
  "appearance.typography.showAdvanced": "显示高级字体设置",
  "appearance.font.interface.description": "应用于代码块和终端之外的所有界面。",
  "appearance.font.prompt.description": "仅应用于输入提示词的区域，等宽字体在此也很合适。",
  "appearance.font.code.description": "应用于代码块、差异和文件预览。",
  "appearance.font.terminal.description": "应用于终端输出，不受代码块和差异字体影响。",
  "appearance.font.monospace.title": "等宽字体",
  "appearance.font.monospace.description": "应用于代码块、差异、文件预览和终端。",
  "appearance.font.size.interface": "界面字号",
  "appearance.font.size.prompt": "输入区字号",
  "appearance.font.size.code": "代码字号",
  "appearance.font.size.terminal": "终端字号",
  "appearance.font.smoothing.description": "使用更纤细的灰度抗锯齿，而不是 macOS 较粗的默认效果。",
  "appearance.font.smoothing.label": "字体平滑",
  "appearance.wordWrap.description": "默认自动换行代码块、表格、差异和文件预览中的长行。",
  "appearance.wordWrap.label": "默认自动换行代码、表格、差异和文件预览",
  "appearance.fontPicker.default": "默认",
  "appearance.fontPicker.search": "搜索字体...",
  "appearance.fontPicker.empty": "未找到字体。",
  "appearance.fontPreview.terminal": "终端字体预览",
  "appearance.fontPreview.sample":
    "使用 $frontend-design 修复 [surface.test.ts](apps/web/src/terminal/ghostty/surface.test.ts) 中不稳定的测试，并在发布前将标题与 [SettingsPanels.tsx](apps/web/src/components/settings/SettingsPanels.tsx) 对齐。",
  "appearance.theme.duplicate": "复制主题",
  "appearance.theme.use": "使用{theme}",
  "appearance.theme.useActive": "使用{theme}，当前已启用",
  "appearance.theme.duplicateNamed": "复制{theme}",
  "appearance.theme.edit": "编辑主题",
  "appearance.theme.export": "导出主题文件",
  "appearance.theme.remove": "移除主题",
  "appearance.theme.removeMany": "移除多个主题",
  "appearance.theme.useLightOnly": "仅用于浅色模式",
  "appearance.theme.useDarkOnly": "仅用于深色模式",
  "appearance.theme.variants.group": "浅色与深色主题变体",
  "appearance.theme.variants.choose": "选择{mode}变体，共 {count} 个选项，当前为{theme}",
  "appearance.theme.variants.use": "使用{mode}变体，当前为{theme}",
  "appearance.theme.variants.useForMode": "将{theme}用于{mode}模式",
  "appearance.theme.variants.useForModeActive": "将{theme}用于{mode}模式，当前已启用",
  "appearance.theme.editNamed": "编辑{theme}",
  "appearance.theme.exportNamed": "导出{theme}",
  "appearance.theme.removeNamed": "移除{theme}",
  "appearance.theme.removeCollectionNamed": "从{collection}中移除主题",
  "appearance.theme.useFirstVariants": "浅色与深色模式均使用第一个主题变体",
  "appearance.theme.useBothModes": "同时用于浅色与深色模式",
  "appearance.theme.useModeOnly": "仅用于{mode}模式",
  "appearance.theme.toast.selectionFailed": "无法保存主题选择",
  "appearance.theme.toast.removeFailed": "无法移除主题",
  "appearance.theme.toast.restoreFailed": "无法恢复主题设置",
  "appearance.theme.cancel": "取消",
  "appearance.theme.removeSelected": "移除所选主题",
  "appearance.theme.removeSelectedCount": "移除所选主题（{count}）",
  "appearance.theme.removeTitle": "移除“{theme}”？",
  "appearance.theme.removeCollectionTitle": "从“{collection}”中移除主题？",
  "appearance.theme.removeDescription": "以后仍可通过导入对应 JSON 文件恢复此主题。",
  "appearance.theme.removeCollectionDescription": "选择要移除的主题变体。重新导入扩展即可恢复。",
  "appearance.theme.import.addTitle": "添加主题",
  "appearance.theme.import.orFile": "或导入文件",
  "appearance.theme.import.chooseFiles": "选择文件",
  "appearance.theme.import.reading": "正在读取...",
  "appearance.theme.import.json": "主题 JSON",
  "appearance.theme.import.installed": "已安装",
  "appearance.theme.import.updateExisting": "更新现有主题",
  "appearance.theme.import.keepBoth": "全部保留",
  "appearance.theme.import.back": "返回",
  "appearance.theme.import.file": "主题文件",
  "appearance.theme.import.dropFiles": "将 T3 Code 或 VS Code 的 .json 文件拖到此处",
  "appearance.theme.import.add": "添加主题",
  "appearance.theme.search.title": "搜索社区主题",
  "appearance.theme.search.description": "从 Open VSX 查找开源主题。",
  "appearance.theme.search.label": "搜索 Open VSX 主题",
  "appearance.theme.search.placeholder": "例如 dracula、nord、catppuccin...",
  "appearance.theme.search.action": "搜索",
  "appearance.theme.search.popular": "热门",
  "appearance.theme.search.sort": "排序",
  "appearance.theme.search.sortLabel": "主题排序",
  "appearance.theme.search.sort.downloaded": "下载最多",
  "appearance.theme.search.sort.rated": "评分最高",
  "appearance.theme.search.sort.newest": "最新",
  "appearance.theme.search.sort.relevant": "最相关",
  "appearance.theme.search.finding": "正在查找主题...",
  "appearance.theme.search.empty": "未找到受支持的开源主题",
  "appearance.theme.search.tryBroader": "尝试更宽泛的搜索词。",
  "appearance.theme.search.downloads": "{count} 次下载",
  "appearance.theme.search.fallbackDescription": "适用于编辑器的社区配色主题。",
  "appearance.theme.search.source": "源码",
  "appearance.theme.search.add": "添加",
  "appearance.theme.search.adding": "正在添加",
  "appearance.theme.search.update": "更新",
  "appearance.theme.search.updating": "正在更新",
  "appearance.theme.search.updateTitle": "更新“{theme}”？",
  "appearance.theme.search.updateDescription":
    "这将替换已安装的主题变体，包括本地修改。扩展中已不存在的变体将被移除。",
  "appearance.theme.search.updateAction": "更新主题",
  "appearance.themeEditor.createTitle": "创建主题",
  "appearance.themeEditor.editTitle": "编辑主题",
  "appearance.themeEditor.selectColor": "在下方选择颜色",
  "appearance.themeEditor.selectElement": "选择界面元素 · 按 Esc 取消",
  "appearance.themeEditor.usageOne": "{label} · {count} 处使用",
  "appearance.themeEditor.usageMany": "{label} · {count} 处使用",
  "appearance.themeEditor.inspect": "检查",
  "appearance.themeEditor.inspectColors": "检查应用颜色",
  "appearance.themeEditor.cancelInspect": "取消检查应用颜色",
  "appearance.themeEditor.pickColor": "从应用界面选取颜色",
  "appearance.themeEditor.cancelSelection": "取消并清除选择",
  "appearance.themeEditor.expand": "展开主题编辑器",
  "appearance.themeEditor.minimize": "最小化主题编辑器",
  "appearance.themeEditor.close": "关闭主题编辑器",
  "appearance.themeEditor.name": "主题名称",
  "appearance.themeEditor.namePlaceholder": "例如：极光",
  "appearance.themeEditor.appearance": "外观",
  "appearance.themeEditor.appearanceGroup": "主题外观",
  "appearance.themeEditor.light": "浅色",
  "appearance.themeEditor.dark": "深色",
  "appearance.themeEditor.colors": "颜色",
  "appearance.themeEditor.derived": "选择两种颜色，其余自动生成",
  "appearance.themeEditor.filter": "筛选颜色",
  "appearance.themeEditor.advanced": "高级",
  "appearance.themeEditor.useAdvanced": "使用高级主题颜色",
  "appearance.themeEditor.background": "背景",
  "appearance.themeEditor.accent": "强调色",
  "appearance.themeEditor.mainColors": "主要颜色",
  "appearance.themeEditor.foundationColors": "基础",
  "appearance.themeEditor.brandContentColors": "品牌与内容",
  "appearance.themeEditor.contextColors": "场景",
  "appearance.themeEditor.statusColors": "状态颜色",
  "appearance.themeEditor.otherColors": "其他颜色",
  "appearance.themeEditor.family.background": "背景",
  "appearance.themeEditor.family.surface": "表面",
  "appearance.themeEditor.family.raisedSurface": "抬升表面",
  "appearance.themeEditor.family.overlay": "覆盖层",
  "appearance.themeEditor.family.text": "文本",
  "appearance.themeEditor.family.mutedText": "弱化文本",
  "appearance.themeEditor.family.border": "边框",
  "appearance.themeEditor.family.input": "输入框",
  "appearance.themeEditor.family.subtleSurface": "次级表面",
  "appearance.themeEditor.family.highlightSurface": "高亮表面",
  "appearance.themeEditor.family.accent": "强调色",
  "appearance.themeEditor.family.action": "操作",
  "appearance.themeEditor.family.messageSurface": "消息表面",
  "appearance.themeEditor.family.codeSurface": "代码表面",
  "appearance.themeEditor.family.sidebarBackground": "侧边栏背景",
  "appearance.themeEditor.family.sidebarControls": "侧边栏控件",
  "appearance.themeEditor.family.sidebarSelection": "侧边栏选中项",
  "appearance.themeEditor.family.terminalBackground": "终端背景",
  "appearance.themeEditor.family.error": "错误",
  "appearance.themeEditor.family.warning": "警告",
  "appearance.themeEditor.noMatches": "没有匹配的颜色。",
  "appearance.themeEditor.saveChanges": "保存更改",
  "appearance.themeEditor.merge": "合并到“{theme}”",
  "appearance.themeEditor.addPalette": "添加{appearance}配色",
  "appearance.themeEditor.color.choose": "选择颜色",
  "appearance.themeEditor.color.chooseNamed": "选择{label}颜色",
  "appearance.themeEditor.color.showUsage": "显示{label}的使用位置",
  "appearance.themeEditor.color.hideUsage": "隐藏{label}的使用位置",
  "appearance.themeEditor.toast.saveFailed": "无法保存主题",
  "appearance.themeEditor.toast.storageUnavailable": "浏览器存储不可用，未能保留此次更改。",
  "appearance.themeEditor.toast.updated": "已更新主题“{theme}”",
  "appearance.themeEditor.toast.paletteAdded": "已添加{appearance}配色。",
  "appearance.themeEditor.toast.saved": "已保存主题“{theme}”",
  "appearance.themeEditor.toast.changesActive": "更改已生效。",
  "appearance.themeEditor.toast.changesSaved": "更改已保存。",
  "appearance.themeEditor.toast.created": "已创建主题“{theme}”",
  "appearance.themeEditor.toast.nowActive": "该主题现已启用。",
  "appearance.panelAnimations.replay": "重新播放面板动画预览",

  "archive.loading": "正在加载已归档任务",
  "archive.loadFailed": "无法加载已归档任务",
  "archive.empty": "暂无已归档任务",
  "archive.checking": "正在检查已连接的环境。",
  "archive.emptyDescription": "已归档任务会显示在这里。",
  "archive.unarchive": "取消归档",
  "archive.failedUnarchive": "取消归档任务失败",
  "archive.failedDelete": "删除任务失败",
  "archive.actionFailed": "已归档任务操作失败",
  "archive.archivedAgo": "归档于 {time}",
  "archive.createdAgo": "创建于 {time}",
  "archive.justNow": "刚刚",
  "archive.ago": "{time}前",

  "providers.title": "服务提供方",
  "providers.accent.customHex": "自定义十六进制强调色",
  "providers.accent.chooseFor": "为 {provider} 选择强调色",
  "providers.accent.clear": "清除颜色",
  "providers.accent.label": "强调色",
  "providers.healthCheck.title": "健康检查间隔",
  "providers.healthCheck.description":
    "在后台刷新服务提供方的可用性、版本、认证状态和模型元数据。设为 0 秒时仅手动刷新。",
  "providers.healthCheck.policyTooltip":
    "此处配置检查间隔；计时器触发后，是否执行服务提供方检查由共享的“后台活动”策略决定。自定义间隔会在常规设置中显示为“高级”。",
  "providers.healthCheck.seconds": "秒",
  "providers.devices.primary": "主设备",
  "providers.devices.local": "本地设备",
  "providers.devices.remote": "远程设备",
  "providers.lastChecked.unavailable": "无法获取检查时间",
  "providers.lastChecked.justNow": "刚刚检查",
  "providers.lastChecked.ago": "{time}前检查",
  "providers.action.add": "添加服务提供方",
  "providers.action.refresh": "刷新服务提供方状态",
  "providers.action.toggleDetails": "展开或收起 {provider} 详情",
  "providers.action.enable": "启用 {provider}",
  "providers.action.delete": "删除服务提供方实例 {instance}",
  "providers.action.deleteShort": "删除实例",
  "providers.action.copyUpdate": "复制更新命令",
  "providers.action.copyCommand": "复制命令",
  "providers.action.toggleEmail": "显示或隐藏账号邮箱",
  "providers.action.revealEmail": "点击显示邮箱",
  "providers.action.hideEmail": "点击隐藏邮箱",
  "providers.add.title": "添加服务提供方实例",
  "providers.add.description":
    "在 {environment} 上配置额外的服务提供方实例，例如将第二套 Codex 安装指向不同的工作区。",
  "providers.add.step.driver": "驱动",
  "providers.add.step.identity": "标识",
  "providers.add.step.config": "配置",
  "providers.add.step.label": "{step}，第 {index} 步",
  "providers.add.workspace": "{driver} 工作区",
  "providers.add.comingSoon": "即将支持",
  "providers.add.label": "名称",
  "providers.add.labelPlaceholder": "例如：工作",
  "providers.add.labelDescription": "显示在服务提供方列表中，可选。",
  "providers.add.instanceId": "实例 ID",
  "providers.add.instanceIdDescription": "任务和会话使用的路由键，可使用字母、数字、“-”或“_”。",
  "providers.add.validation.required": "必须填写实例 ID。",
  "providers.add.validation.tooLong": "实例 ID 不能超过 64 个字符。",
  "providers.add.validation.pattern": "实例 ID 必须以字母开头，且只能包含字母、数字、“-”或“_”。",
  "providers.add.validation.exists": "实例“{id}”已存在。",
  "providers.add.accent": "强调色",
  "providers.add.accentLabel": "服务提供方实例强调色",
  "providers.add.useAccent": "使用强调色 {color}",
  "providers.add.clearAccent": "清除",
  "providers.add.accentDescription": "显示在选择器中的可选标记颜色。",
  "providers.add.noConfig": "此驱动无需必填配置，可以直接添加实例。",
  "providers.add.next": "下一步",
  "providers.add.addInstance": "添加实例",
  "providers.add.toast.added": "已添加服务提供方实例",
  "providers.add.toast.addedDescription": "已添加 {provider} 实例“{id}”。",
  "providers.add.toast.failed": "无法添加服务提供方实例",
  "providers.add.toast.updateFailed": "更新失败。",
  "providers.config.binaryPath": "程序路径",
  "providers.config.binaryPathDescription": "此实例使用的 {provider} 程序路径。",
  "providers.config.codexHome": "CODEX_HOME 路径",
  "providers.config.codexHomeDescription": "自定义 Codex 主目录和配置目录。",
  "providers.config.shadowHome": "独立主目录路径",
  "providers.config.shadowHomeDescription":
    "账号专用的 Codex 主目录；单独保存 auth.json，同时共享 CODEX_HOME 中的其他状态。",
  "providers.config.claudeHome": "CLAUDE_CONFIG_DIR 路径",
  "providers.config.claudeHomeDescription":
    "自定义 Claude 主目录和配置目录，分别保存 .claude.json 和 .claude。",
  "providers.config.launchArgs": "启动参数",
  "providers.config.launchArgsDescription": "会话启动时传递的额外 CLI 参数。",
  "providers.config.codexLaunchArgsDescription":
    "会话启动时传递给 codex app-server 的额外 CLI 参数。",
  "providers.config.apiEndpoint": "API 地址",
  "providers.config.apiEndpointDescription": "覆盖此实例使用的 Cursor API 地址。",
  "providers.config.serverUrl": "服务器 URL",
  "providers.config.serverUrlDescription": "留空时，由 T3 Code 在需要时启动服务器。",
  "providers.config.serverPassword": "服务器密码",
  "providers.config.serverPasswordDescription": "以明文形式保存在磁盘上。",
  "providers.config.autoCompactWindow": "达到以下用量后自动压缩",
  "providers.config.autoCompactWindowDescription":
    "上下文达到 100,000 至 1,000,000 个令牌后压缩。留空则使用 Claude 默认值。",
  "providers.config.autoCompactWindowPlaceholder": "例如 300000",
  "providers.config.optional": "可选",
  "providers.unavailable.loading": "正在加载服务提供方设置",
  "providers.unavailable.connectFailed": "无法连接到此设备",
  "providers.unavailable.title": "服务提供方设置暂不可用",
  "providers.unavailable.checkingPermissions": "正在检查当前会话可修改的内容。",
  "providers.unavailable.waitingConfiguration": "正在等待 {environment} 的配置。",
  "providers.connection.connecting": "正在连接...",
  "providers.connection.connected": "已连接",
  "providers.connection.disconnected": "已断开连接",
  "providers.connection.reconnecting": "正在重新连接...",
  "providers.status.checking": "正在检查服务提供方状态",
  "providers.status.waiting": "正在等待服务器返回安装和认证信息。",
  "providers.status.disabled": "已禁用",
  "providers.status.disabledDetail": "此服务提供方已安装，但不会用于新会话。",
  "providers.status.notFound": "未找到",
  "providers.status.cliNotDetected": "未在 PATH 中检测到 CLI。",
  "providers.status.authenticated": "已认证",
  "providers.status.authenticatedWith": "已认证 · {method}",
  "providers.status.authenticatedAs": "认证账号",
  "providers.status.notAuthenticated": "未认证",
  "providers.status.needsAttention": "需要处理",
  "providers.status.needsAttentionDetail": "服务提供方已安装，但服务器无法完成验证。",
  "providers.status.unavailable": "不可用",
  "providers.status.unavailableDetail": "服务提供方未通过启动检查。",
  "providers.status.available": "可用",
  "providers.status.availableDetail": "已安装并可使用，但无法验证认证状态。",
  "providers.status.disabledMessage": "已在 T3 Code 设置中禁用 {provider}。",
  "providers.status.cliMissing": "未安装 {provider} CLI（{command}），或其不在 PATH 中。",
  "providers.status.versionTooOld":
    "{provider} {current} 版本过旧，无法使用 {model}。请升级到 {required} 或更高版本。",
  "providers.badge.earlyAccess": "抢先体验",
  "providers.badge.new": "新增",
  "providers.update.available": "有可用更新",
  "providers.update.availableDetails": "有可用更新 - 查看详情",
  "providers.update.updating": "正在更新",
  "providers.update.now": "立即更新",
  "providers.update.manual": "或使用以下命令手动更新",
  "providers.update.installVersion": "有可用更新：请安装 {version}。",
  "providers.update.installLatest": "有可用更新：请安装最新的服务提供方版本。",
  "providers.update.commandCopied": "已复制 {provider} 更新命令",
  "providers.update.commandCopiedDescription": "准备好后，在终端中运行此命令即可更新。",
  "providers.update.copyFailed": "无法复制 {provider} 更新命令",
  "providers.instance.displayName": "显示名称",
  "providers.instance.placeholder": "实例名称",
  "providers.instance.displayNameDescription": "可选名称，将显示在服务提供方列表中。",
  "providers.instance.accentDescription": "用于在选择栏和模型列表中区分此实例。",
  "providers.instance.accentColor": "强调色",
  "providers.instance.customAccent": "为 {provider} 选择自定义强调色",
  "providers.instance.customAccentHex": "自定义强调色十六进制值",
  "providers.instance.selectAccent": "选择强调色 {color}",
  "providers.instance.clearAccent": "清除 {provider} 的强调色",
  "providers.instance.environmentVariables": "环境变量",
  "providers.instance.addVariable": "添加变量",
  "providers.instance.environmentVariablesDescription":
    "添加要传给此实例 CLI 的 API 密钥、基础 URL 或其他变量。",
  "providers.instance.sensitiveDescription": "敏感值会单独存储，保存后不会再返回到应用。",
  "providers.instance.variable": "变量",
  "providers.instance.value": "值",
  "providers.instance.valuePlaceholder": "值",
  "providers.instance.redactedValuePlaceholder": "已保存敏感值，输入新值可替换",
  "providers.instance.variableName": "第 {index} 个环境变量名称",
  "providers.instance.variableValue": "第 {index} 个环境变量值",
  "providers.instance.markSensitive": "将环境变量 {variable} 标记为敏感信息",
  "providers.instance.removeVariable": "移除环境变量 {variable}",
  "providers.instance.sensitive": "敏感信息",
  "providers.instance.sensitiveStored": "敏感信息，单独存储",
  "providers.instance.plainText": "明文",
  "providers.instance.options": "选项",
  "providers.instance.configuration": "配置",
  "providers.instance.authenticatedAs": "已认证账号",
  "providers.instance.unknownDriver": "当前版本未包含此服务提供方驱动。配置值会保留，但无法编辑。",
  "providers.models.title": "模型",
  "providers.models.hidden": "已隐藏",
  "providers.models.custom": "自定义",
  "providers.models.moveUp": "上移",
  "providers.models.moveDown": "下移",
  "providers.models.remove": "移除自定义模型",
  "providers.models.removeNamed": "移除自定义模型 {model}",
  "providers.models.available.one": "可用模型：{count} 个。",
  "providers.models.available.many": "可用模型：{count} 个。",
  "providers.models.details": "查看 {model} 详情",
  "providers.models.fastMode": "快速模式",
  "providers.models.thinking": "思考",
  "providers.models.reasoning": "推理",
  "providers.models.addFavorite": "添加到收藏",
  "providers.models.removeFavorite": "从收藏中移除",
  "providers.models.addFavoriteNamed": "将 {model} 添加到收藏",
  "providers.models.removeFavoriteNamed": "将 {model} 从收藏中移除",
  "providers.models.moveUpNamed": "上移 {model}",
  "providers.models.moveDownNamed": "下移 {model}",
  "providers.models.show": "在选择器中显示",
  "providers.models.hide": "从选择器中隐藏",
  "providers.models.showNamed": "显示 {model}",
  "providers.models.hideNamed": "隐藏 {model}",
  "providers.models.add": "添加",
  "providers.models.addCustom": "添加自定义模型",
  "providers.models.filter": "筛选模型",
  "providers.models.customAlwaysShown": "自定义模型会始终显示在选择器中",
  "providers.models.hiddenFromPicker": "在选择器中隐藏",
  "providers.models.shownInPicker": "在选择器中显示",
  "providers.models.count.one": "{count} 个模型",
  "providers.models.count.many": "{count} 个模型",
  "providers.models.count.favorites": "{count} 个收藏",
  "providers.models.count.hidden": "{count} 个已隐藏",
  "providers.models.noMatch": "没有符合筛选条件的模型。",
  "providers.models.notReported": "此服务提供方尚未返回模型。",
  "providers.models.group.favorites": "收藏",
  "providers.models.group.all": "全部",
  "providers.models.empty": "尚未配置服务提供方。",
  "providers.models.validation.required": "请输入模型标识。",
  "providers.models.validation.builtIn": "该模型已内置。",
  "providers.models.validation.tooLong": "模型标识不能超过 {count} 个字符。",
  "providers.models.validation.duplicate": "该自定义模型已保存。",
  "providers.devices.title": "设备",
  "providers.devices.loading": "正在加载设备",
  "providers.devices.loadingDescription": "正在读取已连接的执行环境。",
  "providers.devices.empty": "暂无已连接设备",
  "providers.devices.emptyDescription": "请先连接执行环境，再配置服务提供方。",
  "providers.deviceUnavailable.title": "设备不可用",
  "providers.deviceUnavailable.description": "请重新连接此设备以配置服务提供方，或选择其他设备。",
  "providers.permissions.limited": "权限受限",
  "providers.permissions.limitedDescription":
    "当前会话可以查看 {environment} 的服务提供方，但此凭据无权修改其配置。",
  "providers.instance.missing": "此服务提供方实例已不在该设备上。",
  "providers.setup.aria": "Antigravity 设置",
  "providers.setup.runsOn": "Antigravity 在 {environment} 上运行。",
  "providers.setup.enableDescription": "启用后即可在任务中使用。",
  "providers.setup.enable": "启用 Antigravity",
  "providers.setup.readOnly": "当前连接无权修改服务提供方设置。",
  "providers.setup.updateEnvironment":
    "请更新此环境，以便在这里安装 Antigravity 并使用 Google 登录。",
  "providers.setup.auth.reading": "正在读取登录状态。",
  "providers.setup.auth.signedIn": "已使用 Google 登录。",
  "providers.setup.auth.connected": "已连接。",
  "providers.setup.install.downloading": "正在下载 {downloaded} MB{total}。",
  "providers.setup.install.ofTotal": "，共 {total} MB",
  "providers.setup.install.extracting": "正在解压 Antigravity。",
  "providers.setup.install.verifying": "正在检查已下载的运行时。",
  "providers.setup.install.installed": "Antigravity 已安装。",
  "providers.setup.install.customUnavailable": "配置的 Antigravity 运行时不可用。",
  "providers.setup.install.customUnchecked": "尚未检查配置的 Antigravity 运行时。",
  "providers.setup.install.required": "请先安装官方 Antigravity 运行时，再登录。",
  "providers.setup.failed": "服务提供方设置失败。",
  "providers.setup.failedRetry": "服务提供方设置失败，请重试。",
  "providers.setup.auth.openFailed": "无法打开登录页面。请复制链接并在浏览器中打开。",
  "providers.setup.auth.copyFailed": "无法复制登录链接。请使用“打开登录页面”。",
  "providers.setup.pending.redirect": "正在检查重定向",
  "providers.setup.auth.signOut": "退出 Google 登录",
  "providers.setup.auth.disconnect": "断开连接",
  "providers.setup.auth.signOutConfirm":
    "要在 {environment} 上为 {provider} 执行“{action}”吗？这会停止正在运行的任务，但会保留任务历史。",
  "providers.setup.pending.signingOut": "正在退出登录",
  "providers.setup.install.removeConfirm":
    "要从 {environment} 移除已下载的 Antigravity 运行时吗？Google 登录状态和任务历史会保留。",
  "providers.setup.pending.removing": "正在移除运行时",
  "providers.setup.runtime": "运行时",
  "providers.setup.install.downloadAria": "Antigravity 下载进度",
  "providers.setup.install.customDescription":
    "此实例使用下方的程序路径。安装托管运行时不会更改该路径。",
  "providers.setup.install.downloadSize": "将从 Google 下载 {size} MB。",
  "providers.setup.install.unavailable":
    "此处无法自动安装。请在下方填写已有程序路径，或使用受支持的远程环境。",
  "providers.setup.pending.cancellingInstall": "正在取消安装",
  "providers.setup.install.cancel": "取消安装",
  "providers.setup.pending.startingInstall": "正在开始安装",
  "providers.setup.install.update": "更新 Antigravity",
  "providers.setup.install.reinstall": "重新安装 Antigravity",
  "providers.setup.install.retry": "重试安装",
  "providers.setup.install.managed": "安装托管运行时",
  "providers.setup.install.install": "安装 Antigravity",
  "providers.setup.install.remove": "移除已下载的运行时",
  "providers.setup.auth.open": "打开登录页面",
  "providers.setup.auth.copied": "链接已复制",
  "providers.setup.auth.copy": "复制登录链接",
  "providers.setup.auth.expiresAt": "链接失效时间",
  "providers.setup.auth.callbackDescription":
    "如果最后的本地页面无法加载，请将完整 URL 粘贴到这里。",
  "providers.setup.auth.otherClient": "登录已在另一个客户端打开，请在那里完成或取消。",
  "providers.setup.pending.cancellingSignIn": "正在取消登录",
  "providers.setup.auth.cancel": "取消登录",
  "providers.setup.pending.startingSignIn": "正在开始登录",
  "providers.setup.auth.retryGoogle": "重试 Google 登录",
  "providers.setup.auth.signInGoogle": "使用 Google 登录",
  "providers.setup.auth.retryConnection": "重试连接",
  "providers.setup.auth.connect": "连接",
  "providers.setup.retryStatus": "重试读取设置状态",

  "serverUpdate.stage.downloading": "正在下载…",
  "serverUpdate.stage.restarting": "正在重启…",
  "serverUpdate.status.updating": "正在更新 {server}",
  "serverUpdate.status.failed": "无法更新 {server}",
  "serverUpdate.failure.default": "服务器更新失败。",
  "serverUpdate.action": "更新",
  "serverUpdate.commandCopied": "已复制更新命令",
  "serverUpdate.commandCopiedDescription": "在 {server} 上运行 `{command}` 即可更新。",
  "serverUpdate.copyFailed": "无法复制更新命令",
  "serverUpdate.failed": "服务器更新失败",
  "serverUpdate.updated": "已更新 {server}",
  "serverUpdate.reconnected": "已重新连接到 t3@{version}。",
  "serverUpdate.desktopManaged": "请更新该设备上的桌面应用，以更新此服务器。",
  "serverUpdate.copyCommand": "复制更新命令",

  "connections.thisEnvironment.title": "当前环境",
  "connections.networkAccess.title": "网络访问",
  "connections.networkAccess.localOnly":
    "此后端目前仅能从本机访问。请使用非回环地址重启，以启用远程配对。",
  "connections.networkAccess.remoteConfigured":
    "此后端已配置为可远程访问。网络暴露设置需要在启动服务器的位置修改。",
  "connections.networkAccess.limited": "仅限本机访问。",
  "connections.networkAccess.loading": "正在加载…",
  "connections.networkAccess.reachableAt": "可访问地址",
  "connections.networkAccess.hide": "收起",
  "connections.networkAccess.exposed": "已在所有网络接口上开放。",
  "connections.networkAccess.exposedWithHost": "已在所有网络接口上开放。配对链接使用 {host}。",
  "connections.remoteEnvironments.title": "远程环境",
  "connections.addEnvironment.action": "添加环境",
  "connections.cloudLink.signInRequired": "请登录 T3 Connect 以管理此环境。",
  "connections.cloudLink.permissionRequired": "当前会话无权管理 T3 Connect 访问权限。",
  "connections.cloudLink.linked": "已连接 T3 Connect",
  "connections.cloudLink.tunnelDisabled": "已停用 T3 Connect 隧道",
  "connections.cloudLink.unlinked": "已断开 T3 Connect",
  "connections.cloudLink.available": "此环境现可通过 T3 Connect 访问。",
  "connections.cloudLink.tunnelRemovedPublishingOn": "托管隧道已移除，Agent 动态发布仍保持开启。",
  "connections.cloudLink.unavailable": "此环境已无法通过 T3 Connect 访问。",
  "connections.cloudLink.activityEnabled": "已启用 Agent 动态",
  "connections.cloudLink.activityDisabled": "已停用 Agent 动态",
  "connections.cloudLink.activityPublishing": "此环境会向你的移动客户端发布 Agent 动态。",
  "connections.cloudLink.activityStopped": "此环境将停止发布 Agent 动态。",
  "connections.cloudLink.otherDevicesAvailable": "你的其他设备可通过 T3 Connect 访问此环境。",
  "connections.cloudLink.otherDevicesEnable": "允许你的其他设备通过 T3 Connect 访问此环境。",
  "connections.cloudLink.publishDescription":
    "无需 T3 Connect，即可将动态发送到移动端通知和实时活动。",
  "connections.cloudLink.publishAria": "向移动客户端发布 Agent 动态",
  "connections.ssh.hostOrAlias": "SSH 主机或别名",
  "connections.ssh.searchPlaceholder": "搜索主机或输入 devbox",
  "connections.ssh.loadingHosts": "正在加载主机…",
  "connections.environmentIcon.description":
    "设置其他设备看到的此环境设备类型；自动模式使用服务器检测结果。",
  "connections.addEnvironment.adding": "正在添加...",
  "connections.addEnvironment.title": "添加环境",
  "connections.addEnvironment.description": "将另一个环境与此客户端配对。",
  "connections.remoteLink.title": "远程链接",
  "connections.remoteLink.description": "输入后端主机地址和配对码。",
  "connections.remoteLink.host": "主机地址",
  "connections.remoteLink.pairingCode": "配对码",
  "connections.remoteLink.pairingUrlHint": "在此粘贴完整的配对链接，即可自动填写这两个字段。",
  "connections.saved.connect": "连接",
  "connections.saved.connecting": "正在连接...",
  "connections.saved.disconnect": "断开连接",
  "connections.saved.disconnecting": "正在断开连接...",
  "connections.saved.remove": "移除",
  "connections.saved.removing": "正在移除...",
  "connections.empty.title": "暂无已保存的远程环境",
  "connections.empty.withCloud": "点击“添加环境”以配对其他环境，也可以从 T3 Connect 连接环境。",
  "connections.empty.withoutCloud": "点击“添加环境”以配对其他环境。",
  "connections.networkAccess.enable": "启用网络访问",
  "connections.networkAccess.enableTitle": "启用网络访问？",
  "connections.networkAccess.disableTitle": "停用网络访问？",
  "connections.networkAccess.enableDescription": "T3 Code 将重新启动，并允许通过网络访问此环境。",
  "connections.networkAccess.disableDescription":
    "T3 Code 将重新启动，并把此环境限制为仅本机可访问。",
  "connections.networkAccess.restarting": "正在重新启动...",
  "connections.networkAccess.restartEnable": "重新启动并启用",
  "connections.networkAccess.restartDisable": "重新启动并停用",
  "connections.wsl.confirm.disableOnlyTitle": "关闭 WSL 并切回 Windows？",
  "connections.wsl.confirm.disableTitle": "停用 WSL 后端？",
  "connections.wsl.confirm.distroTitle": "切换 WSL 发行版？",
  "connections.wsl.confirm.enableTitle": "启动 WSL 后端",
  "connections.wsl.confirm.onlyTitle": "仅运行 WSL 后端？",
  "connections.wsl.confirm.windowsTitle": "重新启用 Windows 后端？",
  "connections.wsl.confirm.disableOnlyDescription":
    "T3 Code 将在 Windows 后端上重新启动。通过 WSL 打开的任务和项目会安全保留在发行版中，重新启用 WSL 后即可再次使用。",
  "connections.wsl.confirm.disableDescription":
    "WSL 后端将停止。通过 WSL 打开的任务和项目会安全保留在发行版中，但在重新启用 WSL 前无法在 T3 Code 中访问。",
  "connections.wsl.confirm.distroDescription":
    "T3 Code 将在新的发行版上重新启动 WSL 后端。当前发行版中仍在运行的会话会被中断。",
  "connections.wsl.confirm.enableDescription":
    "可以让 WSL 与 Windows 后端同时运行，也可以停止 Windows 后端并仅使用 WSL。之后仍可在设置中更改。",
  "connections.wsl.confirm.onlyDescription":
    "T3 Code 将重新启动并仅运行 WSL 后端。再次关闭此选项前，Windows 侧的项目将无法访问。",
  "connections.wsl.confirm.windowsDescription":
    "T3 Code 将重新启动，并在 WSL 之外恢复 Windows 后端。",
  "connections.wsl.confirm.applying": "正在应用...",
  "connections.wsl.confirm.onlyAction": "仅使用 WSL",
  "connections.wsl.confirm.bothAction": "同时运行两个后端",
  "connections.wsl.confirm.windowsAction": "切换到 Windows",
  "connections.wsl.confirm.disableAction": "停用 WSL",
  "connections.wsl.confirm.distroAction": "切换发行版",
  "connections.access.scope.one": "{count} 项权限",
  "connections.access.scope.many": "{count} 项权限",
  "connections.access.scope.show": "{label}：查看 {count}",
  "connections.access.scope.granted": "已授予的权限范围",
  "connections.access.authorizedClients": "已授权客户端",
  "connections.access.empty": "暂无配对链接或客户端会话。",
  "connections.access.revoke": "撤销",
  "connections.access.revoking": "正在撤销…",
  "connections.access.revokeOthers": "撤销其他客户端",
  "connections.access.createLink": "创建链接",
  "connections.access.createLinkTitle": "创建配对链接",
  "connections.access.createLinkDescription":
    "生成一次性链接，供其他设备作为已授权客户端与此后端配对。",
  "connections.access.clientLabel": "客户端名称（可选）",
  "connections.access.clientLabelPlaceholder": "例如：客厅 iPad",
  "connections.access.permissions": "权限",
  "connections.access.permissionsDescription": "限制已配对客户端可执行的操作。",
  "connections.access.readOnly": "只读",
  "connections.access.standard": "标准",
  "connections.access.selectPermission": "请至少选择一项权限。",
  "connections.access.manageWarning": "此客户端可以为其他设备创建或撤销访问权限。",
  "connections.access.scope.viewEnvironment": "查看环境",
  "connections.access.scope.viewEnvironmentDescription": "读取任务、状态、差异和配置。",
  "connections.access.scope.operateTasks": "操作任务",
  "connections.access.scope.operateTasksDescription": "启动任务并在环境中执行更改。",
  "connections.access.scope.useTerminals": "使用终端",
  "connections.access.scope.useTerminalsDescription": "创建终端并向运行中的 Shell 发送输入。",
  "connections.access.scope.writeReviews": "编写审查意见",
  "connections.access.scope.writeReviewsDescription": "在审查更改时创建评论。",
  "connections.access.scope.viewAccess": "查看访问权限",
  "connections.access.scope.viewAccessDescription": "查看配对链接和已授权客户端。",
  "connections.access.scope.manageAccess": "管理访问权限",
  "connections.access.scope.manageAccessDescription": "为其他客户端签发或撤销凭据。",
  "connections.access.scope.viewRelay": "查看中继",
  "connections.access.scope.viewRelayDescription": "查看托管中继连接状态。",
  "connections.access.scope.manageRelay": "管理中继",
  "connections.access.scope.manageRelayDescription": "修改托管隧道连接。",
  "connections.access.creating": "正在创建…",
  "connections.access.thisDevice": "当前设备",
  "connections.access.pairingLink": "配对链接",
  "connections.access.pairingCode": "配对码",
  "connections.access.share": "分享",
  "connections.access.copyCode": "复制配对码",
  "connections.access.copyCodeOnly": "仅复制配对码",
  "connections.access.copyLink": "复制链接",
  "connections.access.showLink": "显示链接",
  "connections.access.showCode": "显示配对码",
  "connections.access.reachVia": "通过以下地址访问此设备",
  "connections.access.qrTitle": "扫描配对链接，在其他设备上打开",
  "connections.access.createdAt": "链接创建于 {time}",
  "connections.access.pairingScopes": "配对链接权限",
  "connections.access.clientScopes": "客户端权限",
  "connections.access.noReachableHost":
    "请复制配对码，并在另一个客户端中使用此后端可访问的主机地址完成配对。",
  "connections.access.hostedLink": "托管应用配对链接",
  "connections.access.hostedLinkDescription":
    "此处无法使用剪贴板。请在要连接的设备上打开或手动复制此托管应用链接。",
  "connections.access.pairingLinkDescription":
    "此处无法使用剪贴板。请在要连接的设备上打开或手动复制完整配对链接。",
  "connections.access.pairingCodeDescription":
    "此处无法使用剪贴板。请将此配对码手动复制到另一个客户端。",
  "connections.access.noQr": "此地址无法生成二维码，请复制 URL 在当前设备上使用。",
  "connections.access.endpoint.lan": "同一局域网内的设备",
  "connections.access.endpoint.private": "私有网络中的设备",
  "connections.access.endpoint.public": "可从任意位置访问",
  "connections.access.endpoint.loopback": "当前设备上的客户端",
  "connections.access.endpoint.hosted": "打开托管应用，无需安装",
  "connections.endpoint.setupRequired": "需要设置",
  "connections.endpoint.default": "默认",
  "connections.endpoint.restarting": "正在重启…",
  "connections.endpoint.setup": "设置",
  "connections.endpoint.disable": "禁用",
  "connections.endpoint.setDefault": "设为默认",
  "connections.ssh.description": "使用本机 SSH 配置、Agent 和隧道连接后端。",
  "connections.ssh.username": "用户名",
  "connections.ssh.port": "端口",
  "connections.ssh.suggested": "建议的主机",
  "connections.ssh.suggestedDescription": "来自 SSH 配置和已知主机",
  "connections.ssh.noneDiscovered": "未发现新的 SSH 主机。",
  "connections.wsl.title": "WSL 后端",
  "connections.wsl.loadFailed": "无法加载 WSL 后端状态。",
  "connections.wsl.retrying": "正在重试…",
  "connections.wsl.unavailableDescription":
    "WSL 已不可用，当前正在运行 Windows 后端。关闭 WSL 后端即可清除此偏好。",
  "connections.wsl.switchWindows": "切换到 Windows",
  "connections.wsl.description":
    "在 Windows 后端旁边，再在 WSL 发行版中运行一个后端。选择发行版即可启动，选择“关闭”即可停止。通过 WSL 后端打开的项目位于 Linux 侧，Windows 项目保持原位置。",
  "connections.wsl.startFailed": "WSL 后端无法启动：{error}",
  "connections.wsl.off": "关闭",
  "connections.wsl.defaultDistro": "默认发行版",
  "connections.wsl.defaultSuffix": "默认",
  "connections.wsl.onlyTitle": "仅使用 WSL",
  "connections.wsl.onlyDescription":
    "停止 Windows 后端，仅运行 WSL 后端。适合完全在 WSL 中开发且不需要第二个后端进程的情况。修改后 T3 Code 会重启。",
  "connections.wsl.onlyLabel": "仅运行 WSL 后端",
  "connections.tailscale.title": "Tailscale HTTPS",
  "connections.tailscale.enable": "启用 Tailscale HTTPS",
  "connections.tailscale.description.available":
    "使用 Tailscale Serve，通过 MagicDNS HTTPS 地址开放此后端。",
  "connections.tailscale.description.unavailable":
    "启动 Tailscale，通过 MagicDNS 配置 HTTPS 访问。",
  "connections.tailscale.port": "HTTPS 端口",
  "connections.tailscale.endpoint": "HTTPS 地址",
  "connections.tailscale.portInvalid": "请输入 1 到 65535 之间的端口。",
  "connections.tailscale.pendingEndpoint": "等待生成 MagicDNS 地址",
  "connections.tailscale.disableTitle": "禁用 Tailscale HTTPS？",
  "connections.tailscale.disableDescription":
    "T3 Code 将重启本地后端，并停止使用 Tailscale Serve。",
  "connections.tailscale.setupTitle": "设置 Tailscale HTTPS？",
  "connections.tailscale.setupDescription":
    "T3 Code 将重启本地后端、启用 Tailscale Serve，并让 Tailscale 将 HTTPS 流量转发到此后端。",
  "connections.tailscale.restartDisable": "重启并禁用",
  "connections.admin.title": "管理权限",
  "connections.admin.description": "管理配对链接和客户端会话需要此后端的 access:write 权限。",

  "sourceControl.versionControl": "版本控制",
  "sourceControl.providers": "源代码管理服务提供方",
  "sourceControl.textGeneration": "文本生成",
  "sourceControl.writingStyle.title": "源代码管理写作风格",
  "sourceControl.writingStyle.repoConventions": "遵循仓库惯例",
  "sourceControl.writingStyle.repoConventionsDescription":
    "在每个项目中，参考最近的更改说明和变更请求标题。",
  "sourceControl.writingStyle.conventionalCommits": "Conventional Commits",
  "sourceControl.writingStyle.conventionalCommitsDescription":
    "更改说明使用 Conventional Commit 前缀，变更请求的标题和说明保持简洁。",
  "sourceControl.writingStyle.custom": "自定义说明",
  "sourceControl.writingStyle.customDescription":
    "在每个项目中，将你的说明应用于更改说明以及变更请求的标题和说明。",
  "sourceControl.writingStyle.customPlaceholder": "标题保持简洁，说明使用简短的项目符号。",
  "sourceControl.followTemplates.title": "遵循变更请求模板",
  "sourceControl.followTemplates.description":
    "仓库提供模板时，使用当前仓库的模板组织变更请求说明。",
  "sourceControl.writerModel.title": "源代码管理写作模型",
  "sourceControl.writerModel.description":
    "可为更改说明、变更请求标题与说明，以及分支或书签名称指定专用模型。关闭时使用全局文本生成模型。",
  "sourceControl.writerModel.none": "没有可用的文本生成服务提供方。",
  "sourceControl.action.rescan": "重新扫描服务器环境",
  "sourceControl.action.rescanTooltip": "重新扫描 Git 和托管服务集成",
  "sourceControl.action.toggleDetails": "展开或收起 {provider} 详情",
  "sourceControl.action.availability": "{provider} 可用性",
  "sourceControl.action.accountVisibility": "显示或隐藏源代码管理账号",
  "sourceControl.action.revealAccount": "点击显示账号",
  "sourceControl.action.hideAccount": "点击隐藏账号",
  "sourceControl.status.authenticated": "已认证",
  "sourceControl.status.authenticatedAs": "认证账号",
  "sourceControl.changeRequest.pullRequest": "拉取请求",
  "sourceControl.changeRequest.mergeRequest": "合并请求",
  "sourceControl.changeRequest.generic": "变更请求",
  "sourceControl.status.notAuthenticated": "未认证",
  "sourceControl.status.unknown": "状态未知",
  "sourceControl.status.available": "可用",
  "sourceControl.status.comingSoon": "即将支持",
  "sourceControl.summary.comingSoon": "即将支持 {provider}。",
  "sourceControl.summary.unavailable": "此服务器上不可用：{hint}",
  "sourceControl.summary.availableHint": "可用。{hint}",
  "sourceControl.summary.notAuthenticated":
    "此服务器上的 {provider} 尚未认证。请在服务器主机上使用 {command} 工具登录或配置凭据，以启用变更请求功能。",
  "sourceControl.summary.verifyFailed": "无法验证 {provider}。{hint}",
  "sourceControl.hint.azure":
    "请安装 Azure 命令行工具（`az`），再运行 `az extension add --name azure-devops` 启用 Azure DevOps 支持。",
  "sourceControl.hint.git": "请从 https://git-scm.com/downloads 或使用包管理器安装 Git。",
  "sourceControl.hint.jujutsu":
    "请运行 `brew install jj`，或从 https://github.com/jj-vcs/jj 安装 Jujutsu。",
  "sourceControl.hint.github":
    "请从 https://cli.github.com/ 或使用包管理器安装 GitHub 命令行工具（`gh`），例如运行 `brew install gh`。",
  "sourceControl.hint.gitlab":
    "请从 https://gitlab.com/gitlab-org/cli 或使用包管理器安装 GitLab 命令行工具（`glab`），例如运行 `brew install glab`。",
  "sourceControl.hint.bitbucket":
    "请在服务器上设置 T3CODE_BITBUCKET_EMAIL 和 T3CODE_BITBUCKET_API_TOKEN，并使用具有拉取请求、仓库及用户读取权限的 Bitbucket API Token。",
  "sourceControl.fetch.title": "拉取间隔",
  "sourceControl.fetch.policyTooltip":
    "此间隔仅用于 Git。计时触发后，共享的“后台活动”策略仍会决定是否允许 Git 刷新。自定义间隔会在常规设置中显示为“高级”。",
  "sourceControl.fetch.description":
    "在后台刷新远程分支状态。如果 Git 凭据或安全密钥只应由明确的 Git 操作触发，请设为 0 秒。",
  "sourceControl.fetch.seconds": "秒",
  "sourceControl.fetch.decrease": "缩短拉取间隔",
  "sourceControl.fetch.inSeconds": "Git 自动拉取间隔（秒）",
  "sourceControl.fetch.increase": "延长拉取间隔",
  "sourceControl.empty.section": "服务器环境",
  "sourceControl.empty.scanFailed": "无法扫描服务器环境",
  "sourceControl.empty.title": "尚未检测到相关工具",
  "sourceControl.empty.description":
    "请在服务器上安装 Git，并按工作区需要添加可选的托管服务集成或凭据，然后重新扫描。",
  "sourceControl.empty.scan": "扫描",

  "keybindings.browserWarning":
    "部分快捷键可能会先被浏览器占用，T3 Code 无法接收。使用桌面应用可获得更完整的快捷键支持。",
  "keybindings.column.command": "命令",
  "keybindings.column.keybinding": "快捷键",
  "keybindings.column.when": "生效条件",
  "keybindings.column.status": "状态",
  "keybindings.count.one": "{count} 个快捷键",
  "keybindings.count.many": "{count} 个快捷键",
  "keybindings.search": "搜索快捷键",
  "keybindings.add": "添加快捷键",
  "keybindings.openConfig": "打开 keybindings.json",
  "keybindings.empty": "没有符合搜索条件的快捷键。",
  "keybindings.always": "始终",
  "keybindings.edit": "编辑",
  "keybindings.actionsFor": "“{command}”的操作",
  "keybindings.pressShortcut": "按下快捷键",
  "keybindings.unassigned": "未分配",
  "keybindings.saving": "正在保存",
  "keybindings.save": "保存",
  "keybindings.reset": "恢复默认值",
  "keybindings.remove": "移除",
  "keybindings.cancel": "取消",
  "keybindings.editShortcutFor": "编辑 {command} 的快捷键：{shortcut}",
  "keybindings.bindingFor": "{command} 的快捷键",
  "keybindings.cancelNew": "取消新增快捷键",
  "keybindings.source.custom": "自定义",
  "keybindings.error.openTitle": "无法打开快捷键配置文件",
  "keybindings.error.openDescription": "未能打开快捷键配置文件。",
  "keybindings.error.saveTitle": "无法保存快捷键",
  "keybindings.error.saveDescription": "未能保存快捷键。",
  "keybindings.error.removeTitle": "无法移除快捷键",
  "keybindings.error.removeDescription": "未能移除快捷键。",
  "keybindings.when.title": "生效条件",
  "keybindings.when.condition": "条件",
  "keybindings.when.group": "条件组",
  "keybindings.when.not": "非",
  "keybindings.when.and": "且",
  "keybindings.when.or": "或",
  "keybindings.when.expression": "生效条件表达式",
  "keybindings.when.removeCondition": "移除条件",
  "keybindings.when.removeGroup": "移除条件组",
  "keybindings.when.removeNegatedGroup": "移除取反条件组",
  "keybindings.when.negate": "对 {condition} 取反",
  "keybindings.when.negateGroup": "对条件组取反",
  "keybindings.when.fixExpression": "请先修正上方表达式，再继续使用可视化编辑。",
  "keybindings.when.unknownOne": "未知条件：{condition}",
  "keybindings.when.unknownMany": "未知条件：{conditions}",
  "keybindings.when.unknownDescription":
    "T3 Code 暂时无法识别此条件。仍可保存，但只有运行时提供该条件时才可能匹配。",
  "keybindings.conflict.one": "与 {binding} 冲突。",
  "keybindings.conflict.many": "与 {bindings}{more} 冲突。",
  "keybindings.conflict.more": "等更多快捷键",
  "keybindings.conflict.description": "{conflict} 两个条件都适用时，以最近匹配的快捷键为准。",
  "keybindings.command.sidebarToggle": "侧栏：切换",
  "keybindings.command.terminalToggle": "终端：切换",
  "keybindings.command.rightPanelToggle": "右侧面板：切换",
  "keybindings.command.terminalSplit": "终端：拆分",
  "keybindings.command.terminalSplitVertical": "终端：垂直拆分",
  "keybindings.command.terminalNew": "终端：新建",
  "keybindings.command.terminalClose": "终端：关闭",
  "keybindings.command.diffToggle": "差异面板：切换",
  "keybindings.command.previewToggle": "预览：切换",
  "keybindings.command.previewRefresh": "预览：刷新",
  "keybindings.command.previewFocusUrl": "预览：聚焦地址栏",
  "keybindings.command.previewZoomIn": "预览：放大",
  "keybindings.command.previewZoomOut": "预览：缩小",
  "keybindings.command.previewResetZoom": "预览：重置缩放",
  "keybindings.command.commandPaletteToggle": "命令面板：切换",
  "keybindings.command.filePickerToggle": "文件选择器：切换",
  "keybindings.command.projectSearchToggle": "项目搜索：切换",
  "keybindings.command.themeEditorToggle": "主题编辑器：切换",
  "keybindings.command.composerStash": "输入框：暂存内容",
  "keybindings.command.chatNew": "聊天：新建任务",
  "keybindings.command.chatNewLocal": "聊天：新建本地任务",
  "keybindings.command.modelPickerToggle": "模型选择器：切换",
  "keybindings.command.editorOpenFavorite": "编辑器：在首选编辑器中打开",
  "keybindings.command.threadPrevious": "任务：上一个",
  "keybindings.command.threadNext": "任务：下一个",
  "keybindings.command.threadSettle": "任务：收起或恢复",
  "keybindings.command.threadJump": "任务：跳转到第 {index} 个",
  "keybindings.command.modelPickerJump": "模型选择器：选择第 {index} 个模型",
  "keybindings.command.runScript": "运行脚本：{name}",

  "providerUpdate.title.single": "可用更新：{provider} {version}",
  "providerUpdate.title.multiple": "有 {count} 个服务提供方可更新",
  "providerUpdate.description.installOrSettings": "立即安装更新，或前往服务提供方设置查看。",
  "providerUpdate.description.settingsOnly": "可前往服务提供方设置更新 {providers}。",
  "providerUpdate.providerList.two": "{first} 和 {second}",
  "providerUpdate.providerList.many": "{leading} 和 {last}",
  "providerUpdate.action.settings": "设置",
  "providerUpdate.action.update": "更新",
  "providerUpdate.action.retry": "重试",

  "pairing.pending.title": "正在与此环境配对",
  "pairing.pending.description": "正在验证配对链接并准备会话。",
  "pairing.title": "与此环境配对",
  "pairing.token.label": "配对令牌",
  "pairing.token.placeholder": "粘贴一次性令牌或配对密钥",
  "pairing.submitting": "正在配对...",
  "pairing.auth.trustedRequired": "连接应用前，此环境需要受信任的配对凭据。",
  "pairing.auth.enterToken": "输入配对令牌以与此环境建立会话。",
  "pairing.auth.desktopAndToken": "此环境同时接受桌面端管理的配对和一次性配对令牌。",
  "pairing.auth.desktopOnly": "此环境由桌面端管理。请从桌面应用打开，或粘贴明确签发的引导凭据。",
  "pairing.auth.tokenOnly":
    "此环境接受一次性配对令牌。配对链接可以直接打开此页面，也可以在此粘贴令牌。",
  "pairing.hosted.connecting": "正在连接此后端。",
  "pairing.hosted.missingRequest": "此配对链接缺少后端地址或令牌。",
  "pairing.hosted.tokenUsed": "此一次性配对令牌已提交，请申请新的配对链接。",
  "pairing.hosted.defaultEnvironment": "此环境",
  "pairing.hosted.saved": "{environment} 已保存在此浏览器中。",
  "pairing.hosted.retryWithNewLink":
    "{error} 如果后端已经接受此一次性令牌，请先申请新的配对链接再重试。",
  "pairing.hosted.pairedTitle": "后端已配对",
  "pairing.hosted.failedTitle": "配对失败",
  "pairing.hosted.pairingTitle": "正在配对后端",
  "pairing.hosted.host": "地址：",
  "pairing.hosted.troubleshooting":
    "请确认浏览器能够访问后端、后端允许托管客户端跨域访问，并且从 HTTPS 页面打开时后端也通过 HTTPS 提供服务。",
  "pairing.hosted.openApp": "打开应用",
  "pairing.authenticationFailed": "身份验证失败。",

  "sidebar.status.working": "进行中",
  "sidebar.status.monitoring": "监控中",
  "sidebar.status.approval": "待批准",
  "sidebar.status.input": "等待输入",
  "sidebar.status.failed": "失败",
  "sidebar.status.woke": "已唤醒",
  "sidebar.status.done": "已完成",
  "sidebar.status.pinned": "已置顶",
  "sidebar.pinnedThreads": "已置顶任务",
  "sidebar.goToThreads": "前往任务列表",
  "sidebar.settings": "设置",
  "sidebar.pullRequests": "拉取请求",
  "sidebar.usage": "用量",
  "sidebar.threadTitle": "任务标题",
  "sidebar.searchThreads": "搜索任务",
  "sidebar.clearSearch": "清除任务搜索",
  "sidebar.newThread": "新建任务",
  "sidebar.newThreadCurrentProject": "在当前项目中新建任务：Shift+单击",
  "sidebar.filterByProject": "按项目筛选任务",
  "sidebar.searchProjects": "搜索项目",
  "sidebar.searchProjectsPlaceholder": "搜索项目...",
  "sidebar.noMatchingProjects": "没有匹配的项目。",
  "sidebar.discardDraft": "丢弃草稿",
  "sidebar.unsentDraft": "未发送的草稿",
  "sidebar.allProjects": "全部项目",
  "sidebar.projectSettings": "{project} 的项目设置",
  "sidebar.newProject": "新建项目",
  "sidebar.searchResults": "任务搜索结果",
  "sidebar.noThreadsFound": "未找到任务",
  "sidebar.snoozedTitle": "已稍后处理",
  "sidebar.snoozed": "已稍后处理（{count}）",
  "sidebar.settledTitle": "已收起",
  "sidebar.settled": "已收起（{count}）",
  "sidebar.showMore": "再显示 {count} 项",
  "sidebar.noProjects": "还没有项目",
  "sidebar.addProject": "添加项目",
  "sidebar.noProjectThreads": "{project} 中还没有任务",
  "sidebar.noThreads": "还没有任务",
  "sidebar.snooze.hour": "一小时后",
  "sidebar.snooze.threeHours": "三小时后",
  "sidebar.snooze.evening": "今天晚上",
  "sidebar.snooze.tomorrow": "明天",
  "sidebar.snooze.nextWeek": "下周",
  "sidebar.action.newThreadOnBranch": "在 {branch} 分支新建任务",
  "sidebar.action.pin": "置顶任务",
  "sidebar.action.unpin": "取消置顶任务",
  "sidebar.action.settle": "收起任务",
  "sidebar.bulk.settle": "收起",
  "sidebar.action.unsettle": "恢复任务",
  "sidebar.action.wake": "立即唤醒任务",
  "sidebar.action.snooze": "稍后处理",
  "sidebar.action.rename": "重命名任务",
  "sidebar.action.regenerateTitle": "重新生成标题",
  "sidebar.action.regenerating": "正在重新生成...",
  "sidebar.bulk.regenerateTitles": "重新生成标题",
  "sidebar.bulk.regeneratingTitles": "正在重新生成标题…",
  "sidebar.action.markUnread": "标为未读",
  "sidebar.action.copy": "复制",
  "sidebar.action.path": "路径",
  "sidebar.action.branch": "分支",
  "sidebar.action.threadId": "任务 ID",
  "sidebar.action.copyPath": "复制路径",
  "sidebar.action.copyBranch": "复制分支",
  "sidebar.action.shareGuest": "与访客共享",
  "sidebar.action.copyThreadId": "复制任务 ID",
  "sidebar.action.archive": "归档任务",
  "sidebar.action.dismissWoke": "清除已唤醒提示",
  "sidebar.confirmDeleteThread": "要删除任务“{title}”吗？",
  "sidebar.confirmDeleteThreadDescription": "这会永久清除此任务的对话记录。",
  "sidebar.confirmDeleteThreadsOne": "要删除 1 个任务吗？",
  "sidebar.confirmDeleteThreadsMany": "要删除 {count} 个任务吗？",
  "sidebar.confirmDeleteThreadsDescription": "这会永久清除这些任务的对话记录。",
  "sidebar.thisThread": "此任务",
  "sidebar.deleteThreadFailed": "删除任务失败",
  "sidebar.deleteThreadsFailed": "删除多个任务失败",

  "composer.mode.supervised": "监督模式",
  "composer.mode.supervisedDescription": "执行命令和更改文件前先询问。",
  "composer.mode.autoAcceptEdits": "自动接受编辑",
  "composer.mode.autoAcceptEditsDescription": "自动批准编辑，执行其他操作前仍会询问。",
  "composer.mode.auto": "自动",
  "composer.mode.autoDescription": "支持的服务提供方会批准常规操作，其他操作仍会询问。",
  "composer.mode.fullAccess": "完全访问",
  "composer.mode.fullAccessDescription": "无需提示即可执行命令和编辑。",
  "composer.mode.plan": "方案",
  "composer.mode.build": "执行",
  "composer.mode.switchToBuild": "当前为方案模式，点击返回常规执行模式",
  "composer.mode.switchToPlan": "当前为常规模式，点击进入方案模式",
  "composer.runtimeMode": "运行模式",
  "composer.preparingWorktree": "正在准备工作树...",
  "composer.action.submitting": "正在提交...",
  "composer.action.submit": "提交",
  "composer.action.next": "下一步",
  "composer.action.nextQuestion": "下一题",
  "composer.action.submitAnswers": "提交回答",
  "composer.action.submitAnswer": "提交回答",
  "composer.action.stopGeneration": "停止生成",
  "composer.action.previousQuestion": "上一题",
  "composer.action.previous": "上一步",
  "composer.action.sending": "正在发送...",
  "composer.action.refine": "完善方案",
  "composer.action.implement": "开始实施",
  "composer.action.implementationActions": "实施选项",
  "composer.action.implementNewThread": "在新任务中实施",
  "composer.action.sendMessage": "发送消息",
  "composer.action.writeCustomAnswer": "输入自定义回答",
  "composer.action.expand": "展开输入区",
  "composer.state.environmentDisconnected": "环境已断开连接",
  "composer.state.connecting": "正在连接",
  "composer.state.preparingWorktree": "正在准备工作树",
  "composer.state.sending": "正在发送",
  "composer.state.sendingFeedback": "正在发送反馈",
  "composer.state.messagesLoading": "正在加载消息",
  "composer.state.messagesSyncing": "正在同步消息",
  "composer.command.modelDescription": "切换此任务使用的回复模型",
  "composer.command.planDescription": "将此任务切换到方案模式",
  "composer.command.defaultDescription": "将此任务切回普通构建模式",
  "composer.command.runProviderCommand": "运行服务提供方命令",
  "composer.command.runProviderSkill": "运行服务提供方技能",
  "composer.command.scopedSkill": "{scope} 技能",
  "composer.menu.searchingSkills": "正在搜索工作区技能...",
  "composer.menu.searchingFiles": "正在搜索工作区文件...",
  "composer.menu.noSkills": "未找到技能。可输入 / 浏览服务提供方命令。",
  "composer.menu.noPaths": "没有匹配的文件或文件夹。",
  "composer.menu.noCommands": "没有匹配的命令。",
  "composer.skillSource.app": "应用",
  "composer.skillSource.repo": "仓库",
  "composer.skillSource.project": "项目",
  "composer.skillSource.personal": "个人",
  "composer.skillSource.system": "系统",
  "composer.skillSource.provider": "服务提供方",
  "composer.skillSource.suffix": "技能",
  "composer.banner.wokeTitle": "此任务已结束稍后处理",
  "composer.banner.wokeDescription": "关闭此提示，或直接发送消息继续处理任务。",
  "composer.banner.wokeDismiss": "关闭任务唤醒提示",
  "composer.banner.snoozedTitle": "此任务正在稍后处理",
  "composer.banner.snoozedDescription": "发送消息会唤醒任务，并将其移回侧栏的活跃列表。",
  "composer.banner.waking": "正在唤醒...",
  "composer.banner.wakeNow": "立即唤醒",
  "composer.banner.settledTitle": "此任务已收起",
  "composer.banner.settledDescription": "发送消息会将任务移回侧栏的活跃列表。",
  "composer.banner.unsettling": "正在恢复...",
  "composer.banner.unsettle": "恢复任务",
  "composer.banner.showOtherNotices": "显示其他通知",
  "composer.banner.dismissWarning": "关闭警告",
  "composer.tasks.title": "任务清单",
  "composer.tasks.openLabel": "任务清单：已完成 {completed}/{total}。当前任务：{task}",
  "composer.tasks.collapseLabel": "收起任务清单：已完成 {completed}/{total}。当前任务：{task}",
  "composer.tasks.listLabel": "任务清单。已完成 {completed}/{total}。",
  "composer.tasks.now": "当前",
  "composer.stash.title": "暂存",
  "composer.stash.badgeLabel": "已暂存 {count} 条内容。打开暂存列表。",
  "composer.stash.close": "关闭暂存列表",
  "composer.stash.listLabel": "已暂存内容",
  "composer.stash.empty": "暂存列表为空。",
  "composer.stash.emptyHint": " 在输入区有内容时按 {shortcut} 即可暂存。",
  "composer.stash.restore": "恢复暂存内容：{prompt}",
  "composer.stash.delete": "删除暂存内容",
  "composer.stash.entry.empty": "（空内容）",
  "composer.stash.entry.kind.attachment": "个附件",
  "composer.stash.entry.kind.file": "个文件",
  "composer.stash.entry.kind.image": "张图片",
  "composer.stash.entry.attachmentCount.one": "（{count}{kind}）",
  "composer.stash.entry.attachmentCount.many": "（{count}{kind}）",
  "composer.stash.savingImages.one": "正在保存 {count} 张图片…",
  "composer.stash.savingImages.many": "正在保存 {count} 张图片…",
  "composer.stash.droppedImages.one": "已丢弃 {count} 张图片",
  "composer.stash.droppedImages.many": "已丢弃 {count} 张图片",
  "composer.stash.toast.otherEnvironmentTitle": "暂存文件属于另一个环境",
  "composer.stash.toast.otherEnvironmentDescription": "请在接收这些文件的环境中恢复此内容。",
  "composer.stash.toast.restoreNotDurableTitle": "已恢复的内容可能再次出现在暂存列表中",
  "composer.stash.toast.restoreNotDurableDescription":
    "浏览器存储拒绝了此次更新，重新加载后该内容可能仍会出现。",
  "composer.stash.toast.tooLarge": "保存此内容时，{files} 超出了暂存大小限制。",
  "composer.stash.toast.unreadable": "保存此内容时无法读取 {files}。",
  "composer.stash.toast.atLimit": "无法恢复 {files}：输入区最多只能添加 {count} 个附件。",
  "composer.stash.toast.expired": "{files}：暂存文件仅保留 24 小时，此上传已过期。请重新添加文件。",
  "composer.stash.toast.notRestoredTitle": "部分附件未能恢复",
  "composer.stash.toast.deleteNotDurableTitle": "暂存内容可能再次出现",
  "composer.stash.toast.deleteNotDurableDescription":
    "浏览器存储拒绝了删除操作，重新加载后该内容可能再次出现。",
  "composer.stash.toast.reattachBeforeStash": "暂存前请重新添加已中断的文件，或将其移除",
  "composer.stash.toast.waitForUploads": "请等待文件上传完成后再暂存",
  "composer.stash.toast.saveFailedTitle": "无法暂存此内容",
  "composer.stash.toast.saveFailedDescription":
    "浏览器存储拒绝写入，输入区内容已保留。请清理站点数据后重试。",
  "composer.stash.toast.sessionOnlyTitle": "暂存内容将在重新加载后丢失",
  "composer.stash.toast.sessionOnlyDescription":
    "浏览器存储不可用，此内容仅在当前会话的内存中保留。",
  "composer.stash.toast.evictedTitle": "已丢弃最早的暂存内容",
  "composer.stash.toast.evictedDescription":
    "暂存列表最多保留 {count} 条内容，已移除最早的一条以腾出空间。",
  "composer.stash.toast.imagesNotSavedTitle": "暂存图片未能保存",
  "composer.stash.toast.imagesNotSavedDescription":
    "内容已暂存，但浏览器存储拒绝保存其中的图片；重新加载后图片会缺失。",
  "composer.stash.toast.imagesDidNotAttachTitle": "暂存图片未能附加",
  "composer.stash.toast.imagesDidNotAttachDescription.one":
    "图片保存完成前，该内容已被恢复或删除。如仍需要这 {count} 张图片，请重新添加。",
  "composer.stash.toast.imagesDidNotAttachDescription.many":
    "图片保存完成前，该内容已被恢复或删除。如仍需要这 {count} 张图片，请重新添加。",
  "composer.compaction.sendOrClearDraft": "请先发送或清空草稿，再压缩上下文",
  "composer.compaction.chooseProject": "请先选择项目，再压缩上下文",
  "composer.compaction.enableClaude": "请先启用 Claude 服务提供方，再压缩上下文",
  "composer.compaction.unavailable": "目前无法压缩上下文",
  "composer.compaction.resumeTitle": "精简上下文后继续",
  "composer.compaction.resumeDescription": "来自较早会话的 {tokens} 个令牌",
  "composer.compaction.keepFullHistory": "保留完整历史",
  "composer.compaction.compact": "压缩",
  "composer.compaction.compactContext": "压缩上下文",
  "composer.compaction.native.header": "继续会话",
  "composer.compaction.native.question":
    "此会话已有 {age}，当前使用 {tokens} 个令牌。是否先压缩再继续？",
  "composer.compaction.native.ageMinutes": "{minutes} 分钟",
  "composer.compaction.native.ageHoursMinutes": "{hours} 小时 {minutes} 分钟",
  "composer.compaction.native.compactAndContinue": "压缩并继续",
  "composer.compaction.native.compactDescription": "生成摘要后继续，以减少令牌用量。",
  "composer.compaction.native.keepDescription": "保留对话不变并继续。",
  "composer.compaction.native.dontAskAgain": "不再询问",
  "composer.compaction.native.dontAskDescription": "保留完整历史，并跳过以后继续会话时的提示。",
  "composer.imageCompression.inProgress": "正在压缩粘贴的图片。",
  "composer.imageCompression.sendAgain": "缩略图出现后请再次发送。",
  "composer.imageCompression.compactAgain": "缩略图出现后请再次压缩上下文。",
  "composer.image.unsupportedFile": "“{file}”的文件类型不受支持，请仅添加图片文件。",
  "composer.image.unsupportedType":
    "“{file}”不是支持的图片格式，请添加 GIF、HEIC、HEIF、JPEG、PNG 或 WebP 图片。",
  "composer.image.limit": "每条消息最多可以添加 {count} 张图片。",
  "composer.image.unreadable": "无法将“{file}”读取为图片。",
  "composer.image.tooLarge": "“{file}”即使压缩后仍然过大，无法添加。",
  "composer.thread.settleFailed": "收起任务失败",
  "composer.thread.unsettleFailed": "恢复任务失败",
  "composer.approval.commandRequested": "命令需要批准",
  "composer.approval.fileReadRequested": "读取文件需要批准",
  "composer.approval.fileChangeRequested": "更改文件需要批准",
  "composer.approval.appAccessLabel": "应用访问批准",
  "composer.approval.appAccessRequest": "应用访问请求",
  "composer.approval.commandLabel": "命令批准",
  "composer.approval.fileReadLabel": "文件读取批准",
  "composer.approval.fileChangeLabel": "文件更改批准",
  "composer.approval.command": "命令",
  "composer.approval.fileToRead": "要读取的文件",
  "composer.approval.fileChange": "文件更改",
  "composer.approval.pending": "等待批准",
  "composer.approval.cancelTurn": "取消本轮",
  "composer.approval.decline": "拒绝",
  "composer.approval.allowSession": "本次会话始终允许",
  "composer.approval.approveOnce": "仅允许一次",
  "composer.selectMultiple": "请选择一个或多个选项。",
  "composer.question.show": "显示问题及选项",
  "composer.question.hide": "隐藏问题及选项",
  "composer.question.dismiss": "不回答并关闭问题",
  "composer.attachmentWarningLabel": "草稿附件可能无法保留",
  "composer.attachmentWarningDescription": "草稿附件未能保存到本地，离开当前页面后可能丢失。",
  "composer.retryAttachmentUpload": "重新上传 {name}",
  "composer.attachmentUpload.retryOrRemoveOne": "请重试或移除上传失败的图片",
  "composer.attachmentUpload.retryOrRemoveMany": "请重试或移除上传失败的图片",
  "composer.attachmentUpload.uploadingOne": "图片仍在上传",
  "composer.attachmentUpload.uploadingMany": "图片仍在上传",
  "composer.attachmentUpload.resolveBeforeSending": "请先重试或移除上传失败的图片，再发送消息。",
  "composer.attachment.reattachOne": "请重新添加中断的文件，或将其移除",
  "composer.attachment.reattachMany": "请重新添加中断的文件，或将其移除",
  "composer.attachment.attachFiles": "添加文件",
  "composer.attachment.remove": "移除 {name}",
  "composer.attachment.play": "播放 {name}",
  "composer.attachment.loading": "正在加载 {name}",
  "composer.attachment.attachAfterQuestions": "请先回答方案问题，再添加文件。",
  "composer.attachment.retryOrRemoveBeforeSending": "请先重试或移除上传失败的附件，再发送消息。",
  "composer.attachment.fileLimit": "每条消息最多可添加 {count} 个文件。",
  "composer.attachment.serverUnsupported": "此服务器暂不支持文件附件。",
  "composer.attachment.emptyOrUnreadable": "“{file}”为空或无法读取。",
  "composer.noProviderAvailable": "没有可用的服务提供方",
  "composer.planReady": "方案已就绪",
  "composer.placeholder.approval": "请先处理此批准请求",
  "composer.placeholder.customAnswer": "输入自定义回答，留空则使用已选选项",
  "composer.placeholder.refinePlan": "补充反馈以完善方案，留空则直接实施",
  "composer.placeholder.chooseProject": "请先在上方选择项目",
  "composer.placeholder.enableProvider": "请在设置中启用服务提供方后再发送消息",
  "composer.placeholder.collapsedEnableProvider": "请先在设置中启用服务提供方",
  "composer.placeholder.disconnected": "提出修改、发送后续消息或添加图片",
  "composer.placeholder.default": "输入任何问题，@ 引用文件或文件夹，$ 使用技能，/ 使用命令",
  "composer.placeholder.collapsedDefault": "输入任何问题...",

  "feedback.startThreadTitle": "请先启动 Codex 任务",
  "feedback.startThreadDescription": "发送一条消息后才能提交反馈。",
  "feedback.sendFailed": "无法向 OpenAI 发送反馈",
  "feedback.sent": "反馈已发送给 OpenAI",
  "feedback.threadId": "任务 ID：{id}",
  "feedback.copyId": "复制 ID",
  "feedback.clipboardTarget": "Codex 反馈任务 ID",
  "feedback.copyFailed": "无法复制任务 ID",
  "feedback.dismiss": "关闭反馈提示",
  "feedback.sending": "正在向 OpenAI 发送反馈...",
  "feedback.sentMessage": "反馈已发送给 OpenAI。\n\n任务 ID：`{id}`",
  "feedback.failedMessage": "无法向 OpenAI 发送反馈。\n\n{error}",
  "backgroundThread.started": "已在后台启动",
  "backgroundThread.open": "打开",
  "backgroundThread.startedWarning": "任务已在后台启动",
  "backgroundThread.openFreshComposerFailed": "无法打开新的输入区。",
  "backgroundThread.openFreshComposerFailedWithReason": "无法打开新的输入区：{reason}",

  "chat.imageUnavailable": "图片不可用",
  "chat.imageUnavailableWithAlt": "图片不可用 · {alt}",
  "chat.imageLoading": "正在加载图片",
  "chat.activity.thinking": "思考中",

  "terminal.splitHorizontal": "水平拆分终端",
  "terminal.splitHorizontalMax": "水平拆分终端（每组最多 {max} 个）",
  "terminal.splitVertical": "垂直拆分终端",
  "terminal.splitVerticalMax": "垂直拆分终端（每组最多 {max} 个）",
  "terminal.new": "新建终端",
  "terminal.close": "关闭终端",
  "terminal.empty": "此任务还没有终端会话。",
  "terminal.group.single": "单终端",
  "terminal.group.stacked": "上下排列",
  "terminal.group.sideBySide": "并排显示",
  "terminal.defaultLabel": "终端",
  "terminal.closeNamed": "关闭 {name}",

  "draftHero.changeProject": "更改项目",
  "draftHero.chooseProject": "选择项目",
  "draftHero.newProject": "新建项目",
  "draftHero.addProject": "添加项目",
  "draftHero.question": "想在 {project} 中构建什么？",
  "draftHero.chooseProjectToStart": "选择 {project} 开始",
  "draftHero.addProjectToStart": "添加项目后开始",

  "emptyThread.title": "当前没有任务",
  "emptyThread.heading": "选择一个任务继续",
  "emptyThread.description": "选择现有任务或新建任务即可开始。",
  "home.startFailed": "无法新建任务",
  "home.startFailedDescription": "项目仍然可用，请重新打开草稿。",
  "home.heroTitle": "我们要做什么？",
  "home.heroDescription": "添加项目以开始第一个任务。",
  "home.addProject": "添加项目",
  "home.connectEnvironment": "连接环境以开始使用",
  "home.connectCloudDescription":
    "登录 T3 Connect，通过托管隧道连接已关联的环境，或手动添加可访问的后端。",
  "home.connectManualDescription": "手动添加可访问的后端，即可在此浏览器中开始工作。",
  "home.openConnections": "打开连接设置",
  "home.addEnvironment": "添加环境",
  "home.connectComputer": "连接运行 T3 Code 的设备",
  "home.connectComputerDescription":
    "此浏览器会连接到电脑或服务器上运行的 T3 Code。请在该设备上启动 T3 Code 桌面客户端或命令行服务器，并保持其运行。",
  "home.connectComputerCloud":
    "请在该设备上启用 T3 Connect，再在此处打开“连接”并使用同一账号登录；也可以使用配对链接添加设备。",
  "home.connectComputerManual":
    "请打开“连接”，使用该设备的配对链接进行添加。此浏览器必须能够访问该设备。",

  "commandPalette.dialog.command": "命令面板",
  "commandPalette.dialog.files": "文件选择器",
  "commandPalette.dialog.content": "搜索项目内容",
  "commandPalette.search": "搜索...",
  "commandPalette.search.root": "搜索命令、项目和任务...",
  "commandPalette.search.rootBrowse": "输入项目路径（例如 ~/projects/my-app）",
  "commandPalette.search.submenu": "搜索...",
  "commandPalette.search.submenuBrowse": "输入路径（例如 ~/projects/my-app）",
  "commandPalette.navigate": "导航",
  "commandPalette.select": "选择",
  "commandPalette.group.sources": "来源",
  "commandPalette.group.repository": "仓库",
  "commandPalette.source.localFolder": "本地文件夹",
  "commandPalette.source.localFolderDescription": "浏览磁盘上的文件夹",
  "commandPalette.source.gitUrl": "Git URL",
  "commandPalette.source.repository": "{provider} 仓库",
  "commandPalette.source.cloneUrl": "从远程 URL 克隆",
  "commandPalette.source.cloneRepository": "克隆 {provider} {path}",
  "commandPalette.source.setupRequired": "需要设置",
  "commandPalette.source.configure": "打开“设置 -> 源代码管理”以配置此服务提供方。",
  "commandPalette.empty.enterGitUrl": "输入 Git 克隆 URL，然后按 Enter 继续。",
  "commandPalette.empty.enterRepository": "输入仓库路径，然后按 Enter 查找。",
  "commandPalette.empty.chooseDestination": "选择目标路径，然后按 Enter 克隆。",
  "commandPalette.empty.relativePath": "相对路径需要一个当前项目。",
  "commandPalette.empty.createFolder": "按 Enter 创建此文件夹并将其添加为项目。",
  "commandPalette.empty.searchingThreads": "正在搜索任务消息...",
  "commandPalette.action.lookup": "查找",
  "commandPalette.action.working": "处理中",
  "commandPalette.action.cloning": "正在克隆",
  "commandPalette.action.clone": "克隆",
  "commandPalette.action.createAndClone": "创建并克隆",
  "commandPalette.action.createAndAdd": "创建并添加",
  "commandPalette.action.add": "添加",
  "commandPalette.action.openIn": "在 {fileManager} 中打开",
  "commandPalette.group.cloneDestination": "选择克隆位置",

  "pullRequests.title": "拉取请求",
  "pullRequests.breadcrumb": "拉取请求导航",
  "pullRequests.scope": "拉取请求范围",
  "pullRequests.search.placeholder": "搜索拉取请求，或输入 label:bug",
  "pullRequests.search.label": "搜索拉取请求",
  "pullRequests.refresh": "刷新拉取请求",
  "pullRequests.filter.label": "筛选拉取请求",
  "pullRequests.filter.state": "状态",
  "pullRequests.filter.involvement": "参与情况",
  "pullRequests.filter.draft": "草稿",
  "pullRequests.filter.review": "评审",
  "pullRequests.filter.checks": "检查",
  "pullRequests.filter.host": "托管服务",
  "pullRequests.filter.server": "服务器",
  "pullRequests.filter.project": "项目",
  "pullRequests.filter.all": "全部",
  "pullRequests.filter.reviewing": "需要我评审",
  "pullRequests.filter.authored": "由我创建",
  "pullRequests.filter.open": "开放",
  "pullRequests.filter.closed": "已关闭",
  "pullRequests.filter.merged": "已合并",
  "pullRequests.filter.draftsOnly": "仅草稿",
  "pullRequests.filter.hideDrafts": "隐藏草稿",
  "pullRequests.filter.approved": "已批准",
  "pullRequests.filter.changesRequested": "要求修改",
  "pullRequests.filter.reviewRequired": "需要评审",
  "pullRequests.filter.noReviews": "无评审",
  "pullRequests.filter.passing": "通过",
  "pullRequests.filter.failing": "失败",
  "pullRequests.filter.allProjects": "全部项目",
  "pullRequests.filter.allHosts": "全部托管服务",
  "pullRequests.filter.allServers": "全部服务器",
  "pullRequests.filter.byState": "按状态筛选",
  "pullRequests.filter.byInvolvement": "按参与情况筛选",
  "pullRequests.filter.byHost": "按托管服务筛选",
  "pullRequests.filter.unavailable": "不可用",
  "pullRequests.filter.trigger": "筛选",
  "pullRequests.filter.author": "作者",
  "pullRequests.filter.anyone": "任何人",
  "pullRequests.filter.searchAuthors": "搜索作者",
  "pullRequests.filter.noAuthors": "未找到作者",
  "pullRequests.filter.mergesLoaded": "已加载 {count} 次合并",
  "pullRequests.filter.labels": "标签",
  "pullRequests.filter.any": "任意",
  "pullRequests.filter.selected": "已选择 {count} 项",
  "pullRequests.filter.noLabels": "当前视图中没有标签",
  "pullRequests.empty.noProjects": "此工作区中还没有项目",
  "pullRequests.empty.noProjectsDescription": "添加项目后，其仓库中的拉取请求会显示在这里。",
  "pullRequests.empty.searching": "正在所有托管服务中搜索“{query}”",
  "pullRequests.empty.noMatches": "没有与“{query}”匹配的结果",
  "pullRequests.empty.noMatchesDescription":
    "已搜索所有托管服务。请尝试减少关键词，或按编号、作者、分支搜索。",
  "pullRequests.empty.clearSearch": "清除搜索",
  "pullRequests.empty.checking": "正在检查...",
  "pullRequests.empty.checkAgain": "再次检查",
  "pullRequests.empty.filtered": "当前筛选条件下没有结果",
  "pullRequests.empty.none": "暂无拉取请求",
  "pullRequests.empty.filteredDescription": "放宽状态、参与情况或项目筛选条件以查看更多结果。",
  "pullRequests.empty.noneDescription": "此工作区所有项目的拉取请求都会显示在这里。",
  "pullRequests.empty.loading": "正在加载...",
  "pullRequests.empty.loadMore": "加载更多拉取请求",
  "pullRequests.unavailable.title": "拉取请求不可用",
  "pullRequests.unavailable.upgrade": "请更新 T3 Code 服务器后再浏览拉取请求。",
  "pullRequests.unavailable.host": "无法读取此托管服务。",
  "pullRequests.latestFailed": "最近一次请求失败，当前显示上次已加载的拉取请求。",
  "pullRequests.latestLoaded": "当前显示上次已加载的拉取请求。",
  "pullRequests.updating": "正在更新拉取请求",
  "pullRequests.narrowSearch": "请缩小搜索范围以查找更多拉取请求。",
  "pullRequests.loadingMore": "正在加载更多",
  "pullRequests.panel.selectFirst": "请先选择一个拉取请求",
  "pullRequests.unavailable.loadFailed": "无法加载拉取请求",
  "pullRequests.unavailable.retry": "重试",
  "pullRequests.openOnGitHub": "在 GitHub 上打开",
  "pullRequests.activity.loadFailed": "无法加载拉取请求动态",
  "pullRequests.labels.removeFailed": "无法移除标签 {label}",
  "pullRequests.labels.addFailed": "无法添加标签 {label}",
  "pullRequests.labels.permissionFailure": "托管服务拒绝了操作。请确认你拥有此仓库的分类权限。",
  "pullRequests.labels.change": "更改标签",
  "pullRequests.labels.needsTriage": "更改标签需要此仓库的分类权限",
  "pullRequests.labels.search": "搜索标签",
  "pullRequests.labels.empty": "此仓库没有标签。",
  "pullRequests.labels.noMatch": "没有匹配的标签。",
  "pullRequests.labels.readFailed": "无法读取标签。",
  "pullRequests.labels.truncated": "此处未列出仓库的全部标签，请在托管服务中应用其余标签。",
  "pullRequests.labels.applied": "已应用",
  "pullRequests.labels.title": "标签",
  "pullRequests.reviewers.withdrawFailed": "无法撤回向 {reviewer} 发出的评审请求",
  "pullRequests.reviewers.requestFailed": "无法请求 {reviewer} 进行评审",
  "pullRequests.reviewers.permissionFailure":
    "托管服务拒绝了操作。请确认你拥有此仓库的写入权限，并且对方仍可访问该仓库。",
  "pullRequests.reviewers.withdrawn": "已撤回向 {reviewer} 发出的评审请求",
  "pullRequests.reviewers.requested": "已请求 {reviewer} 进行评审",
  "pullRequests.reviewers.request": "请求评审",
  "pullRequests.reviewers.needsWrite": "请求他人评审需要此仓库的写入权限",
  "pullRequests.reviewers.search": "搜索有权访问的人员",
  "pullRequests.reviewers.empty": "没有其他人可以访问此仓库。",
  "pullRequests.reviewers.noMatch": "没有匹配的可访问人员。",
  "pullRequests.reviewers.readFailed": "无法读取有权访问的人员。",
  "pullRequests.reviewers.truncated": "此处未列出所有有权访问的人员，请在托管服务中选择其余人员。",
  "pullRequests.reviewers.team": "团队",
  "pullRequests.reviewers.alreadyAsked": "已请求",
  "pullRequests.reviewers.title": "评审者",
  "pullRequests.comments.title": "评论",
  "pullRequests.comments.edit": "编辑评论",
  "pullRequests.comments.postFailed": "无法发布评论",
  "pullRequests.comments.saveFailed": "无法保存评论",
  "pullRequests.comments.leave": "发表评论",
  "pullRequests.comments.onPullRequest": "评论此拉取请求",
  "pullRequests.comments.closeWithComment": "评论并关闭",
  "pullRequests.comments.reopenWithComment": "评论并重新打开",
  "pullRequests.comments.posting": "正在发布...",
  "pullRequests.comments.comment": "评论",
  "pullRequests.comments.showOldestOne": "显示最早的 {count} 条评论",
  "pullRequests.comments.showOldestMany": "显示最早的 {count} 条评论",
  "pullRequests.comments.loading": "正在加载对话…",
  "pullRequests.comments.unavailable": "对话不可用",
  "pullRequests.comments.countOne": "{count} 条评论",
  "pullRequests.comments.countMany": "{count} 条评论",
  "pullRequests.comments.showOldestFirst": "最早评论优先",
  "pullRequests.comments.showNewestFirst": "最新评论优先",
  "pullRequests.comments.newestFirst": "最新优先",
  "pullRequests.comments.oldestFirst": "最早优先",
  "pullRequests.comments.truncated":
    "此对话内容较长，当前显示最近 {count} 条；请在托管服务中查看其余内容。",
  "pullRequests.comments.empty": "暂无评论。",
  "pullRequests.comments.resolved": "已解决",
  "pullRequests.comments.approvalDismissed": "批准已撤销",
  "pullRequests.description.title": "说明",
  "pullRequests.description.label": "拉取请求说明",
  "pullRequests.description.placeholder": "描述此拉取请求",
  "pullRequests.description.emptyMarkdown": "_未提供说明。_",
  "pullRequests.description.edit": "编辑说明",
  "pullRequests.description.saveFailed": "无法保存说明",
  "pullRequests.checks.title": "检查",
  "pullRequests.checks.none": "未报告检查结果。",
  "pullRequests.checks.openFailed": "无法打开检查详情",
  "pullRequests.actions.closing": "正在关闭...",
  "pullRequests.actions.reopening": "正在重新打开...",
  "pullRequests.actions.preparing": "正在准备...",
  "pullRequests.review.approved": "已批准",
  "pullRequests.review.changesRequested": "已要求修改",
  "pullRequests.review.dismissed": "评审已撤销",
  "pullRequests.review.approvedEarlier": "曾批准较早的更改",
  "pullRequests.review.changesRequestedEarlier": "曾要求修改较早的更改",
  "pullRequests.review.dismissedEarlier": "较早的评审已撤销",
  "pullRequests.state.merged": "已合并",
  "pullRequests.state.closed": "已关闭",
  "pullRequests.state.draft": "草稿",
  "pullRequests.state.conflictsWith": "与 {branch} 冲突",
  "pullRequests.state.conflicts": "存在冲突",
  "pullRequests.state.open": "开放",
  "pullRequests.checks.status.running": "运行中",
  "pullRequests.checks.status.awaitingAction": "等待操作",
  "pullRequests.checks.status.awaitingApproval": "等待批准",
  "pullRequests.checks.status.passed": "已通过",
  "pullRequests.checks.status.failed": "失败",
  "pullRequests.checks.status.cancelled": "已取消",
  "pullRequests.checks.status.skipped": "已跳过",
  "pullRequests.checks.status.neutral": "中立",
  "pullRequests.checks.allPassed": "所有检查均已通过",
  "pullRequests.checks.someFailed": "部分检查未成功",
  "pullRequests.checks.somePending": "部分检查尚未完成",
  "pullRequests.checks.nonePlain": "未报告检查结果",
  "pullRequests.checks.failingCount": "{total} 项中有 {failed} 项失败",
  "pullRequests.checks.workflowsAndChecksAwaiting":
    "{workflows} 个工作流和 {checks} 项检查等待操作",
  "pullRequests.checks.workflowsAwaitingApproval": "{count} 个工作流等待批准",
  "pullRequests.checks.checksAwaitingAction": "{count} 项检查等待操作",
  "pullRequests.checks.runningCount": "{total} 项中有 {pending} 项运行中",
  "pullRequests.checks.allPassedShort": "所有检查均已通过",
  "pullRequests.checks.passingCount": "{total} 项中有 {passed} 项通过",
  "pullRequests.checks.loading": "正在加载检查…",
  "pullRequests.checks.details": "详情",
  "pullRequests.checks.aria": "检查：{status}",
  "pullRequests.detail.titleSaveFailed": "无法保存标题",
  "pullRequests.detail.titleRefused": "托管服务拒绝了新标题。",
  "pullRequests.detail.openRepository": "打开 {repository} 仓库",
  "pullRequests.detail.openOnHost": "在托管服务中打开拉取请求 #{number}",
  "pullRequests.detail.stacked": "堆叠式拉取请求",
  "pullRequests.detail.receivesChangesFrom": "接收来自以下分支的更改",
  "pullRequests.detail.changedFiles": "更改了 {count} 个文件",
  "pullRequests.detail.editTitle": "编辑标题",
  "pullRequests.detail.title": "拉取请求标题",
  "pullRequests.detail.updated": "更新于 {time}",
  "pullRequests.detail.copyCheckoutCommand": "复制检出命令",
  "pullRequests.detail.checkoutCommandCopied": "检出命令已复制",
  "pullRequests.detail.copyCheckoutCommandFailed": "无法复制检出命令",
  "pullRequests.detail.copyBranch": "复制拉取请求分支",
  "pullRequests.detail.branchCopied": "分支名称已复制",
  "pullRequests.detail.files": "个文件",
  "pullRequests.detail.tabs": "拉取请求标签页",
  "pullRequests.detail.collapsePanel": "收起拉取请求面板",
  "pullRequests.branch.outOfDate": "此分支落后于 {branch}。",
  "pullRequests.branch.behind": "此分支落后 {branch} {count} 个提交。",
  "pullRequests.branch.cleanMerge": "更改可以干净地合并。",
  "pullRequests.branch.updateRebase": "通过变基更新",
  "pullRequests.branch.update": "更新分支",
  "pullRequests.checkout.action": "检出",
  "pullRequests.checkout.checkingOut": "正在检出...",
  "pullRequests.checkout.separateWorktree": "在独立工作树中",
  "pullRequests.checkout.separateWorktreeDescription":
    "使用独立文件夹和任务，不会移动当前打开的内容。",
  "pullRequests.checkout.inRepository": "在此仓库中",
  "pullRequests.checkout.inRepositoryDescription": "像 `gh pr checkout` 一样切换当前工作分支。",
  "pullRequests.autoMerge.label": "自动合并",
  "pullRequests.autoMerge.method": "自动合并（{method}）",
  "pullRequests.autoMerge.hostWillMerge": "{label}：满足要求后，托管服务将自动完成合并",
  "pullRequests.autoMerge.enable": "启用自动合并",
  "pullRequests.autoMerge.disable": "停用自动合并",
  "pullRequests.actions.resolveConflicts": "解决冲突",
  "pullRequests.actions.readyForReview": "标记为可供评审",
  "pullRequests.actions.convertToDraft": "转换为草稿",
  "pullRequests.actions.enabling": "正在启用...",
  "pullRequests.actions.merging": "正在合并...",
  "pullRequests.actions.approving": "正在批准...",
  "pullRequests.actions.approveWorkflows": "批准工作流运行",
  "pullRequests.actions.more": "更多拉取请求操作",
  "pullRequests.actions.opening": "正在打开...",
  "pullRequests.actions.askQuestion": "提问",
  "pullRequests.actions.explain": "解释此 PR",
  "pullRequests.actions.explainDescription": "逐步讲解差异以及需要重点阅读的内容。",
  "pullRequests.actions.mergeNow": "立即合并",
  "pullRequests.actions.close": "关闭拉取请求",
  "pullRequests.actions.closeShort": "关闭",
  "pullRequests.actions.reopen": "重新打开拉取请求",
  "pullRequests.actions.revert": "还原更改",
  "pullRequests.handoff.addedToComposer": "已添加到输入区",
  "pullRequests.handoff.questionInComposer": "问题已加入输入区，请检查后发送。",
  "pullRequests.handoff.requestInComposer": "拉取请求已加入输入区，请输入问题后发送。",
  "pullRequests.handoff.openThreadFailed": "无法打开任务",
  "pullRequests.handoff.openThreadFailedDescription": "请从项目中重试，或先打开一个任务。",
  "pullRequests.handoff.askedInThread": "已在任务中提问",
  "pullRequests.handoff.taskInComposer": "任务已加入输入区，请检查后发送。",
  "pullRequests.handoff.preparingCheckout": "正在准备拉取请求检出目录...",
  "pullRequests.handoff.openCheckoutThreadFailed": "无法为检出目录打开任务",
  "pullRequests.handoff.prepareCheckoutFailed": "无法准备拉取请求检出目录",
  "pullRequests.handoff.threadNotMoved": "检出完成，但任务未切换过去",
  "pullRequests.handoff.threadNotMovedDescription":
    "检出目录已在 `{branch}` 上准备好。请从分支选择器将任务切换过去，然后重试。",
  "pullRequests.handoff.checkoutStale": "已检出，但不是最新提交",
  "pullRequests.handoff.checkoutStaleDescription":
    "检出目录无法移动到拉取请求的最新提交，因此其中代码早于该拉取请求。本地未提交更改或本地提交会使它保持原位。",
  "pullRequests.handoff.checkedOutHere": "已在此处检出",
  "pullRequests.handoff.checkedOut": "已检出",
  "pullRequests.handoff.localReadyDescription": "此仓库已切换到拉取请求分支，并打开了一个任务。",
  "pullRequests.handoff.worktreeReadyDescription": "拉取请求已进入独立工作树，并打开了一个任务。",
  "pullRequests.handoff.checkoutReady": "检出目录已就绪",
  "pullRequests.handoff.addToCurrentComposer": "将拉取请求添加到当前任务的输入区。",
  "pullRequests.handoff.openAwareThread": "打开一个已关联此拉取请求的任务。",
  "pullRequests.commits.unavailable": "提交不可用",
  "pullRequests.commits.count": "{count} 个提交",
  "pullRequests.review.approvalCount": "{count} 个批准",
  "pullRequests.timeline.showOldestFirst": "最早动态优先",
  "pullRequests.timeline.showNewestFirst": "最新动态优先",
  "pullRequests.code.loadingDiff": "正在加载拉取请求差异...",
  "pullRequests.code.remainderLoadFailed": "无法加载此差异的剩余内容。",
  "pullRequests.code.loadingMoreFiles": "正在加载更多文件...",
  "pullRequests.code.loadMoreFiles": "加载更多文件",
  "pullRequests.code.expandDiff": "展开差异",
  "pullRequests.code.collapseDiff": "折叠差异",
  "pullRequests.code.fixInThread": "在任务中修复",
  "pullRequests.code.addToReview": "添加到评审",
  "pullRequests.code.addToAgent": "添加给智能体",
  "pullRequests.code.closeReview": "关闭评审",
  "pullRequests.code.review": "评审",
  "pullRequests.code.allCommits": "所有提交",
  "pullRequests.code.diffScope": "差异范围：{scope}",
  "pullRequests.code.showMoreCommits": "显示更多（剩余 {count} 个）",
  "pullRequests.code.fileCountOne": "{count} 个文件",
  "pullRequests.code.fileCountMany": "{count} 个文件",
  "pullRequests.code.diffPartiallyHidden": "部分差异未显示",
  "pullRequests.code.diffPartiallyHiddenDescription":
    "托管服务省略了部分差异，可能是二进制文件，也可能是更改过大而无法内嵌显示。",
  "pullRequests.code.commentsUseWholeChange": "行评论将基于整个更改创建",
  "pullRequests.code.commentsUseWholeChangeDescription":
    "评论会锚定到整个更改。若要创建评论，请切换到“所有提交”。",
  "pullRequests.code.expandAllFiles": "展开所有文件",
  "pullRequests.code.collapseAllFiles": "折叠所有文件",
  "pullRequests.code.stackedDiffView": "堆叠差异视图",
  "pullRequests.code.diffLayout": "差异布局",
  "pullRequests.code.splitDiffView": "并排差异视图",
  "pullRequests.code.disableDiffWrapping": "关闭差异行换行",
  "pullRequests.code.enableDiffWrapping": "开启差异行换行",
  "pullRequests.code.disableWrapping": "关闭自动换行",
  "pullRequests.code.enableWrapping": "开启自动换行",
  "pullRequests.code.hideFileTree": "隐藏文件树",
  "pullRequests.code.showFileTree": "显示文件树",
  "pullRequests.code.noPullRequestChanges": "此拉取请求没有文件更改。",
  "pullRequests.code.noCommitChanges": "此提交没有文件更改。",
  "pullRequests.code.orphanConversationsCurrent": "不在当前差异中的对话",
  "pullRequests.code.orphanConversationsLoaded": "不在已加载差异中的对话",
  "pullRequests.code.conversationCountOne": "{count} 个对话",
  "pullRequests.code.conversationCountMany": "{count} 个对话",
  "pullRequests.code.line": "第 {line} 行",
  "pullRequests.code.fileTreeAria": "拉取请求 #{number} 的文件",
  "pullRequests.comments.loadMoreFailed": "无法加载更多评论",
  "pullRequests.comments.replyFailed": "无法发布回复",
  "pullRequests.comments.updateConversationFailed": "无法更新对话",
  "pullRequests.confirm.mergeTitle": "合并拉取请求？",
  "pullRequests.confirm.autoMergeTitle": "启用自动合并？",
  "pullRequests.confirm.revertTitle": "还原这些更改？",
  "pullRequests.confirm.approveTitle": "批准工作流运行？",
  "pullRequests.confirm.closeTitle": "关闭拉取请求？",
  "pullRequests.confirm.mergeDescription": "这会使用{method}合并 #{number}。",
  "pullRequests.confirm.autoMergeDescription":
    "托管服务认为 #{number} 已就绪时，会立即使用{method}合并；这可能马上发生。",
  "pullRequests.confirm.revertDescription": "这会新建一个拉取请求，以还原 #{number} 合并的更改。",
  "pullRequests.confirm.approveDescription":
    "这会允许 #{number} 中的 {count} 个工作流运行。请先检查代码和工作流更改。",
  "pullRequests.confirm.closeDescription": "这会关闭 #{number}，但不会合并。",
  "pullRequests.confirm.createRevert": "创建还原 PR",
  "pullRequests.confirm.approveAndRun": "批准并运行",
  "pullRequests.row.matchedDescription": "在说明中匹配",
  "pullRequests.reactions.saveFailed": "无法保存回应",
  "pullRequests.reactions.add": "添加回应",
  "pullRequests.review.pendingComment": "待提交，将在提交评审时发送",
  "pullRequests.review.discardComment": "放弃此评论",
  "pullRequests.review.unresolve": "标记为未解决",
  "pullRequests.review.resolve": "标记为已解决",
  "pullRequests.review.beforeLatest": "，早于最新提交",
  "pullRequests.comments.loadMore": "加载更多评论",
  "pullRequests.comments.reply": "回复",
  "pullRequests.comments.replyToConversation": "回复此对话",
  "pullRequests.timeline.openOnHost": "在托管服务中打开动态",
  "pullRequests.timeline.authorCount": "{count} 位作者",
  "pullRequests.timeline.viewCommit": "查看提交 {commit}",
  "pullRequests.timeline.untitledCommit": "无标题提交",
  "pullRequests.timeline.opened": "拉取请求已创建",
  "pullRequests.timeline.merged": "拉取请求已合并",
  "pullRequests.timeline.closed": "拉取请求已关闭",
  "pullRequests.timeline.empty": "暂无动态。",
  "pullRequests.review.submitFailed": "无法提交评审",
  "pullRequests.review.submittedSuccess": "评审已提交",
  "pullRequests.review.approvedSuccess": "拉取请求已批准",
  "pullRequests.review.changesRequestedSuccess": "已要求修改",
  "pullRequests.review.noLineComments": "暂无行评论",
  "pullRequests.review.pendingCommentCountOne": "{count} 条评论待提交",
  "pullRequests.review.pendingCommentCountMany": "{count} 条评论待提交",
  "pullRequests.review.discard": "放弃",
  "pullRequests.review.summaryPlaceholder": "总结本次评审（可选）",
  "pullRequests.review.summaryAria": "评审总结",
  "pullRequests.review.approve": "批准",
  "pullRequests.review.requestChanges": "要求修改",
  "pullRequests.markdown.write": "编辑",
  "pullRequests.markdown.preview": "预览",
  "pullRequests.markdown.nothingToPreview": "没有可预览的内容。",
  "pullRequests.markdown.saving": "正在保存...",
  "pullRequests.markdown.playVideoOnHost": "在 GitHub 上播放视频",
  "pullRequests.markdown.openAttachmentOnHost": "在 GitHub 上打开附件",
  "pullRequests.loading.list": "正在加载拉取请求",
  "pullRequests.loading.detail": "正在加载拉取请求",
  "pullRequests.loading.people": "正在加载人员",
  "pullRequests.loading.timeline": "正在加载动态",
  "pullRequests.loading.conversation": "正在加载拉取请求对话",
  "files.copyPath": "复制文件路径",
  "files.breadcrumb.backTo": "返回 {path}",
  "files.breadcrumb.loadingFolder": "正在加载文件夹…",
  "files.breadcrumb.retryFolder": "重试加载文件夹",
  "files.breadcrumb.unavailableFolder": "此文件夹已不可用。",
  "files.breadcrumb.partialEmpty": "部分工作区索引中没有此文件夹的可用条目。",
  "files.breadcrumb.emptyFolder": "此文件夹为空。",
  "files.breadcrumb.refreshFailed": "刷新失败，点击重试",
  "files.breadcrumb.truncated": "部分工作区条目未显示。",
  "files.breadcrumb.browse": "浏览 {path}",
  "citation.viewText": "查看引用的智能体文本：{text}",
  "citation.viewSource": "查看来源",
  "citation.editComment": "编辑引用批注",
  "citation.addComment": "为引用添加批注",
  "citation.remove": "移除智能体引用",
  "citation.commentOnSelection": "批注所选文本",
  "citation.commentKeyboardHelp":
    "按 Enter 保存引用批注；按 Command/Ctrl+Enter 保存并发送；按 Shift+Enter 换行。",
  "citation.commentPlaceholder": "添加可选批注...",
  "citation.commentTooLong": "批注最多可包含 {count} 个字符。",
  "citation.shortenComment": "缩短批注",
  "media.videoUnavailable": "视频不可用",
  "media.retrying": "正在重试…",
  "media.retryVideo": "重试播放视频",
  "media.videoPreview": "视频预览",
  "media.loadingVideo": "正在加载视频",
  "sidebar.update.readyToDownload": "更新已可下载",
  "sidebar.update.viewRelease": "在 GitHub 上查看版本",
  "sidebar.update.moreChange": "GitHub 上还有 {count} 项更改",
  "sidebar.update.moreChanges": "GitHub 上还有 {count} 项更改",
  "sidebar.update.whatsChanged": "更新内容",
  "sidebar.update.changesIn": "{version} 中的更改",
  "sidebar.update.olderRelease": "在 GitHub 上查看更早的 {count} 个版本",
  "sidebar.update.olderReleases": "在 GitHub 上查看更早的 {count} 个版本",

  "usage.title": "用量",
  "usage.breadcrumb": "用量导航",
  "usage.range.separator": "至",
  "usage.window.past24h": "过去 24 小时",
  "usage.window.days": "{count} 天",
  "usage.refresh": "刷新用量",
  "usage.metric.label": "用量指标",
  "usage.period.label": "统计时段",
  "usage.metric.cost": "费用",
  "usage.metric.tokens": "令牌",
  "usage.metric.limits": "限额",
  "usage.limits.refresh": "刷新限额",
  "usage.limits.title": "用量限额",
  "usage.limits.dismiss": "关闭用量限额",
  "usage.limits.accounts": "{count} 个账号",
  "usage.limits.account": "账号 {account}",
  "usage.limits.plan": "套餐",
  "usage.limits.signedIn": "登录位置",
  "usage.limits.via": "来源",
  "usage.limits.left": "剩余",
  "usage.limits.resetsLabel": "重置时间",
  "usage.limits.restores": "恢复额度",
  "usage.limits.restoresPool": "恢复池额度的 {percent}%",
  "usage.limits.segment": "区段",
  "usage.rawTokenCost": "原始令牌费用",
  "usage.processedTokens": "已处理令牌",
  "usage.fullApiRate": "* 按完整 API 费率估算",
  "usage.sessionSummary": "{count} 个会话的输入、缓存读取和输出总量。",
  "usage.session": "{count} 个会话",
  "usage.sessions": "{count} 个会话",
  "usage.sessionEstimate": "{count} 个会话 · API 估算",
  "usage.sessionsEstimate": "{count} 个会话 · API 估算",
  "usage.provider.costShare": "占费用 {share} · {tokens} 令牌",
  "usage.provider.tokenShare": "占令牌 {share} · {cost}",
  "usage.hourlyCost": "每小时费用",
  "usage.dailyCost": "每日费用",
  "usage.hourlyTokens": "每小时已处理令牌",
  "usage.dailyTokens": "每日已处理令牌",
  "usage.chart.aria": "按服务提供方统计的{period}{metric}",
  "usage.period.hourly": "每小时",
  "usage.period.daily": "每日",
  "usage.period.hour": "小时",
  "usage.period.day": "天",
  "usage.metric.cachedInput": "缓存输入",
  "usage.metric.uncachedInput": "未缓存输入",
  "usage.metric.output": "输出",
  "usage.metric.cacheSavings": "缓存节省",
  "usage.metric.perActive": "每个活跃{period} {value}",
  "usage.metric.observedInput": "占已统计输入 {share}",
  "usage.metric.cacheWrites": "{count} 次缓存写入",
  "usage.metric.reasoning": "其中推理令牌 {count}",
  "usage.metric.rawCostMultiple": "相当于原始令牌费用的 {value} 倍",
  "usage.metric.fullInputRates": "与完整输入费率相比",
  "usage.breakdown": "明细",
  "usage.breakdown.label": "用量明细",
  "usage.totals": "总计",
  "usage.breakdown.model": "模型",
  "usage.breakdown.total": "总计",
  "usage.breakdown.cost": "费用",
  "usage.breakdown.share": "占比",
  "usage.breakdown.tokens": "令牌",
  "usage.noActivity": "此时间范围内没有活动。",
  "usage.coverage.failed": "{environment} 无法报告用量。",
  "usage.coverage.stale": "{environment} 的服务器版本较旧，未计入总量。",
  "usage.coverage.duplicates": "共享同一记录目录的环境只计一次：{sources}",
  "usage.scanning.one": "仍有 1 台设备正在扫描",
  "usage.scanning.many": "仍有 {count} 台设备正在扫描",
  "usage.limits.paceAhead": "用量进度偏快：消耗速度高于时间窗口进度",
  "usage.limits.paceOn": "用量进度与时间窗口一致",
  "usage.limits.paceUnder": "用量进度偏慢：当前窗口仍有余量",
  "usage.limits.used": "已使用 {percent}%",
  "usage.limits.remaining": "剩余 {percent}%",
  "usage.limits.windowRemaining": "此时间窗口剩余 {percent}%",
  "usage.limits.elapsed": "时间窗口已过去 {percent}%",
  "usage.limits.evenLine": "竖线表示均匀使用时应达到的位置。",
  "usage.limits.resetsAt": "重置时间：{time}",
  "usage.limits.resetsNow": "立即重置",
  "usage.limits.resetsIn": "{duration} 后重置",
  "usage.limits.resetApplied": "已完成重置，用量窗口已清零。",
  "usage.limits.nothingToReset": "当前无需重置。",
  "usage.limits.noCredit": "没有可用的重置额度。",
  "usage.limits.alreadyRedeemed": "该额度已兑换。",
  "usage.limits.noneBanked": "没有储备的重置额度",
  "usage.limits.creditBanked": "已储备 1 个重置额度",
  "usage.limits.creditsBanked": "已储备 {count} 个重置额度",
  "usage.limits.nextExpires": "下一个额度将在 {duration} 后过期",
  "usage.limits.useFailed": "无法使用重置额度。",
  "usage.limits.usingCredit": "正在使用额度…",
  "usage.limits.useReset": "使用重置额度",
  "usage.limits.expiresIn": "{duration} 后过期",
  "usage.limits.useResetCredit": "使用重置额度",
  "usage.limits.confirmTitle": "使用一个重置额度？",
  "usage.limits.confirmDescription": "这会兑换账号中的一个额度并清空当前速率限制窗口，且无法撤销。",
  "usage.limits.useCredit": "使用额度",
  "usage.limits.allAccountsShown": "所有账号均已通过已连接的服务提供方显示。",
  "usage.limits.noAccounts": "未报告任何账号。",
  "usage.limits.empty": "已连接环境中的服务提供方均未报告订阅限额。",

  "headerAction.addAction": "添加操作",
  "headerAction.open": "打开",
  "headerAction.openInEditor": "在编辑器中打开",
  "headerAction.openPreferredEditor": "在首选编辑器中打开文件",
  "headerAction.chooseEditor": "选择编辑器",
  "headerAction.options": "复制选项",
  "headerAction.noEditors": "未找到已安装的编辑器",
  "headerAction.noSshRoute": "没有通往 {environment} 的 SSH 路由",
  "headerAction.opensOverSsh": "将通过 SSH 打开，需要在 {environment} 上配置你的密钥",
  "gitAction.group": "Git 操作",
  "gitAction.options": "Git 操作选项",
  "gitAction.commit": "提交",
  "gitAction.commitPush": "提交并推送",
  "gitAction.commitPushCreateChangeRequest": "提交、推送并创建{request}",
  "gitAction.pull": "拉取",
  "gitAction.push": "推送",
  "gitAction.pushCreateChangeRequest": "推送并创建{request}",
  "gitAction.syncRef": "同步分支",
  "gitAction.createChangeRequest": "创建{request}",
  "gitAction.viewChangeRequest": "查看{request}",
  "gitAction.publishRepository": "发布仓库...",
  "gitAction.hint.actionInProgress": "Git 操作正在进行。",
  "gitAction.hint.branchDiverged": "分支已与上游分叉，请先变基或合并。",
  "gitAction.hint.branchRequired": "请先创建并签出分支，再推送或打开{request}。",
  "gitAction.hint.worktreeClean": "工作树没有更改，请先修改文件再提交。",
  "gitAction.hint.commitUnavailable": "当前无法提交。",
  "gitAction.hint.detachedBeforePush": "当前处于分离 HEAD 状态，请先签出分支再推送。",
  "gitAction.hint.localChangesBeforePush": "请先提交或暂存本地更改，再进行推送。",
  "gitAction.hint.behindBeforePush": "分支落后于上游，请先拉取或变基再推送。",
  "gitAction.hint.originBeforePush": "请先添加“origin”远程仓库再推送。",
  "gitAction.hint.noLocalCommits": "没有可推送的本地提交。",
  "gitAction.hint.pushUnavailable": "当前无法推送。",
  "gitAction.hint.viewUnavailable": "当前无法查看{request}。",
  "gitAction.hint.detachedBeforeCreate": "当前处于分离 HEAD 状态，请先签出分支再创建{request}。",
  "gitAction.hint.localChangesBeforeCreate": "请先提交本地更改，再创建{request}。",
  "gitAction.hint.originBeforeCreate": "请先添加“origin”远程仓库再创建{request}。",
  "gitAction.hint.noLocalCommitsForCreate": "没有可包含在{request}中的本地提交。",
  "gitAction.hint.behindBeforeCreate": "分支落后于上游，请先拉取或变基再创建{request}。",
  "gitAction.hint.createUnavailable": "当前无法创建{request}。",
  "gitAction.hint.statusUnavailable": "无法获取 Git 状态。",
  "gitAction.hint.unavailable": "当前无法执行此操作。",
  "gitAction.hint.upToDate": "分支已是最新，无需操作。",

  "projectAction.dialog.addTitle": "添加操作",
  "projectAction.dialog.editTitle": "编辑操作",
  "projectAction.dialog.description": "操作是项目级命令，可从顶部工具栏或快捷键运行。",
  "projectAction.icon.choose": "选择图标",
  "projectAction.icon.play": "运行",
  "projectAction.icon.test": "测试",
  "projectAction.icon.lint": "代码检查",
  "projectAction.icon.configure": "配置",
  "projectAction.icon.build": "构建",
  "projectAction.icon.debug": "调试",
  "projectAction.field.name": "名称",
  "projectAction.field.keybinding": "快捷键",
  "projectAction.field.command": "命令",
  "projectAction.field.previewUrl": "预览 URL（可选）",
  "projectAction.keybinding.placeholder": "按下快捷键",
  "projectAction.keybinding.hint": "按下快捷键进行设置，按 {key} 清除。",
  "projectAction.preview.description": "运行此操作时，在应用内预览中打开该 URL。",
  "projectAction.runOnWorktreeCreate": "创建工作树时自动运行",
  "projectAction.autoOpenPreview": "运行此操作时自动打开预览",
  "projectAction.validation.nameRequired": "请输入名称。",
  "projectAction.validation.commandRequired": "请输入命令。",
  "projectAction.validation.saveFailed": "保存操作失败。",
  "projectAction.action.saveChanges": "保存更改",
  "projectAction.action.save": "保存操作",
  "projectAction.action.delete": "删除操作",
  "projectAction.delete.title": "删除操作“{name}”？",
  "projectAction.delete.description": "此操作无法撤销。",

  "plan.badge": "方案",
  "plan.fallbackTitle": "建议方案",
  "plan.actions": "方案操作",
  "plan.copied": "已复制",
  "plan.copy": "复制到剪贴板",
  "plan.download": "下载为 Markdown",
  "plan.saveToWorkspace": "保存到工作区",
  "plan.expand": "展开方案",
  "plan.collapse": "收起方案",
  "plan.copyFailed": "无法复制方案",
  "plan.copyFailedDescription": "复制时发生错误。",
  "plan.workspaceUnavailable": "工作区路径不可用",
  "plan.workspaceUnavailableDescription": "此任务没有可用于保存的工作区路径。",
  "plan.enterWorkspacePath": "请输入工作区路径",
  "plan.saved": "方案已保存到工作区",
  "plan.saveFailed": "无法保存方案",
  "plan.saveFailedDescription": "保存时发生错误。",
  "plan.dialog.title": "将方案保存到工作区",
  "plan.dialog.description": "输入相对于 {workspace} 的路径。",
  "plan.dialog.workspaceFallback": "工作区",
  "plan.dialog.workspacePath": "工作区路径",

  "relayInstall.step.checking": "正在检查当前安装",
  "relayInstall.step.waiting": "正在等待安装程序",
  "relayInstall.step.downloading": "正在下载中继客户端",
  "relayInstall.step.verifying": "正在验证下载内容",
  "relayInstall.step.installing": "正在安装中继客户端",
  "relayInstall.step.validating": "正在验证可执行文件",
  "relayInstall.step.activating": "正在启用安装",
  "relayInstall.title.installing": "正在安装中继客户端",
  "relayInstall.title.confirm": "安装中继客户端？",
  "relayInstall.description.installing": "T3 Code 正在为此环境配置通过 T3 Connect 的安全访问。",
  "relayInstall.description.confirm":
    "T3 Code 需要安装中继客户端，才能通过 T3 Connect 访问此环境。",
  "relayInstall.progress": "第 {current} 项，共 {total} 项",
  "relayInstall.progressLabel": "中继客户端安装进度",
  "relayInstall.keepOpen": "安装中继客户端期间请保持 T3 Code 运行。",
  "relayInstall.managedClient": "托管中继客户端",
  "relayInstall.versionDescription": "T3 Code 将在本机下载并安装版本 {version}。",
  "relayInstall.downloadAndInstall": "下载并安装",

  "sshPassword.title": "需要 SSH 验证",
  "sshPassword.description":
    "请输入 {target} 所需的密码或验证码。T3 Code 会将响应传递给本机 SSH 进程，并保留在内存中，以便再次连接此主机时使用，不会写入磁盘。",
  "sshPassword.password": "密码",
  "sshPassword.expired": "已过期",
  "sshPassword.expiredDescription": "此 SSH 验证请求已过期，请重新连接。",
  "sshPassword.promptFailed": "SSH 验证请求失败。",
  "sshPassword.keyHint": "SSH 密钥可以代替密码，但服务器仍可能要求输入验证码。",

  "connectOnboarding.title": "设置 T3 Connect",
  "connectOnboarding.description": "发布当前环境并连接其他设备，在一个位置完成设备互联。",
  "connectOnboarding.enabled": "已启用 T3 Connect",
  "connectOnboarding.enabled.environment": "其他设备现在可以通过 T3 Connect 访问此环境。",
  "connectOnboarding.enabled.activity": "此环境会向移动客户端发布 Agent 活动。",
  "connectOnboarding.dontShowAgain": "不再显示",
  "connectOnboarding.notNow": "暂不设置",
  "connectOnboarding.enabling": "正在启用...",
  "connectOnboarding.step": "第 {index} 步",
  "connectOnboarding.step.publish": "发布环境",
  "connectOnboarding.step.devices": "连接设备",
  "connectOnboarding.publishEnvironment": "发布此环境",
  "connectOnboarding.publishEnvironmentDescription": "让其他设备可通过 T3 Connect 访问此环境。",
  "connectOnboarding.publishActivity": "发布 Agent 活动",
  "connectOnboarding.publishActivityDescription":
    "将此环境中的活动发送到移动客户端，用于推送通知和实时活动。",
  "connectOnboarding.emptyDevices":
    "你的账号还没有发布其他环境。请从另一台设备发布环境，随后会显示在这里。",
  "connectEnvironment.added": "已添加环境",
  "connectEnvironment.connectingThrough": "正在通过 T3 Connect 连接到 {environment}。",
  "connectEnvironment.connectFailed": "无法连接环境",
  "connectEnvironment.connectFailedDescription": "无法连接 T3 Connect 环境。",
  "connectEnvironment.copyTraceId": "复制跟踪 ID",
  "connectEnvironment.offline": "当前似乎处于离线状态。",
  "connectEnvironment.loadFailed": "无法加载 T3 Connect 环境",
  "connectEnvironment.status.online": "可用 - 中继在线",
  "connectEnvironment.status.offline": "可用 - 中继离线",
  "connectEnvironment.status.checking": "可用 - 正在检查中继状态...",
  "connectEnvironment.status.unavailable": "可用 - 中继状态不可用",
  "connectEnvironment.relay.online": "中继在线",
  "connectEnvironment.relay.offline": "中继离线",
  "connectEnvironment.relay.checking": "正在检查中继状态",
  "connectEnvironment.relay.unavailable": "中继状态不可用",
  "connectEnvironment.connect": "连接",
  "connectEnvironment.connecting": "正在连接...",
  "connectEnvironment.connection.connected": "已连接",
  "connectEnvironment.connection.reconnecting": "正在重新连接...",
  "connectEnvironment.connection.reconnectingAfterError": "连接出错，正在重新连接：{reason}",
  "connectEnvironment.connection.failed": "连接失败",
  "connectEnvironment.connection.failedWithReason": "连接失败：{reason}",
  "connectEnvironment.connection.offline": "离线",
  "connectEnvironment.connection.notConnected": "未连接",

  "chat.branchSwitch.title": "切换到 {branch}？",
  "chat.branchSwitch.description":
    "你有尚未提交的更改。这些更改会随分支切换保留；如有冲突，切换将被阻止。",
  "chat.branchSwitch.action": "切换分支",

  "sidebar.projectRename.title": "重命名项目",
  "sidebar.projectRename.description": "更新 {path} 的项目标题。",
  "sidebar.projectRename.descriptionGeneric": "更新项目标题。",
  "sidebar.projectRename.field": "项目标题",
  "sidebar.projectRename.environment": "环境：{environment}",
  "sidebar.projectGrouping.title": "项目分组",
  "sidebar.projectGrouping.description": "选择 {path} 在侧栏中的分组方式。",
  "sidebar.projectGrouping.descriptionGeneric": "选择此项目在侧栏中的分组方式。",
  "sidebar.projectGrouping.rule": "分组规则",
  "sidebar.projectGrouping.useGlobal": "使用全局默认值",
  "sidebar.projectGrouping.useGlobalValue": "使用全局默认值（{value}）",
  "sidebar.projectGrouping.repository": "按仓库分组",
  "sidebar.projectGrouping.repositoryPath": "按仓库路径分组",
  "sidebar.projectGrouping.separate": "单独显示",
  "sidebar.projectGrouping.repositoryDescription": "同一仓库中的项目共用一个侧栏条目。",
  "sidebar.projectGrouping.repositoryPathDescription":
    "仓库与仓库内相对路径均相同的项目共用一个侧栏条目。",
  "sidebar.projectGrouping.separateDescription": "在侧栏中单独显示此项目。",

  "pullRequestThread.title": "签出{request}",
  "pullRequestThread.description":
    "解析 {provider} {request}，然后在主仓库或独立工作树中创建草稿任务。",
  "pullRequestThread.request": "拉取请求",
  "pullRequestThread.placeholder": "{label} URL、签出命令或 #42",
  "pullRequestThread.validation.empty": "粘贴{request} URL、签出命令，或输入 123 / #123。",
  "pullRequestThread.validation.invalid": "请输入{request} URL、签出命令、123 或 #123。",
  "pullRequestThread.prepareFailed": "无法准备{request}任务。",
  "pullRequestThread.branchRange": "{head} 到 {base}",
  "pullRequestThread.state.open": "开放",
  "pullRequestThread.state.closed": "已关闭",
  "pullRequestThread.state.merged": "已合并",
  "pullRequestThread.resolving": "正在解析{request}...",
  "pullRequestThread.local": "本地",
  "pullRequestThread.worktree": "工作树",
  "pullRequestThread.preparingLocal": "正在准备本地分支...",
  "pullRequestThread.preparingWorktree": "正在准备工作树...",
  "pullRequestConfirm.mergeTitle": "合并拉取请求？",
  "pullRequestConfirm.autoMergeTitle": "启用自动合并？",
  "pullRequestConfirm.closeTitle": "关闭拉取请求？",
  "pullRequestConfirm.mergeDescription": "将使用 {method} 合并 #{number}。",
  "pullRequestConfirm.autoMergeDescription":
    "托管服务认为拉取请求准备就绪后，将使用 {method} 合并 #{number}；可能会立即执行。",
  "pullRequestConfirm.closeDescription": "关闭 #{number}，但不进行合并。",
  "pullRequestConfirm.merge": "合并",
  "pullRequestConfirm.enableAutoMerge": "启用自动合并",

  "gitDialog.publish.title": "发布仓库",
  "gitDialog.publish.description": "选择托管服务，然后指定要推送到的仓库。",
  "gitDialog.publish.step": "第 {index} 步",
  "gitDialog.publish.provider": "服务提供方",
  "gitDialog.publish.repository": "仓库",
  "gitDialog.publish.summary": "摘要",
  "gitDialog.publish.setupRequired": "需要设置",
  "gitDialog.publish.configureProvider": "打开“设置 -> 源代码管理”以配置此服务提供方。",
  "gitDialog.publish.statusUnavailable":
    "服务提供方状态不可用。请打开“设置 -> 源代码管理”并重新扫描。",
  "gitDialog.publish.notAuthenticated":
    "{provider} 尚未认证。请打开“设置 -> 源代码管理”查看设置指引。",
  "gitDialog.publish.visibility": "可见性",
  "gitDialog.publish.private": "私有",
  "gitDialog.publish.privateDescription": "仅受邀成员可见",
  "gitDialog.publish.public": "公开",
  "gitDialog.publish.publicDescription": "互联网上的任何人都可见",
  "gitDialog.publish.advanced": "高级",
  "gitDialog.publish.remote": "远程仓库名",
  "gitDialog.publish.protocol": "协议",
  "gitDialog.publish.publishingTo": "正在将仓库发布到 {provider}...",
  "gitDialog.publish.failed": "发布失败",
  "gitDialog.publish.published": "仓库已发布",
  "gitDialog.publish.created": "仓库已创建",
  "gitDialog.publish.publishedDescription": "{branch} 现已发布到 {provider}。",
  "gitDialog.publish.createdDescription": "已设置远程仓库“{remote}”。提交并推送后即可共享代码。",
  "gitDialog.publish.openOn": "在 {provider} 上打开",
  "gitDialog.publish.resultUnavailable": "发布结果不可用。",
  "gitDialog.publish.publishing": "正在发布...",
  "gitDialog.publish.action": "发布",
  "gitDialog.commit.title": "提交更改",
  "gitDialog.commit.description": "检查并确认本次提交。留空提交信息可自动生成。",
  "gitDialog.commit.branch": "分支",
  "gitDialog.commit.detachedHead": "游离 HEAD",
  "gitDialog.commit.defaultBranchWarning": "警告：当前是默认分支",
  "gitDialog.commit.files": "文件",
  "gitDialog.commit.fileCount": "已选 {selected} 个，共 {total} 个",
  "gitDialog.commit.excluded": "已排除",
  "gitDialog.commit.message": "提交信息（可选）",
  "gitDialog.commit.messagePlaceholder": "留空以自动生成",
  "gitDialog.commit.onNewBranch": "在新分支上提交",
  "gitDialog.defaultBranch.fallbackTitle": "在默认分支上执行操作？",
  "gitDialog.defaultBranch.commitPushTitle": "提交并推送到默认分支？",
  "gitDialog.defaultBranch.commitPushDescription":
    "此操作会在“{branch}”上提交并推送更改。你可以继续使用此分支，也可以创建功能分支后执行相同操作。",
  "gitDialog.defaultBranch.commitPushContinue": "提交并推送到 {branch}",
  "gitDialog.defaultBranch.pushTitle": "推送到默认分支？",
  "gitDialog.defaultBranch.pushDescription":
    "此操作会在“{branch}”上推送更改。你可以继续使用此分支，也可以创建功能分支后执行相同操作。",
  "gitDialog.defaultBranch.pushContinue": "推送到 {branch}",
  "gitDialog.defaultBranch.pushCreateTitle": "从默认分支推送并创建{request}？",
  "gitDialog.defaultBranch.pushCreateDescription":
    "此操作会推送本地提交，并从“{branch}”创建{request}。你可以继续使用此分支，也可以创建功能分支后执行相同操作。",
  "gitDialog.defaultBranch.pushCreateContinue": "从 {branch} 推送并创建{request}",
  "gitDialog.defaultBranch.commitCreateTitle": "从默认分支提交、推送并创建{request}？",
  "gitDialog.defaultBranch.commitCreateDescription":
    "此操作会从“{branch}”提交、推送并创建{request}。你可以继续使用此分支，也可以创建功能分支后执行相同操作。",
  "gitDialog.defaultBranch.commitCreateContinue": "提交、推送并创建{request}",
  "gitDialog.defaultBranch.checkoutFeature": "签出功能分支并继续",
  "gitDialog.defaultBranch.pullRequest": "拉取请求",
  "gitDialog.defaultBranch.mergeRequest": "合并请求",
  "gitDialog.defaultBranch.changeRequest": "变更请求",

  "filePicker.label": "文件选择器",
  "filePicker.openFile": "打开文件",
  "filePicker.search": "搜索文件...",
  "filePicker.searching": "正在搜索工作区文件...",
  "filePicker.indexing": "正在索引工作区文件...",
  "filePicker.noMatches": "没有匹配的文件。",
  "filePicker.empty": "未找到文件。",
  "filePicker.openProject": "打开项目后即可搜索其中的文件。",
  "contextWindow.title": "上下文窗口",
  "contextWindow.usedPercentage": "上下文窗口已使用 {percentage}",
  "contextWindow.usedTokens": "上下文窗口已使用 {tokens} 个令牌",
  "contextWindow.usage": "上下文窗口用量",
  "contextWindow.totalProcessed": "累计处理",
  "contextWindow.compactsAt": "达到 {tokens} 个令牌后自动压缩。",
  "contextWindow.compactsForModel": "{model} 的上下文会在需要时自动压缩。",
  "contextWindow.compactsAutomatically": "上下文会在需要时自动压缩。",
  "markdownFile.openInEditor": "在编辑器中打开",
  "markdownFile.openInNamedEditor": "在 {editor} 中打开",
  "markdownFile.revealInFinder": "在访达中显示",
  "markdownFile.revealInFileExplorer": "在文件资源管理器中显示",
  "markdownFile.revealInFiles": "在文件管理器中显示",
  "markdownFile.openInIntegratedBrowser": "在内置浏览器中打开",
  "markdownFile.openInSystemBrowser": "在系统浏览器中打开",
  "markdownFile.copyLink": "复制链接",
  "markdownFile.copyRelativePath": "复制相对路径",
  "markdownFile.copyFullPath": "复制完整路径",
  "markdownFile.relativePath": "相对路径",
  "markdownFile.fullPath": "完整路径",
  "markdownFile.copied": "已复制{target}",
  "markdownFile.copyFailed": "无法复制{target}",
  "markdownFile.clipboardUnavailable": "当前无法访问剪贴板。",
  "markdownFile.openFailed": "无法打开文件",
  "markdownFile.openBrowserFailed": "无法在浏览器中打开文件",
  "markdownFile.revealFailed": "无法在文件管理器中显示文件",
  "markdownFile.fileOptions": "{file} 的文件选项",
  "markdownLink.linkThread": "关联到任务",
  "markdownLink.unlinkThread": "取消关联任务",
  "markdownLink.pullRequestUnavailable": "此环境中没有该拉取请求。",
  "markdownLink.linkFailed": "无法关联拉取请求",
  "markdownLink.unlinkFailed": "无法取消关联拉取请求",
  "markdownLink.requestFailed": "请求失败。",
  "markdown.toggleTask": "切换任务状态",
  "projectIcon.label": "选择项目图标",
  "projectIcon.select": "选择图标",
  "projectIcon.search": "搜索图片文件...",
  "projectIcon.searching": "正在搜索项目文件...",
  "projectIcon.indexing": "正在索引项目文件...",
  "projectIcon.noMatches": "没有匹配的图片文件。",
  "projectIcon.empty": "未找到图片文件。",
  "projectIcon.openFailed": "无法打开图片选择器",
  "contentSearch.label": "搜索项目内容",
  "contentSearch.labelInProject": "在 {project} 中搜索文件内容",
  "contentSearch.search": "搜索项目内容...",
  "contentSearch.searchIn": "在 {project} 中搜索",
  "contentSearch.matchCase": "区分大小写",
  "contentSearch.wholeWord": "全字匹配",
  "contentSearch.regex": "使用正则表达式",
  "contentSearch.searching": "正在搜索...",
  "contentSearch.invalidRegex": "正则表达式无效",
  "contentSearch.results": "在 {files} 个文件中找到 {results} 个结果",
  "contentSearch.noResults": "未找到结果。",
  "contentSearch.typeToSearch": "输入内容以搜索整个项目。",
  "imagePreview.label": "展开图片预览",
  "imagePreview.close": "关闭图片预览",
  "imagePreview.previous": "上一张图片",
  "imagePreview.next": "下一张图片",
  "mediaPreview.image": "图片",
  "mediaPreview.video": "视频",
  "mediaPreview.expanded": "展开{media}预览",
  "mediaPreview.close": "关闭{media}预览",
  "mediaPreview.videoDownloadFailed": "无法下载此视频。",
  "mediaPreview.videoUnsupported": "此视频格式无法在这里播放。",
  "mediaPreview.videoDownloading": "正在下载…",
  "mediaPreview.videoDownload": "下载视频",
  "browserPreview.navigation": "导航",
  "browserPreview.back": "后退",
  "browserPreview.forward": "前进",
  "browserPreview.stop": "停止",
  "browserPreview.refresh": "刷新",
  "browserPreview.loading": "正在加载…",
  "browserPreview.urlPlaceholder": "搜索或输入网址",
  "browserPreview.openSystemBrowser": "在系统浏览器中打开",
  "browserPreview.cancelAnnotation": "取消批注",
  "browserPreview.annotate": "批注预览内容",
  "browserPreview.cancelAnnotationHint": "取消批注（Esc）",
  "browserPreview.annotateHint": "批注元素、区域或手绘内容",
  "browserPreview.stopRecording": "停止录制",
  "browserPreview.captureScreenshot": "截取屏幕截图",
  "browserPreview.recordHint": "截图 · 按住 Shift 点击可录制",
  "browserPreview.float": "将预览悬浮在对话上方",
  "browserPreview.closeFloating": "关闭悬浮预览",
  "browserPreview.floatingLabel": "悬浮浏览器预览",
  "browserPreview.openRightPanel": "在右侧面板中打开预览",
  "browserPreview.closePoppedOut": "关闭已弹出的预览",
  "browserPreview.popOut": "将预览弹出为单独窗口",
  "browserPreview.closeSeparateWindow": "关闭单独窗口",
  "browserPreview.popIntoSeparateWindow": "弹出到单独窗口",
  "browserPreview.reconnecting": "正在重新连接预览…",
  "browserPreview.popOutUpdateFailed": "无法更新弹出的预览",
  "common.copied": "已复制！",
  "common.copyFailed": "复制失败",
  "common.copyLink": "复制链接",
  "common.copyToClipboard": "复制到剪贴板",
  "common.loading": "正在加载…",
  "composer.controls.more": "更多输入控制",
  "composer.controls.mode": "模式",
  "composer.controls.chat": "对话",
  "composer.controls.plan": "规划",
  "composer.controls.access": "权限",
  "composer.controls.supervised": "需确认",
  "composer.controls.autoAcceptEdits": "自动接受编辑",
  "composer.controls.auto": "自动",
  "composer.controls.fullAccess": "完全访问",
  "changedFiles.count": "已更改 {count} 个文件",
  "changedFiles.countOne": "已更改 {count} 个文件",
  "changedFiles.countMany": "已更改 {count} 个文件",
  "changedFiles.show": "显示文件",
  "changedFiles.hide": "隐藏文件",
  "changedFiles.expandAll": "展开所有文件夹",
  "changedFiles.collapseAll": "折叠所有文件夹",
  "changedFiles.openDiff": "打开差异",
  "changedFiles.openFullDiff": "打开完整差异",
  "changedFiles.scopeCount": "{count} 个文件",
  "changedFiles.scopeCountOne": "{count} 个文件",
  "changedFiles.scopeCountMany": "{count} 个文件",
  "timeline.empty": "发送一条消息以开始对话。",
  "timeline.revert": "恢复到此消息",
  "timeline.loadingAttachment": "正在加载{name}",
  "timeline.playAttachment": "播放{name}",
  "timeline.downloadAttachment": "下载{name}",
  "projectSettings.breadcrumb": "项目设置导航",
  "projectSettings.projects": "项目",
  "projectSettings.switchProject": "切换项目",
  "projectSettings.unavailable": "项目不可用",
  "projectSettings.empty": "请先从侧栏添加项目，再在这里进行配置。",
  "projectSettings.removed": "此项目已不可用。",
  "projectSettings.pathCopied": "路径已复制",
  "projectSettings.copyPathFailed": "复制路径失败",
  "projectSettings.titleEmpty": "项目名称不能为空",
  "projectSettings.renameFailed": "重命名项目失败",
  "projectSettings.modelUpdateFailed": "更新默认模型失败",
  "projectSettings.workspaceUpdateFailed": "更新新任务工作区失败",
  "projectSettings.iconUpdateFailed": "更新项目图标失败",
  "projectSettings.autoPullUpdateFailed": "更新自动拉取设置失败",
  "projectSettings.scriptsSaveFailed": "保存脚本失败",
  "projectSettings.keybindingSaveFailed": "保存快捷键失败",
  "projectSettings.keybindingRemoveFailed": "移除快捷键失败",
  "projectSettings.importFailed": "导入操作失败。",
  "projectSettings.removeFailed": "移除“{name}”失败",
  "projectSettings.failureOnEnvironment": "{environment}上的{failure}",
  "projectSettings.currentEnvironment": "当前环境",
  "projectSettings.scriptSaving": "另一项脚本更改仍在保存，请稍后重试。",
  "projectSettings.removeConfirmWithThreads": "移除项目“{name}”并删除其中 {count} 个任务？",
  "projectSettings.removeConfirm": "移除项目“{name}”？",
  "projectSettings.path": "路径：{path}",
  "projectSettings.environment": "环境：{environment}",
  "projectSettings.removeGroupedEntries": "这会移除 {count} 个已分组的项目条目。",
  "projectSettings.clearHistory": "这会永久清除这些任务的对话历史。",
  "projectSettings.clearHistoryWithArchived": "这会永久清除这些任务及所有已归档任务的对话历史。",
  "projectSettings.clearArchivedHistory": "这会永久清除所有已归档的对话历史。",
  "projectSettings.removeEntriesOnly": "只会移除项目条目，不会删除磁盘上的文件。",
  "projectSettings.otherEntriesUnaffected": "此项目组中的其他条目不受影响。",
  "projectSettings.cannotUndo": "此操作无法撤销。",
  "projectSettings.section.project": "项目",
  "projectSettings.name": "名称",
  "projectSettings.nameDescription": "此项目组在侧栏和任务列表中使用的共享名称。",
  "projectSettings.projectName": "项目名称",
  "projectSettings.icon": "项目图标",
  "projectSettings.automatic": "自动",
  "projectSettings.chooseIcon": "选择项目图标文件",
  "projectSettings.chooseBuiltInIcon": "选择图标",
  "projectSettings.chooseFile": "选择文件",
  "projectSettings.section.newThreads": "新任务",
  "projectSettings.model": "模型",
  "projectSettings.modelDescription": "此项目的新任务默认使用该模型，并应用于组内所有检出目录。",
  "projectSettings.noProviders": "没有可用的服务提供方",
  "projectSettings.workspace": "工作区",
  "projectSettings.workspaceDescription":
    "设置此项目中新任务的启动位置。此设置会覆盖 t3.json 和全局默认值，并应用于组内所有检出目录。",
  "projectSettings.newThreadWorkspace": "新任务工作区",
  "projectSettings.defaultPerCheckout": "默认（按检出目录）",
  "projectSettings.defaultValue": "默认（{value}）",
  "projectSettings.defaultEachCheckout": "默认（使用各检出目录的 t3.json 或全局设置）",
  "projectSettings.defaultSource": "默认（{source}：{value}）",
  "projectSettings.worktree": "工作树",
  "projectSettings.local": "本地检出目录",
  "projectSettings.global": "全局设置",
  "projectSettings.autoPull": "自动拉取",
  "projectSettings.autoPullDescription":
    "当检出目录没有本地更改或提交时，在后台保持默认分支为最新状态。",
  "projectSettings.autoPullAria": "自动拉取默认分支",
  "projectSettings.section.checkout": "检出目录",
  "projectSettings.selectedCheckout": "已选择的检出目录",
  "projectSettings.thisMachine": "此设备",
  "projectSettings.copyCheckoutPath": "复制检出目录路径",
  "projectSettings.copyPath": "复制路径",
  "projectSettings.threadCount": "{count} 个任务",
  "projectSettings.grouping": "项目分组",
  "projectSettings.groupingDescription":
    "设置此检出目录在侧栏中的分组方式。更改后，它可能会移入另一个项目组。",
  "projectSettings.groupingRule": "{checkout} 的分组规则",
  "projectSettings.groupByRepository": "按仓库分组",
  "projectSettings.groupByRepositoryPath": "按仓库路径分组",
  "projectSettings.keepSeparate": "保持独立",
  "projectSettings.useGlobalDefault": "使用全局默认值",
  "projectSettings.removeCheckout": "移除检出目录",
  "projectSettings.removeCheckoutDescription":
    "从项目组中移除此检出目录及其任务，不会删除磁盘上的文件。",
  "projectSettings.actions": "操作",
  "projectSettings.actionsDescription": "仅保存在 {checkout} 中并从这里运行。",
  "projectSettings.importScripts": "导入脚本",
  "projectSettings.importFromT3": "从 t3.json 导入",
  "projectSettings.importDescription": "直接添加此检出目录声明的操作，无需先编辑。",
  "projectSettings.addAction": "添加操作",
  "projectSettings.noActions": "此检出目录尚未配置操作。",
  "projectSettings.setupBadge": "初始化",
  "projectSettings.previewBadge": "预览 · 仅桌面端",
  "projectSettings.editAction": "编辑{name}",
  "projectSettings.invalidT3": "t3.json 无效",
  "projectSettings.invalidT3Description":
    "此检出目录中的 t3.json 无法解析，其中声明的操作和图标均已忽略。请检查 JSON 语法与图标值。",
  "projectSettings.section.danger": "危险操作",
  "projectSettings.removeEverywhere": "从所有位置移除此项目",
  "projectSettings.removeProject": "移除项目",
  "projectSettings.removeAllEntries": "移除所有条目",
  "projectSettings.removeEverywhereDescription":
    "删除全部 {count} 个检出目录条目及其在各设备上的任务，不会删除磁盘上的文件。",
  "projectSettings.removeDescription": "删除项目条目及其任务，不会删除磁盘上的文件。",
  "chat.reconnectFailed": "无法重新连接环境",
  "chat.reconnectFailedDescription": "重新连接失败。",
  "chat.environmentUpdating": "环境可能正在完成更新，请稍候。",
  "chat.environmentDisconnected": "环境尚未连接。",
  "chat.attachmentPlayFailed": "无法播放{name}",
  "chat.attachmentDownloadFailed": "无法下载{name}",
  "chat.attachmentUnavailable": "附件不可用。",
  "chat.addToChatFailed": "无法添加到对话",
  "chat.composerBusy": "输入区正在忙碌，请在其就绪后重试。",
  "chat.actionDeleted": "已删除操作“{name}”",
  "chat.actionUnknown": "未知操作",
  "chat.actionDeleteFailed": "无法删除操作",
  "chat.unexpectedError": "发生意外错误。",
  "chat.copyPathFailed": "复制路径失败",
  "chat.pathCopied": "路径已复制",
  "chat.wakeFailed": "唤醒任务失败",
  "chat.checkoutSwitchFailed": "切换检出目录失败",
  "chat.checkoutThreadUpdateFailed": "检出目录已切换，但无法更新任务",
  "chat.branchChangedWas": "分支已变更，原分支为",
  "chat.branchChangedDescription": "此任务上次在 {previous} 上运行，发送后将在 {current} 上继续。",
  "chat.annotationAttached": "批注已附加到草稿",
  "chat.annotationSendUnavailable": "当前无法发送。请先完成正在进行的操作，再发送消息。",
  "chat.messageNotSentDisconnected": "尚未连接，消息未发送",
  "chat.reconnectingDescription": "正在重新连接环境，请在连接恢复后重试。",
  "chat.chooseProjectFirst": "请先选择项目",
  "chat.draftProjectUnavailable": "此草稿指向的项目已不可用。",
  "chat.implementationStartFailed": "无法启动实施任务",
  "chat.implementationCreateError": "创建新任务时发生错误。",
  "chat.pullRequestsUnavailable": "拉取请求不可用",
  "chat.pullRequestsUpdateServer": "请更新此环境的 T3 Code 服务端以浏览拉取请求。",
  "chat.scrollToEnd": "滚动到底部",
  "chat.connectingTo": "正在连接到{environment}",
  "chat.reconnectingTo": "正在重新连接到{environment}",
  "chat.reconnectBeforeActions": "发送消息或运行操作前，请先重新连接此环境。",
  "chat.reconnect": "重新连接",
  "chat.serverUpdateAvailable": "有可用的服务器更新",
  "chat.dismissUpdateNotice": "关闭更新提示",
  "chat.restoringBranch": "正在恢复...",
  "chat.restoreBranch": "恢复分支",
  "chat.dismissBranchNotice": "关闭分支变更提示",
  "sidebar.tooltip.branchMismatch": "当前检出的是另一个分支。",
  "sidebar.tooltip.terminalProcesses": "有 {count} 个终端进程正在运行",
  "sidebar.tooltip.errorOccurred": "发生错误",
  "sidebar.pathCopied": "路径已复制",
  "sidebar.copyPathFailed": "复制路径失败",
  "sidebar.branchCopied": "分支已复制",
  "sidebar.copyBranchFailed": "复制分支失败",
  "sidebar.threadIdCopied": "任务 ID 已复制",
  "sidebar.copyThreadIdFailed": "复制任务 ID 失败",
  "sidebar.titleEmpty": "任务标题不能为空",
  "sidebar.renameFailed": "重命名任务失败",
  "sidebar.wakeFailed": "唤醒任务失败",
  "sidebar.pinFailed": "固定任务失败",
  "sidebar.unpinFailed": "取消固定任务失败",
  "sidebar.reorderFailed": "调整固定任务顺序失败",
  "sidebar.snoozeFailed": "稍后处理任务失败",
  "sidebar.snoozedUntil": "已稍后处理至 {time}",
  "sidebar.snoozeManyFailed": "稍后处理多个任务失败",
  "sidebar.regenerateManyFailed": "重新生成多个任务标题失败",
  "sidebar.createFailed": "无法创建任务",
  "sidebar.regenerateFailed": "重新生成任务标题失败",
  "sidebar.pathUnavailable": "路径不可用",
  "sidebar.pathUnavailableDescription": "此任务没有可复制的工作区路径。",
  "sidebar.snoozedMany": "已稍后处理 {count} 个任务",
  "sidebar.snoozedPartial": "已稍后处理 {total} 个任务中的 {done} 个",
  "sidebar.snoozePartialDescription": "有 {count} 个任务无法稍后处理。",
  "sidebar.confirmArchiveThread": "归档任务“{title}”？",
  "sidebar.archiveFailed": "归档任务失败",
  "sidebar.archiveNavigationFailed": "任务已归档，但无法跳转",
  "settings.about.error.trackTitle": "无法更改更新通道",
  "settings.about.error.trackDescription": "更改更新通道失败。",
  "settings.about.error.downloadTitle": "无法下载更新",
  "settings.about.error.downloadDescription": "下载失败。",
  "settings.about.error.confirmTitle": "无法确认更新",
  "settings.about.error.confirmDescription": "确认更新失败。",
  "settings.about.error.installTitle": "无法安装更新",
  "settings.about.error.installDescription": "安装失败。",
  "settings.about.error.checkTitle": "无法检查更新",
  "settings.about.error.unavailable": "此构建不支持自动更新。",
  "settings.about.error.checkDescription": "检查更新失败。",
  "appearance.theme.import.toastAdded": "已添加主题“{theme}”",
  "appearance.theme.import.toastUpdated": "已更新主题“{theme}”",
  "appearance.theme.import.toastManyAdded": "已添加 {count} 个主题",
  "appearance.theme.import.toastManyUpdated": "已更新 {count} 个主题",
  "appearance.theme.import.toastAppearanceActive": "现已将该主题用于{appearance}模式。",
  "providers.update.failedTitle": "无法更新 {provider}",
  "providers.update.failedDescription": "无法启动服务提供方更新命令。",
} as const satisfies WebMessages;

const WEB_MESSAGES: Readonly<Record<ResolvedAppLocale, WebMessages>> = {
  en: EN_MESSAGES,
  "zh-CN": ZH_CN_MESSAGES,
};

const WEB_MESSAGE_KEY_BY_ENGLISH_SOURCE = new Map<string, WebMessageKey>();
for (const [key, source] of Object.entries(EN_MESSAGES) as ReadonlyArray<
  readonly [WebMessageKey, string]
>) {
  if (!source.includes("{") && !WEB_MESSAGE_KEY_BY_ENGLISH_SOURCE.has(source)) {
    WEB_MESSAGE_KEY_BY_ENGLISH_SOURCE.set(source, key);
  }
}

// New upstream settings copy can be localized here before it receives a stable
// message key. Settings containers and search both consume this same table.
const ZH_CN_SOURCE_MESSAGES: Readonly<Record<string, string>> = {
  "No labels found": "未找到标签",
  "Couldn't search labels on the host": "无法在托管平台搜索标签",
  "Could not merge this merge request": "无法合并此合并请求",
  "Could not close this merge request": "无法关闭此合并请求",
  "Could not reopen this merge request": "无法重新打开此合并请求",
  "Could not open a revert merge request": "无法创建回退合并请求",
  "Merge request closed": "合并请求已关闭",
  "Merge request reopened": "合并请求已重新打开",
  "Revert merge request opened": "已创建回退合并请求",
  "The host refused it. Check that you have write access and that this merge request was merged on the host.":
    "托管平台拒绝了请求。请确认你具有写入权限，且此合并请求已在托管平台合并。",
  "Could not merge this change request": "无法合并此变更请求",
  "Could not close this change request": "无法关闭此变更请求",
  "Could not reopen this change request": "无法重新打开此变更请求",
  "Could not open a revert change request": "无法创建回退变更请求",
  "Change request closed": "变更请求已关闭",
  "Change request reopened": "变更请求已重新打开",
  "Revert change request opened": "已创建回退变更请求",
  "The host refused it. Check that you have write access and that this change request was merged on the host.":
    "托管平台拒绝了请求。请确认你具有写入权限，且此变更请求已在托管平台合并。",

  "Preview limited to the first 1 MB.": "预览仅显示前 1 MB。",
  Table: "表",
  "Previous rows": "上一组行",
  "Next rows": "下一组行",
  "Archive entries": "压缩包条目",
  "This folder is empty.": "此文件夹为空。",
  "This table is empty.": "此表为空。",
  "This database has no readable tables.": "此数据库没有可读取的表。",
  "Saved outputs only. Code is never executed.": "仅显示已保存的输出，不会执行代码。",
  "This notebook is empty.": "此笔记本为空。",
  "Loading the rest of the file…": "正在加载文件的其余内容…",
  "No preview for this file": "无法预览此文件",
  Sheet: "工作表",
  Chapter: "章节",
  "This sheet is empty.": "此工作表为空。",
  "Previous page": "上一页",
  "Next page": "下一页",
  "Zoom out": "缩小",
  "Zoom in": "放大",
  "Find in PDF": "在 PDF 中查找",
  "Next matching page": "下一个匹配页面",
  "Decrease reading size": "缩小阅读字号",
  "Increase reading size": "放大阅读字号",
  "Reset reading size": "重置阅读字号",
  "Fit image": "适应窗口",
  "Actual image size": "图像原始大小",
  Send: "发送",
  "Connect an environment": "连接环境",
  "No connected environment can add a project": "没有可添加项目的已连接环境",
  "Thread search is unavailable on this server.": "此服务器不支持会话搜索。",
  "Update the server to enable it.": "更新服务器以启用此功能。",
  "Searching messages…": "正在搜索消息…",

  Advanced: "高级设置",
  Beta: "测试功能",
  "Sharing & access": "共享与访问",
  "Guest links": "访客链接",
  "Share a thread with a guest who watches it live in their browser and sends messages you approve. Sign in to T3 Connect to use guest links.":
    "与访客共享会话，让其在浏览器中实时查看，并发送由你批准的消息。登录 T3 Connect 后即可使用访客链接。",
  "Share a thread with a guest who watches it live in their browser and sends messages you approve. Needs T3 Connect: sign in and link this machine in":
    "与访客共享会话，让其在浏览器中实时查看，并发送由你批准的消息。需要 T3 Connect：请登录并在以下页面关联此设备：",
  ". While it is off, every guest link stops working.": "。关闭后，所有访客链接都将失效。",
  "Appended to the prompt for new and regenerated thread titles.":
    "附加到生成新会话标题和重新生成标题时使用的提示词。",
  "Thread title instructions": "会话标题生成指令",
  "thread title instructions": "会话标题生成指令",
  "Mixed. Enter instructions to apply to all selected targets.":
    "所选目标的设置不同。输入指令以应用到全部所选目标。",
  "Keep titles to a few words. Lead with the main subject.": "标题尽量简短，以主要主题开头。",
  Creation: "创建",
  Branches: "分支",
  "Writing & review": "撰写与审查",
  "Change in Sharing & access": "在“共享与访问”中修改",

  Disabled: "已禁用",
  "Switches the branch you are working in.": "切换当前工作分支。",
  "The host withheld part of this diff: a binary file, or a change too large to inline.":
    "托管平台未提供部分差异：可能包含二进制文件，或变更过大而无法内联显示。",
  "No authors found": "未找到作者",
  "Couldn't search authors on the host": "无法在托管平台搜索作者",
  Chat: "聊天",
  "Diffs & pull requests": "差异与拉取请求",
  Threads: "会话",
  Browser: "浏览器",
  Devices: "设备",
  Worktrees: "工作树",
  Environments: "环境",
  Maintenance: "维护",
  Labs: "实验室",
  About: "关于",

  "Separate fonts": "分别设置字体",
  "Snooze until limits reset": "暂停至用量限制重置",
  "Resume after usage limits": "用量限制解除后继续",
  "New design": "新设计",
  "Working section": "进行中分区",

  "Open Environments": "打开环境",
  "Share with guest": "与访客共享",
  Loading: "正在加载",
  "Could not load older threads. Retry": "无法加载更早的会话，重试",
  "Close and reopen the terminal to retry.": "关闭并重新打开终端以重试。",
  "Send feedback to decline this request": "发送反馈以拒绝此请求",
  "Audio unavailable": "音频不可用",
  "Show full title": "显示完整标题",
  "Show details": "显示详情",
  "Keep full": "保留完整历史",
  "Compact next": "下次压缩",
  "Branch changed from": "原分支为",
  "Hide this server's threads. Switch it on again in Environments.":
    "隐藏此服务器的会话，可在“环境”中重新开启。",
  "Could not update": "无法更新",
  Request: "请求",
  "Could not create a guest link.": "无法创建访客链接。",
  "A new link ends the current one.": "创建新链接将使当前链接失效。",
  "The link works once and expires in 24 hours.": "链接仅可使用一次，24 小时后过期。",
  "Creating...": "正在创建…",
  "Create link": "创建链接",
  "Guest link active": "访客链接已启用",
  Guest: "访客",
  "Guest link": "访客链接",
  "Guest link expired": "访客链接已过期",
  "Stop sharing": "停止共享",
  "The guest sees the thread as you do, including command output and the agent’s reasoning. Each message they send waits for you.":
    "访客可以看到与您相同的会话内容，包括命令输出和智能体的推理。访客发送的每条消息都需要您确认。",
  "Keybindings updated": "快捷键已更新",
  "Keybindings configuration reloaded successfully.": "快捷键配置已重新加载。",
  "Invalid keybindings configuration": "快捷键配置无效",
  "This connection still uses the old permissions, so some actions may no longer be available. Pair again using a new link with the permissions you need.":
    "此连接仍使用旧权限，部分操作可能已不可用。请使用包含所需权限的新链接重新配对。",
  "Open Sharing & access": "打开共享与访问",

  "Choose a folder on the other machine.": "请选择另一台设备上的文件夹。",
  "Could not start the clone.": "无法开始克隆。",
  "Add to another machine": "添加到另一台设备",
  "no files": "不复制文件",
  "Machine to add the project to": "要添加项目的设备",
  "No machine available": "没有可用设备",
  "Clone over": "克隆方式",
  "Clone protocol": "克隆协议",
  "the other machine": "另一台设备",
  machine: "设备",
  "Copy ignored files": "复制被忽略的文件",
  "No ignored files to copy.": "没有需要复制的被忽略文件。",
  "Finding ignored files…": "正在查找被忽略的文件…",
  "Files are copied after the clone finishes. Folders git ignores whole, like node_modules, are left out.":
    "文件将在克隆完成后复制。Git 整体忽略的文件夹（如 node_modules）不会复制。",
  "Starting…": "正在启动…",
  "Over 1 MB": "超过 1 MB",
  "Search refs...": "搜索引用…",
  "No refs found.": "未找到引用。",
  "Start from remote": "从远程分支开始",
  "Start worktree from remote": "从远程分支创建工作树",
  current: "当前",
  worktree: "工作树",
  remote: "远程",
  "Each model starts in its own worktree.": "每个模型都会在独立的工作树中启动。",
  "Run on": "运行于",
  "Run context": "运行上下文",
  "Current checkout": "当前检出目录",
  "Current worktree": "当前工作树",
  "Previous worktree": "上一个工作树",
  "This computer is off": "此计算机已关闭",
  "No computer connected": "未连接计算机",
  "Turn it back on, or connect a different computer.": "请重新启用它，或连接另一台计算机。",
  "Your agents run on your computer.": "你的智能体在你的计算机上运行。",
  "Your agents run on your computer. Start T3 Code there first.":
    "你的智能体在你的计算机上运行。请先在那台计算机上启动 T3 Code。",
  "Waiting for your computer…": "正在等待你的计算机…",
  "Add pairing link": "添加配对链接",
  "Run on your computer:": "在你的计算机上运行：",
  "Or download the desktop app": "或下载桌面应用",
  "Read projects and threads. Cannot start, message or change anything.":
    "读取项目和对话，不能启动对话、发送消息或进行任何更改。",
  "Could not reach this environment. Try again.": "无法连接此环境，请重试。",
  "The sign-in could not continue.": "无法继续登录。",
  "Checking the sign-in request": "正在检查登录请求",
  "One moment while this environment verifies the agent's request.":
    "请稍候，此环境正在验证智能体的请求。",
  "This sign-in cannot continue": "无法继续此次登录",
  "Close this page and start the sign-in again from your agent.":
    "请关闭此页面，然后从你的智能体重新发起登录。",
  "What it may do": "允许执行的操作",
  "Beyond read only, it can start, message and stop threads, and none of them can run with more than the mode you pick.":
    "选择只读之外的模式后，它可以启动对话、发送消息和停止对话，但任何对话的权限都不会超过你选择的模式。",
  "Paste a one-time pairing code": "粘贴一次性配对码",
  "Approving…": "正在批准…",
  "Denying…": "正在拒绝…",
  Deny: "拒绝",
  "Agent sign-in": "智能体登录",
  "Managed tunnel": "托管隧道",
  "Activity publishing only": "仅发布活动",
  Deregister: "取消注册",
  "Deregister server": "取消服务器注册",
  "T3 Connect access will be revoked, any managed tunnel will be removed, and a host space will become available. Local connections on your devices are not changed.":
    "T3 Connect 访问权限将被撤销，所有托管隧道将被移除，并释放一个主机名额。你设备上的本地连接不会改变。",
  "Deregistering…": "正在取消注册…",
  "Server deregistered": "服务器已取消注册",
  "T3 Connect access was revoked and a host space is now available.":
    "T3 Connect 访问权限已撤销，现已释放一个主机名额。",
  "Could not deregister the server.": "无法取消服务器注册。",
  "Could not deregister server": "无法取消服务器注册",
  "Environments registered to your account. Connections on this device are managed in Settings.":
    "注册到你账户的环境。此设备上的连接请在设置中管理。",
  "Loading environments…": "正在加载环境…",
  "No T3 Connect environments": "没有 T3 Connect 环境",
  "Link an environment from its local Settings to make it available through T3 Connect.":
    "在环境的本地设置中关联该环境，即可通过 T3 Connect 访问它。",
  "Could not load output:": "无法加载输出：",
  "PDF document": "PDF 文档",
  "Loading output…": "正在加载输出…",
  "No output.": "没有输出。",
  "Earlier output not shown": "更早的输出未显示",
  "more selected elements": "个其他已选元素",
  "Structured accessibility elements were included, but they have no readable names or values.":
    "已包含结构化辅助功能元素，但这些元素没有可读的名称或值。",
  "Watching: the agent wakes when checks finish, someone comments, or the branch conflicts. Click to stop.":
    "正在监控：检查完成、有人评论或分支发生冲突时会唤醒 Agent。点击停止。",
  "Workflow started": "工作流已启动",
  "Check the host connection and network access, then retry. Your device settings are saved.":
    "请检查主机连接和网络访问后重试。设备设置已保存。",
  "No Android virtual devices found. Create one in Android Studio's Device Manager, then refresh.":
    "未找到 Android 虚拟设备。请在 Android Studio 的设备管理器中创建一个，然后刷新。",
  "Device discovery warning ·": "设备发现警告 ·",
  "File path": "文件路径",
  "No matching files.": "没有匹配的文件。",
  "T3 Code collects anonymous usage data to help us improve it. To read more about how your data is used and how to opt out, see our":
    "T3 Code 会收集匿名使用数据以帮助改进产品。要了解数据用途及退出方式，请查看我们的",
  "was downloaded instead.": "已转为下载。",
  "Use arrow keys to adjust the focused value. Hold Shift for larger steps. Use Home and End for the minimum and maximum. Press Tab to move between saturation and brightness.":
    "使用方向键调整当前值，按住 Shift 可增大调整幅度。使用 Home 和 End 设为最小值或最大值，按 Tab 在饱和度与亮度之间切换。",
  "This device is offline": "此设备已离线",
  "{host} runs an older T3 Code": "{host} 正在运行旧版 T3 Code",
  "This device's clock is off": "此设备的系统时间不准确",
  "{host} is not linked to your T3 Connect account": "{host} 尚未关联到你的 T3 Connect 账户",
  "T3 Connect returned an error": "T3 Connect 返回错误",
  "{host}'s tunnel is still being set up": "正在为 {host} 设置隧道",
  "T3 Connect is not turned on for {host}": "{host} 尚未启用 T3 Connect",
  "{host}'s tunnel is offline": "{host} 的隧道已离线",
  "Tunnel works, but T3 Code on {host} did not answer": "隧道正常，但 {host} 上的 T3 Code 未响应",
  "T3 Code on {host} sent an unexpected answer": "{host} 上的 T3 Code 返回了异常响应",
  "{host}'s tunnel address did not resolve": "无法解析 {host} 的隧道地址",
  "Secure connection to {host}'s tunnel failed": "无法与 {host} 的隧道建立安全连接",
  "{host}'s tunnel refused the connection": "{host} 的隧道拒绝了连接",
  "{host} did not answer in time": "{host} 未及时响应",
  "This device could not prepare the connection": "此设备未能完成连接准备",
  "T3 Connect did not accept this device's sign-in": "T3 Connect 未接受此设备的登录凭据",
  "This device can't reach T3 Connect": "此设备无法连接 T3 Connect",
  "This device can't reach {host}'s tunnel": "此设备无法连接 {host} 的隧道",
  "T3 Connect could not reach {host}'s tunnel": "T3 Connect 无法连接 {host} 的隧道",
  "T3 Code on {host} did not accept this device": "{host} 上的 T3 Code 未允许此设备连接",
  "T3 Code on {host} did not answer": "{host} 上的 T3 Code 未响应",
  "On {host}, check Settings > Connections or run `t3 connect status`.":
    "在 {host} 上查看“设置 > 连接”，或运行 `t3 connect status`。",
  "Connect this device to the internet. T3 Code reconnects on its own.":
    "请将此设备连接到互联网，T3 Code 会自动重新连接。",
  "Update T3 Code on {host}.": "请更新 {host} 上的 T3 Code。",
  "Turn on automatic date and time on this device and on {host}, then retry.":
    "请在此设备和 {host} 上启用自动设置日期和时间，然后重试。",
  "Check that this device is signed in to the same T3 Connect account as the host.":
    "请确认此设备与主机登录的是同一个 T3 Connect 账户。",
  "On {host}, open Settings > Connections and turn T3 Connect on.":
    "在 {host} 上打开“设置 > 连接”，并启用 T3 Connect。",
  "Wait a minute. T3 Code retries on its own.": "请稍等片刻，T3 Code 会自动重试。",
  "Wait a few minutes. T3 Code retries on its own.": "请稍等几分钟，T3 Code 会自动重试。",
  "If it keeps failing, copy the diagnostics and report it.":
    "如果持续失败，请复制诊断信息并反馈问题。",
  "Make sure {host} is awake and T3 Code is running on it.":
    "请确保 {host} 处于唤醒状态，且 T3 Code 正在运行。",
  "A VPN or Tailscale exit node on {host} can block the tunnel (port 7844). Turn it off or allow the tunnel.":
    "{host} 上的 VPN 或 Tailscale 出口节点可能会阻止隧道连接（端口 7844）。请关闭它，或放行隧道连接。",
  "Open or restart T3 Code on {host}.": "请打开或重启 {host} 上的 T3 Code。",
  "Make sure {host} is awake and online.": "请确保 {host} 处于唤醒状态且已联网。",
  "Restart T3 Code on this device.": "请重启此设备上的 T3 Code。",
  "If it keeps failing, remove {host} from this device and add it again.":
    "如果持续失败，请从此设备中移除 {host}，然后重新添加。",
  "Sign out of T3 Connect and sign in again.": "请退出 T3 Connect 账户，然后重新登录。",
  "Check this device's internet connection.": "请检查此设备的网络连接。",
  "A VPN, firewall, or ad blocker on this device can block T3 Connect.":
    "此设备上的 VPN、防火墙或广告拦截器可能会阻止 T3 Connect 连接。",
  "A VPN, firewall, or DNS filter on this device can block the tunnel.":
    "此设备上的 VPN、防火墙或 DNS 过滤器可能会阻止隧道连接。",
  "Try another network, or add a direct route such as a Tailscale address.":
    "请尝试切换网络，或添加直连路径，例如 Tailscale 地址。",
  "Make sure T3 Code is running on {host}.": "请确保 T3 Code 正在 {host} 上运行。",
  "Retry. If it keeps failing, copy the diagnostics.": "请重试。如果持续失败，请复制诊断信息。",
  "This device": "此设备",
  "T3 Connect": "T3 Connect",
  "{host} tunnel": "{host} 隧道",
  "T3 Code on {host}": "{host} 上的 T3 Code",
  Cause: "原因",
  "HTTP status": "HTTP 状态",
  Attempts: "尝试次数",
  "Next retry": "下次重试",
  Route: "连接路径",
  "App version": "应用版本",
  "Host version": "主机版本",
  Error: "错误",
  "Trace ID": "追踪 ID",
  "EAS host not removed": "未能移除 EAS 主机",
  "EAS host not saved": "未能保存 EAS 主机",
  "Edit EAS host": "编辑 EAS 主机",
  "Idle minutes must be smaller than session minutes.": "空闲时长必须小于会话时长。",
  "Give the host a label and limits within the ranges shown.":
    "请为主机命名，并在显示的范围内设置限制。",
  "Checking the Expo account…": "正在检查 Expo 账号…",
  "Check the Expo account before choosing a project.": "选择项目前请先检查 Expo 账号。",
  "Sessions at once": "并发会话数",
  "Session minutes": "会话时长（分钟）",
  "Idle minutes": "空闲时长（分钟）",
  "Saving restarts this host, which stops its running sessions.":
    "保存会重启此主机，并停止正在运行的会话。",
  "the access token saved here": "此处保存的访问令牌",
  "EXPO_TOKEN on the server": "服务器上的 EXPO_TOKEN",
  "the server's eas login": "服务器上的 eas 登录状态",
  "connection diagnostics": "连接诊断",
  "Diagnostics copied": "已复制诊断信息",
  "Could not copy diagnostics": "无法复制诊断信息",
  "Not checked": "未检查",
  equals: "等于",
  "does not equal": "不等于",
  "is one of": "为以下之一",
  "starts with": "开头为",
  exists: "存在",
  "minutes, optional": "分钟，可选",
  "template, optional": "模板，可选",
  "seconds, 10–3600": "秒，10–3600",
  "Each request runs the prompt. Use {{body.path}}, {{headers.name}}, {{query.name}}, {{body}} or {{request}} in the prompt; only what it names reaches the agent.":
    "每个请求都会运行提示词。可在提示词中使用 {{body.path}}、{{headers.name}}、{{query.name}}、{{body}} 或 {{request}}；只有引用的内容会发送给 Agent。",
  "Official version (not translated)": "官方原版（未汉化）",
  "Shortcuts are environment-wide. Projects using the same action share its shortcut.":
    "快捷键在整个环境中生效，使用相同操作的项目共用其快捷键。",
  "Update timed out: try again.": "更新超时，请重试。",
  "This environment isn’t connected: try again once it reconnects.":
    "此环境尚未连接，请在重新连接后重试。",
  Updated: "已更新",
  "Show title details": "显示标题详情",
  "The previous request may have started. Open its thread to check before sending again.":
    "上一次请求可能已启动，请先打开对应对话确认，再重新发送。",
  "The previous request may already be running. Check its thread first. Allow another send that could create a duplicate thread?":
    "上一次请求可能正在运行，请先检查对应对话。仍要再次发送吗？这可能会创建重复的对话。",
  "Wait for attachments to finish preparing before rewinding.": "请等待附件准备完成后再回退。",
  "The environment is not connected.": "环境尚未连接。",
  "This connection cannot change keyboard shortcuts.": "此连接无权更改键盘快捷键。",
  "Script not found.": "找不到脚本。",
  "Wait for attachments to finish uploading, or remove failed uploads.":
    "请等待附件上传完成，或移除上传失败的附件。",
  "Retry or remove failed uploads before saving.": "保存前请重试或移除上传失败的附件。",
  "Rewind chat to before this message. Your prompt and attachments return to the composer. “Revert files too” also undoes your own edits.":
    "将对话回退到此消息之前。提示词和附件将返回输入框。选择“同时还原文件”也会撤销你自己对文件的修改。",
  attempt: "尝试次数",
  "What to try": "建议尝试",
  "More info": "更多信息",
  "Retry now": "立即重试",
  "Copy diagnostics": "复制诊断信息",
  "Add direct route": "添加直连路径",
  Database: "数据库",
  "Old history is cleaned up automatically, but this database still holds the freed space. Run the command on":
    "旧历史会自动清理，但数据库仍占用已释放的空间。请在",
  "to return it to disk; it pauses and restarts the background service. Quit the desktop app first if it hosts this environment.":
    "上运行此命令，将空间归还给磁盘。此操作会暂停并重启后台服务。如果此环境由桌面应用托管，请先退出桌面应用。",
  from: "从",
  "EAS Simulator": "EAS 模拟器",
  "Run iOS Simulators in Expo's cloud from this environment. Sessions bill the Expo account until they stop, so each host limits how many run and for how long. Editing or removing a host stops its running sessions.":
    "通过此环境在 Expo 云端运行 iOS 模拟器。会话停止前会持续向 Expo 账户计费，因此每个主机都设有并发会话数和运行时长限制。编辑或移除主机会停止其正在运行的会话。",
  "Add EAS host": "添加 EAS 主机",
  "No EAS hosts.": "暂无 EAS 主机。",
  "The environment's server signs in to Expo and starts the sessions. Without a token here it uses EXPO_TOKEN, then its eas login.":
    "此环境的服务器负责登录 Expo 并启动会话。如果未在此设置令牌，将依次尝试使用 EXPO_TOKEN 和服务器上的 eas login 登录状态。",
  "Expo access token": "Expo 访问令牌",
  "Clear access token": "清除访问令牌",
  "Check account": "检查账户",
  "EAS project": "EAS 项目",
  "From the thread's project": "使用任务所属项目",
  "Sessions bill the project's Expo account. \"From the thread's project\" reads extra.eas.projectId from the project's app config.":
    "会话费用计入项目的 Expo 账户。“使用任务所属项目”会读取该项目应用配置中的 extra.eas.projectId。",
  "Expo allows sessions of up to 40 minutes on free accounts and 115 on paid ones, and ends one after the idle minutes without activity, so idle minutes must be smaller than session minutes.":
    "Expo 免费账户的会话最长可运行 40 分钟，付费账户最长 115 分钟。连续无操作达到空闲时限后，会话将结束，因此空闲时限必须短于会话时限。",
  "Save host": "保存主机",
  "What the address bar searches when you type something that isn't a URL.":
    "在地址栏输入非网址内容时使用的搜索引擎。",
  "search engine": "搜索引擎",
  "Search engine": "搜索引擎",
  "Custom search URL": "自定义搜索网址",
  "Use %s where the search terms go.": "用 %s 表示搜索词的位置。",
  "default browser access": "默认浏览器访问权限",
  "Connect to your server from your phone or tablet.": "通过手机或平板电脑连接到服务器。",
  "Scan with your phone camera.": "使用手机相机扫描。",
  "Run only when every filter matches. Other requests are logged as filtered. Separate “is one of” values with commas; a value can't contain a comma.":
    "仅在所有筛选条件均匹配时运行。其他请求会被记录为已过滤。“属于以下任一值”的多个值用逗号分隔，单个值不能包含逗号。",
  "Add filter": "添加筛选条件",
  "Next run": "下次运行",
  "Replay key": "重放去重键",
  "Coalesce key": "合并键",
  "Coalesce window": "合并时间窗口",
  "Replay keys suppress repeat deliveries for 7 days. Coalescing runs the latest payload once when the window from the first delivery closes. Empty keys run normally.":
    "重放去重键会在 7 天内拦截重复投递。合并时间窗口从首次投递开始计时，窗口结束后仅使用最新请求内容运行一次。键为空时照常运行。",
  "Same on all machines": "所有设备一致",
  "Typeset LaTeX math, such as $x^2$ or \\[x^2\\], in messages and Markdown previews. The composer keeps TeX source editable.":
    "在消息和 Markdown 预览中排版 LaTeX 公式，例如 $x^2$ 或 \\[x^2\\]。输入框中仍可编辑 TeX 源码。",
  "math rendering": "公式渲染",
  "Render math": "渲染公式",
  "Compact long idle Claude threads just before their prompt cache expires, so coming back costs less.":
    "在长时间闲置的 Claude 任务的提示词缓存即将过期前压缩上下文，降低稍后继续任务的费用。",
  "compact idle Claude threads": "压缩闲置的 Claude 任务",
  "Compact idle Claude threads (beta)": "压缩闲置的 Claude 任务（测试版）",
  "Uses the latest commit of the base branch’s tracked remote branch, even if its name differs. Otherwise uses the same-named branch on origin. Uses your local branch if that remote branch is missing.":
    "使用基础分支所跟踪的远程分支的最新提交，即使两者名称不同。未设置跟踪关系时，使用 origin 上的同名分支；若该远程分支不存在，则使用本地分支。",
  "new worktrees start from remote": "从远程分支创建新工作树",
  "Start new worktrees from remote by default": "默认从远程分支创建新工作树",
  Archived: "已归档",
  "No matches found.": "未找到匹配项。",
  Machine: "设备",
  Folder: "文件夹",
  Thread: "任务",
  ": Nothing needed cleanup.": "：没有需要清理的内容。",
  "Even pace": "均匀使用",
  "% left by now": "%（当前应剩余）",
  "API estimate excludes": "API 费用估算未包含",
  "unpriced records.": "的未定价记录。",
  "Usage breakdown by time": "按时间查看用量明细",
  updated: "更新于",
  "Loading breakdown": "正在加载分类统计",
  Code: "代码",
  Tests: "测试",
  Mocks: "模拟数据",
  Generated: "生成的文件",
  Docs: "文档",
  Total: "总计",
  "Open comment on host": "在托管平台打开评论",
  "Choose an agent": "选择一个 Agent",
  "Search ACP Registry": "搜索 ACP 注册表",
  "Search agents…": "搜索 Agent…",
  "Enter manually": "手动输入",
  "Local ACP command": "本地 ACP 命令",
  "Searching the registry...": "正在搜索注册表…",
  "No compatible agents found": "未找到兼容的 Agent",
  "Native sessions": "原生会话",
  "Resume agent-owned conversations as T3 threads.": "将 Agent 中的对话恢复为 T3 任务。",
  "Project for ACP sessions": "ACP 会话所属项目",
  "Agent providers": "Agent 提供商",
  "Configure non-secret routing. Headers are write-only.": "配置非敏感路由信息。请求头仅可写入。",
  "Project for ACP providers": "ACP 提供商所属项目",
  "Choose how new worktree branches are named from your first message.":
    "选择如何根据第一条消息为新工作树分支命名。",
  "branch naming": "分支命名",
  "Worktree branch naming": "工作树分支命名",
  "Branch prefix": "分支前缀",
  "For example, t3 or t3/ produces t3/add-search. Leave empty for no prefix.":
    "例如，t3 或 t3/ 会生成 t3/add-search。留空则不添加前缀。",
  "branch prefix": "分支前缀",
  "Branch naming instructions": "分支命名说明",
  "Appended to the naming prompt. The model returns the complete branch name; no prefix or suffix is added.":
    "附加到命名提示词。模型将返回完整分支名，不再添加前缀或后缀。",
  "branch naming instructions": "分支命名说明",
  "In use": "使用中",
  "Remove route": "移除路由",
  "Sign in with": "请使用",
  "on the server host, then rescan to choose accounts here.":
    "在服务器主机上登录，然后重新扫描以在此选择账号。",
  "Choose which": "选择每个 GitHub 主机使用的",
  "login each GitHub host uses, or turn a host off.": "登录账号，或停用某个主机。",
  "Signed in as": "已登录为",
  Account: "账号",
  "Active gh account": "当前 gh 账号",
  "The chosen login is no longer signed in, so the active gh login is used.":
    "所选账号已退出登录，因此将使用当前 gh 登录账号。",
  "Use active login": "使用当前登录账号",
  "can't be used:": "无法使用：",
  "is set on the server, so it overrides the account chosen here until it is unset.":
    "已在服务器上设置；在取消设置之前，它会覆盖此处选择的账号。",
  "A token saved here is used before": "此处保存的令牌优先于",
  "and the": "和",
  "login, so GitHub works without the GitHub CLI. Give it read and write access to pull requests and contents.":
    "登录账号，因此无需安装 GitHub CLI 也可使用 GitHub。请授予令牌对拉取请求和仓库内容的读写权限。",
  "Create a token": "创建令牌",
  Token: "令牌",
  "Provider sign-in terminal": "提供商登录终端",
  "Starting sign-in…": "正在开始登录…",
  "Checking your account…": "正在检查账号…",
  "Complete sign-in in the terminal below.": "请在下方终端中完成登录。",
  "Enter your credentials below.": "请在下方输入登录凭据。",
  "Finish signing in in your browser.": "请在浏览器中完成登录。",
  "Signed in.": "已登录。",
  "Discovering sign-in methods…": "正在查找登录方式…",
  "No in-app sign-in advertised. Follow the provider's docs to finish setup.":
    "提供商未提供应用内登录方式。请按照其文档完成设置。",
  "Provider sign-in failed. Try again.": "提供商登录失败，请重试。",
  "Sign-in method": "登录方式",
  "Provider default": "提供商默认值",
  "Open browser": "打开浏览器",
  "Open docs": "打开文档",
  "Sign out": "退出登录",
  "Enter code": "输入验证码",
  "in your browser.": "（在浏览器中操作）。",
  "Loading sign-in terminal…": "正在加载登录终端…",
  "Device that runs this provider.": "运行此提供商的设备。",
  "Setup unavailable": "无法设置",
  "Provider setup is read-only.": "提供商设置为只读。",
  "Update this environment to manage Antigravity.": "请更新此环境以管理 Antigravity。",
  "Install and manage Antigravity.": "安装和管理 Antigravity。",
  "Uses the custom binary path below. Installation keeps that path.":
    "使用下方的自定义可执行文件路径。安装时会保留该路径。",
  "Automatic installation unavailable. Set a binary path or use another environment.":
    "无法自动安装。请设置可执行文件路径或使用其他环境。",
  "Sign in": "登录",
  "Scheduled tasks": "定时任务",
  "New task": "新建任务",
  "Unavailable selection": "所选项目不可用",
  "No environments available": "没有可用环境",
  "Connect an environment to manage scheduled tasks.": "请连接一个环境以管理定时任务。",
  "Could not load scheduled tasks": "无法加载定时任务",
  "Loading scheduled tasks…": "正在加载定时任务…",
  "Task unavailable": "任务不可用",
  "This task no longer exists or is outside the selected project scope.":
    "此任务已不存在，或不属于所选项目范围。",
  "No scheduled tasks": "没有定时任务",
  "No tasks match this environment and project selection.": "没有符合所选环境和项目的任务。",
  Deliveries: "投递记录",
  "Run now": "立即运行",
  "Deliveries ·": "投递记录 ·",
  "Recent requests to this task's webhook URL.": "最近发送到此任务 Webhook URL 的请求。",
  "Loading delivery…": "正在加载投递记录…",
  "Signature verified": "签名已验证",
  "Prompt sent to the agent": "发送给 Agent 的提示词",
  "Empty placeholders:": "空占位符：",
  Headers: "请求头",
  Query: "查询参数",
  "Loading deliveries…": "正在加载投递记录…",
  "No requests yet.": "尚无请求。",
  "empty placeholder": "空占位符",
  "The URL appears after you save.": "保存后将显示 URL。",
  "Webhook URL": "Webhook URL",
  "Change in Connections": "在连接设置中更改",
  "Run a prompt automatically: on an interval, at a fixed time, or when a webhook is called.":
    "按指定间隔、固定时间或收到 Webhook 请求时自动运行提示词。",
  "Reconnect this environment before saving.": "请重新连接此环境后再保存。",
  "Runs on": "运行环境",
  "This scheduled task no longer exists.": "此定时任务已不存在。",
  "e.g. Check for Sentry issues": "例如：检查 Sentry 问题",
  "Create a new worktree": "创建新工作树",
  "Use the project checkout": "使用项目检出目录",
  "Use a specific checkout": "使用指定检出目录",
  "Base branch": "基准分支",
  "Checkout path": "检出路径",
  Prompt: "提示词",
  "What should the agent do each time this runs?": "每次运行时需要 Agent 做什么？",
  "This task uses a legacy interval below one minute. Saving updates it to at least one minute.":
    "此任务使用旧版的小于一分钟的间隔。保存后将调整为至少一分钟。",
  Schedule: "运行计划",
  "Schedule type": "计划类型",
  "At a time": "指定时间",
  "Every interval": "固定间隔",
  "On webhook": "收到 Webhook 时",
  "Skip requests older than": "跳过早于以下时长的请求",
  "Run every request": "处理每个请求",
  "Require signature": "要求签名",
  "Reject requests without a valid HMAC-SHA256 signature of the body.":
    "拒绝缺少有效请求体 HMAC-SHA256 签名的请求。",
  Header: "请求头",
  Prefix: "前缀",
  Encoding: "编码",
  Secret: "密钥",
  "Run at": "运行时间",
  on: "于",
  "Days to run": "运行日期",
  "Run every": "运行间隔",
  minutes: "分钟",
  "Disabled tasks stay saved but do not run.": "停用的任务仍会保留，但不会运行。",
  "These permissions apply to your active route. Other routes may have different permissions.":
    "这些权限适用于当前使用的路由。其他路由的权限可能不同。",
  "These permissions apply to this client’s current connection.":
    "这些权限适用于此客户端当前的连接。",
  Allowed: "已允许",
  "Not granted": "未授予",
  "Permissions not checked. Connect to this environment to view this session’s permissions.":
    "尚未检查权限。请连接此环境以查看当前会话的权限。",
  "OpenTelemetry export": "OpenTelemetry 导出",
  "Choose one environment to configure its telemetry exports.": "选择一个环境以配置其遥测导出。",
  "Send traces, metrics, and logs to an OTLP HTTP receiver. Restart the server to apply. Environment variables override these settings.":
    "将链路追踪、指标和日志发送到 OTLP HTTP 接收端。重启服务器后生效。环境变量会覆盖这些设置。",
  "This connection lacks permission to change settings on": "此连接没有权限更改以下环境的设置：",
  endpoint: "端点",
  "Enter an HTTP or HTTPS endpoint, or leave empty to disable export.":
    "输入 HTTP 或 HTTPS 端点；留空以停用导出。",
  "Running:": "当前运行值：",
  "Cost by type": "按类型划分费用",
  "Tokens by type": "按类型划分 Token",
  "Cost by speed": "按速度划分费用",
  "tokens have no known price": "个 Token 的价格未知",
  "Set price": "设置价格",
  Premium: "高级",
  "Ask about this stop": "询问本站内容",
  "Open in Code": "在代码中打开",
  "Notes on this stop": "本站注释",
  "Context, no code": "背景信息，无代码",
  "Submit review": "提交评审",

  "Confirm auto-merge?": "确认自动合并？",
  "Confirm merge?": "确认合并？",
  "Confirm close?": "确认关闭？",
  "Confirm revert?": "确认还原？",
  "Confirm approve?": "确认批准？",
  "Branch deleted": "分支已删除",
  "Deleting branch...": "正在删除分支...",
  "Delete branch": "删除分支",
  "Reconnect to delete this branch": "重新连接后即可删除此分支",
  "Writing guide...": "正在编写指南...",
  "Write guide": "编写指南",
  "Rewrite guide": "重新编写指南",
  Guide: "指南",
  "No checks reported": "尚无检查结果",
  "It may be an issue rather than a pull request, or this account can't see it.":
    "它可能是议题而非拉取请求，或者当前账户无权查看。",
  "Copy checkout command": "复制检出命令",
  "Checkout command copied": "检出命令已复制",
  "PR number": "拉取请求编号",
  "PR link": "拉取请求链接",
  Filter: "筛选",
  "Bot comments": "机器人评论",
  "Resolved or dismissed": "已解决或已驳回",
  Resolved: "已解决",
  "Review dismissed": "评审已驳回",
  "Summarize your review (optional)": "总结你的评审（可选）",
  "Summarize your review or add line comments": "总结你的评审或添加行内评论",
  "No merge method is available for this repository.": "此仓库没有可用的合并方式。",
  "Open this pull request to merge its stack": "打开此拉取请求以合并其堆叠",
  "Close immediately, or drag across rows to close several":
    "立即关闭，或拖过多行以关闭多个拉取请求",
  Close: "关闭",
  Merge: "合并",
  "Ready for review": "可供评审",
  Reopen: "重新打开",
  "Review guide": "评审指南",
  "Reading the change...": "正在阅读更改...",
  "Writing a review guide": "正在编写评审指南",
  "No review guide yet": "尚无评审指南",
  "An agent is reading the change. A large one takes a few minutes. You can leave; you will be told when it is ready.":
    "Agent 正在阅读更改。较大的更改可能需要几分钟。你可以先离开，完成后会收到通知。",
  "A guide is a tour of the change: stops through the code in reading order, with the calls only a maintainer can make.":
    "指南将按阅读顺序带你浏览代码更改，突出需要维护者判断的事项。",
  "Try again": "重试",
  "Rewriting this guide. You will be told when the new one is ready.":
    "正在重新编写指南。新指南完成后会通知你。",
  "Written before the latest push.": "此指南编写于最近一次推送之前。",
  Rewrite: "重新编写",
  "Add a comment…": "添加评论…",
  "Ask the agent about these lines…": "向 Agent 询问这些行…",
  "Add to review": "添加到评审",
  "Ask agent": "询问 Agent",
  "Review tour": "评审导览",
  "A tradeoff to weigh": "需要权衡的取舍",
  "A contract to check": "需要检查的契约",
  "A product call": "需要做出的产品决策",
  "A risk to judge": "需要判断的风险",
  "An accessibility call": "需要做出的无障碍决策",
  "Previous stop": "上一站",
  "Next stop": "下一站",
  "Also in this stop": "本站还包括",
  "Files in this stop": "本站文件",
  "Loading the diff...": "正在加载差异...",
  "This file is not in the loaded diff.": "此文件不在已加载的差异中。",
  "Questions to consider": "需要考虑的问题",
  "Ask an agent:": "询问 Agent：",
  "This change has no files to tour.": "此更改没有可供导览的文件。",
  "Review guide ready": "评审指南已就绪",
  "Review guide failed": "评审指南生成失败",
  "Open guide": "打开指南",
  "Could not start a review guide": "无法开始编写评审指南",
  "Check the review guide model in Settings → Source Control.":
    "请检查“设置 → 源代码管理”中的评审指南模型。",
  "Could not delete this branch": "无法删除此分支",
  "The host refused it. Check that you have write access to the source repository and that the branch has not moved since this page loaded.":
    "托管平台拒绝了请求。请确认你对源仓库拥有写入权限，且该分支自页面加载后未发生变化。",
  "Could not enable auto-merge": "无法启用自动合并",
  "Could not disable auto-merge": "无法停用自动合并",
  "Look closely": "仔细查看",
  "Finish review": "完成评审",
  "In-memory process samples retained by the server. The server samples only while diagnostics is open, and this resets when the server restarts.":
    "服务器保留的内存进程采样。仅在打开诊断页面时采样，服务器重启后会重置。",
  "On demand": "按需",
  "This client can create or revoke access for other devices.":
    "此客户端可以为其他设备创建或撤销访问权限。",
  "This environment was removed from T3 Connect. Turn it on to link it again.":
    "此环境已从 T3 Connect 移除。开启后可重新关联。",
  "T3 Connect is out of sync. This environment is linked only to publish agent activity, so other devices cannot connect. Turn T3 Connect on to repair it.":
    "T3 Connect 状态不同步。此环境目前仅关联用于发布智能体活动，其他设备无法连接。开启 T3 Connect 可修复。",
  "This connection cannot view or change network exposure.": "此连接无法查看或更改网络开放设置。",
  "Network exposure information is unavailable.": "网络开放信息不可用。",
  "No environment selected": "未选择环境",
  "Connect an environment to view its settings and access.": "连接环境后即可查看其设置和访问权限。",
  "Pair this machine again over another address, such as its Tailscale name. It joins the existing routes instead of adding a second machine.":
    "通过其他地址（例如 Tailscale 名称）再次配对此设备。该地址会加入现有连接路线，而不会重复添加设备。",
  "Its cookies and logins are deleted. Desktop tabs already open in this profile stay open until you close them; server browser tabs close now.":
    "其 Cookie 和登录信息将被删除。此配置中已打开的桌面标签页会保留，直到你手动关闭；服务器浏览器标签页会立即关闭。",
  "Refresh provider availability, versions, and auth state. Model catalog updates follow Provider update checks.":
    "刷新提供方的可用性、版本和认证状态。模型目录会随提供方更新检查一起更新。",
  "Prices and mappings apply to all past and future usage on the environments you select.":
    "价格和映射适用于所选环境中所有历史及未来用量。",
  "Map to": "映射到",
  "Counted as": "计入",
  "No custom prices or mappings. Add a row to set one.": "暂无自定义价格或映射。添加一行进行设置。",
  "Blank cache rates use the input price. Enter 0 for free tokens. A mapped model’s usage counts as the model it maps to.":
    "缓存费率留空时使用输入价格。免费令牌请填 0。映射模型的用量会计入目标模型。",
  "All providers": "所有提供方",
  "No providers": "无提供方",
  "API estimate": "API 估算",

  "Collapse table cells": "折叠表格单元格",
  "Expand table cells": "展开表格单元格",
  Copied: "已复制",
  "Copy table": "复制表格",
  "Disable line wrap": "关闭自动换行",
  "Wrap lines": "自动换行",
  "Show diagram": "显示图表",
  "Rendering diagram": "正在渲染图表",
  "Clipboard API unavailable.": "剪贴板 API 不可用。",
  "Preview media": "预览媒体",
  "The composer is not ready": "输入框尚未就绪",
  "Try citing the selection after the connection or pending input is resolved.":
    "请在连接恢复或待处理输入完成后，再尝试引用所选内容。",
  "Cloning repository": "正在克隆仓库",
  "Repository not cloned": "仓库未克隆",
  "Retry to bring in the repository.": "重试以导入仓库。",
  "Could not disconnect server": "无法断开服务器连接",
  "Failed to disconnect.": "断开连接失败。",
  "Usage limits are unavailable for this provider": "此提供商的用量限制不可用",
  "Keep attachments on this machine": "将附件保留在此设备上",
  "Remove attachments before choosing automatic routing, then attach them on the selected machine.":
    "选择自动路由前请先移除附件，再在所选设备上重新添加。",
  "Auto balance": "自动均衡",
  "Checking machines…": "正在检查设备…",
  "Auto balance unavailable": "自动均衡不可用",
  "Failed to interrupt the current turn.": "无法中断当前轮次。",
  "Queued message is no longer queued": "消息已不在队列中",
  "Your unsaved edit was kept in the composer.": "未保存的编辑已保留在输入框中。",
  "Your unsaved edit was discarded.": "未保存的编辑已丢弃。",
  "Could not open preview": "无法打开预览",
  "The script was deleted, but its keyboard shortcut could not be removed because permission changed.":
    "脚本已删除，但因权限变更，无法移除其快捷键。",
  "The script was saved, but this connection can no longer change keyboard shortcuts.":
    "脚本已保存，但此连接已无权更改快捷键。",
  "Unable to open browser": "无法打开浏览器",
  "Unable to update the thread pull request": "无法更新会话的拉取请求",
  "Failed to stop background work.": "停止后台任务失败。",
  "Open approval": "打开审批",
  "Open question": "打开问题",
  Resume: "继续",
  "Stopping...": "正在停止…",
  "Woke from snooze": "已从稍后提醒状态唤醒",
  "This connection cannot change threads.": "此连接无权修改会话。",
  "Compaction is unavailable for this provider": "此提供商不支持压缩上下文",
  "Failed to revert thread state.": "还原会话状态失败。",
  "Failed to fork this response.": "从此回复创建分支失败。",
  "Could not resume thread.": "无法恢复会话。",
  "Checking machine resources": "正在检查设备资源",
  "Choose a machine to continue": "选择一台设备以继续",
  "Resource checks are still running. You can choose a machine in the composer.":
    "资源检查仍在进行中。您可以在输入框中选择设备。",
  "No eligible machine has available resources. Choose a machine in the composer to override.":
    "符合条件的设备均无可用资源。您可以在输入框中手动选择设备。",
  "Choose models and a base branch": "选择模型和基准分支",
  "Multiple models need a new thread in a Git project. Each gets its own worktree.":
    "多模型需要在 Git 项目中新建会话，每个模型使用独立的工作树。",
  "Could not save the edited queued message.": "无法保存编辑后的队列消息。",
  "Failed to send message.": "发送消息失败。",
  "Allow retry": "允许重试",
  "Open thread": "打开会话",
  "Failed to send messages.": "发送消息失败。",
  "A background prompt could not be sent": "无法发送后台提示词",
  "Your newer draft is unchanged. Restore the failed prompt when this composer is empty.":
    "较新的草稿未受影响。输入框清空后，您可以恢复发送失败的提示词。",
  "Restore prompt": "恢复提示词",
  "Return to the original draft and send or clear its current prompt before restoring.":
    "请先返回原草稿并发送或清除当前提示词，再执行恢复。",
  "Could not open a fresh composer": "无法打开新的输入框",
  "Background task failed": "后台任务失败",
  "Open draft": "打开草稿",
  "Failed to submit approval decision.": "提交审批决定失败。",
  "Failed to submit user input.": "提交用户输入失败。",
  "Failed to dismiss the question.": "忽略问题失败。",
  "Failed to send plan follow-up.": "发送计划后续消息失败。",
  "Preparing machine": "正在准备设备",
  "Rewinding conversation": "正在回退对话",
  "Environment unavailable": "环境不可用",
  "this device": "此设备",
  "New thread in...": "新建会话于…",
  "Find in current thread": "在当前会话中查找",
  "Copy prompt link": "复制提示词链接",
  "Copy prompt link with repo and model": "复制含仓库和模型的提示词链接",
  "Copy PR link": "复制拉取请求链接",
  "Link pull request to thread": "将拉取请求关联到会话",
  "Show linked pull requests": "显示关联的拉取请求",
  "Go to file": "转到文件",
  "Open WSL folder": "打开 WSL 文件夹",
  "Change theme": "更改主题",
  "Change appearance": "更改外观",
  "Toggle theme editor": "切换主题编辑器",
  "Open pull requests": "打开拉取请求",
  "Open usage": "打开用量",
  "Open settings": "打开设置",
  "Project settings": "项目设置",
  "Failed to add project": "添加项目失败",
  "Windows-style paths are only supported on Windows.": "Windows 格式的路径仅在 Windows 上受支持。",
  "Cannot add project": "无法添加项目",
  "This connection cannot add projects.": "此连接无权添加项目。",
  "Repository lookup failed": "查找仓库失败",
  "Clone unavailable": "无法克隆",
  "This connection needs permission to write source control and add projects.":
    "此连接需要源代码管理写入权限及添加项目权限。",
  "Clone failed": "克隆失败",
  "Create private repository": "创建私有仓库",
  "Host for the new repository": "新仓库托管平台",
  "Unable to run command": "无法运行命令",
  "Could not add WSL project": "无法添加 WSL 项目",
  "Start the matching WSL backend, then choose the folder again.":
    "请启动对应的 WSL 后端，然后重新选择文件夹。",
  "Loading folders...": "正在加载文件夹…",
  "Unable to open preview": "无法打开预览",
  "The preview could not be opened.": "无法打开预览。",
  "Thread action failed": "会话操作失败",
  "Show more": "显示更多",
  "Project is not empty": "项目不为空",
  "Delete all threads in this project before removing it.":
    "移除此项目前，请先删除其中的所有会话。",
  "Delete anyway": "仍然删除",
  "Unknown error removing project.": "移除项目时发生未知错误。",
  "Failed to archive threads": "归档会话失败",
  "Could not choose environment": "无法选择环境",
  "Copy Path": "复制路径",
  "Copy Thread ID": "复制会话 ID",
  "Local sandbox project": "本地沙盒项目",
  "Remote project": "远程项目",
  "This connection cannot change projects.": "此连接无权修改项目。",
  "Couldn't connect": "无法连接",
  "Sidebar options": "侧边栏选项",
  "Sort projects": "项目排序",
  "Sort threads": "会话排序",
  "Visible threads": "可见会话",
  "Visible thread count": "可见会话数量",
  "Decrease visible thread count": "减少可见会话数量",
  "Increase visible thread count": "增加可见会话数量",
  "Intel build on Apple Silicon": "Apple 芯片设备上的 Intel 版本",
  "Download ARM build": "下载 ARM 版本",
  "Install ARM build": "安装 ARM 版本",
  "Could not start update download": "无法开始下载更新",
  "Add project script": "添加项目脚本",
  "Pair this client again with permission to run terminal commands.":
    "请重新配对此客户端，并授予运行终端命令的权限。",
  "Choose project action": "选择项目操作",
  "This connection cannot manage provider accounts.": "此连接无权管理提供商账户。",
  "This connection cannot manage provider accounts. View provider settings for update details.":
    "此连接无权管理提供商账户。请在提供商设置中查看更新详情。",
  "Handed off from": "转交自",
  "Usage limit reached": "已达到用量限制",
  "Snooze thread": "稍后提醒会话",
  Goal: "目标",
  Waiting: "等待中",
  Limited: "受限",
  "Regenerating title": "正在重新生成标题",
  "Wake thread now": "立即唤醒会话",
  "Failed to reorder active threads": "重新排列活跃会话失败",
  "Failed to update auto-settle": "更新自动收起设置失败",
  "Searching thread messages…": "正在搜索会话消息…",
  Active: "活跃",
  Show: "显示",
  more: "更多",
  "Terminal closed": "终端已关闭",
  "Process exited": "进程已退出",
  "Unable to open link": "无法打开链接",
  "Unable to open path": "无法打开路径",
  "Terminal write failed": "终端写入失败",
  "Unable to initialize libghostty-vt": "无法初始化 libghostty-vt",
  "Go to source": "转到来源",
  "Plan mode: click to return to normal build mode": "计划模式：点击返回普通构建模式",
  "Default mode: click to enter plan mode": "默认模式：点击进入计划模式",
  "Update this server to send files with question answers":
    "请更新此服务器，以在回答问题时发送文件",
  "Select at least one model.": "请至少选择一个模型。",
  "No pull requests found in this repository.": "此仓库中未找到拉取请求。",
  "Resume thread": "继续会话",
  "Open composer to compact and send": "打开输入框以压缩上下文并发送",
  "Still bringing attached context into this message.": "仍在将附加上下文导入此消息。",
  "Send again once its chip resolves.": "请在上下文标签解析完成后重新发送。",
  "Stash again once its chip resolves.": "请在上下文标签解析完成后重新暂存。",
  "Open provider settings": "打开提供商设置",
  "This question cannot accept attachments.": "此问题不支持附件。",
  "Pasted text is too large for this message": "粘贴的文本超出此消息的大小限制",
  "Remove some text or an attachment, then paste again.":
    "请移除部分文本或一个附件，然后重新粘贴。",
  "Pasted text is too large to attach": "粘贴的文本太大，无法作为附件添加",
  "Reduce the clipboard contents or save a smaller excerpt as a file.":
    "请减少剪贴板内容，或将较短的片段另存为文件。",
  "Loading thread context…": "正在加载会话上下文…",
  "Unable to read the dropped thread": "无法读取拖入的会话",
  "This thread exceeds the attachment size limit. Try a shorter thread.":
    "此会话超出附件大小限制。请尝试较短的会话。",
  "Check that its environment is connected and up to date, then try again.":
    "请确认会话所在环境已连接并更新到最新版本，然后重试。",
  "Unable to add thread": "无法添加会话",
  "Folders can't be dropped into remote environments": "无法将文件夹拖入远程环境",
  "Type the folder path with @ instead.": "请改用 @ 输入文件夹路径。",
  "Choose an option above": "请选择上方的选项",
  "Attach again": "重新添加附件",
  "Remove to send": "移除后发送",
  "Other notices": "其他通知",
  "Show notice details": "显示通知详情",
  "Notice details": "通知详情",
  Interrupt: "中断",
  "Update queued message": "更新队列消息",
  "Compact and send": "压缩上下文并发送",
  "Steer message": "引导当前任务",
  "Submit message": "提交消息",
  "Updating queued message": "正在更新队列消息",
  "Submitting message": "正在提交消息",
  Full: "完整",
  "Your prompt contains &quot;ultrathink&quot; in the text. Remove it to change this option.":
    "提示词中包含“ultrathink”。请移除该词以更改此选项。",
  "T3 Connect route added": "已添加 T3 Connect 连接路径",
  "Saved without T3 Connect": "已保存，但未启用 T3 Connect",
  "Not added": "未添加",
  "Add route": "添加连接路径",
  "Some history was not imported": "部分历史记录未导入",
  "This connection cannot control terminals.": "此连接无权控制终端。",
  "Import your projects": "导入项目",
  "Import projects and conversations from Claude Code and Codex on your selected computers.":
    "从所选设备上的 Claude Code 和 Codex 导入项目及对话。",
  "Unable to pop out preview": "无法弹出预览",
  "Recording preview": "正在录制预览",
  "Run in the thread's worktree when the thread settles": "会话收起时在其工作树中运行",
  "Checking file access…": "正在检查文件访问权限…",

  "Choose a project to manage its name, icon, checkouts and actions.":
    "选择项目以管理其名称、图标、检出目录和操作。",
  "This checkout is no longer available in the selected project and environment.":
    "所选项目和环境中的此检出目录已不可用。",
  Mixed: "混合",
  Icon: "图标",
  detected: "已检测",
  default: "默认",
  Project: "项目",
  "Queue message": "将消息加入队列",
  "Finding pull request...": "正在查找拉取请求…",
  "App permission approval": "应用权限审批",
  "Permission request": "权限请求",
  "Video preview unavailable": "视频预览不可用",
  "Adding…": "正在添加…",
  "Connecting…": "正在连接…",
  Available: "可用",
  Unavailable: "不可用",
  "Checking…": "正在检查…",
  "Ready to code.": "可以开始编程了。",
  "Summarize your review (required to request changes)": "总结评审意见（要求修改时必填）",
  "Refreshing pull request": "正在刷新拉取请求",
  "T3 Connect · Not added · Client not supported": "T3 Connect · 未添加 · 不支持此客户端",
  "T3 Connect · Not added · Relay online": "T3 Connect · 未添加 · 中继在线",
  "T3 Connect · Not added · Relay offline": "T3 Connect · 未添加 · 中继离线",
  "T3 Connect · Not added · Checking relay status…": "T3 Connect · 未添加 · 正在检查中继状态…",
  "T3 Connect · Not added · Relay status unavailable": "T3 Connect · 未添加 · 中继状态不可用",
  "Awaiting review": "等待评审",
  "Use template": "使用模板",
  "Copy as Markdown": "复制为 Markdown",
  "Copy as CSV": "复制为 CSV",
  "Code block actions": "代码块操作",
  "Run in terminal": "在终端运行",
  "Loading image": "正在加载图片",
  Cancel: "取消",
  Retry: "重试",
  "Hide this server's threads. Switch it on again in Connections.":
    "隐藏此服务器的任务。可在连接设置中重新启用。",
  "Disconnect server": "断开服务器连接",
  Reconnect: "重新连接",
  "Server update available": "服务器有可用更新",
  "View agents": "查看智能体",
  View: "查看",
  Compact: "压缩上下文",
  "Branch changed: was": "分支已更改，原分支为",
  "This thread last ran on": "此任务上次运行于",
  ". Sending will continue on": "。发送消息后将继续使用",
  "Pull requests unavailable": "拉取请求不可用",
  "Drop files to attach": "拖放文件以添加附件",
  "Scroll to end": "滚动到底部",
  "Switch to": "切换到",
  "You have uncommitted changes. They'll carry over to the other branch, or block the switch if they conflict.":
    "你有未提交的更改。它们将保留到另一分支；如存在冲突，则无法切换。",
  "Switch branch": "切换分支",
  "Edit from here?": "从此处编辑？",
  "Rewind chat to before this message. Your prompt and attachments return to the composer.":
    "将对话回退到此消息之前。提示词和附件将返回输入框。",
  "Revert files too": "同时还原文件",
  "Revert and keep changes": "回退并保留更改",
  Current: "当前",
  "New thread in": "新建任务于",
  "From t3.json": "来自 t3.json",
  Import: "导入",
  Run: "运行",
  "Project actions": "项目操作",
  "Add project action…": "添加项目操作…",
  "Project scripts": "项目脚本",
  "Script actions": "脚本操作",
  "Manage usage": "管理用量",
  "Open in": "打开方式",
  "Open in…": "选择打开方式…",
  Add: "添加",
  "Mesh your devices together: publish this environment and connect the rest, all in one place.":
    "将设备连接起来：在此发布当前环境并连接其他设备。",
  "Don't show this again": "不再显示",
  "Connect your agents": "连接智能体",
  "Choose an agent to start coding. You can add more later.":
    "选择一个智能体开始编程。之后可以添加更多。",
  "Connect another ChatGPT account": "连接另一个 ChatGPT 账户",
  of: "/",
  selected: "已选择",
  "Select all": "全选",
  "Select none": "取消全选",
  "Other folders": "其他文件夹",
  "Floating device preview": "浮动设备预览",
  "Wait for it to finish before the agent starts": "等待操作完成后再启动智能体",
  "Check details are out of date. Refresh the pull request to update them.":
    "检查详情已过期。刷新拉取请求以获取最新信息。",
  Changed: "已更改",
  "This file has been pushed to since you marked it viewed.":
    "你标记为已读后，此文件又有新的提交。",
  "These ticks are kept here, not on the host": "这些已读标记保存在当前环境中",
  "This host keeps no shared record of which files you have read, so these ticks are kept by this environment. They follow you between the apps connected to it, but the host's own web UI will not show them.":
    "代码托管平台不记录已读文件，因此这些标记由当前环境保存。它们会在连接到此环境的应用间同步，但不会显示在托管平台的网页上。",
  "Your ticks could not be read": "无法读取已读标记",
  "The boxes below are whatever was last read, and empty if nothing has been read yet.":
    "以下显示上次读取的标记；如果尚未读取过，则显示为空。",
  "This count covers only part of the change": "此计数仅包含部分更改",
  "This change has more files than the host will report ticks for in one read, so the count is short and some boxes below start empty.":
    "更改的文件数超过托管平台单次返回已读标记的数量，因此计数不完整，部分标记最初显示为空。",
  Line: "行",
  "Check out this pull request": "检出此拉取请求",
  "Back to this thread's pull requests": "返回此任务的拉取请求",
  "Back to pull requests": "返回拉取请求",
  "Retry stack lookup": "重试查找堆叠",
  Refresh: "刷新",
  "Copy PR number": "复制 PR 编号",
  "Pull request actions loading": "正在加载拉取请求操作",
  "Collapse pull request panel": "收起拉取请求面板",

  "Pull request tabs": "拉取请求选项卡",
  Reviewers: "评审者",
  "Reviewers loading": "正在加载评审者",
  Labels: "标签",
  None: "无",
  "Labels loading": "正在加载标签",
  Description: "描述",
  opened: "创建于",
  "Pull request video": "拉取请求视频",
  "Markdown editor mode": "Markdown 编辑器模式",
  outdated: "已过期",
  Outdated: "已过期",
  "Review verdict": "评审结论",
  "matched in the description": "在描述中匹配",
  "· Latest": "· 最新",
  "Check details are out of date.": "检查详情已过期。",
  "Searching…": "正在搜索…",
  "Toggle Sidebar": "切换侧边栏",
  left: "剩余",
  "No provider on the selected environments reports subscription limits.":
    "所选环境中的提供商均未报告订阅限额。",
  "View usage in ChatGPT with your connected account.": "使用已连接的账户在 ChatGPT 中查看用量。",
  sessions: "个会话",
  "Unpriced usage details": "未定价用量详情",
  "ChatGPT shared usage": "ChatGPT 共享用量",
  Unpriced: "未定价",
  "This app connects to T3 Code running on your computer or a server. Start the T3 Code desktop app or command-line server on that machine and keep it running.":
    "此应用连接到你电脑或服务器上运行的 T3 Code。请在该设备上启动 T3 Code 桌面应用或命令行服务器，并保持运行。",
  "Showing the last pull requests loaded.": "显示上次加载的拉取请求。",
  "Filter by provider": "按提供商筛选",
  "Restore device defaults": "恢复此设备的默认设置",
  "to change its settings.": "以更改其设置。",
  "Background Activity": "后台活动",
  "Pull request merged": "拉取请求已合并",
  "Marked ready for review": "已标记为可供评审",
  "Converted to draft": "已转换为草稿",
  "Pull request closed": "拉取请求已关闭",
  "Pull request reopened": "拉取请求已重新打开",
  "Branch updated with the base branch": "分支已根据基础分支更新",
  "Auto-merge turned on: merges as soon as this is ready, sooner if it already is":
    "已启用自动合并；准备就绪后会立即合并，若当前已就绪则会马上合并",
  "Auto-merge turned off": "已停用自动合并",
  "Revert pull request opened": "还原拉取请求已创建",
  "Workflows approved": "工作流已批准",
  Squash: "压缩合并",
  Rebase: "变基",
  "Could not merge this pull request": "无法合并此拉取请求",
  "Could not mark this ready for review": "无法标记为可供评审",
  "Could not convert this to a draft": "无法转换为草稿",
  "Could not close this pull request": "无法关闭此拉取请求",
  "Could not reopen this pull request": "无法重新打开此拉取请求",
  "Could not update this branch": "无法更新此分支",
  "Could not turn on auto-merge": "无法启用自动合并",
  "Could not turn off auto-merge": "无法停用自动合并",
  "Could not open a revert pull request": "无法创建还原拉取请求",
  "Could not approve workflows": "无法批准工作流",
  "The host refused the merge. Check that you have write access, that the checks it requires have passed, and that the branch is not conflicting.":
    "托管服务拒绝了合并。请确认你拥有写入权限、所需检查均已通过，并且分支没有冲突。",
  "The host refused it. Check that you have write access to this repository.":
    "托管服务拒绝了操作。请确认你拥有此仓库的写入权限。",
  "The host refused it. Check that you have write access, or that you opened it.":
    "托管服务拒绝了操作。请确认你拥有写入权限，或此请求由你创建。",
  "The host refused it. Check that you have write access, and that the branch still exists.":
    "托管服务拒绝了操作。请确认你拥有写入权限且分支仍然存在。",
  "The host refused it. Check that you have write access to the branch: one from a fork also needs its author to allow edits from maintainers: and that it does not conflict with the base.":
    "托管服务拒绝了操作。请确认你拥有该分支的写入权限；来自复刻仓库的分支还需作者允许维护者编辑，并确保它与基础分支没有冲突。",
  "The host refused it. Check that this repository allows auto-merge, that you have write access, and that there is something left for it to wait on.":
    "托管服务拒绝了操作。请确认仓库允许自动合并、你拥有写入权限，并且仍有条件需要等待。",
  "The host refused it. Check that you have write access, and that the merge has not already happened.":
    "托管服务拒绝了操作。请确认你拥有写入权限，并且合并尚未完成。",
  "The host refused it. Check that you have write access and that this pull request was merged on the host.":
    "托管服务拒绝了操作。请确认你拥有写入权限，并且此拉取请求已在托管服务中合并。",
  "The host refused it. Check that you have Actions write access and that these workflow runs are still awaiting approval.":
    "托管服务拒绝了操作。请确认你拥有 Actions 写入权限，并且这些工作流运行仍在等待批准。",
  "The host refused it. A rebase stops at the first commit that does not apply cleanly; updating with a merge commit may still work.":
    "托管服务拒绝了操作。变基会在首个无法干净应用的提交处停止；使用合并提交更新仍可能成功。",
  Summary: "摘要",
  Timeline: "动态",

  "Fix in this thread": "在当前任务中修复",
  "Fix findings in this thread": "在当前任务中修复问题",
  "Fix in a thread": "在新任务中修复",
  "Fix findings in a thread": "在新任务中修复问题",
  Running: "运行中",
  "Awaiting action": "等待操作",
  "Awaiting approval": "等待批准",
  Passed: "已通过",
  Failed: "失败",
  Cancelled: "已取消",
  Skipped: "已跳过",
  Neutral: "中立",
  "All checks have passed": "所有检查均已通过",
  "Some checks were not successful": "部分检查未成功",
  "Some checks haven't completed yet": "部分检查尚未完成",
  Organization: "组织与更新",
  Behavior: "使用行为",
  "Projects & threads": "项目与任务",
  Confirmations: "操作确认",
  "Colors & themes": "颜色与主题",
  Interface: "界面",
  "Version control": "版本控制",
  "Panel animations": "面板动效",
  "Diff layout": "差异布局",
  "Proactive panels": "智能面板",
  "Collapse composer": "自动收起输入区",
  "Continue threads after server updates": "服务端更新后继续任务",
  "Quit shortcut": "退出快捷键",
  "Context window indicator (legacy)": "上下文窗口指示器（旧版）",
  "Usage providers": "用量服务",
  "Browser profiles": "浏览器配置文件",
  "Default browser profile": "默认浏览器配置文件",
  "Browser recording frame rate": "浏览器录制帧率",
  "Open links in": "链接打开方式",
  "Environment icon": "环境图标",
  "Sort pull requests": "排序拉取请求",
  Sort: "排序",
  "Merge readiness": "合并就绪度",
  "Recently updated": "最近更新",
  "Newest shown": "最新显示",
  "Oldest shown": "最早显示",
  "Largest shown": "改动最多",
  "Smallest shown": "改动最少",
  "This account has no subscription limits.": "此账号没有订阅限额。",
  "Could not read limits.": "无法读取限额。",
  "No limits reported.": "未报告限额。",
  "Version pill": "版本标记",
  "On unfocus": "失去焦点时",
  "On scroll": "滚动时",
  Never: "从不",
  Split: "并排",
  Direct: "直接退出",
  Hold: "长按退出",
  "Double press": "连按两次退出",
  "Uses custom intervals.": "使用自定义间隔。",
  "No text generation providers available.": "没有可用的文本生成服务提供方。",
  "Configure background activity": "配置后台活动",
  "Pauses probes for idle clients, locked hosts, or low power mode.":
    "客户端空闲、主机锁定或处于低功耗模式时暂停检查。",
  "This shared policy gates background work such as Git refreshes and provider health probes after their individual intervals elapse.":
    "各自的执行间隔到期后，此共享策略会决定是否运行 Git 刷新、服务提供方健康检查等后台任务。",
  contrast: "对比度",
  "glass opacity": "玻璃效果透明度",
  "environment identification": "环境标识",
  "panel animations": "面板动效",
  "font smoothing": "字体平滑",
  "word wrapping": "自动换行",
  "project grouping": "项目分组",
  "auto-settle on merge": "合并后自动收起",
  "auto-settle": "自动收起",
  "time format": "时间格式",
  "diff whitespace changes": "差异中的空白字符更改",
  "diff layout": "差异布局",
  "proactive panels": "智能面板",
  "skills in slash menu": "斜杠菜单中的技能",
  "collapse composer": "收起输入区",
  "provider update checks": "服务提供方更新检查",
  "continue threads after server updates": "服务端更新后继续任务",
  "background activity": "后台活动",
  "new threads": "新任务",
  "new worktrees start from origin": "新工作树从 origin 开始",
  "add project base directory": "添加项目的基础目录",
  "unpin confirmation": "取消置顶确认",
  "archive confirmation": "归档确认",
  "delete confirmation": "删除确认",
  "quit shortcut behavior": "退出快捷键行为",
  "text generation model": "文本生成模型",
  "Use stable releases or nightly builds. Switch back anytime.":
    "使用稳定版或每日构建版，可随时切换。",
  Motion: "动效",
  "Set how fast panels open and close.": "设置面板打开和关闭的速度。",
  "Higher values make menus, dialogs, and the composer more solid.":
    "数值越高，菜单、对话框和输入区越不透明。",
  "Use thinner grayscale text smoothing instead of the macOS default.":
    "使用更轻薄的灰阶文字平滑效果，替代 macOS 默认效果。",
  "Restore Build/Plan, /plan, /default, and Shift+Tab. Off uses build mode.":
    "恢复构建/规划、/plan、/default 和 Shift+Tab。关闭后使用构建模式。",
  "Shows context window usage as a circular indicator in the composer.":
    "在输入区中以圆形指示器显示上下文窗口用量。",
  "Stream output token by token. This legacy mode is slower and harder to follow.":
    "逐个 Token 流式输出。此旧版模式速度较慢，也较难阅读。",
  "Restore per-project thread trees instead of the default flat sidebar.":
    "恢复按项目分组的任务树，替代默认的扁平侧栏。",
  "Show diffs stacked or side by side. The toggle in the diff toolbar changes this too.":
    "选择上下或并排显示差异；差异工具栏中的开关也会更改此设置。",
  "Open linked pull requests when found and turn diffs when work finishes.":
    "发现关联拉取请求时自动打开，并在工作完成后切换到差异面板。",
  "Rest the composer of an existing thread into a single line when it loses focus, when you scroll the conversation, or both. Pick neither to keep it expanded.":
    "现有任务的输入区失去焦点、滚动对话或同时满足两者时收起为单行；均不选择则保持展开。",
  "Automatically resume active threads after a server update restarts the environment.":
    "服务端更新并重启环境后，自动恢复进行中的任务。",
  "Choose whether the desktop app quits immediately, after a hold, or after two quick presses.":
    "选择桌面客户端立即退出、长按后退出，或快速按两次后退出。",
  "Hold mode also quits on two quick presses.": "长按模式下，快速按两次也会退出客户端。",
  "Used for thread titles and other generated text. Source control can override it.":
    "用于任务标题和其他生成文本；源代码管理可以单独覆盖此设置。",
  "Tab size for you and agents. Fill fits the panel; other sizes show the device toolbar.":
    "设置你和 Agent 使用的标签页尺寸。填满面板会自适应，其余尺寸会显示设备工具栏。",
  "Maximum recording rate. 30 fps saves CPU and storage; 60 fps is smoother.":
    "最大录制帧率。30 fps 更节省处理器和存储空间，60 fps 更流畅。",
  "Where links in the chat and terminal open. Hold ⌘ or Ctrl while clicking a chat link to open it in your default browser either way.":
    "选择对话和终端中的链接打开位置。按住 ⌘ 或 Ctrl 点击对话链接时，始终使用默认浏览器打开。",
  "Allow agents to use the preview browser. Off hides browser tools from agents, not you.":
    "允许 Agent 使用预览浏览器。关闭后仅对 Agent 隐藏浏览器工具，不影响你使用。",
  "Show the floating preview when an agent opens a browser unless the agent says otherwise.":
    "Agent 打开浏览器时显示浮动预览，除非 Agent 明确要求其他方式。",
  "You're not connected to a server yet.": "尚未连接到服务端。",
  "Profiles separate cookies and logins. Incognito data is cleared when the app closes.":
    "配置文件会隔离 Cookie 和登录状态；客户端关闭后会清除无痕数据。",
  "Refresh provider status, versions, and models in the background. Set to 0 to disable.":
    "在后台刷新服务提供方状态、版本和模型；设为 0 可禁用。",
  "Use Conventional Commit prefixes and keep change request text concise.":
    "使用 Conventional Commit 前缀，并保持变更请求文案简洁。",
  "Use your instructions for change descriptions and change requests in every project.":
    "在所有项目中使用你的变更说明和变更请求写作指令。",
  "Use the repository's template for change request descriptions when available.":
    "仓库提供模板时，使用该模板编写变更请求说明。",
  "Model for source control text and branch or bookmark names. Off uses the global default.":
    "用于源代码管理文案以及分支或书签名称的模型。关闭时使用全局默认模型。",
  "Connect a CLIProxyAPI hub to show its accounts on Usage → Limits.":
    "连接 CLIProxyAPI Hub，在“用量 → 限额”中显示其账号。",
  "Not installed on this machine.": "此设备上未安装。",
  "Needs Keychain access to read its cookies.": "需要访问系统钥匙串才能读取 Cookie。",
  "No encryption key in your Keychain: sign in to that browser once, then retry.":
    "系统钥匙串中没有加密密钥。请先在该浏览器中登录一次，然后重试。",
  "Quit the browser first so its cookie database can be read.":
    "请先退出浏览器，以便读取其 Cookie 数据库。",
  "Importing from this browser isn't possible on this platform.": "当前平台无法从此浏览器导入。",
  "The system keyring could not be accessed. Make sure your desktop keyring is running and unlocked, then retry.":
    "无法访问系统密钥环。请确认桌面密钥环正在运行且已解锁，然后重试。",
  "That browser is no longer available to import from.": "该浏览器已无法作为导入来源。",
  "That browser profile no longer exists.": "该浏览器配置文件已不存在。",
  "The target profile could not be opened.": "无法打开目标配置文件。",
  "The cookies were imported, but the new profile couldn't be saved. Try again.":
    "Cookie 已导入，但无法保存新配置文件。请重试。",
  "You've reached the profile limit. Delete a profile or import into an existing one.":
    "配置文件数量已达上限。请删除一个配置文件，或导入到已有配置文件。",
  "The browser's cookie database could not be read.": "无法读取浏览器的 Cookie 数据库。",
  Setup: "设置",
  Runtime: "运行时",
  Environment: "环境",
  Variables: "变量",
  Driver: "驱动",
  "API keys, base URLs, and other per-instance CLI settings.":
    "每个实例单独使用的 API 密钥、基础 URL 及其他 CLI 设置。",
  "Sensitive values are stored separately and never returned to the app.":
    "敏感值会单独存储，且不会返回给客户端。",
  "Sign in with your Google account.": "使用 Google 账号登录。",
  "Starting Google sign-in.": "正在启动 Google 登录。",
  "Waiting for Google sign-in.": "正在等待 Google 登录。",
  "Checking Google sign-in and available models.": "正在检查 Google 登录和可用模型。",
  "Google sign-in complete.": "Google 登录已完成。",
  "Google sign-in failed.": "Google 登录失败。",
  "Google sign-in cancelled.": "Google 登录已取消。",
  "Connect with the credentials in the provider settings.": "使用服务提供方设置中的凭据连接。",
  "Checking credentials.": "正在检查凭据。",
  "Checking credentials and available models.": "正在检查凭据和可用模型。",
  "Connected.": "已连接。",
  "Could not connect with the configured credentials.": "无法使用已配置的凭据连接。",
  "Connection cancelled.": "连接已取消。",
  "Google account": "Google 账号",
  Server: "服务器",
  "Cloud VM": "云服务器",
  Desktop: "台式机",
  Laptop: "笔记本电脑",
  "Mac mini": "Mac mini",
  "Mac Studio": "Mac Studio",
  "Connect to this environment to change its icon.": "连接到此环境后才能更改图标。",
  "This environment's server is too old to keep an icon. Update it to choose one.":
    "此环境的服务器版本过旧，无法保存图标。请先更新服务器。",
  "Your session on this environment cannot change its settings.": "当前会话无权更改此环境的设置。",
  "Settings scope": "设置范围",
  "Project scope": "项目范围",
  "Machine scope": "设备范围",
  "All projects": "全部项目",
  "All machines": "全部设备",
  "Unavailable project": "不可用的项目",
  "Unavailable machine": "不可用的设备",
  "This machine is no longer available.": "此设备已不可用。",
  "Automatically balance load": "自动均衡负载",
  "Choose a machine automatically for new threads in shared projects.":
    "为共享项目中的新任务自动选择设备。",
  "Connect another machine to automatically balance load across environments.":
    "再连接一台设备，即可在各环境之间自动均衡负载。",
  "load preference": "负载偏好",
  Prefer: "优先",
  Normal: "普通",
  "Less often": "降低频率",
  "Manual only": "仅手动选择",
  "Project defaults": "项目默认设置",
  "Select a project to change its name.": "选择一个项目后可修改名称。",
  "Select a project to change its icon.": "选择一个项目后可修改图标。",
  "Select a project": "选择项目",
  "Project name": "项目名称",
  "Choose file": "选择文件",
  "Connect a machine to change its project defaults.": "请先连接一台设备，再修改项目默认设置。",
  "Changes apply to connected machines only. Offline machines keep their current defaults.":
    "更改仅应用于已连接的设备；离线设备会保留当前默认设置。",
  "Default model for new threads. Projects can override it.":
    "新任务使用的默认模型，项目可以单独覆盖。",
  "Default model not saved": "未能保存默认模型",
  "Project defaults not saved on every machine": "部分设备未能保存项目默认设置",
  "No providers available": "没有可用的服务提供方",
  "Where new threads start, unless overridden by the project or t3.json.":
    "新任务的默认工作区；项目设置或 t3.json 可以覆盖此项。",
  "Default workspace": "默认工作区",
  "Automatically pull": "自动拉取",
  "Keeps the default branch current when the checkout has no local changes or commits. Projects can override it.":
    "检出目录没有本地更改或提交时保持默认分支为最新；项目可以单独覆盖。",
  "Default automatic pull": "默认自动拉取",
  "Agent browser access": "Agent 浏览器访问权限",
  "Allow agents to use the shared browser. Projects can override it.":
    "允许 Agent 使用共享浏览器；项目可以单独覆盖。",
  "Default agent browser access": "默认 Agent 浏览器访问权限",
  "Select a project to choose one of its checkouts.": "选择一个项目后可指定其检出目录。",
  "Default grouping across all machines in this client. Individual checkout overrides are preserved.":
    "此客户端中所有设备采用的默认分组方式；各检出目录的单独设置会保留。",
  "Default project grouping": "默认项目分组",
  "Remove checkout": "移除检出目录",
  "Select a project to remove one of its checkouts.": "选择一个项目后可移除其检出目录。",
  Danger: "危险操作",
  "Remove project": "移除项目",
  "Select a project to remove its entries and threads. Files on disk are not touched.":
    "选择一个项目后可移除其条目和任务；磁盘上的文件不会被删除。",
  "Choice value": "选项值",
  "Choice label": "选项名称",
  "Default choice": "默认选项",
  "Remove choice": "移除选项",
  Option: "选项",
  "Option id": "选项标识",
  "Option label": "选项名称",
  "Option type": "选项类型",
  "Custom…": "自定义…",
  Choices: "选项列表",
  Toggle: "开关",
  "Add choice": "添加选项",
  "Display name": "显示名称",
  "Options shown in the composer": "输入区中显示的选项",
  "Copy options from a built-in model": "从内置模型复制选项",
  "Copy from…": "复制自…",
  "No custom options. The composer uses the provider's default options.":
    "没有自定义选项，输入区将使用服务提供方的默认选项。",
  "Custom option": "自定义选项",
  value: "值",
  Label: "名称",
  Connect: "连接",
  Agents: "Agent",
  Projects: "项目",
  "Could not finish setup": "无法完成设置",
  "Your settings could not be saved. Try again.": "无法保存设置，请重试。",
  "Set up T3 Code": "设置 T3 Code",
  "Connect your computers": "连接你的设备",
  "Choose one or more computers. We’ll set up agents and projects on each.":
    "选择一台或多台设备，我们会在每台设备上设置 Agent 和项目。",
  "Computers to set up": "要设置的设备",
  "Add a computer": "添加设备",
  "Loading sign-in…": "正在加载登录状态…",
  "Loading computers…": "正在加载设备…",
  "No computers linked yet.": "尚未关联设备。",
  "Run this on each computer you want to connect.": "请在每台要连接的设备上运行此命令。",
  "Keep T3 Code running. Select the computers you want to set up above.":
    "请保持 T3 Code 运行，并在上方选择要设置的设备。",
  "Pairing failed.": "配对失败。",
  "Pairing link": "配对链接",
  "Need a pairing link?": "需要配对链接？",
  "Pairing...": "正在配对...",
  Pair: "配对",
  "Run this on the computer with your code.": "请在存放代码的设备上运行此命令。",
  "Start T3 Code first, or run": "请先启动 T3 Code，或运行",
  "Add --tailscale to use your tailnet.": "如需使用 Tailnet，请添加 --tailscale。",
  "Your agents": "你的 Agent",
  "Agents available on your selected computers.": "所选设备上可用的 Agent。",
  Computer: "设备",
  Ready: "就绪",
  "Checking...": "正在检查...",
  Install: "安装",

  "in this terminal.": "此终端中运行。",
  "Review the command, then press Enter to run it.": "请检查命令，然后按 Enter 运行。",
  "Could not open the setup terminal.": "无法打开设置终端。",
  "Preparing command...": "正在准备命令...",
  "Could not import thread history.": "无法导入任务历史。",
  "Your projects": "你的项目",
  "Choose your projects": "选择项目",
  "Import projects and conversations from your selected computers.": "从所选设备导入项目和对话。",
  "Looking for projects from Claude Code and Codex…": "正在查找 Claude Code 和 Codex 项目…",
  "Do not import projects": "不导入项目",
  "Looking for projects…": "正在查找项目…",
  "Could not check projects.": "无法检查项目。",
  "No existing Claude Code or Codex projects found.": "未找到已有的 Claude Code 或 Codex 项目。",
  "Scan limit reached. Some projects or conversations may be missing.":
    "已达到扫描上限，部分项目或对话可能未显示。",
  thread: "个任务",
  threads: "个任务",
  "Continue without the rest": "忽略其余内容并继续",
  "Importing…": "正在导入…",
  "Copy command": "复制命令",
  "All environments": "全部环境",
  "Some environments could not report usage": "部分环境无法报告用量",
  "Update required": "需要更新",
  "Scanning…": "正在扫描…",
  "Refreshing…": "正在刷新…",
  "No environments connected.": "尚未连接环境。",
  "Totals are partial while selected environments scan.": "所选环境扫描期间，总计数据可能不完整。",
  "Model prices": "模型价格",
  Offline: "离线",
  "Prices not loaded": "价格尚未加载",
  "Update server to edit prices": "请更新服务器后编辑价格",
  "Checking permissions…": "正在检查权限…",
  "Read-only access": "只读权限",
  "Environment removed": "环境已移除",
  "Enter a model ID.": "请输入模型 ID。",
  "This model already has a row. Edit its prices there.": "此模型已有价格行，请直接编辑该行。",
  "Custom model prices": "自定义模型价格",
  "Prices apply to all past and future usage on the environments you select.":
    "价格将应用于所选环境过去和未来的全部用量。",
  "Apply to": "应用到",
  "USD / million tokens": "美元 / 百万 Token",
  "Connect an environment to set model prices.": "请先连接环境，再设置模型价格。",
  "Select an environment to see and change its model prices.": "选择一个环境以查看和修改模型价格。",
  "Model ID": "模型 ID",
  Input: "输入",
  Output: "输出",
  "Cache read": "缓存读取",
  "Cache write": "缓存写入",
  "Add model price": "添加模型价格",
  "Some environment prices are unavailable.": "部分环境的价格不可用。",
  "No custom prices. Add a row to override automatic pricing.":
    "尚无自定义价格；添加一行即可覆盖自动定价。",
  "New model ID": "新模型 ID",
  "Automatic pricing after saving": "保存后使用自动定价",
  "Input rate": "输入价格",

  "Remove new model": "移除新模型",
  "Undo reset": "撤销重置",
  "Remove row": "移除价格行",
  "Reset to automatic": "恢复自动定价",
  "Blank cache rates use the input price. Enter 0 for free tokens.":
    "缓存价格留空时使用输入价格；免费 Token 请输入 0。",
  "Mixed cells keep each environment’s rate until you edit them.":
    "值不同的单元格会保留各环境原有价格，直到你进行编辑。",
  "Not saved": "未保存",
  Saved: "已保存",
  "Saving…": "正在保存…",
  "Discard pending changes": "放弃待处理的更改",
  "Discard changes": "放弃更改",
  "Retry failed saves": "重试保存失败的项目",
  "Save changes": "保存更改",
  "Use non-negative numbers for prices.": "价格必须是非负数。",
  "Show full error": "显示完整错误",
  "Show less": "收起",
  Actions: "操作",
  "Import scripts": "导入脚本",
  "Select a project to import actions from its checkout's t3.json.":
    "选择一个项目，从其检出目录的 t3.json 导入操作。",
  "Default actions": "默认操作",
  "Available in every inheriting checkout. Commands run in that checkout or its worktree.":
    "可用于所有继承此设置的检出目录；命令会在该检出目录或其工作树中运行。",
  "Add action": "添加操作",
  "Different actions across machines": "不同设备上的操作不一致",
  "Select a machine to edit its actions. Adding an action applies to all selected connected machines.":
    "选择一台设备以编辑其操作；新增操作会应用到所有选中的已连接设备。",
  "Loading pull request details…": "正在加载拉取请求详情…",
  "Could not read settings": "无法读取设置",
  "Still connecting": "仍在连接",
  "Your saved settings could not be loaded.": "无法加载已保存的设置。",
  "T3 Code could not confirm this workspace.": "T3 Code 无法确认此工作区。",

  Reload: "重新加载",
  "No actions configured.": "尚未配置操作。",
  setup: "设置",
  "preview · desktop only": "预览 · 仅桌面端",
  "This project has no checkout on this machine.": "此项目在当前设备上没有检出目录。",
  "No available machine, or another action change is saving.":
    "没有可用设备，或另一项操作更改仍在保存。",
  "Actions not saved": "操作未保存",
  "Failed to save project actions": "保存项目操作失败",
  "Setting not saved": "设置未保存",
  "Project model not saved": "项目模型未保存",
  Overridden: "已覆盖",
  On: "开",
  Off: "关",
  "automatic pull": "自动拉取",
  "Reset to inherited automatic pull setting": "恢复为继承的自动拉取设置",
  "Mixed defaults or overrides across selected checkouts.":
    "所选检出目录使用了不同的默认值或覆盖设置。",
  "Inherited from machine defaults. Controls agent access to the preview browser.":
    "继承设备默认设置，用于控制 Agent 对预览浏览器的访问权限。",
  "Overridden for this project. Applies when the agent session next starts.":
    "已为此项目覆盖，将在 Agent 会话下次启动时生效。",
  "project browser access": "项目浏览器访问权限",
  "Reset to inherited browser access": "恢复为继承的浏览器访问设置",
  "Project agent browser access": "项目 Agent 浏览器访问权限",
  "Inherit defaults": "继承默认设置",
};

/** Translate static UI copy supplied by upstream components as English source text. */
export function translateWebSource(locale: ResolvedAppLocale, source: string): string {
  if (locale === "zh-CN") {
    const translatedSource = ZH_CN_SOURCE_MESSAGES[source];
    if (translatedSource !== undefined) return translatedSource;
  }
  const key = WEB_MESSAGE_KEY_BY_ENGLISH_SOURCE.get(source);
  return key === undefined ? source : WEB_MESSAGES[locale][key];
}

export function translateWebMessage(
  locale: ResolvedAppLocale,
  key: WebMessageKey,
  values?: WebMessageValues,
): string {
  const message = WEB_MESSAGES[locale][key];
  if (!values) return message;

  return message.replace(/\{([A-Za-z][A-Za-z0-9]*)\}/g, (placeholder, name: string) => {
    const value = values[name];
    return value === undefined ? placeholder : String(value);
  });
}
